import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, Code, Terminal, Activity, TrendingUp, AlertTriangle, 
  Play, CheckCircle, Lock, ChevronRight, ChevronLeft, Menu, X, 
  Cpu, Database, Layers, Search, DollarSign, Filter, Target, 
  Zap, Clock, Shield, RefreshCw, BarChart2, Map, Server, Briefcase, ArrowRight,
  Ship, XCircle, FastForward, Globe, Wifi, Radio, Users, Scale, Lightbulb, 
  FileText, Rocket, GitMerge, History, Wrench, Brain, GraduationCap, BookOpen as BookOpenIcon, 
  TrendingUp as TrendingUpIcon, LineChart
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCourseProgress } from './useCourseProgress';

// Lesson 0: Market Mechanics for Quants
const LessonMarketMechanics = () => {
    const [calcResult, setCalcResult] = useState(null);

    return (
        <div className="space-y-12 max-w-4xl mx-auto p-6 pb-24">
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-2 text-blue-400">
                    <Activity size={20} />
                    <span className="font-mono text-xs tracking-wider uppercase">Module 0.3</span>
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">Market Mechanics for Quants</h1>
                <p className="text-xl text-gray-400">The hidden variables that determine if your code survives the live market.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div className="bg-blue-900/10 border border-blue-500/20 p-4 rounded-xl">
                        <strong className="text-blue-400 block mb-1">For Coders:</strong>
                        <p className="text-sm text-gray-300">If you don't know why a "Buy" order happens at the "Ask" price, read this.</p>
                    </div>
                    <div className="bg-purple-900/10 border border-purple-500/20 p-4 rounded-xl">
                        <strong className="text-purple-400 block mb-1">For Traders:</strong>
                        <p className="text-sm text-gray-300">If you think you know the basics, skim this to see how we define them mathematically.</p>
                    </div>
                </div>
            </div>

            {/* Lesson 1: The Instrument */}
            <section className="space-y-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="bg-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm">1</div>
                    The Instrument (Pairs & Ratios)
                </h2>
                
                <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
                    <p className="text-gray-300 mb-6">
                        In the retail world, we trade "Forex." In the Quant world, we trade <strong>ratios</strong>.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                        <div>
                            <h3 className="text-lg font-bold text-white mb-2">1.1 The Math of a Pair</h3>
                            <p className="text-sm text-gray-400 mb-4">
                                A currency pair like EURUSD is simply a fraction: <code className="text-blue-400">EUR / USD</code>.
                            </p>
                            <ul className="space-y-2 text-sm text-gray-300">
                                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div> <strong>Base (Top):</strong> The fixed unit (1 Lot = 100k EUR).</li>
                                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div> <strong>Quote (Bottom):</strong> The variable unit.</li>
                            </ul>
                            <div className="mt-4 p-3 bg-white/5 rounded-lg border-l-2 border-yellow-500">
                                <p className="text-xs text-gray-400">
                                    <strong className="text-yellow-400">Quant Rule:</strong> Buying EURUSD = Buying EUR + Selling USD (Simultaneously).
                                </p>
                            </div>
                        </div>

                        {/* Visual Ratio Diagram */}
                        <div className="bg-black/40 p-6 rounded-xl border border-white/5 flex flex-col items-center justify-center">
                             <div className="relative text-center">
                                <div className="text-3xl font-bold text-blue-400 mb-2">EUR</div>
                                <div className="h-0.5 w-32 bg-gray-500 mx-auto my-2"></div>
                                <div className="text-3xl font-bold text-green-400 mt-2">USD</div>
                                
                                {/* Movement Arrows */}
                                <div className="absolute -right-16 top-0 bottom-0 flex flex-col justify-center text-xs text-gray-500 space-y-4">
                                     <div className="flex items-center">
                                        <ArrowRight size={12} className="mr-1"/> Chart UP
                                     </div>
                                     <div className="flex items-center">
                                        <ArrowRight size={12} className="mr-1"/> Chart DOWN
                                     </div>
                                </div>
                             </div>
                        </div>
                    </div>
                </div>

                {/* Correlation */}
                <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
                    <h3 className="text-lg font-bold text-white mb-4">1.2 Correlation & Redundancy</h3>
                    <div className="flex flex-col md:flex-row gap-6 items-center">
                        <div className="flex-1">
                             <p className="text-sm text-gray-300 mb-4">
                                A common mistake is "stacking risk". <br/>
                                Buying <strong>EURUSD</strong> and Selling <strong>USDCHF</strong> is effectively the same bet.
                            </p>
                            <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                                <p className="text-xs text-red-300">
                                    <strong>The Trap:</strong> If you have a bot on both, you aren't diversified. You are doubling your bet on the USD.
                                </p>
                            </div>
                            <div className="mt-2 text-xs text-gray-500 font-mono">
                                &gt; Always check the correlation matrix. If &gt; 0.8, pick one.
                            </div>
                        </div>
                        <div className="flex-1 flex justify-center">
                            {/* Simple Venn Diagram-ish Visual */}
                            <svg width="200" height="120" viewBox="0 0 200 120">
                                <circle cx="70" cy="60" r="50" fill="#3b82f6" fillOpacity="0.3" stroke="#3b82f6" strokeWidth="2" />
                                <text x="50" y="65" fill="white" fontSize="10" fontWeight="bold">EURUSD</text>
                                
                                <circle cx="130" cy="60" r="50" fill="#ef4444" fillOpacity="0.3" stroke="#ef4444" strokeWidth="2" />
                                <text x="150" y="65" fill="white" fontSize="10" fontWeight="bold">USDCHF</text>
                                
                                <path d="M100,20 Q100,100 100,100" stroke="white" strokeWidth="1" strokeDasharray="2,2" />
                                <text x="100" y="115" fill="white" fontSize="9" textAnchor="middle">USD Exposure (Overlap)</text>
                            </svg>
                        </div>
                    </div>
                </div>

                {/* The Point vs. Pip (Quant Nuance) */}
                <div className="bg-yellow-900/10 border-l-4 border-yellow-500 p-6 rounded-r-xl">
                    <h3 className="text-lg font-bold text-yellow-400 mb-2 flex items-center gap-2">
                        <AlertTriangle size={18} /> 1.3 The "Point" vs. "Pip" Trap
                    </h3>
                    <p className="text-gray-300 text-sm mb-4">
                        This is the <strong>#1 Bug</strong> in new Algorithmic Trading code.
                        Humans speak in "Pips". Robots speak in "Points". Only the Point exists in raw memory.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                        <div className="space-y-4 font-mono text-sm">
                            <div className="bg-black/50 p-3 rounded border border-gray-700">
                                <div className="text-gray-500 text-xs uppercase mb-1">Human View (Pip)</div>
                                <div className="text-white text-lg">1.050<span className="text-green-400 font-bold">1</span>0</div>
                                <div className="text-gray-500 text-xs mt-1">4th Decimal Place</div>
                            </div>
                            <div className="bg-black/50 p-3 rounded border border-gray-700">
                                <div className="text-gray-500 text-xs uppercase mb-1">Robot View (Point)</div>
                                <div className="text-white text-lg">1.0501<span className="text-blue-400 font-bold">0</span></div>
                                <div className="text-gray-500 text-xs mt-1">5th Decimal Place (Raw Tick)</div>
                            </div>
                        </div>

                        <div className="bg-black/30 p-4 rounded-lg">
                            <strong className="text-yellow-400 block mb-2 text-xs uppercase">The Conversion Logic</strong>
                            <code className="block text-green-300 text-xs mb-2">
                                1 Pip = 10 Points
                            </code>
                            <div className="text-gray-400 text-xs italic">
                                If you tell your bot <code className="text-white bg-gray-800 px-1 rounded">StopLoss = 50</code>, it thinks you mean <strong>50 Points</strong> (5 Pips). <br/>
                                You will be stopped out instantly.
                                <br/><br/>
                                <span className="text-white">Correct Code:</span> <code className="text-blue-400">StopLoss = 50 * 10</code>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Lesson 2: Cost of Business */}
            <section className="space-y-6">
                 <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="bg-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm">2</div>
                    The Cost of Business (Spread, Comm, Swap)
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Spread */}
                    <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
                        <h3 className="font-bold text-white mb-2 flex items-center gap-2"><Filter size={16} className="text-orange-400"/> 2.1 Spread & Commission</h3>
                        <p className="text-sm text-gray-400 mb-4">The "Entry Tax".</p>
                        
                        <div className="space-y-3 text-sm">
                            <div className="p-3 rounded bg-black/20 border border-white/5">
                                <strong className="text-gray-300 block">Standard Account</strong>
                                <span className="text-xs text-gray-500">Zero Comm, High Spread (2.0 pips). Bad for Quants (Data is manipulated).</span>
                            </div>
                            <div className="p-3 rounded bg-green-900/10 border border-green-500/20">
                                <strong className="text-green-400 block">ECN / Raw Account</strong>
                                <span className="text-xs text-gray-300">Real Spread (0.1 pips) + Fixed Comm ($7/lot). Quant Standard (Clean data + predictable cost).</span>
                            </div>
                        </div>
                    </div>

                    {/* Swap */}
                    <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
                         <h3 className="font-bold text-white mb-2 flex items-center gap-2"><Clock size={16} className="text-purple-400"/> 2.2 Swap (Holding Tax)</h3>
                        <p className="text-sm text-gray-400 mb-4">Interest rate differential.</p>
                        
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between items-center p-2 rounded bg-green-900/10 border border-green-500/10">
                                <span className="text-gray-300">Positive Swap</span>
                                <span className="text-green-400 font-bold">+ Paid to hold</span>
                            </div>
                             <div className="flex justify-between items-center p-2 rounded bg-red-900/10 border border-red-500/10">
                                <span className="text-gray-300">Negative Swap</span>
                                <span className="text-red-400 font-bold">- Pay to hold</span>
                            </div>
                        </div>

                         <div className="mt-4 p-3 bg-black/40 rounded border border-gray-700">
                             <h4 className="text-xs font-bold text-gray-500 mb-1">MQL5 Code Snippet</h4>
                             <pre className="text-[10px] font-mono text-blue-300 overflow-x-auto">
{`double swap = SymbolInfoDouble(_Symbol, SYMBOL_SWAP_LONG);
if(swap < -5.0) Print("Warning: Negative Carry");`}
                             </pre>
                        </div>
                    </div>
                </div>
            </section>

             {/* Lesson 3: The Order Book */}
             <section className="space-y-6">
                 <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="bg-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm">3</div>
                    The Order Book (Bid vs. Ask)
                </h2>
                
                <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
                    <div className="flex flex-col md:flex-row gap-8 items-center">
                        <div className="flex-1 space-y-4">
                            <p className="text-gray-300">
                                Charts usually show the <strong>Bid</strong> price. But you don't always trade at the Bid.
                            </p>
                            <div className="p-4 bg-black/40 rounded-xl border-l-4 border-blue-500">
                                <h4 className="font-bold text-white mb-2">The Golden Rule</h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="block text-gray-500 text-xs uppercase">To Open</span>
                                        <div className="text-green-400">Buy @ Ask</div>
                                        <div className="text-red-400">Sell @ Bid</div>
                                    </div>
                                    <div>
                                        <span className="block text-gray-500 text-xs uppercase">To Close</span>
                                        <div className="text-gray-300">Close Buy @ Bid</div>
                                        <div className="text-gray-300">Close Sell @ Ask</div>
                                    </div>
                                </div>
                            </div>
                             <p className="text-xs text-gray-400 italic">
                                <strong>"The Missing Wick":</strong> If your Sell Limit was at the exact high of the candle but didn't trigger, it's because the <strong>Ask</strong> line (visible only to buyers) never reached it.
                             </p>
                        </div>

                        {/* Order Book Visual */}
                        <div className="flex-1 w-full max-w-sm">
                            <div className="bg-black/40 p-4 rounded-xl border border-white/10 relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-2 text-[10px] text-gray-600 font-mono">LIVE</div>
                                
                                {/* Ask Side */}
                                <div className="space-y-1 mb-2 border-b border-gray-700 pb-2">
                                     <div className="flex justify-between text-xs text-red-500 opacity-50"><span className="md:hidden">Vol: </span>1.5M <span>1.0503</span></div>
                                     <div className="flex justify-between text-xs text-red-500 opacity-70"><span className="md:hidden">Vol: </span>2.2M <span>1.0502</span></div>
                                     <div className="flex justify-between text-sm font-bold text-red-400 bg-red-900/10 p-1 rounded">
                                        <span>ASK (Buy Here)</span>
                                        <span>1.0501</span>
                                     </div>
                                </div>

                                {/* Spread Gap */}
                                <div className="h-6 flex items-center justify-center text-[10px] text-gray-500 italic">
                                    Spread: 1 Pip
                                </div>

                                {/* Bid Side */}
                                <div className="space-y-1 mt-2 border-t border-gray-700 pt-2">
                                     <div className="flex justify-between text-sm font-bold text-blue-400 bg-blue-900/10 p-1 rounded">
                                        <span>BID (Sell Here)</span>
                                        <span>1.0500</span>
                                     </div>
                                     <div className="flex justify-between text-xs text-blue-500 opacity-70"><span className="md:hidden">Vol: </span>3.0M <span>1.0499</span></div>
                                     <div className="flex justify-between text-xs text-blue-500 opacity-50"><span className="md:hidden">Vol: </span>1.2M <span>1.0498</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
             </section>

             {/* Lesson 4: Data Granularity */}
             <section className="space-y-6">
                 <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="bg-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm">4</div>
                    Data Granularity (Candle vs. Tick)
                </h2>
                
                <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
                    <p className="text-gray-300 mb-6">
                        A candle is a summary. It deletes data. Inside a 1-minute candle, price might have whipped up and down 50 times.
                    </p>
                    
                    {/* Visual Comparison */}
                    <div className="h-48 w-full bg-black/40 rounded-xl relative overflow-hidden mb-4 border border-white/5">
                         {/* Background Grid */}
                         <div className="absolute inset-0 grid grid-cols-6 grid-rows-4 opacity-10">
                            {[...Array(24)].map((_,i) => <div key={i} className="border border-gray-500"></div>)}
                         </div>

                         <div className="absolute inset-0 flex items-center justify-center">
                            {/* The Candle */}
                            <div className="relative w-32 h-32 mr-12 opacity-80 group cursor-pointer hover:opacity-100 transition-opacity">
                                <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-green-500 -translate-x-1/2"></div>
                                <div className="absolute left-[20%] right-[20%] top-[20%] bottom-[30%] bg-green-500/20 border border-green-500"></div>
                                <div className="absolute -bottom-8 left-0 right-0 text-center text-xs text-green-400">1 Min Candle</div>
                            </div>

                            {/* The Ticks */}
                            <div className="relative w-64 h-32">
                                <svg width="100%" height="100%" viewBox="0 0 200 100" className="overflow-visible">
                                    <path 
                                        d="M0,70 L10,65 L20,80 L30,60 L40,50 L50,55 L60,40 L70,45 L80,20 L90,25 L100,10 L110,15 L120,30 L130,25 L140,50 L150,45 L160,60 L170,55 L180,70 L190,65 L200,80" 
                                        fill="none" 
                                        stroke="#3b82f6" 
                                        strokeWidth="1.5"
                                        className="drop-shadow-[0_0_2px_rgba(59,130,246,0.5)]"
                                    />
                                </svg>
                                <div className="absolute -bottom-8 left-0 right-0 text-center text-xs text-blue-400">Real Stats (50 Ticks)</div>
                            </div>
                         </div>
                    </div>

                    <div className="bg-yellow-900/10 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                        <strong className="text-yellow-400 block mb-1">Strategy Implication</strong>
                        <p className="text-sm text-gray-300">
                             Don't run heavy calculations on every Tick (`OnTick`). It slows down your bot. <br/>
                             Most strategies only need to check logic when a candle closes (`isNewBar`).
                        </p>
                    </div>
                </div>
             </section>

             {/* Lesson 5: Market Sessions */}
             <section className="space-y-6">
                 <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="bg-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm">5</div>
                    Volatility Profiles (Time is Price)
                </h2>
                <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
                    <div className="space-y-4">
                        <div className="flex flex-col gap-2">
                             <div className="flex items-center gap-4 p-3 rounded bg-blue-900/10 border border-blue-500/10">
                                <span className="font-bold text-blue-300 w-24">Asian</span>
                                <span className="text-sm text-gray-400">Quiet. Good for scalping ranges.</span>
                             </div>
                             <div className="flex items-center gap-4 p-3 rounded bg-purple-900/10 border border-purple-500/10">
                                <span className="font-bold text-purple-300 w-24">London/NY</span>
                                <span className="text-sm text-gray-400">Chaos. Max volume. Good for Trends.</span>
                             </div>
                              <div className="flex items-center gap-4 p-3 rounded bg-red-900/10 border border-red-500/10">
                                <span className="font-bold text-red-300 w-24">Rollover</span>
                                <span className="text-sm text-gray-400">1 Hour a day (5pm NY). Spreads widen 50x. <strong>Do Not Trade.</strong></span>
                             </div>
                        </div>
                    </div>
                </div>
             </section>

             {/* Check-In Exercise */}
             <div className="border-t border-white/10 pt-8 mt-12">
                 <h3 className="text-2xl font-bold text-white mb-6">Interactive Check-In</h3>
                 <div className="bg-black/30 p-6 rounded-xl border border-white/10">
                    <p className="text-gray-300 mb-4">
                        <strong>Scenario:</strong> You want to buy 1.0 Lot of EURUSD. <br/>
                        Commission: $7.00/lot. Spread: 2 Pips ($20). Swap: -$5.00/night.
                    </p>
                    <p className="text-sm text-gray-400 mb-4">
                        How much does it cost you to open this trade and hold it for 1 night?
                    </p>
                    
                    <div className="flex gap-4">
                        <input 
                            type="number" 
                            placeholder="Enter amount ($)"
                            className="bg-gray-800 border border-gray-600 rounded px-4 py-2 text-white outline-none focus:border-blue-500"
                            onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                if(val === 32) setCalcResult("correct");
                                else setCalcResult("incorrect");
                            }}
                        />
                         {calcResult === 'correct' && <div className="flex items-center text-green-400 font-bold"><CheckCircle size={20} className="mr-2"/> Correct! ($7 + $20 + $5)</div>}
                         {calcResult === 'incorrect' && <div className="flex items-center text-red-400"><XCircle size={20} className="mr-2"/> Try Again</div>}
                    </div>
                 </div>
             </div>

        </div>
    );
};

// --- Visual Components for Module 0.2 ---

const MarketComparativeVisual = () => {
  return (
    <div className="bg-black/40 p-6 rounded-xl border border-white/10 my-8">
      <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6 text-center">The Quant's Paradox: Volume vs. Crowd</h4>
      
      <div className="grid grid-cols-2 gap-8">
        {/* Forex Side */}
        <div className="flex flex-col items-center">
            <div className="relative group">
                <div className="absolute inset-0 bg-green-500/20 blur-xl rounded-full"></div>
                <div className="relative w-32 h-32 rounded-full border-4 border-green-500 flex items-center justify-center bg-gray-900">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-white">$7.5T</div>
                        <div className="text-[10px] text-green-400 uppercase">Daily Volume</div>
                    </div>
                </div>
            </div>
            
            <div className="mt-4 flex flex-col items-center gap-2">
                <div className="flex gap-1 text-gray-500">
                    <Users size={16} />
                </div>
                <span className="text-xs text-center text-gray-400">
                    Fewer Retail Traders <br/> 
                    <span className="text-green-500">(Dominated by Banks/Algos)</span>
                </span>
            </div>
            <h5 className="mt-4 font-bold text-green-400 text-lg">FOREX</h5>
        </div>

        {/* Stock Side */}
        <div className="flex flex-col items-center">
             <div className="relative group">
                <div className="relative w-12 h-12 rounded-full border-2 border-blue-500 flex items-center justify-center bg-gray-900 mt-10 mb-10">
                    <div className="text-center">
                        <div className="text-xs font-bold text-white">$0.2T</div>
                    </div>
                </div>
            </div>

            <div className="mt-4 flex flex-col items-center gap-2">
                <div className="flex gap-1 text-blue-400">
                    <Users size={16} /><Users size={16} /><Users size={16} />
                    <Users size={16} /><Users size={16} />
                </div>
                <span className="text-xs text-center text-gray-400">
                    Massive Retail Crowd <br/>
                    <span className="text-blue-500">(Investors & Retirement)</span>
                </span>
            </div>
            <h5 className="mt-4 font-bold text-blue-400 text-lg">STOCKS</h5>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-white/5 text-center text-xs text-gray-500 italic">
        "Follow the money (Forex), or follow the crowd (Stocks)?"
      </div>
    </div>
  );
};

const RiskSpectrumVisual = () => {
    return (
        <div className="my-8">
            <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">The Risk/Reward Spectrum</h4>
            <div className="relative h-16 bg-gray-900/50 rounded-xl border border-white/10 flex items-center px-6 overflow-hidden">
                {/* Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-green-900/10 via-yellow-900/10 to-purple-900/20"></div>
                
                {/* Line */}
                <div className="absolute left-6 right-6 h-1 bg-gradient-to-r from-gray-600 to-gray-600 top-1/2 -translate-y-1/2 z-0"></div>

                {/* Nodes */}
                <div className="w-full flex justify-between relative z-10">
                    {/* Bonds */}
                    <div className="flex flex-col items-center gap-2 group cursor-default">
                        <div className="w-4 h-4 rounded-full bg-gray-600 ring-4 ring-gray-900 group-hover:bg-gray-400 transition-colors"></div>
                        <span className="text-xs font-bold text-gray-500 uppercase mt-4">Bonds</span>
                    </div>

                    {/* Stocks */}
                    <div className="flex flex-col items-center gap-2 group cursor-default">
                        <div className="w-4 h-4 rounded-full bg-blue-600 ring-4 ring-gray-900 group-hover:scale-125 transition-transform shadow-[0_0_10px_rgba(37,99,235,0.5)]"></div>
                        <span className="text-xs font-bold text-blue-400 uppercase mt-4">Stocks</span>
                    </div>

                    {/* Forex */}
                    <div className="flex flex-col items-center gap-2 group cursor-default">
                        <div className="w-4 h-4 rounded-full bg-green-500 ring-4 ring-gray-900 group-hover:scale-125 transition-transform shadow-[0_0_15px_rgba(34,197,94,0.5)]"></div>
                        <span className="text-xs font-bold text-green-400 uppercase mt-4">Forex</span>
                    </div>

                    {/* Crypto */}
                    <div className="flex flex-col items-center gap-2 group cursor-default">
                        <div className="w-4 h-4 rounded-full bg-purple-500 ring-4 ring-gray-900 animate-pulse shadow-[0_0_20px_rgba(168,85,247,0.6)]"></div>
                        <span className="text-xs font-bold text-purple-400 uppercase mt-4">Crypto</span>
                    </div>
                </div>
            </div>
             <div className="flex justify-between text-[10px] text-gray-500 mt-2 px-2">
                <span>Low Risk / Low Return</span>
                <span>Max Risk / Max Return</span>
            </div>
        </div>
    );
};

// Lesson 0.2: The Arena (Choosing Your Asset Class)
const LessonTheArena = () => {
    return (
        <div className="space-y-12 max-w-5xl mx-auto p-6 pb-24">
            {/* Header */}
            <div className="mb-12">
                <div className="flex items-center gap-2 mb-2 text-indigo-400">
                    <Target size={20} />
                    <span className="font-mono text-xs tracking-wider uppercase">Module 0.1</span>
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">The Arena: Choose Your Battlefield</h1>
                <p className="text-xl text-gray-400">
                    Financial markets are platforms where various assets are bought and sold. Before you write a single line of code, you must decide where you will fight.
                </p>
            </div>

            {/* The Main Types of Financial Markets */}
            <section className="space-y-8">
                <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-indigo-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">1</div>
                    The Main Types of Financial Markets
                </h2>

                {/* Stock Market */}
                <div className="bg-blue-900/20 p-6 rounded-xl border border-blue-500/20 relative overflow-hidden group hover:border-blue-500/40 transition-all">
                    <div className="absolute right-0 top-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                        <TrendingUp size={100} className="text-blue-500" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-400 mb-3 flex items-center gap-2">
                        <BarChart2 size={24} />
                        1. Stock Market (Equity Market)
                    </h3>
                    <p className="text-gray-300 mb-3 relative z-10"><strong>What it is:</strong> Trading shares of ownership in public companies.</p>
                    <p className="text-gray-300 mb-3 relative z-10"><strong className="text-blue-300">Examples:</strong> Apple (AAPL), Tesla (TSLA), NVIDIA (NVDA), GTCO (Guaranty Trust).</p>
                    <p className="text-gray-300 relative z-10"><strong className="text-blue-300">Primary Drivers:</strong> Company earnings, quarterly reports, and sector health.</p>
                </div>

                {/* Forex Market */}
                <div className="bg-green-900/20 p-6 rounded-xl border border-green-500/20 relative overflow-hidden group hover:border-green-500/40 transition-all">
                    <div className="absolute right-0 top-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                        <RefreshCw size={100} className="text-green-500" />
                    </div>
                    <h3 className="text-xl font-bold text-green-400 mb-3 flex items-center gap-2">
                        <Globe size={24} />
                        2. Forex Market (Foreign Exchange)
                    </h3>
                    <p className="text-gray-300 mb-3 relative z-10"><strong>What it is:</strong> The decentralized global network where currencies are traded in pairs.</p>
                    <p className="text-gray-300 mb-3 relative z-10"><strong className="text-green-300">Examples:</strong> EURUSD (Euro vs Dollar), GBPJPY (Pound vs Yen), XAUUSD (Gold vs Dollar).</p>
                    <p className="text-gray-300 relative z-10"><strong className="text-green-300">Primary Drivers:</strong> Interest rates (Central Banks), inflation data (CPI), and geopolitics.</p>
                </div>

                {/* Commodities Market */}
                <div className="bg-yellow-900/20 p-6 rounded-xl border border-yellow-500/20 relative overflow-hidden group hover:border-yellow-500/40 transition-all">
                     <div className="absolute right-0 top-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Ship size={100} className="text-yellow-500" />
                    </div>
                    <h3 className="text-xl font-bold text-yellow-400 mb-3 flex items-center gap-2">
                        <Layers size={24} />
                        3. Commodities Market
                    </h3>
                    <p className="text-gray-300 mb-3 relative z-10"><strong>What it is:</strong> Trading raw materials and agricultural products.</p>
                    <div className="text-gray-300 mb-3 relative z-10">
                        <strong className="text-yellow-300">Examples:</strong>
                        <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                            <li><em>Hard Commodities:</em> Gold, Silver, Crude Oil (WTI/Brent).</li>
                            <li><em>Soft Commodities:</em> Coffee, Wheat, Corn.</li>
                        </ul>
                    </div>
                    <p className="text-gray-300 relative z-10"><strong className="text-yellow-300">Primary Drivers:</strong> Supply/Demand shocks (e.g., a drought raises Wheat prices) and global risk.</p>
                </div>

                {/* Cryptocurrency Market */}
                <div className="bg-purple-900/20 p-6 rounded-xl border border-purple-500/20 relative overflow-hidden group hover:border-purple-500/40 transition-all">
                    <div className="absolute right-0 top-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Cpu size={100} className="text-purple-500" />
                    </div>
                    <h3 className="text-xl font-bold text-purple-400 mb-3 flex items-center gap-2">
                        <Cpu size={24} />
                        4. Cryptocurrency Market
                    </h3>
                    <p className="text-gray-300 mb-3 relative z-10"><strong>What it is:</strong> 24/7 decentralized trading of digital assets.</p>
                    <p className="text-gray-300 mb-3 relative z-10"><strong className="text-purple-300">Examples:</strong> Bitcoin (BTC), Ethereum (ETH), Solana (SOL).</p>
                    <p className="text-gray-300 relative z-10"><strong className="text-purple-300">Primary Drivers:</strong> Network adoption, regulatory news, and tech sentiment.</p>
                </div>

                {/* Bond Market */}
                <div className="bg-gray-700/20 p-6 rounded-xl border border-gray-500/20 relative overflow-hidden group hover:border-gray-500/40 transition-all">
                    <div className="absolute right-0 top-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Scale size={100} className="text-gray-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-300 mb-3 flex items-center gap-2">
                         <Shield size={24} />
                        5. Bond Market (Fixed Income)
                    </h3>
                    <p className="text-gray-300 mb-3 relative z-10"><strong>What it is:</strong> Loaning money to governments or companies in exchange for interest payments.</p>
                    <p className="text-gray-300 mb-3 relative z-10"><strong className="text-gray-400">Examples:</strong> US 10-Year Treasury Note, Eurobonds.</p>
                    <p className="text-gray-300 relative z-10"><strong className="text-gray-400">Primary Drivers:</strong> Central Bank interest rate policies.</p>
                </div>

                <RiskSpectrumVisual />
            </section>

            {/* Which Markets Do Retail Traders Use? */}
            <section className="space-y-8 mt-16">
                <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-indigo-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">2</div>
                    Which Markets Do Retail Traders Use?
                </h2>
                <p className="text-gray-400 text-lg italic">Where do most individual traders actually put their money?</p>
                
                <MarketComparativeVisual />

                {/* PRIMARY: Stock Market */}
                <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/10 p-8 rounded-2xl border border-blue-500/30">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="px-3 py-1 bg-blue-600 text-white text-sm font-bold rounded-full">PRIMARY</div>
                        <h3 className="text-2xl font-bold text-blue-300">Stock Market (The Investor's Choice)</h3>
                    </div>
                    <div className="space-y-3 text-gray-300">
                        <p><strong className="text-white">Status:</strong> The most popular destination for long-term wealth.</p>
                        <p><strong className="text-white">Why:</strong> It is intuitive ("I use an iPhone, so I buy Apple stock") and accessible via apps like Robinhood or Bamboo.</p>
                        <p><strong className="text-white">Stat:</strong> Stocks make up ~57% of the average retail portfolio.</p>
                    </div>
                </div>

                {/* SPECIALIZED: Forex Market */}
                <div className="bg-gradient-to-br from-green-900/30 to-green-800/10 p-8 rounded-2xl border border-green-500/30">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="px-3 py-1 bg-green-600 text-white text-sm font-bold rounded-full">SPECIALIZED</div>
                        <h3 className="text-2xl font-bold text-green-300">Forex Market (The Active Trader's Choice)</h3>
                    </div>
                    <div className="space-y-3 text-gray-300">
                        <p><strong className="text-white">Status:</strong> While fewer <em>people</em> trade Forex than Stocks, it is the #1 choice for <strong>Algorithmic and High-Frequency Traders</strong>.</p>
                        <div>
                            <p className="font-bold text-white mb-2">Why:</p>
                            <ul className="list-disc list-inside space-y-1 ml-4">
                                <li><strong>Leverage:</strong> You can control large positions with small capital (e.g., 1:100 leverage).</li>
                                <li><strong>Hours:</strong> Open 24/5 (unlike stocks which close at 4 PM).</li>
                                <li><strong>Volume:</strong> Highly liquid; you can buy/sell millions instantly without moving the price.</li>
                            </ul>
                        </div>
                        <div className="bg-green-900/30 p-4 rounded-lg border-l-4 border-green-400">
                            <p className="text-sm"><strong className="text-green-300">Note for Quants:</strong> This is where most retail trading <em>bots</em> operate due to better API access (MetaTrader).</p>
                        </div>
                    </div>
                </div>

                {/* GROWING: Cryptocurrency Market */}
                <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/10 p-8 rounded-2xl border border-purple-500/30">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="px-3 py-1 bg-purple-600 text-white text-sm font-bold rounded-full">GROWING</div>
                        <h3 className="text-2xl font-bold text-purple-300">Cryptocurrency Market (The Risk Taker's Choice)</h3>
                    </div>
                    <div className="space-y-3 text-gray-300">
                        <p><strong className="text-white">Status:</strong> Rapidly expanding, especially among Gen Z and Millennials.</p>
                        <p><strong className="text-white">Why:</strong> 24/7 access and high volatility (big moves = big potential profit/loss).</p>
                        <p><strong className="text-white">Overlap:</strong> Many Forex traders now trade Crypto because the technical analysis is similar.</p>
                    </div>
                </div>

                {/* MINIMAL: Money Markets & Interbank */}
                <div className="bg-gradient-to-br from-gray-800/30 to-gray-700/10 p-8 rounded-2xl border border-gray-500/30">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="px-3 py-1 bg-gray-600 text-white text-sm font-bold rounded-full">MINIMAL</div>
                        <h3 className="text-2xl font-bold text-gray-300">Money Markets & Interbank</h3>
                    </div>
                    <div className="space-y-3 text-gray-300">
                        <p><strong className="text-white">Status:</strong> Mostly for Banks and Hedge Funds.</p>
                        <p><strong className="text-white">Why:</strong> Low volatility. Retail traders generally only use "Money Market Funds" as a safe place to park cash, not to trade for profit.</p>
                    </div>
                </div>
            </section>

            {/* Summary Table: Visual Cards Grid */}
            <section className="space-y-8 mt-16">
                <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-indigo-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">3</div>
                    Summary: Which Market Should You Trade?
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Stocks */}
                    <div className="bg-gradient-to-br from-blue-900/20 to-gray-900/50 p-6 rounded-2xl border border-blue-500/20 hover:border-blue-500/50 transition-all hover:-translate-y-1 group">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-3 bg-blue-900/30 text-blue-400 rounded-xl group-hover:scale-110 transition-transform">
                                <TrendingUp size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-white">Stocks</h3>
                        </div>
                        
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Best For</div>
                                <div className="text-gray-200 font-medium">Long-term investing & Swing trading</div>
                            </div>
                            
                            <div className="space-y-2">
                                <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                                    <span className="text-gray-500">Capital Needed</span>
                                    <span className="text-yellow-400">Low - Medium</span>
                                </div>
                                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-yellow-400 w-1/3"></div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                                    <span className="text-gray-500">Risk Level</span>
                                    <span className="text-yellow-400">Medium</span>
                                </div>
                                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-yellow-400 w-1/2"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Forex */}
                    <div className="bg-gradient-to-br from-green-900/20 to-gray-900/50 p-6 rounded-2xl border border-green-500/20 hover:border-green-500/50 transition-all hover:-translate-y-1 group">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-3 bg-green-900/30 text-green-400 rounded-xl group-hover:scale-110 transition-transform">
                                <Globe size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-white">Forex</h3>
                        </div>
                        
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Best For</div>
                                <div className="text-gray-200 font-medium">Algo Trading (Bots) & Day Trading</div>
                            </div>
                            
                            <div className="space-y-2">
                                <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                                    <span className="text-gray-500">Capital Needed</span>
                                    <span className="text-green-400">Low (Leverage)</span>
                                </div>
                                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-green-400 w-1/4"></div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                                    <span className="text-gray-500">Risk Level</span>
                                    <span className="text-red-400">High</span>
                                </div>
                                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-red-400 w-3/4"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Crypto */}
                    <div className="bg-gradient-to-br from-purple-900/20 to-gray-900/50 p-6 rounded-2xl border border-purple-500/20 hover:border-purple-500/50 transition-all hover:-translate-y-1 group">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-3 bg-purple-900/30 text-purple-400 rounded-xl group-hover:scale-110 transition-transform">
                                <Cpu size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-white">Crypto</h3>
                        </div>
                        
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Best For</div>
                                <div className="text-gray-200 font-medium">24/7 Trading & High Volatility Plays</div>
                            </div>
                            
                            <div className="space-y-2">
                                <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                                    <span className="text-gray-500">Capital Needed</span>
                                    <span className="text-green-400">Low</span>
                                </div>
                                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-green-400 w-1/4"></div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                                    <span className="text-gray-500">Risk Level</span>
                                    <span className="text-purple-500">Very High</span>
                                </div>
                                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-purple-500 w-full animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Futures */}
                    <div className="bg-gradient-to-br from-orange-900/20 to-gray-900/50 p-6 rounded-2xl border border-orange-500/20 hover:border-orange-500/50 transition-all hover:-translate-y-1 group">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-3 bg-orange-900/30 text-orange-400 rounded-xl group-hover:scale-110 transition-transform">
                                <Zap size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-white">Futures</h3>
                        </div>
                        
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider">Best For</div>
                                <div className="text-gray-200 font-medium">Professional Speculation (Commodities)</div>
                            </div>
                            
                            <div className="space-y-2">
                                <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                                    <span className="text-gray-500">Capital Needed</span>
                                    <span className="text-red-400">High</span>
                                </div>
                                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-red-400 w-3/4"></div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                                    <span className="text-gray-500">Risk Level</span>
                                    <span className="text-red-400">High</span>
                                </div>
                                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-red-400 w-3/4"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final Verdict */}
            <div className="mt-12 bg-gradient-to-br from-indigo-900/30 to-purple-900/20 p-8 rounded-2xl border border-indigo-500/30">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <Target className="text-indigo-400" />
                    Final Verdict
                </h3>
                <div className="space-y-4 text-gray-300">
                    <p>
                        <strong className="text-white">For Beginners with Low Capital:</strong> Start with <strong className="text-green-400">Crypto (Spot)</strong> or <strong className="text-green-400">Forex (Micro Lots)</strong>. The barrier to entry is lowest here.
                    </p>
                    <p>
                        <strong className="text-white">For Long-Term Wealth:</strong> <strong className="text-blue-400">Stocks</strong> are the gold standard. They move slower but carry less risk of total ruin.
                    </p>
                    <p>
                        <strong className="text-white">For Professionals:</strong> <strong className="text-orange-400">Futures</strong> offer the cleanest order flow and centralization, but require significant capital to handle the contract sizes.
                    </p>
                </div>
            </div>
        </div>
    );
};

// --- Visual Components for Module 0.3 ---

const HierarchyPyramidVisual = () => {
    return (
        <div className="bg-black/40 p-8 rounded-xl border border-white/10 my-8 flex justify-center">
            <div className="relative w-full max-w-lg h-64">
                {/* Connection Lines (Background) */}
                <div className="absolute inset-0 flex flex-col items-center justify-center opacity-30">
                    <div className="w-1 h-full bg-gradient-to-b from-purple-500 via-green-500 to-blue-500"></div>
                </div>

                {/* Layer 3: Strategies (Top) */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 text-center group z-30">
                     <div className="bg-gradient-to-r from-purple-900 via-purple-800 to-purple-900 border border-purple-500/50 text-purple-100 p-2 rounded-lg shadow-[0_0_20px_rgba(168,85,247,0.3)] animate-bounce-slow">
                        <div className="text-xs font-bold text-purple-300 uppercase tracking-widest mb-1">Layer 3</div>
                        <div className="font-bold flex items-center justify-center gap-2">
                             <Target size={16} /> HOW (Strategy)
                        </div>
                    </div>
                </div>

                {/* Layer 2: Instruments (Middle) */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 text-center group z-20">
                     <div className="bg-gradient-to-r from-green-900 via-green-800 to-green-900 border border-green-500/50 text-green-100 p-3 rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.3)]">
                        <div className="text-xs font-bold text-green-300 uppercase tracking-widest mb-1">Layer 2</div>
                         <div className="font-bold flex items-center justify-center gap-2">
                             <Briefcase size={16} /> WHAT (Instrument)
                        </div>
                    </div>
                    {/* Down Arrows */}
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-gray-500">↓</div>
                </div>

                {/* Layer 1: Markets (Base) */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-80 text-center group z-10">
                     <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 border border-blue-500/50 text-blue-100 p-4 rounded-lg shadow-[0_0_20px_rgba(59,130,246,0.3)]">
                        <div className="text-xs font-bold text-blue-300 uppercase tracking-widest mb-1">Layer 1</div>
                         <div className="font-bold flex items-center justify-center gap-2">
                             <Map size={16} /> WHERE (Market)
                        </div>
                    </div>
                    {/* Up Arrows */}
                     <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-gray-500">↓</div>
                </div>
            </div>
        </div>
    );
};

const TradeFlowVisual = () => {
    return (
        <div className="bg-black/40 p-4 rounded-xl border border-white/10 my-6 overflow-x-auto">
            <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6 text-center">Visualizing The Connections</h4>
            <div className="min-w-[500px] flex justify-between items-center relative h-32 px-10">
                
                {/* Connecting Line */}
                <div className="absolute top-1/2 left-20 right-20 h-1 bg-gray-800 -translate-y-1/2 z-0"></div>
                <div className="absolute top-1/2 left-20 right-20 h-1 bg-gradient-to-r from-blue-500 via-green-500 to-purple-500 -translate-y-1/2 z-0 animate-pulse"></div>

                {/* Market Point */}
                <div className="relative z-10 flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-900 border-2 border-blue-500 flex items-center justify-center shadow-[0_0_15px_rgba(59,130,246,0.5)]">
                        <Map size={20} className="text-blue-400" />
                    </div>
                    <div className="text-center">
                        <div className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">Venue</div>
                        <div className="text-sm font-bold text-white">Stock Mkt</div>
                    </div>
                </div>

                {/* Arrow 1 */}
                <ArrowRight className="text-gray-500 relative z-10" />

                {/* Instrument Point */}
                 <div className="relative z-10 flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-900 border-2 border-green-500 flex items-center justify-center shadow-[0_0_15px_rgba(34,197,94,0.5)]">
                        <Briefcase size={20} className="text-green-400" />
                    </div>
                    <div className="text-center">
                        <div className="text-[10px] font-bold text-green-400 uppercase tracking-wider">Product</div>
                        <div className="text-sm font-bold text-white">Apple CFD</div>
                    </div>
                </div>

                {/* Arrow 2 */}
                <ArrowRight className="text-gray-500 relative z-10" />

                {/* Strategy Point */}
                <div className="relative z-10 flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-purple-900 border-2 border-purple-500 flex items-center justify-center shadow-[0_0_15px_rgba(168,85,247,0.5)]">
                        <Target size={20} className="text-purple-400" />
                    </div>
                    <div className="text-center">
                        <div className="text-[10px] font-bold text-purple-400 uppercase tracking-wider">Method</div>
                         <div className="text-sm font-bold text-white">Day Trade</div>
                    </div>
                </div>

            </div>
        </div>
    );
};

// Lesson 0.3: The Hierarchy of Trade
const LessonTheHierarchy = () => {
    return (
        <div className="space-y-12 max-w-5xl mx-auto p-6 pb-24">
            {/* Header */}
            <div className="mb-12">
                <div className="flex items-center gap-2 mb-2 text-cyan-400">
                    <Layers size={20} />
                    <span className="font-mono text-xs tracking-wider uppercase">Module 0.2</span>
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">The Hierarchy of Trade</h1>
                <p className="text-xl text-gray-400">
                    Understanding the three layers: WHERE you trade, WHAT you trade, and HOW you trade.
                </p>
                
                <HierarchyPyramidVisual />
            </div>

            {/* Three-Layer Hierarchy */}
            <section className="space-y-8">
                <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-cyan-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">1</div>
                    The Three-Layer Hierarchy
                </h2>

                {/* Layer 1: Markets */}
                <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/10 p-8 rounded-2xl border border-blue-500/30 relative overflow-hidden group">
                    <div className="absolute right-0 bottom-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
                        <Map size={150} />
                    </div>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-full">LAYER 1</div>
                        <h3 className="text-2xl font-bold text-blue-300">Financial MARKETS</h3>
                    </div>
                    <p className="text-gray-400 mb-4 text-lg">WHERE You Trade (The Venue)</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 relative z-10">
                        <div className="bg-blue-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Stock Market</div>
                        <div className="bg-blue-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Forex Market</div>
                        <div className="bg-blue-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Commodities</div>
                        <div className="bg-blue-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Bond Market</div>
                        <div className="bg-blue-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Crypto Market</div>
                        <div className="bg-blue-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Derivatives</div>
                    </div>
                </div>

                {/* Layer 2: Instruments */}
                <div className="bg-gradient-to-br from-green-900/30 to-green-800/10 p-8 rounded-2xl border border-green-500/30 relative overflow-hidden group">
                    <div className="absolute right-0 bottom-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
                        <Briefcase size={150} />
                    </div>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="px-4 py-2 bg-green-600 text-white text-sm font-bold rounded-full">LAYER 2</div>
                        <h3 className="text-2xl font-bold text-green-300">Financial INSTRUMENTS</h3>
                    </div>
                    <p className="text-gray-400 mb-4 text-lg">WHAT You Trade (The Product)</p>
                    
                    <div className="space-y-4 relative z-10">
                        <div>
                            <h4 className="text-sm font-bold text-green-400 uppercase mb-2">Direct Ownership</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                <div className="bg-green-900/20 px-3 py-2 rounded-lg text-center text-sm text-gray-300">Stocks</div>
                                <div className="bg-green-900/20 px-3 py-2 rounded-lg text-center text-sm text-gray-300">Bonds</div>
                                <div className="bg-green-900/20 px-3 py-2 rounded-lg text-center text-sm text-gray-300">Commodities</div>
                                <div className="bg-green-900/20 px-3 py-2 rounded-lg text-center text-sm text-gray-300">Crypto</div>
                            </div>
                        </div>
                        <div>
                            <h4 className="text-sm font-bold text-green-400 uppercase mb-2">Derivatives (No ownership)</h4>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                <div className="bg-green-900/20 px-3 py-2 rounded-lg text-center text-sm text-gray-300">CFDs</div>
                                <div className="bg-green-900/20 px-3 py-2 rounded-lg text-center text-sm text-gray-300">Futures</div>
                                <div className="bg-green-900/20 px-3 py-2 rounded-lg text-center text-sm text-gray-300">Options</div>
                                <div className="bg-green-900/20 px-3 py-2 rounded-lg text-center text-sm text-gray-300">Swaps</div>
                                <div className="bg-green-900/20 px-3 py-2 rounded-lg text-center text-sm text-gray-300">Forwards</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Layer 3: Strategies */}
                <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/10 p-8 rounded-2xl border border-purple-500/30 relative overflow-hidden group">
                    <div className="absolute right-0 bottom-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
                        <Target size={150} />
                    </div>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="px-4 py-2 bg-purple-600 text-white text-sm font-bold rounded-full">LAYER 3</div>
                        <h3 className="text-2xl font-bold text-purple-300">Trading STRATEGIES</h3>
                    </div>
                    <p className="text-gray-400 mb-4 text-lg">HOW You Trade (The Method)</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 relative z-10">
                        <div className="bg-purple-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Buy & Hold</div>
                        <div className="bg-purple-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Day Trading</div>
                        <div className="bg-purple-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Swing Trading</div>
                        <div className="bg-purple-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Scalping</div>
                        <div className="bg-purple-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Position Trading</div>
                        <div className="bg-purple-900/20 px-4 py-2 rounded-lg text-center text-gray-300">Hedging</div>
                    </div>
                </div>
            </section>

            {/* How They Work Together */}
            <section className="space-y-6">
                <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-cyan-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">2</div>
                    How They Work Together
                </h2>
                
                <TradeFlowVisual />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Example 1 */}
                    <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
                        <h4 className="text-sm font-bold text-cyan-400 uppercase mb-4">Example 1</h4>
                        <div className="space-y-3">
                            <div>
                                <div className="text-xs text-blue-400 font-bold">WHERE (Market)</div>
                                <div className="text-gray-300">Stock Market</div>
                            </div>
                            <div>
                                <div className="text-xs text-green-400 font-bold">WHAT (Instrument)</div>
                                <div className="text-gray-300">Apple Stock</div>
                            </div>
                            <div>
                                <div className="text-xs text-purple-400 font-bold">HOW (Strategy)</div>
                                <div className="text-gray-300">Buy and Hold</div>
                            </div>
                        </div>
                    </div>

                    {/* Example 2 */}
                    <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
                        <h4 className="text-sm font-bold text-cyan-400 uppercase mb-4">Example 2</h4>
                        <div className="space-y-3">
                            <div>
                                <div className="text-xs text-blue-400 font-bold">WHERE (Market)</div>
                                <div className="text-gray-300">Commodities</div>
                            </div>
                            <div>
                                <div className="text-xs text-green-400 font-bold">WHAT (Instrument)</div>
                                <div className="text-gray-300">Gold Futures</div>
                            </div>
                            <div>
                                <div className="text-xs text-purple-400 font-bold">HOW (Strategy)</div>
                                <div className="text-gray-300">Day Trading</div>
                            </div>
                        </div>
                    </div>

                    {/* Example 3 */}
                    <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
                        <h4 className="text-sm font-bold text-cyan-400 uppercase mb-4">Example 3</h4>
                        <div className="space-y-3">
                            <div>
                                <div className="text-xs text-blue-400 font-bold">WHERE (Market)</div>
                                <div className="text-gray-300">Forex Market</div>
                            </div>
                            <div>
                                <div className="text-xs text-green-400 font-bold">WHAT (Instrument)</div>
                                <div className="text-gray-300">EUR/USD CFD</div>
                            </div>
                            <div>
                                <div className="text-xs text-purple-400 font-bold">HOW (Strategy)</div>
                                <div className="text-gray-300">Scalping</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Deep Dive: Instruments */}
            <section className="space-y-8">
                <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-cyan-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">3</div>
                    Deep Dive: CFDs vs Futures vs Direct Ownership
                </h2>

                {/* Direct Ownership */}
                <div className="bg-blue-900/20 p-6 rounded-xl border border-blue-500/20">
                    <h3 className="text-xl font-bold text-blue-400 mb-3 flex items-center gap-2">
                        <CheckCircle size={24} />
                        Direct Ownership (Traditional)
                    </h3>
                    <p className="text-gray-300 mb-4">When you buy Apple stock directly:</p>
                    <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                        <li>You <strong className="text-white">OWN</strong> a piece of the company</li>
                        <li>You receive dividends (if any)</li>
                        <li>You have voting rights</li>
                        <li>You pay full price upfront</li>
                        <li>No expiration date</li>
                        <li><strong className="text-blue-400">Used for:</strong> Long-term investing</li>
                    </ul>
                </div>

                {/* Futures */}
                <div className="bg-orange-900/20 p-6 rounded-xl border border-orange-500/20">
                    <h3 className="text-xl font-bold text-orange-400 mb-3 flex items-center gap-2">
                        <Clock size={24} />
                        Futures Contracts
                    </h3>
                    <p className="text-gray-300 mb-4">Interchangeable transactions where traders profit from price movements without taking delivery</p>
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold text-orange-300 uppercase">Characteristics:</h4>
                        <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                            <li>Have a <strong className="text-white">set expiration date</strong> and predetermined price</li>
                            <li>Traded on <strong className="text-white">centralized exchanges</strong></li>
                            <li>Standardized contract sizes (often large)</li>
                            <li>Designed for institutions - a single Platinum futures = 100 ounces</li>
                            <li>Exchange-traded, more regulated</li>
                            <li><strong className="text-orange-400">Used for:</strong> Hedging, speculation, institutional trading</li>
                        </ul>
                    </div>
                </div>

                {/* CFDs */}
                <div className="bg-green-900/20 p-6 rounded-xl border border-green-500/20">
                    <h3 className="text-xl font-bold text-green-400 mb-3 flex items-center gap-2">
                        <RefreshCw size={24} />
                        CFDs (Contracts for Difference)
                    </h3>
                    <p className="text-gray-300 mb-4">A contract to pay/receive the difference between entry and exit price, with no set future date</p>
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold text-green-300 uppercase">Characteristics:</h4>
                        <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                            <li><strong className="text-white">No expiry date</strong> or predetermined price</li>
                            <li>Traded <strong className="text-white">over the counter (OTC)</strong> against your broker</li>
                            <li>Contract sizes are smaller, more accessible</li>
                            <li>Access multiple markets with transparent pricing</li>
                            <li>Higher leverage available</li>
                            <li><strong className="text-green-400">Used for:</strong> Retail speculation, short-term trading</li>
                        </ul>
                        <div className="bg-red-900/20 p-4 rounded-lg border-l-4 border-red-500 mt-4">
                            <p className="text-sm text-gray-300"><strong className="text-red-400">⚠ Note:</strong> CFDs are <strong>banned in the United States</strong> due to counterparty risk.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison Table */}
            <section className="space-y-6">
                <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-cyan-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">4</div>
                    Key Differences Summary
                </h2>

                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-cyan-900/30">
                                <th className="border border-cyan-500/30 p-3 text-left text-cyan-400 font-bold">Feature</th>
                                <th className="border border-cyan-500/30 p-3 text-left text-blue-400 font-bold">Direct Ownership</th>
                                <th className="border border-cyan-500/30 p-3 text-left text-orange-400 font-bold">Futures</th>
                                <th className="border border-cyan-500/30 p-3 text-left text-green-400 font-bold">CFDs</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="bg-gray-900/20">
                                <td className="border border-gray-700 p-3 text-gray-300 font-bold">Own the asset?</td>
                                <td className="border border-gray-700 p-3 text-green-400">✅ Yes</td>
                                <td className="border border-gray-700 p-3 text-red-400">❌ No</td>
                                <td className="border border-gray-700 p-3 text-red-400">❌ No</td>
                            </tr>
                            <tr className="bg-gray-900/10">
                                <td className="border border-gray-700 p-3 text-gray-300 font-bold">Expiration date?</td>
                                <td className="border border-gray-700 p-3 text-red-400">❌ No</td>
                                <td className="border border-gray-700 p-3 text-green-400">✅ Yes</td>
                                <td className="border border-gray-700 p-3 text-red-400">❌ No</td>
                            </tr>
                            <tr className="bg-gray-900/20">
                                <td className="border border-gray-700 p-3 text-gray-300 font-bold">Where traded?</td>
                                <td className="border border-gray-700 p-3 text-gray-300">Exchange</td>
                                <td className="border border-gray-700 p-3 text-gray-300">Exchange</td>
                                <td className="border border-gray-700 p-3 text-gray-300">OTC (broker)</td>
                            </tr>
                            <tr className="bg-gray-900/10">
                                <td className="border border-gray-700 p-3 text-gray-300 font-bold">Contract size</td>
                                <td className="border border-gray-700 p-3 text-gray-300">Any amount</td>
                                <td className="border border-gray-700 p-3 text-gray-300">Large/standardized</td>
                                <td className="border border-gray-700 p-3 text-gray-300">Small/flexible</td>
                            </tr>
                            <tr className="bg-gray-900/20">
                                <td className="border border-gray-700 p-3 text-gray-300 font-bold">Leverage?</td>
                                <td className="border border-gray-700 p-3 text-gray-300">Limited</td>
                                <td className="border border-gray-700 p-3 text-gray-300">Yes</td>
                                <td className="border border-gray-700 p-3 text-gray-300">High</td>
                            </tr>
                            <tr className="bg-gray-900/10">
                                <td className="border border-gray-700 p-3 text-gray-300 font-bold">Best for</td>
                                <td className="border border-gray-700 p-3 text-gray-300">Long-term</td>
                                <td className="border border-gray-700 p-3 text-gray-300">Institutions</td>
                                <td className="border border-gray-700 p-3 text-gray-300">Retail traders</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* The Correct Way to Think */}
            <section className="space-y-6">
                <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-cyan-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">5</div>
                    The Correct Way to Think About It
                </h2>

                <div className="space-y-4">
                    <div className="bg-red-900/20 p-4 rounded-xl border-l-4 border-red-500">
                        <div className="flex items-start gap-3">
                            <XCircle className="text-red-400 mt-1" size={20} />
                            <div>
                                <p className="text-red-400 font-bold">WRONG:</p>
                                <p className="text-gray-300">"I trade the CFD market"</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-green-900/20 p-4 rounded-xl border-l-4 border-green-500">
                        <div className="flex items-start gap-3">
                            <CheckCircle className="text-green-400 mt-1" size={20} />
                            <div>
                                <p className="text-green-400 font-bold">RIGHT:</p>
                                <p className="text-gray-300">"I trade stock CFDs in the stock market"</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-red-900/20 p-4 rounded-xl border-l-4 border-red-500">
                        <div className="flex items-start gap-3">
                            <XCircle className="text-red-400 mt-1" size={20} />
                            <div>
                                <p className="text-red-400 font-bold">WRONG:</p>
                                <p className="text-gray-300">"I'm doing buy and hold in the futures market"</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-green-900/20 p-4 rounded-xl border-l-4 border-green-500">
                        <div className="flex items-start gap-3">
                            <CheckCircle className="text-green-400 mt-1" size={20} />
                            <div>
                                <p className="text-green-400 font-bold">RIGHT:</p>
                                <p className="text-gray-300">"I'm trading oil futures using a day trading strategy"</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-red-900/20 p-4 rounded-xl border-l-4 border-red-500">
                        <div className="flex items-start gap-3">
                            <XCircle className="text-red-400 mt-1" size={20} />
                            <div>
                                <p className="text-red-400 font-bold">WRONG:</p>
                                <p className="text-gray-300">"Should I invest in futures?"</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-green-900/20 p-4 rounded-xl border-l-4 border-green-500">
                        <div className="flex items-start gap-3">
                            <CheckCircle className="text-green-400 mt-1" size={20} />
                            <div>
                                <p className="text-green-400 font-bold">RIGHT:</p>
                                <p className="text-gray-300">"Should I use futures contracts to speculate on commodities?"</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom Line */}
            <div className="mt-12 bg-gradient-to-br from-cyan-900/30 to-blue-900/20 p-8 rounded-2xl border border-cyan-500/30">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <Target className="text-cyan-400" />
                    Bottom Line
                </h3>
                <div className="space-y-3 text-gray-300 text-lg">
                    <p><strong className="text-blue-400">Markets</strong> = WHERE (Stock, Forex, Commodities)</p>
                    <p><strong className="text-green-400">Instruments</strong> = WHAT (Stocks, CFDs, Futures, Options)</p>
                    <p><strong className="text-purple-400">Strategies</strong> = HOW (Buy and hold, Day trading, Scalping)</p>
                    <div className="mt-6 pt-4 border-t border-white/10">
                        <p className="text-white italic">
                            CFDs and Futures are <strong>instruments</strong> that can be traded across multiple <strong>markets</strong>. 
                            Buy and hold is a <strong>strategy</strong> you use with various <strong>instruments</strong>. 
                            They're all different categories in the trading ecosystem!
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Visual Components for Module 0.4 ---

const QuantAlgoVennVisual = () => {
    return (
        <div className="bg-black/40 p-8 rounded-xl border border-white/10 my-8">
            <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6 text-center">The Convergence</h4>
            <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-0 relative">
                
                {/* Quant Circle */}
                <div className="w-64 h-64 rounded-full border-4 border-blue-500/50 bg-blue-900/20 flex flex-col items-center justify-center p-6 text-center relative z-10 md:-mr-12 hover:bg-blue-900/30 transition-colors">
                    <Activity size={32} className="text-blue-400 mb-2" />
                    <h5 className="font-bold text-blue-300 text-lg mb-1">Quantitative</h5>
                    <p className="text-xs text-blue-200">The Brain & Analysis</p>
                    <ul className="text-[10px] text-gray-300 mt-2 space-y-1 text-left">
                        <li>• Math & Stats</li>
                        <li>• Hypothesis</li>
                        <li>• Backtesting</li>
                    </ul>
                </div>

                {/* Overlap Area */}
                 <div className="w-32 h-64 flex items-center justify-center z-20 md:-mt-8">
                    <div className="text-center bg-white/10 p-2 rounded-lg backdrop-blur-sm border border-white/20">
                        <Zap size={20} className="text-yellow-400 mx-auto mb-1 animate-pulse" />
                         <span className="text-[10px] font-bold text-white uppercase">Sweet Spot</span>
                    </div>
                </div>

                {/* Algo Circle */}
                <div className="w-64 h-64 rounded-full border-4 border-green-500/50 bg-green-900/20 flex flex-col items-center justify-center p-6 text-center relative z-10 md:-ml-12 hover:bg-green-900/30 transition-colors">
                    <Cpu size={32} className="text-green-400 mb-2" />
                    <h5 className="font-bold text-green-300 text-lg mb-1">Algorithmic</h5>
                    <p className="text-xs text-green-200">The Hands & Execution</p>
                    <ul className="text-[10px] text-gray-300 mt-2 space-y-1 text-left">
                        <li>• Automation</li>
                        <li>• Speed</li>
                        <li>• No Emotion</li>
                    </ul>
                </div>
            </div>
             <div className="mt-6 text-center text-xs text-gray-400 italic">
                "Quant is the methodology. Algo is the execution tool."
            </div>
        </div>
    );
};

const InstitutionalVsRetailVisual = () => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            {/* Goliath / Institutional */}
            <div className="bg-gradient-to-br from-red-900/20 to-gray-900/50 p-6 rounded-xl border border-red-500/20 flex flex-col relative overflow-hidden group">
                 <div className="absolute right-0 top-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Server size={120} />
                </div>
                <div className="flex items-center gap-3 mb-6 relative z-10">
                    <div className="bg-red-500/20 p-3 rounded-full">
                        <Briefcase size={24} className="text-red-400" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-white">Institutional</h4>
                        <span className="text-xs text-red-400 uppercase tracking-widest font-bold">The Tanker</span>
                    </div>
                </div>
                
                <div className="space-y-4 relative z-10">
                    <div className="bg-black/30 p-3 rounded-lg border-l-2 border-red-500">
                        <div className="text-xs text-gray-400 uppercase mb-1">Infrastructure</div>
                        <div className="font-bold text-gray-200 text-sm">Colocated Servers, FPGA, Microwave</div>
                    </div>
                    <div className="bg-black/30 p-3 rounded-lg border-l-2 border-red-500">
                        <div className="text-xs text-gray-400 uppercase mb-1">Budget</div>
                        <div className="font-bold text-gray-200 text-sm">Billions ($$$$$)</div>
                    </div>
                    <div className="bg-black/30 p-3 rounded-lg border-l-2 border-red-500">
                        <div className="text-xs text-gray-400 uppercase mb-1">Advantage</div>
                        <div className="font-bold text-gray-200 text-sm">Speed & Market Making</div>
                    </div>
                </div>
            </div>

            {/* David / Retail */}
            <div className="bg-gradient-to-br from-blue-900/20 to-gray-900/50 p-6 rounded-xl border border-blue-500/20 flex flex-col relative overflow-hidden group">
                 <div className="absolute right-0 top-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
                    <Globe size={120} />
                </div>
                <div className="flex items-center gap-3 mb-6 relative z-10">
                    <div className="bg-blue-500/20 p-3 rounded-full">
                        <Cpu size={24} className="text-blue-400" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-white">Retail Quant</h4>
                        <span className="text-xs text-blue-400 uppercase tracking-widest font-bold">The Speedboat</span>
                    </div>
                </div>
                
                <div className="space-y-4 relative z-10">
                    <div className="bg-black/30 p-3 rounded-lg border-l-2 border-blue-500">
                        <div className="text-xs text-gray-400 uppercase mb-1">Infrastructure</div>
                        <div className="font-bold text-gray-200 text-sm">Cloud (AWS), Python, APIs</div>
                    </div>
                    <div className="bg-black/30 p-3 rounded-lg border-l-2 border-blue-500">
                        <div className="text-xs text-gray-400 uppercase mb-1">Budget</div>
                        <div className="font-bold text-gray-200 text-sm">Thousands ($ - $$)</div>
                    </div>
                     <div className="bg-black/30 p-3 rounded-lg border-l-2 border-blue-500">
                        <div className="text-xs text-gray-400 uppercase mb-1">Advantage</div>
                        <div className="font-bold text-gray-200 text-sm">Agility & Niche Strategies</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Lesson 0.4: The Identity (Quant vs. Algo vs. Institutional)
const LessonTheIdentity = () => {
    return (
        <div className="space-y-12 max-w-5xl mx-auto p-6 pb-24">
             {/* Header */}
            <div className="mb-12">
                <div className="flex items-center gap-2 mb-2 text-indigo-400">
                    <Zap size={20} />
                    <span className="font-mono text-xs tracking-wider uppercase">Module 0.4</span>
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">The Identity</h1>
                <p className="text-xl text-gray-400">
                    Defining who you are: Quant vs. Algo and Retail vs. Institutional.
                </p>
            </div>

            {/* DEFINITIONS SECTION */}
            <section className="space-y-12">
                <div className="flex items-center gap-3">
                    <div className="bg-indigo-600 w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold text-white">1</div>
                    <h2 className="text-3xl font-bold text-white">Quant vs. Algo: The Distinction</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                     {/* Quantitative */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-blue-400">
                            <Activity size={24} />
                            <h3 className="text-2xl font-bold">What is Quantitative?</h3>
                        </div>
                        <div className="bg-blue-900/10 p-6 rounded-xl border border-blue-500/20 h-full">
                            <p className="text-gray-300 leading-relaxed mb-4">
                                An investment approach relying on <strong>mathematical computations, statistical models, and data analysis</strong>. Instead of intuition ("I feel like Apple will go up"), quants use numbers ("Apple goes up 80% of the time when X happens").
                            </p>
                            <p className="text-gray-300 border-t border-blue-500/20 pt-4 mt-4">
                                <strong className="text-blue-400">Core:</strong> Methodology & Analysis. <br/>
                                <strong className="text-blue-400">Example:</strong> Finding that a moving average crossover predicts price rises.
                            </p>
                        </div>
                    </div>

                    {/* Algorithmic */}
                    <div className="space-y-4">
                         <div className="flex items-center gap-2 text-green-400">
                            <Cpu size={24} />
                            <h3 className="text-2xl font-bold">What is Algorithmic?</h3>
                        </div>
                         <div className="bg-green-900/10 p-6 rounded-xl border border-green-500/20 h-full">
                            <p className="text-gray-300 leading-relaxed mb-4">
                                Taking quantitative trading a step further by <strong>automating the execution</strong>. An algorithm is a set of coded instructions that monitors markets and executes trades without human intervention.
                            </p>
                             <p className="text-gray-300 border-t border-green-500/20 pt-4 mt-4">
                                <strong className="text-green-400">Core:</strong> Execution & Automation. <br/>
                                <strong className="text-green-400">Example:</strong> Writing Python code to buy automatically at 9:30 AM.
                            </p>
                        </div>
                    </div>
                </div>
                
                <QuantAlgoVennVisual />

                <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-600/30">
                    <h4 className="font-bold text-white mb-2 flex items-center gap-2">
                        <Filter size={18} className="text-gray-400"/>
                        Note: You can be one without the other
                    </h4>
                    <p className="text-sm text-gray-400 italic">
                        "Imagine a trader who uses complex maths to find opportunities but calls a broker to buy manually. They are a <strong>Quant</strong>, but not <strong>Algo</strong>. Conversely, automating a simple 'buy on news' rule makes you <strong>Algo</strong>, but barely <strong>Quant</strong>."
                    </p>
                </div>
            </section>

             {/* RETAIL VS INSTITUTIONAL SECTION */}
            <section className="space-y-12 pt-12 border-t border-white/10">
                <div className="flex items-center gap-3">
                    <div className="bg-purple-600 w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold text-white">2</div>
                    <h2 className="text-3xl font-bold text-white">Institutional vs. Retail Trading</h2>
                </div>
                
                <p className="text-gray-400 text-lg">
                    Hedge funds vs. You. It is crucial to understand the battlefield discrepancies before engaging.
                </p>

                <InstitutionalVsRetailVisual />

                {/* Institutional Detail */}
                <div className="bg-red-900/10 p-8 rounded-2xl border border-red-500/10">
                    <h3 className="text-2xl font-bold text-red-400 mb-6 flex items-center gap-3">
                        <Briefcase size={28} />
                        The Institutional Landscape
                    </h3>
                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h4 className="font-bold text-white mb-2">Infrastructure</h4>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                They colocate servers next to exchanges for microsecond advantages. They employ teams of engineers to maintain 24/7 uptime. Funds like Renaissance employ more PhDs in Physics than finance pros.
                            </p>
                        </div>
                         <div>
                            <h4 className="font-bold text-white mb-2">Data & Capital</h4>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Access to Level 2 Order Books, satellite imagery, and credit card data. Trading billions allows them to scalp tiny inefficiencies that are meaningless to small accounts.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Retail Detail */}
                <div className="bg-blue-900/10 p-8 rounded-2xl border border-blue-500/10">
                    <h3 className="text-2xl font-bold text-blue-400 mb-6 flex items-center gap-3">
                        <Globe size={28} />
                        The Retail Trader Reality
                    </h3>
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-bold text-white mb-2">Modern Capabilities (Democratization)</h4>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Access to APIs (Interactive Brokers, Alpaca), cloud platforms (QuantConnect), and professional data (Polygon.io) has narrowed the gap. Python has made building 'machines' accessible to anyone.
                            </p>
                        </div>
                         <div>
                            <h4 className="font-bold text-white mb-2">The Retail Edge (Agility)</h4>
                            <div className="bg-blue-500/10 p-4 rounded-lg border-l-4 border-blue-500 mt-2">
                                <p className="text-gray-300 italic">
                                    "We are nimble. We can enter/exit without moving the market. We have no investors to answer to. We can pursue niche strategies (small cap, crypto anomalies) that are too small for a billion-dollar fund to bother with."
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

// --- Visual Components for Module 0.5 ---

const StrategyArchetypesVisual = () => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            {/* Momentum Visual */}
            <div className="bg-black/40 p-6 rounded-xl border border-blue-500/20 flex flex-col items-center">
                <h4 className="text-blue-400 font-bold mb-4 flex items-center gap-2">
                    <TrendingUp size={20} /> Momentum
                </h4>
                <div className="w-full h-32 relative flex items-end px-4 border-b border-gray-600">
                    {/* Trend Line */}
                    <svg className="absolute inset-0 w-full h-full p-4 overflow-visible" preserveAspectRatio="none">
                         <path d="M 0,100 C 50,90 100,60 150,50 S 250,20 300,0" fill="none" stroke="#60a5fa" strokeWidth="3" strokeDasharray="4 2" />
                         <path d="M 0,100 C 50,90 100,60 150,50 S 250,20 300,0" fill="none" stroke="#3b82f6" strokeWidth="3" className="animate-draw-path" />
                    </svg>
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center">"Buying High, Selling Higher"</p>
            </div>

            {/* Mean Reversion Visual */}
            <div className="bg-black/40 p-6 rounded-xl border border-purple-500/20 flex flex-col items-center">
                <h4 className="text-purple-400 font-bold mb-4 flex items-center gap-2">
                    <Activity size={20} /> Mean Reversion
                </h4>
                <div className="w-full h-32 relative flex items-center px-4 border-b border-gray-600">
                    {/* Mean Line */}
                     <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-500 border-dashed border-gray-500"></div>
                     <div className="absolute top-1/4 left-0 right-0 h-px bg-purple-900/50"></div>
                     <div className="absolute bottom-1/4 left-0 right-0 h-px bg-purple-900/50"></div>

                     {/* Oscillating Path */}
                    <svg className="absolute inset-0 w-full h-full p-4 overflow-visible" preserveAspectRatio="none">
                         <path d="M 0,64 Q 40,0 80,64 Q 120,128 160,64 Q 200,0 240,64" fill="none" stroke="#a855f7" strokeWidth="3" />
                         <circle cx="80" cy="64" r="4" fill="#a855f7" />
                         <circle cx="160" cy="64" r="4" fill="#a855f7" />
                    </svg>
                </div>
                 <p className="text-xs text-gray-400 mt-2 text-center">"Buying Low, Selling High (Range)"</p>
            </div>
        </div>
    );
};

// Lesson 0.5: Core Trading Strategy Archetypes
const LessonStrategyArchetypes = () => {
    return (
        <div className="space-y-12 max-w-5xl mx-auto p-6 pb-24">
             {/* Header */}
            <div className="mb-12">
                <div className="flex items-center gap-2 mb-2 text-indigo-400">
                    <Layers size={20} />
                    <span className="font-mono text-xs tracking-wider uppercase">Module 0.5</span>
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">Core Trading Strategy Archetypes</h1>
                <p className="text-xl text-gray-400">
                    Every trading bot falls into one of these mathematical buckets.
                </p>
            </div>

            <StrategyArchetypesVisual />

            <div className="grid gap-8">
                
                {/* 1. Momentum & Trend Following */}
                <div className="bg-blue-900/10 border border-blue-500/20 rounded-xl p-6 hover:bg-blue-900/20 transition-colors">
                     <div className="flex items-center justify-between mb-4">
                        <h3 className="text-2xl font-bold text-blue-400 flex items-center gap-2">
                             <div className="bg-blue-600 w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold">1</div>
                             Momentum & Trend Following
                        </h3>
                        <div className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full border border-green-500/30">Retail Viable: YES ✅</div>
                     </div>
                     <p className="text-gray-300 italic mb-4">The Physics of Finance: "Objects in motion tend to stay in motion."</p>
                     <ul className="space-y-2 text-sm text-gray-400">
                         <li><strong className="text-white">The Logic:</strong> Strategies that identify and trade in the direction of market movement.</li>
                         <li><strong className="text-white">Time-Series Momentum:</strong> "Bitcoin is going up, so I buy Bitcoin." (Classic Trend Following).</li>
                         <li><strong className="text-white">Cross-Sectional Momentum:</strong> "Apple is going up *faster* than Microsoft, so I buy Apple and Short Microsoft." (Relative Strength).</li>
                     </ul>
                </div>

                {/* 2. Mean Reversion */}
                <div className="bg-purple-900/10 border border-purple-500/20 rounded-xl p-6 hover:bg-purple-900/20 transition-colors">
                     <div className="flex items-center justify-between mb-4">
                        <h3 className="text-2xl font-bold text-purple-400 flex items-center gap-2">
                             <div className="bg-purple-600 w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold">2</div>
                             Mean Reversion
                        </h3>
                        <div className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full border border-green-500/30">Retail Viable: YES ✅</div>
                     </div>
                     <p className="text-gray-300 italic mb-4">The Rubber Band Effect: "What goes up must come down."</p>
                     <ul className="space-y-2 text-sm text-gray-400">
                         <li><strong className="text-white">The Logic:</strong> Betting that prices will return to their historical average (mean) after an extreme move.</li>
                         <li><strong className="text-white">The Setup:</strong> Bollinger Bands, RSI extremes. If price spikes 3 SDs away, short it.</li>
                         <li><strong className="text-white">Risk:</strong> "Catching a falling knife." In a crash, price *doesn't* revert.</li>
                     </ul>
                </div>

                {/* 3. Stat Arb */}
                <div className="bg-yellow-900/10 border border-yellow-500/20 rounded-xl p-6 hover:bg-yellow-900/20 transition-colors">
                     <div className="flex items-center justify-between mb-4">
                        <h3 className="text-2xl font-bold text-yellow-400 flex items-center gap-2">
                             <div className="bg-yellow-600 w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold">3</div>
                             Statistical Arbitrage
                        </h3>
                        <div className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full border border-yellow-500/30">Retail Viable: MEDIUM ⚠️</div>
                     </div>
                     <p className="text-gray-300 italic mb-4">The Math Play: "These two things should move together."</p>
                     <ul className="space-y-2 text-sm text-gray-400">
                         <li><strong className="text-white">The Logic:</strong> Identifying pricing inefficiencies between related assets (e.g., Coke vs Pepsi).</li>
                         <li><strong className="text-white">Requirement:</strong> Rigorous data analysis and cointegration testing (Python), not just visual correlation.</li>
                     </ul>
                </div>

                {/* 4. News & NLP */}
                <div className="bg-cyan-900/10 border border-cyan-500/20 rounded-xl p-6 hover:bg-cyan-900/20 transition-colors">
                     <div className="flex items-center justify-between mb-4">
                        <h3 className="text-2xl font-bold text-cyan-400 flex items-center gap-2">
                             <div className="bg-cyan-600 w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold">4</div>
                             News & Sentiment Analysis
                        </h3>
                        <div className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full border border-green-500/30">Retail Viable: YES ✅</div>
                     </div>
                     <p className="text-gray-300 italic mb-4">The AI Play: "Reading the internet faster than humans."</p>
                     <ul className="space-y-2 text-sm text-gray-400">
                         <li><strong className="text-white">The Logic:</strong> Using NLP to convert text (Twitter, News) into tradeable numbers.</li>
                         <li><strong className="text-white">The Execution:</strong> Sentiment score &gt; 0.8 = Buy.</li>
                     </ul>
                </div>

                 {/* 5. Market Making */}
                <div className="bg-red-900/10 border border-red-500/20 rounded-xl p-6 hover:bg-red-900/20 transition-colors opacity-75">
                     <div className="flex items-center justify-between mb-4">
                        <h3 className="text-2xl font-bold text-red-500 flex items-center gap-2">
                             <div className="bg-red-600 w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold">5</div>
                             Market Making (HFT Domain)
                        </h3>
                        <div className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full border border-red-500/30">Retail Viable: NO ❌</div>
                     </div>
                     <p className="text-gray-300 italic mb-4">The Liquidity Provider: "Profiting from the Spread."</p>
                     <ul className="space-y-2 text-sm text-gray-400">
                         <li><strong className="text-white">The Logic:</strong> Continuously placing Buy Limit and Sell Limit orders to capture the spread.</li>
                         <li><strong className="text-white">The Wall:</strong> You must be fastest. If not, you get run over by toxic flow. Requires colocation.</li>
                     </ul>
                </div>
            </div>

            {/* Special Section: HFT */}
             <div className="mt-12 bg-gray-800 p-6 rounded-xl border-l-4 border-orange-500">
                <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <Zap size={20} className="text-orange-500" />
                    Special Section: Where does HFT fit?
                </h3>
                <p className="text-gray-300 mb-4">
                    HFT is not a single strategy; it is a <strong className="text-orange-400">Speed Class</strong>. It applies to multiple strategies but changes <em>how</em> they are executed (Market Making, Arb, Scalping).
                </p>
            </div>

             {/* Verdict */}
            <div className="bg-green-900/20 p-6 rounded-xl border border-green-500/30">
                <h3 className="font-bold text-green-400 mb-2">KSQuant Verdict:</h3>
                <p className="text-gray-300">
                    As a Retail Quant, you will mostly build <strong>Momentum</strong>, <strong>Mean Reversion</strong>, and <strong>Stat Arb</strong> strategies. You generally strictly avoid <strong>Market Making</strong> and <strong>Latency Arbitrage</strong> because you cannot win the speed war against institutions.
                </p>
            </div>

        </div>
    );
};

// --- Visual Components for Module 0.6 ---

const WorkflowVisual = () => {
    const steps = [
        { id: 1, title: "Hypothesis", icon: Lightbulb, color: "text-blue-400", bg: "bg-blue-500/20", border: "border-blue-500/50" },
        { id: 2, title: "Data Clean", icon: Database, color: "text-purple-400", bg: "bg-purple-500/20", border: "border-purple-500/50" },
        { id: 3, title: "Backtest", icon: History, color: "text-yellow-400", bg: "bg-yellow-500/20", border: "border-yellow-500/50" },
        { id: 4, title: "Risk Mgmt", icon: Shield, color: "text-red-400", bg: "bg-red-500/20", border: "border-red-500/50" },
        { id: 5, title: "Paper Trade", icon: FileText, color: "text-cyan-400", bg: "bg-cyan-500/20", border: "border-cyan-500/50" },
        { id: 6, title: "Live Deploy", icon: Rocket, color: "text-green-400", bg: "bg-green-500/20", border: "border-green-500/50" }
    ];

    return (
        <div className="bg-black/40 p-6 rounded-xl border border-white/10 my-8 overflow-x-auto">
             <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-6 text-center">The Lifecycle</h4>
             <div className="min-w-[700px] flex justify-between items-center relative px-6 py-4">
                {/* Connecting Line */}
                <div className="absolute top-1/2 left-10 right-10 h-1 bg-gray-800 -translate-y-1/2 z-0"></div>
                <div className="absolute top-1/2 left-10 right-1/3 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-yellow-500 -translate-y-1/2 z-0 opacity-50"></div>

                {steps.map((step) => (
                    <div key={step.id} className="relative z-10 flex flex-col items-center gap-3 group">
                        <div className={`w-14 h-14 rounded-full ${step.bg} ${step.border} border-2 flex items-center justify-center transition-all group-hover:scale-110 shadow-lg`}>
                            <step.icon size={24} className={step.color} />
                        </div>
                        <div className="text-center">
                            <div className={`text-xs font-bold ${step.color} uppercase mb-1`}>Phase {step.id}</div>
                            <div className="text-sm font-bold text-white">{step.title}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Lesson 0.6: The Quantitative Workflow
const LessonQuantitativeWorkflow = () => {
    return (
        <div className="space-y-12 max-w-5xl mx-auto p-6 pb-24">
             {/* Header */}
            <div className="mb-12">
                <div className="flex items-center gap-2 mb-2 text-indigo-400">
                    <Activity size={20} />
                    <span className="font-mono text-xs tracking-wider uppercase">Module 0.6</span>
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">The Quantitative Workflow</h1>
                <p className="text-xl text-gray-400">
                    The Lifecycle of a Trading Strategy: From Idea to Profit.
                </p>
            </div>

            <WorkflowVisual />

            <div className="space-y-8">
                {/* Phase 1 */}
                <div className="bg-blue-900/10 border-l-4 border-blue-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-blue-500 w-8 h-8 rounded-full flex items-center justify-center text-sm">1</div>
                         Research & Hypothesis (The "Idea")
                    </h3>
                    <p className="text-gray-300 mb-4">Everything starts with a specific question about market behavior.</p>
                    <ul className="text-sm space-y-2 text-gray-400">
                        <li><strong className="text-blue-400">The Source:</strong> Academic papers, observation, microstructure theories.</li>
                        <li><strong className="text-blue-400">The Hypothesis:</strong> <em>"I believe that when a stock's 50-day MA crosses the 200-day MA, it rises over the next 30 days."</em></li>
                        <li className="flex items-start gap-2 text-white bg-blue-500/10 p-2 rounded">
                            <Zap size={16} className="mt-1 text-yellow-400 shrink-0"/>
                            <span><strong>Key Rule:</strong> You must have a logical reason <em>why</em> the strategy should work before you test it. Don't just hunt for patterns (Data Mining).</span>
                        </li>
                    </ul>
                </div>

                {/* Phase 2 */}
                <div className="bg-purple-900/10 border-l-4 border-purple-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-purple-500 w-8 h-8 rounded-full flex items-center justify-center text-sm">2</div>
                         Data Collection & Cleaning (The "Grind")
                    </h3>
                    <p className="text-gray-300 italic mb-4">Garbage In, Garbage Out.</p>
                    <div className="grid md:grid-cols-2 gap-6">
                        <ul className="text-sm space-y-2 text-gray-400">
                             <li><strong className="text-purple-400">The Task:</strong> Gathering Price (OHLCV), Volume, and Alt Data.</li>
                             <li><strong className="text-purple-400">The Cleaning:</strong> Adjusting for Splits, Filling "Gaps", Filtering bad ticks.</li>
                        </ul>
                        <div className="bg-purple-500/10 p-3 rounded flex items-center gap-3 border border-purple-500/30">
                            <Clock size={24} className="text-purple-400" />
                            <div className="text-xs text-purple-200"><strong>Reality Check:</strong> Professional Quants spend <strong>80% of their time</strong> here.</div>
                        </div>
                    </div>
                </div>

                {/* Phase 3 */}
                <div className="bg-yellow-900/10 border-l-4 border-yellow-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-yellow-500 w-8 h-8 rounded-full flex items-center justify-center text-sm text-black font-bold">3</div>
                         Backtesting (The "Simulation")
                    </h3>
                    <p className="text-gray-300 mb-4">Simulating your rules against the past to see if they <em>would have</em> made money.</p>
                    
                    <div className="bg-red-900/20 border border-red-500/30 p-4 rounded-lg mt-4">
                        <h4 className="font-bold text-red-400 mb-2 flex items-center gap-2"><AlertTriangle size={18}/> The 3 Deadly Sins of Backtesting</h4>
                        <ul className="text-sm space-y-3 text-gray-300">
                            <li><strong className="text-white">1. Look-Ahead Bias:</strong> Using data you wouldn't have had (e.g., using Close price to decide Entry on the <em>same</em> candle).</li>
                            <li><strong className="text-white">2. Survivorship Bias:</strong> Testing only on companies that exist today (ignoring bankruptcies).</li>
                            <li><strong className="text-white">3. Overfitting (Curve Fitting):</strong> Tweaking parameters until the curve is perfect. This creates a strategy that "memorized the past" but fails in the future.</li>
                        </ul>
                    </div>
                </div>

                {/* Phase 4 */}
                <div className="bg-red-900/10 border-l-4 border-red-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-red-500 w-8 h-8 rounded-full flex items-center justify-center text-sm">4</div>
                         Risk Management (The "Shield")
                    </h3>
                    <p className="text-gray-300 italic mb-4">A great strategy with bad risk management will still go bankrupt.</p>
                     <ul className="text-sm space-y-2 text-gray-400">
                        <li><strong className="text-red-400">Position Sizing:</strong> How much to buy? (Fixed Lot? % Risk? Kelly?).</li>
                        <li><strong className="text-red-400">Stop Loss:</strong> Where do you admit you are wrong?</li>
                        <li><strong className="text-red-400">Drawdown Tolerance:</strong> Can you handle losing 10% before profit returns?</li>
                    </ul>
                </div>

                {/* Phase 5 */}
                <div className="bg-cyan-900/10 border-l-4 border-cyan-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-cyan-500 w-8 h-8 rounded-full flex items-center justify-center text-sm text-black font-bold">5</div>
                         Paper Trading (The "Dress Rehearsal")
                    </h3>
                     <p className="text-gray-300 mb-4">Before risking real capital, run the bot in a live simulation (Demo Account).</p>
                     <ul className="text-sm space-y-2 text-gray-400">
                        <li><strong className="text-cyan-400">Why?</strong> Backtests assume perfect execution. Live markets have <strong>Slippage</strong>, <strong>Spread Widening</strong>, and <strong>Latency</strong>.</li>
                        <li><strong className="text-cyan-400">Goal:</strong> Verify that "Live Trades" match "Backtest Trades."</li>
                    </ul>
                </div>

                {/* Phase 6 */}
                <div className="bg-green-900/10 border-l-4 border-green-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-green-500 w-8 h-8 rounded-full flex items-center justify-center text-sm">6</div>
                         Live Deployment & Monitoring (The "War")
                    </h3>
                     <p className="text-gray-300 mb-4">Going live is not the end; it is the beginning of maintenance.</p>
                     <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-black/30 p-4 rounded border border-green-500/20">
                            <div className="font-bold text-green-400 mb-1">Strategy Decay</div>
                            <div className="text-xs text-gray-400">Alpha fades over time as other traders discover your edge.</div>
                        </div>
                        <div className="bg-black/30 p-4 rounded border border-green-500/20">
                             <div className="font-bold text-green-400 mb-1">Performance Drift</div>
                            <div className="text-xs text-gray-400">If backtest said "55% Win Rate" but live is "40%", turn it off and investigate.</div>
                        </div>
                     </div>
                </div>
            </div>
        </div>
    );
};

// --- Visual Components for Module 0.7 ---

const TechStackVisual = () => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
            {/* Starter Kit Visual */}
            <div className="bg-black/40 p-6 rounded-xl border border-blue-500/20">
                <h4 className="text-blue-400 font-bold mb-4 flex items-center gap-2">
                    <Code size={20} /> The Starter Kit (Python)
                </h4>
                 <div className="space-y-3">
                    <div className="flex items-center gap-3 bg-blue-900/10 p-2 rounded border border-blue-500/10">
                        <div className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">Pandas</div>
                        <span className="text-gray-400 text-xs">The Excel Killer (Data Tables)</span>
                    </div>
                    <div className="flex items-center gap-3 bg-blue-900/10 p-2 rounded border border-blue-500/10">
                        <div className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">NumPy</div>
                        <span className="text-gray-400 text-xs">The Math Engine (Calculations)</span>
                    </div>
                     <div className="flex items-center gap-3 bg-blue-900/10 p-2 rounded border border-blue-500/10">
                        <div className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">Matplotlib</div>
                        <span className="text-gray-400 text-xs">The Artist (Charting)</span>
                    </div>
                </div>
            </div>

            {/* Pro Setup Visual */}
            <div className="bg-black/40 p-6 rounded-xl border border-purple-500/20">
                <h4 className="text-purple-400 font-bold mb-4 flex items-center gap-2">
                    <Server size={20} /> The Pro Setup (Infrastructure)
                </h4>
                 <div className="space-y-3">
                    <div className="flex items-center gap-3 bg-purple-900/10 p-2 rounded border border-purple-500/10">
                        <Database size={16} className="text-purple-400" />
                        <span className="text-gray-300 text-xs font-bold">PostgreSQL / InfluxDB</span>
                    </div>
                     <div className="flex items-center gap-3 bg-purple-900/10 p-2 rounded border border-purple-500/10">
                        <Layers size={16} className="text-purple-400" />
                        <span className="text-gray-300 text-xs font-bold">Docker (Containerization)</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const HybridStackVisual = () => {
    return (
         <div className="bg-gradient-to-r from-gray-900 to-black p-8 rounded-xl border border-green-500/30 my-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-green-500/5 animate-pulse"></div>
            <h4 className="text-xl font-bold text-center text-white mb-8 relative z-10">The KSQuant Hybrid Approach</h4>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 relative z-10">
                {/* Brain */}
                <div className="flex flex-col items-center">
                    <div className="w-24 h-24 bg-blue-900/30 rounded-full border-2 border-blue-500 flex items-center justify-center mb-3 shadow-[0_0_20px_rgba(59,130,246,0.3)]">
                        <Brain size={40} className="text-blue-400" />
                    </div>
                    <div className="text-center">
                        <div className="font-bold text-blue-400">RESEARCH</div>
                        <div className="text-xs text-gray-400">Python (Pandas/AI)</div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">THE BRAIN</div>
                    </div>
                </div>

                {/* Connection */}
                <div className="hidden md:flex flex-col items-center text-gray-500">
                    <div className="text-xs uppercase tracking-widest mb-1">Bridge</div>
                    <ArrowRight size={32} className="animate-pulse text-green-500" />
                </div>
                <div className="md:hidden text-gray-500 rotate-90 my-2">
                    <ArrowRight size={32} className="animate-pulse text-green-500" />
                </div>

                {/* Hands */}
                <div className="flex flex-col items-center">
                    <div className="w-24 h-24 bg-green-900/30 rounded-full border-2 border-green-500 flex items-center justify-center mb-3 shadow-[0_0_20px_rgba(34,197,94,0.3)]">
                        <Terminal size={40} className="text-green-400" />
                    </div>
                    <div className="text-center">
                        <div className="font-bold text-green-400">EXECUTION</div>
                        <div className="text-xs text-gray-400">MQL5 (MetaTrader 5)</div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">THE HANDS</div>
                    </div>
                </div>
            </div>
         </div>
    );
};

// Lesson 0.7: Tools of the Trade
const LessonToolsOfTheTrade = () => {
    return (
        <div className="space-y-12 max-w-5xl mx-auto p-6 pb-24">
             {/* Header */}
            <div className="mb-12">
                <div className="flex items-center gap-2 mb-2 text-indigo-400">
                    <Wrench size={20} />
                    <span className="font-mono text-xs tracking-wider uppercase">Module 0.7</span>
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">Tools of the Trade (The Tech Stack)</h1>
                <p className="text-xl text-gray-400">
                    You cannot build a house without a hammer. You cannot build a bot without a stack.
                </p>
            </div>

            {/* 1. Starter Kit */}
            <div className="space-y-4">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="bg-blue-600 w-8 h-8 rounded flex items-center justify-center text-sm">1</div>
                    The Starter Kit (Retail Traders)
                </h2>
                <p className="text-gray-300">The standard setup for 90% of retail quants. Low cost, high flexibility.</p>
                
                <div className="bg-blue-900/10 p-6 rounded-xl border border-blue-500/20">
                     <p className="text-blue-300 mb-2 font-bold">The Language: Python 🐍</p>
                     <p className="text-sm text-gray-400 mb-4">It has become the "Lingua Franca" (common language) of finance.</p>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         <div>
                             <strong className="text-white block mb-2 text-sm">The Libraries:</strong>
                             <ul className="text-sm space-y-1 text-gray-400 list-disc list-inside">
                                 <li><strong className="text-blue-400">Pandas:</strong> The Excel killer.</li>
                                 <li><strong className="text-blue-400">NumPy:</strong> The math engine.</li>
                                 <li><strong className="text-blue-400">Matplotlib:</strong> The artist.</li>
                                 <li><strong className="text-blue-400">Backtrader:</strong> For simulation.</li>
                             </ul>
                         </div>
                         <div>
                             <strong className="text-white block mb-2 text-sm">The Execution:</strong>
                             <p className="text-sm text-gray-400">Most connect Python directly to brokers via API (Interactive Brokers, Alpaca).</p>
                         </div>
                     </div>
                </div>
            </div>

            {/* 2. Cloud Platforms */}
             <div className="space-y-4 pt-8">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="bg-purple-600 w-8 h-8 rounded flex items-center justify-center text-sm">2</div>
                    Cloud Platforms
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gray-800/50 p-5 rounded-xl border border-gray-700">
                         <h4 className="font-bold text-green-400 mb-2">Pros</h4>
                         <ul className="text-sm text-gray-300 space-y-1">
                             <li>✅ No infrastructure to manage.</li>
                             <li>✅ Data & Backtesting engine provided.</li>
                             <li>✅ "Just write code."</li>
                         </ul>
                    </div>
                     <div className="bg-gray-800/50 p-5 rounded-xl border border-gray-700">
                         <h4 className="font-bold text-red-400 mb-2">Cons</h4>
                         <ul className="text-sm text-gray-300 space-y-1">
                             <li>❌ Monthly subscription costs.</li>
                             <li>❌ "Vendor Lock-in" (Hard to leave).</li>
                         </ul>
                    </div>
                </div>
            </div>

             {/* 3. Pro & Institutional */}
             <div className="grid md:grid-cols-2 gap-8 pt-8">
                <div className="space-y-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-3">
                        <div className="bg-yellow-600 w-8 h-8 rounded flex items-center justify-center text-sm">3</div>
                        The "Pro" Retail Setup
                    </h2>
                     <div className="bg-yellow-900/10 p-6 rounded-xl border border-yellow-500/20 h-full">
                         <ul className="text-sm space-y-3 text-gray-300">
                             <li><strong className="text-yellow-400">Databases:</strong> PostgreSQL (Logs), InfluxDB (Tick Data).</li>
                             <li><strong className="text-yellow-400">Full Infrastructure:</strong> Docker (Containers), RabbitMQ (Messaging).</li>
                         </ul>
                     </div>
                </div>

                <div className="space-y-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-3">
                        <div className="bg-red-600 w-8 h-8 rounded flex items-center justify-center text-sm">4</div>
                        The Institutional Standard
                    </h2>
                     <div className="bg-red-900/10 p-6 rounded-xl border border-red-500/20 h-full opacity-75">
                         <ul className="text-sm space-y-3 text-gray-300">
                             <li><strong className="text-red-400">Language:</strong> C++ (Python is too slow for HFT).</li>
                             <li><strong className="text-red-400">Hardware:</strong> FPGA chips & Microwave towers.</li>
                             <li><strong className="text-red-400">Cost:</strong> Millions annually.</li>
                         </ul>
                     </div>
                </div>
             </div>

             <TechStackVisual />

             {/* KSQuant Approach */}
             <div className="pt-8">
                 <h2 className="text-3xl font-bold text-green-400 mb-4 text-center">Important: The KSQuant Hybrid Approach</h2>
                 <p className="text-center text-gray-400 max-w-2xl mx-auto mb-6">
                     While the industry uses pure Python, we use a <strong>Hybrid Stack</strong> optimized for the Retail FX market.
                 </p>
                 
                 <HybridStackVisual />

                 <div className="bg-green-900/20 p-6 rounded-xl border border-green-500/30 text-center">
                     <h4 className="text-lg font-bold text-white mb-2">Why this mix?</h4>
                     <p className="text-gray-300">
                         MQL5 is <strong>free</strong>, connects to <strong>1,000+ brokers</strong> directly, and handles the "boring stuff" (connection drops, margin checks) better than a raw Python script. Python stays the brain; MQL5 is just the muscle.
                     </p>
                 </div>
             </div>
        </div>
    );
};

// --- Visual Components for Module 0.8 ---

const RoadmapVisual = () => {
    const roadmapSteps = [
        { id: 1, title: "Learn Foundations", icon: GraduationCap, color: "blue" },
        { id: 2, title: "Start Simple", icon: Code, color: "purple" },
        { id: 3, title: "Proper Backtesting", icon: History, color: "yellow" },
        { id: 4, title: "Paper Trade", icon: FileText, color: "cyan" },
        { id: 5, title: "Go Live (Small)", icon: Rocket, color: "green" },
        { id: 6, title: "Never Stop Learning", icon: BookOpenIcon, color: "pink" }
    ];

    const colorMap = {
        blue: { bg: "bg-blue-500/20", border: "border-blue-500", text: "text-blue-400" },
        purple: { bg: "bg-purple-500/20", border: "border-purple-500", text: "text-purple-400" },
        yellow: { bg: "bg-yellow-500/20", border: "border-yellow-500", text: "text-yellow-400" },
        cyan: { bg: "bg-cyan-500/20", border: "border-cyan-500", text: "text-cyan-400" },
        green: { bg: "bg-green-500/20", border: "border-green-500", text: "text-green-400" },
        pink: { bg: "bg-pink-500/20", border: "border-pink-500", text: "text-pink-400" }
    };

    return (
        <div className="bg-black/40 p-8 rounded-xl border border-white/10 my-8 overflow-x-auto">
            <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-8 text-center">Your Journey</h4>
            <div className="min-w-[800px] grid grid-cols-6 gap-4 relative">
                {roadmapSteps.map((step, idx) => {
                    const colors = colorMap[step.color];
                    return (
                        <div key={step.id} className="flex flex-col items-center relative">
                            {/* Connector */}
                            {idx < roadmapSteps.length - 1 && (
                                <div className="absolute top-8 left-1/2 w-full h-0.5 bg-gray-700 z-0"></div>
                            )}
                            {/* Icon */}
                            <div className={`w-16 h-16 rounded-full ${colors.bg} ${colors.border} border-2 flex items-center justify-center mb-3 relative z-10 transition-all hover:scale-110`}>
                                <step.icon size={28} className={colors.text} />
                            </div>
                            {/* Label */}
                            <div className="text-center">
                                <div className={`text-xs font-bold ${colors.text} uppercase mb-1`}>Step {step.id}</div>
                                <div className="text-sm font-bold text-white leading-tight">{step.title}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Lesson 0.8: Getting Started
const LessonGettingStarted = () => {
    return (
        <div className="space-y-12 max-w-5xl mx-auto p-6 pb-24">
            {/* Header */}
            <div className="mb-12">
                <div className="flex items-center gap-2 mb-2 text-indigo-400">
                    <Rocket size={20} />
                    <span className="font-mono text-xs tracking-wider uppercase">Module 0.8</span>
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">Getting Started</h1>
                <p className="text-xl text-gray-400">
                    A realistic path forward for aspiring quantitative traders.
                </p>
            </div>

            <RoadmapVisual />

            <div className="space-y-8">
                {/* Step 1: Build Foundational Knowledge */}
                <div className="bg-blue-900/10 border-l-4 border-blue-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-blue-500 w-8 h-8 rounded-full flex items-center justify-center text-sm">1</div>
                        Build Foundational Knowledge
                    </h3>
                    <ul className="text-sm space-y-3 text-gray-300">
                        <li className="flex items-start gap-2">
                            <Code size={16} className="text-blue-400 mt-1 shrink-0" />
                            <span><strong className="text-white">Programming:</strong> Learn Python if you're a beginner. You don't need to be an expert, but you need enough skill to implement and test ideas.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <BarChart2 size={16} className="text-blue-400 mt-1 shrink-0" />
                            <span><strong className="text-white">Statistics & Probability:</strong> These underpin all quantitative analysis.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <LineChart size={16} className="text-blue-400 mt-1 shrink-0" />
                            <span><strong className="text-white">Market Microstructure:</strong> How orders are executed, what bid-ask spreads represent, and how liquidity affects trading.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <TrendingUpIcon size={16} className="text-blue-400 mt-1 shrink-0" />
                            <span><strong className="text-white">Behavioral Finance:</strong> Understand the market inefficiencies you might exploit.</span>
                        </li>
                    </ul>
                </div>

                {/* Step 2: Start Simple */}
                <div className="bg-purple-900/10 border-l-4 border-purple-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-purple-500 w-8 h-8 rounded-full flex items-center justify-center text-sm">2</div>
                        Start Simple
                    </h3>
                    <p className="text-gray-300 mb-3">Don't begin by trying to build a complex machine learning system.</p>
                    <div className="bg-purple-500/10 p-4 rounded border border-purple-500/20">
                        <p className="text-sm text-gray-300 mb-2">Start with simple strategies like:</p>
                        <ul className="text-sm space-y-1 text-gray-400 list-disc list-inside">
                            <li>Moving Average Crossovers</li>
                            <li>RSI Mean Reversion</li>
                        </ul>
                        <p className="text-xs text-purple-200 mt-3">
                            <strong>Goal:</strong> Learn the entire workflow from data gathering through backtesting to execution, not to make money immediately.
                        </p>
                    </div>
                </div>

                {/* Step 3: Practice Proper Backtesting */}
                <div className="bg-yellow-900/10 border-l-4 border-yellow-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-yellow-500 w-8 h-8 rounded-full flex items-center justify-center text-sm text-black font-bold">3</div>
                        Practice Proper Backtesting
                    </h3>
                    <p className="text-gray-300 mb-3">Learn to avoid common pitfalls:</p>
                    <div className="space-y-2">
                        <div className="flex items-start gap-2 bg-yellow-500/10 p-2 rounded">
                            <CheckCircle size={16} className="text-yellow-400 mt-1 shrink-0" />
                            <span className="text-sm text-gray-300"><strong className="text-white">Out-of-Sample Data:</strong> Develop on one period, test on a different period it's never seen.</span>
                        </div>
                        <div className="flex items-start gap-2 bg-yellow-500/10 p-2 rounded">
                            <CheckCircle size={16} className="text-yellow-400 mt-1 shrink-0" />
                            <span className="text-sm text-gray-300"><strong className="text-white">Realistic Costs:</strong> Account for commissions, slippage, and market impact.</span>
                        </div>
                        <div className="flex items-start gap-2 bg-yellow-500/10 p-2 rounded">
                            <AlertTriangle size={16} className="text-yellow-400 mt-1 shrink-0" />
                            <span className="text-sm text-gray-300"><strong className="text-white">Skepticism:</strong> If it looks too good to be true, it probably is.</span>
                        </div>
                    </div>
                </div>

                {/* Step 4: Paper Trade Extensively */}
                <div className="bg-cyan-900/10 border-l-4 border-cyan-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-cyan-500 w-8 h-8 rounded-full flex items-center justify-center text-sm text-black font-bold">4</div>
                        Paper Trade Extensively
                    </h3>
                    <p className="text-gray-300 mb-2">Before risking real money, run your algorithm in a <strong>paper trading environment</strong> for at least several months.</p>
                    <div className="bg-cyan-500/10 p-3 rounded border border-cyan-500/20 flex items-center gap-3">
                        <Clock size={24} className="text-cyan-400 shrink-0" />
                        <p className="text-xs text-cyan-200">This reveals issues that backtesting misses and builds confidence in your system. Many traders skip this step and regret it.</p>
                    </div>
                </div>

                {/* Step 5: Start Small with Real Money */}
                <div className="bg-green-900/10 border-l-4 border-green-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-green-500 w-8 h-8 rounded-full flex items-center justify-center text-sm">5</div>
                        Start Small with Real Money
                    </h3>
                    <div className="space-y-3">
                        <p className="text-gray-300">When you do go live, start with a <strong>small account you can afford to lose</strong>.</p>
                        <div className="bg-green-500/10 p-4 rounded border border-green-500/20">
                            <p className="text-sm text-green-200">
                                <strong>Mindset Shift:</strong> Consider your first live trading as <em>paid education</em> rather than expected profit.
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                                The psychological experience of watching real money move based on your algorithm's decisions is completely different from paper trading.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Step 6: Never Stop Learning */}
                <div className="bg-pink-900/10 border-l-4 border-pink-500 p-6 rounded-r-xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-4">
                        <div className="bg-pink-500 w-8 h-8 rounded-full flex items-center justify-center text-sm">6</div>
                        Never Stop Learning
                    </h3>
                    <p className="text-gray-300 mb-4">Markets evolve constantly. The most successful quantitative traders are perpetual students of the market.</p>
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-black/30 p-4 rounded border border-pink-500/20 text-center">
                            <BookOpenIcon size={24} className="text-pink-400 mx-auto mb-2" />
                            <div className="text-xs text-pink-200 font-bold">Research</div>
                            <div className="text-[10px] text-gray-400">Read academic papers</div>
                        </div>
                        <div className="bg-black/30 p-4 rounded border border-pink-500/20 text-center">
                            <Users size={24} className="text-pink-400 mx-auto mb-2" />
                            <div className="text-xs text-pink-200 font-bold">Network</div>
                            <div className="text-[10px] text-gray-400">Study what other quants do</div>
                        </div>
                        <div className="bg-black/30 p-4 rounded border border-pink-500/20 text-center">
                            <RefreshCw size={24} className="text-pink-400 mx-auto mb-2" />
                            <div className="text-xs text-pink-200 font-bold">Improve</div>
                            <div className="text-[10px] text-gray-400">Continuously upgrade skills</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Final Message */}
            <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 p-8 rounded-xl border border-blue-500/30 text-center">
                <h3 className="text-2xl font-bold text-white mb-3">You're Ready to Begin</h3>
                <p className="text-gray-300 max-w-2xl mx-auto">
                    This is not a get-rich-quick scheme. It's a disciplined, evidence-based approach to markets that rewards patience, skepticism, and continuous learning. Start your journey today.
                </p>
            </div>
        </div>
    );
};

// Lesson 1.0: Understanding Financial Markets and How to Access Them
const LessonFinancialMarkets = () => {
    return (
        <div className="space-y-12 max-w-5xl mx-auto p-6 pb-24">
            <div className="mb-12">
               <div className="flex items-center gap-2 mb-2 text-indigo-400">
                    <Target size={20} />
                    <span className="font-mono text-xs tracking-wider uppercase">Module 0.9</span>
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">Understanding Financial Markets & Access</h1>
                <p className="text-xl text-gray-400">
                    You can't trade directly. You need a gateway. Understanding how your broker operates—and how they profit—is as important as your strategy itself.
                </p>
            </div>

            {/* Intermediaries */}
            <section className="space-y-6">
                <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-indigo-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">1</div>
                    The Gateways: Brokers vs. Exchanges
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gray-900 border border-gray-700 p-6 rounded-xl">
                        <h3 className="text-xl font-bold text-blue-400 mb-2">Brokers</h3>
                        <p className="text-gray-300 text-sm">
                            Intermediaries for traditional markets (Stocks, Forex). They execute orders on your behalf. Regulated agencies.
                        </p>
                    </div>
                    <div className="bg-gray-900 border border-gray-700 p-6 rounded-xl">
                         <h3 className="text-xl font-bold text-purple-400 mb-2">Exchanges</h3>
                         <p className="text-gray-300 text-sm">
                            Marketplaces where buyers meet sellers directly (Crypto). You trade peer-to-peer or against the exchange's order book.
                         </p>
                    </div>
                     <div className="bg-gray-900 border border-gray-700 p-6 rounded-xl">
                         <h3 className="text-xl font-bold text-green-400 mb-2">Exchangers</h3>
                         <p className="text-gray-300 text-sm">
                            Simple swap services (Fiat to Crypto, Crypto to Crypto). High fees, low functionality, purely for conversion.
                         </p>
                    </div>
                </div>
            </section>

            {/* Deep Dive: Forex Broker Models */}
            <section className="space-y-8">
                 <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-blue-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">2</div>
                    The Forex Broker Reality: A-Book vs B-Book
                </h2>
                <div className="bg-yellow-900/20 border border-yellow-500/30 p-6 rounded-xl">
                    <div className="flex items-start gap-4">
                        <AlertTriangle className="text-yellow-500 shrink-0 mt-1" size={24} />
                         <div>
                            <h3 className="text-xl font-bold text-yellow-400 mb-2">Why This Matters</h3>
                            <p className="text-gray-300">
                                Not all brokers are on your side. In the "B-Book" model, <strong>when you lose, the broker wins</strong>. Understanding this conflict of interest is critical for survival.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* A-Book */}
                    <div className="bg-gray-800/50 p-6 rounded-xl border border-green-500/30">
                         <div className="flex items-center justify-between mb-4">
                            <h3 className="text-2xl font-bold text-green-400">A-Book (The Agent)</h3>
                            <span className="px-3 py-1 bg-green-900/50 text-green-300 text-xs rounded-full border border-green-500/30">ECN / STP</span>
                         </div>
                         <p className="text-gray-400 mb-4 text-sm">
                            The broker passes your trade directly to the real market (Banks, Liquidity Providers). They earn a commission. They want you to win so you keep trading.
                         </p>
                         <ul className="space-y-2">
                            <li className="flex items-center gap-2 text-gray-300 text-sm"><CheckCircle size={14} className="text-green-500"/> No Conflict of Interest</li>
                            <li className="flex items-center gap-2 text-gray-300 text-sm"><CheckCircle size={14} className="text-green-500"/> True Market Spreads</li>
                            <li className="flex items-center gap-2 text-gray-300 text-sm"><CheckCircle size={14} className="text-green-500"/> Best for Scalpers & Algos</li>
                         </ul>
                    </div>

                    {/* B-Book */}
                    <div className="bg-gray-800/50 p-6 rounded-xl border border-red-500/30">
                         <div className="flex items-center justify-between mb-4">
                            <h3 className="text-2xl font-bold text-red-400">B-Book (The House)</h3>
                            <span className="px-3 py-1 bg-red-900/50 text-red-300 text-xs rounded-full border border-red-500/30">Market Maker</span>
                         </div>
                         <p className="text-gray-400 mb-4 text-sm">
                            The broker takes the other side of your trade. They keep your trade "in-house". If you profit, they pay you from their pocket. If you lose, they keep your money.
                         </p>
                         <ul className="space-y-2">
                            <li className="flex items-center gap-2 text-gray-300 text-sm"><X size={14} className="text-red-500"/> Direct Conflict of Interest</li>
                             <li className="flex items-center gap-2 text-gray-300 text-sm"><X size={14} className="text-red-500"/> Potential "Stop Hunting"</li>
                            <li className="flex items-center gap-2 text-gray-300 text-sm"><CheckCircle size={14} className="text-yellow-500"/> Good for Small Accounts (No Commissions)</li>
                         </ul>
                    </div>
                </div>

                <div className="bg-blue-900/20 p-6 rounded-xl border border-blue-500/30">
                    <h3 className="text-lg font-bold text-white mb-2">The Hybrid Model</h3>
                    <p className="text-gray-300 text-sm">
                        Most brokers use both. They <strong>B-Book</strong> losing traders (pocketing the losses) and <strong>A-Book</strong> profitable traders (passing the risk to the market). 
                        <br/><br/>
                        <em>Goal:</em> Be good enough that the broker is forced to A-Book you.
                    </p>
                 </div>
            </section>

             {/* Market Access Overview */}
            <section className="space-y-8">
                 <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-purple-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">3</div>
                    Accessing Other Markets
                </h2>
                
                <div className="space-y-4">
                    <div className="flex gap-4 p-4 bg-gray-800/30 rounded-lg hover:bg-gray-800/50 transition">
                         <TrendingUp className="text-blue-400 shrink-0" size={24} />
                         <div>
                             <h4 className="font-bold text-white">Stock Markets</h4>
                             <p className="text-sm text-gray-400">Accessed via Stock Brokers. Usually Agency model (they route to NYSE/NASDAQ). Watch out for PFOF (Payment for Order Flow) on "free" apps.</p>
                         </div>
                    </div>
                     <div className="flex gap-4 p-4 bg-gray-800/30 rounded-lg hover:bg-gray-800/50 transition">
                         <Cpu className="text-purple-400 shrink-0" size={24} />
                         <div>
                             <h4 className="font-bold text-white">Crypto Markets</h4>
                             <p className="text-sm text-gray-400">Accessed via Centralized Exchanges (CEX) like Binance/Coinbase (Custodial) or Decentralized Exchanges (DEX) like Uniswap (Non-Custodial).</p>
                         </div>
                    </div>
                    <div className="flex gap-4 p-4 bg-gray-800/30 rounded-lg hover:bg-gray-800/50 transition">
                         <Layers className="text-yellow-400 shrink-0" size={24} />
                         <div>
                             <h4 className="font-bold text-white">Commodities & Bonds</h4>
                             <p className="text-sm text-gray-400">Often traded as CFDs (Contracts for Difference) by retail traders. You don't own the oil barrel; you bet on its price.</p>
                         </div>
                    </div>
                </div>
            </section>

            {/* Checklist */}
             <section className="space-y-6">
                 <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <div className="bg-green-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">4</div>
                    How to Choose
                </h2>
                <div className="bg-black/40 p-6 rounded-xl border border-white/10">
                    <ul className="space-y-4">
                        <li className="flex items-start gap-3">
                             <div className="bg-gray-700 p-1 rounded text-white font-bold text-xs mt-1">1</div>
                             <div>
                                 <strong className="text-white block">Capital &lt; $1,000</strong>
                                 <span className="text-gray-400 text-sm">Accept B-Book/Hybrid. Focus on low spreads and learning. Don't worry about "market manipulation" yet—your biggest enemy is you.</span>
                             </div>
                        </li>
                         <li className="flex items-start gap-3">
                             <div className="bg-gray-700 p-1 rounded text-white font-bold text-xs mt-1">2</div>
                             <div>
                                 <strong className="text-white block">Capital &gt; $10,000</strong>
                                 <span className="text-gray-400 text-sm">Demand A-Book / ECN. You need execution speed, transparency, and safety of funds.</span>
                             </div>
                        </li>
                         <li className="flex items-start gap-3">
                             <div className="bg-gray-700 p-1 rounded text-white font-bold text-xs mt-1">3</div>
                             <div>
                                 <strong className="text-white block">Regulation is King</strong>
                                 <span className="text-gray-400 text-sm">Only use brokers regulated by tier-1 authorities (FCA, ASIC, CFTC/NFA). Offshore is for cowboys.</span>
                             </div>
                        </li>
                    </ul>
                </div>
            </section>
        </div>
    );
};

// Lesson Content Components
const LessonDavidGoliath = () => (
  <div className="space-y-8">
    <div className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 p-8 rounded-2xl border border-blue-500/20">
      <h3 className="text-2xl font-bold mb-4 text-blue-400">Agility vs. Size</h3>
      <p className="text-lg text-gray-300 leading-relaxed mb-6">
        Institutional whales move markets, but they are slow. They cannot enter or exit positions without slippage. 
        You are a speedboat. You can enter and exit in milliseconds without anyone noticing.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <div className="bg-black/40 p-6 rounded-xl border border-white/5 flex flex-col items-center text-center">
          <div className="bg-gray-800 p-4 rounded-full mb-4">
            <Ship size={40} className="text-gray-400" />
          </div>
          <h4 className="font-bold text-xl mb-2">The Institution (Tanker)</h4>
          <ul className="text-sm text-gray-400 space-y-2 text-left w-full mt-2">
            <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>Huge capital requirements</li>
            <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>Regulatory red tape</li>
            <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>Moves the market (Slippage)</li>
          </ul>
        </div>
        
        <div className="bg-blue-900/10 p-6 rounded-xl border border-blue-500/30 flex flex-col items-center text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-blue-500/5 animate-pulse"></div>
          <div className="bg-blue-500/20 p-4 rounded-full mb-4 relative z-10">
            <Zap size={40} className="text-blue-400" />
          </div>
          <h4 className="font-bold text-xl mb-2 text-blue-300">You (Speedboat)</h4>
          <ul className="text-sm text-gray-300 space-y-2 text-left w-full mt-2 relative z-10">
            <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>Instant execution</li>
            <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>No market impact</li>
            <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>Can pursue niche anomalies</li>
          </ul>
        </div>
      </div>
      <div className="mt-8 p-4 bg-yellow-900/10 border border-yellow-500/20 rounded-xl">
        <h5 className="font-bold text-yellow-500 mb-1 flex items-center gap-2">
            <DollarSign size={16} /> Quant Note: The Cost of Speed
        </h5>
        <p className="text-sm text-gray-400">
            While we pay higher spreads/commissions than institutions, our ability to enter and exit instantly often outweighs the cost difference. 
            We trade agility for fees.
        </p>
      </div>

      <div className="p-4 bg-red-900/10 border border-red-500/20 rounded-xl">
        <h5 className="font-bold text-red-500 mb-1 flex items-center gap-2">
            <Zap size={16} /> Reality Check: Fast ≠ HFT
        </h5>
        <div className="text-sm text-gray-400 space-y-2">
            <p>
                <strong className="text-white">We do not fight High-Frequency Traders (HFT) on speed.</strong> <br/>
                They have microwave towers and FPGA chips; we have fiber optics and Python. If we try to scalp ticks in microseconds, we lose.
            </p>
            <p>
                The Retail Quant Edge is not "Faster," it is <strong className="text-blue-400">"Smarter."</strong> We trade minutes and hours, not nanoseconds.
            </p>
        </div>
      </div>
    </div>
  </div>
);

const LessonCasinoMindset = () => (
  <div className="space-y-8">
    <div className="flex flex-col md:flex-row gap-8 items-center">
      <div className="flex-1">
        <h3 className="text-2xl font-bold mb-4 text-purple-400">Harvesting the Edge</h3>
        <p className="text-gray-300 leading-relaxed mb-4">
          A manual trader tries to be right on <em>this</em> trade. <br/>
          A quant knows that the outcome of any single trade is random. 
          The profit comes from the <strong>statistical edge</strong> over 100 trades.
        </p>
        <div className="bg-purple-900/10 p-4 rounded-lg border-l-4 border-purple-500">
          <p className="italic text-purple-300">"We don't predict. We react to probabilities."</p>
        </div>
      </div>
      <div className="flex-1 w-full bg-black/40 p-6 rounded-xl border border-white/10">
        <div className="space-y-4">
          <div className="flex justify-between text-sm text-gray-400">
            <span>Manual Trader Anxiety</span>
            <span>Quant Confidence</span>
          </div>
          <div className="h-4 bg-gray-700 rounded-full overflow-hidden flex">
            <div className="w-1/2 bg-red-500/50"></div>
            <div className="w-1/2 bg-green-500/50"></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Win or Loss? (Stressed)</span>
            <span>Law of Large Numbers (Relaxed)</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const LessonAutomationStages = () => (
  <div className="space-y-8">
    <div className="relative">
      <div className="absolute left-[15px] top-6 bottom-6 w-0.5 bg-gradient-to-b from-blue-500 to-purple-600"></div>
      
      {[
        { 
          title: "Stage 1: Semi-Automated (The Cyborg)",
          desc: "You trade manually, but use scripts for risk calculation and instant execution. The dashboard helps you, but you pull the trigger.",
          color: "blue" 
        },
        { 
          title: "Stage 2: Grey Box (The Supervisor)",
          desc: "The algorithm finds the setup and proposes the trade. You receive an alert (Telegram/Push). You click 'Approve' or 'Reject'.",
          color: "purple" 
        },
        { 
          title: "Stage 3: Black Box (The Ghost)",
          desc: "Full automation. The system runs on a VPS 24/7. You check the P&L once a day. This is the ultimate goal.",
          color: "green" 
        }
      ].map((stage, i) => (
        <motion.div 
          key={i}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.2 }}
          className="flex gap-6 mb-8 relative"
        >
          <div className={`w-8 h-8 rounded-full bg-${stage.color}-500/20 border-2 border-${stage.color}-500 flex items-center justify-center shrink-0 z-10 bg-black`}>
            <span className={`text-${stage.color}-400 font-bold text-sm`}>{i + 1}</span>
          </div>
          <div className="bg-white/5 p-6 rounded-xl border border-white/10 flex-1 hover:border-white/20 transition-colors">
            <h4 className={`text-xl font-bold mb-2 text-${stage.color}-400`}>{stage.title}</h4>
            <p className="text-gray-400">{stage.desc}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

const LessonQuantDictionary = () => {
    const [searchTerm, setSearchTerm] = useState("");
    
    const terms = [
      { letter: "A", items: [
          { t: "Alpha", d: "The excess return of an investment relative to a benchmark index. In quant trading, 'generating alpha' means finding strategies that consistently outperform the market. Alpha is also used to describe the edge or profitability of a trading strategy." },
          { t: "Algorithm", d: "A set of rules or instructions programmed into a computer to automatically execute trades based on predefined conditions without human intervention." },
          { t: "Arbitrage", d: "The simultaneous purchase and sale of an asset in different markets to profit from price discrepancies. In efficient markets, arbitrage opportunities are rare and short-lived." },
          { t: "Ask Price", d: "The lowest price at which a seller is willing to sell a security. Also called the 'offer price'." },
          { t: "Asset Class", d: "A group of securities with similar characteristics, such as stocks, bonds, commodities, currencies, or derivatives." },
          { t: "Autocorrelation", d: "The correlation of a time series with itself at different time lags. Important for identifying momentum or mean reversion patterns." }
      ]},
      { letter: "B", items: [
          { t: "Backtest", d: "The process of testing a trading strategy on historical data to evaluate how it would have performed in the past. Critical for strategy validation but prone to numerous biases." },
          { t: "Basis Point (bp)", d: "One hundredth of one percent (0.01%). Used to describe small changes in interest rates or returns." },
          { t: "Basket Trading", d: "Simultaneously trading a group of securities as a single unit, often used in index arbitrage or sector rotation strategies." },
          { t: "Beta", d: "A measure of a security's volatility relative to the overall market. A beta of 1 means the security moves with the market; greater than 1 means more volatile; less than 1 means less volatile." },
          { t: "Bid Price", d: "The highest price a buyer is willing to pay for a security." },
          { t: "Bid-Ask Spread", d: "The difference between the bid and ask prices. This represents a transaction cost and reflects market liquidity." },
          { t: "Black-Scholes Model", d: "A mathematical model for pricing European-style options, fundamental to options trading and derivatives pricing." },
          { t: "Bollinger Bands", d: "A technical indicator consisting of a moving average with upper and lower bands based on standard deviations, used to identify overbought or oversold conditions." }
      ]},
      { letter: "C", items: [
          { t: "Carry Trade", d: "A strategy that borrows in a low-interest-rate currency and invests in a higher-interest-rate currency to profit from the interest rate differential." },
          { t: "Colocation", d: "Placing trading servers physically close to exchange servers to minimize latency and gain speed advantages. Common in high-frequency trading." },
          { t: "Correlation", d: "A statistical measure of how two securities move in relation to each other, ranging from -1 (perfect negative correlation) to +1 (perfect positive correlation)." },
          { t: "Covariance", d: "A measure of how two variables change together. Used in portfolio optimization and risk management." },
          { t: "Cross-Validation", d: "A technique for testing model performance by dividing data into multiple subsets and testing on data the model hasn't seen during training." }
      ]},
      { letter: "D", items: [
          { t: "Dark Pool", d: "Private exchanges where large institutional orders are matched anonymously, away from public markets, to minimize market impact." },
          { t: "Data Mining Bias", d: "Overfitting that occurs when you test so many variables and combinations that you eventually find patterns that appear profitable by pure chance." },
          { t: "Day Trading", d: "Buying and selling securities within the same trading day, with no positions held overnight." },
          { t: "Drawdown", d: "The decline from a historical peak in portfolio value. Maximum drawdown is the largest peak-to-trough decline and is a key risk metric." },
          { t: "DMA (Direct Market Access)", d: "Technology that allows traders to interact directly with the order book of exchanges without going through a broker's trading desk." }
      ]},
      { letter: "E", items: [
          { t: "Edge", d: "A trading advantage that gives you a higher probability of profit than random chance. The foundation of any profitable strategy." },
          { t: "Equity Curve", d: "A chart showing the cumulative profit or loss of a trading strategy over time. Used to visualize strategy performance." },
          { t: "EMA (Exponential Moving Average)", d: "A moving average that gives more weight to recent prices, making it more responsive to new information than a simple moving average." },
          { t: "Exchange", d: "A marketplace where securities, commodities, or derivatives are traded, such as NYSE, NASDAQ, or CME." },
          { t: "Execution", d: "The completion of a buy or sell order. Execution quality involves factors like speed, price improvement, and slippage." }
      ]},
      { letter: "F", items: [
          { t: "Factor Model", d: "A model that explains asset returns based on exposure to various factors like value, momentum, size, or volatility." },
          { t: "Fill", d: "The execution of an order. A 'partial fill' means only part of your order was executed." },
          { t: "Forward Testing", d: "Testing a strategy on new, out-of-sample data after it's been developed, but before deploying real capital. Also called 'paper trading' when done in real-time." },
          { t: "Futures Contract", d: "A standardized agreement to buy or sell an asset at a predetermined price at a specified time in the future." }
      ]},
      { letter: "G", items: [
          { t: "Greeks", d: "Measures of option price sensitivity to various factors: Delta (price), Gamma (delta change), Theta (time decay), Vega (volatility), and Rho (interest rates)." },
          { t: "Grid Trading", d: "A strategy that places buy and sell orders at regular intervals above and below a set price, profiting from market oscillation." }
      ]},
      { letter: "H", items: [
          { t: "Hedge", d: "A position taken to offset potential losses in another investment. Hedging reduces risk but also limits potential gains." },
          { t: "High-Frequency Trading (HFT)", d: "Algorithmic trading characterized by extremely high speeds, short position holding periods (often seconds or milliseconds), and very high order-to-trade ratios." },
          { t: "Historical Volatility", d: "The actual volatility of an asset's price over a past period, calculated from historical price data." },
          { t: "Holding Period", d: "The length of time a position is maintained, from entry to exit." }
      ]},
      { letter: "I", items: [
          { t: "Implied Volatility", d: "The market's forecast of likely price movement, derived from option prices. Higher implied volatility means options are more expensive." },
          { t: "In-Sample Data", d: "The historical data used to develop and optimize a trading strategy. Should be kept separate from out-of-sample data used for validation." },
          { t: "Intraday", d: "Occurring within a single trading day. Intraday trading means opening and closing positions within the same day." }
      ]},
      { letter: "K", items: [
          { t: "Kelly Criterion", d: "A formula for optimal position sizing that maximizes long-term growth rate based on edge and odds. Often used conservatively (fractional Kelly) due to estimation errors." },
          { t: "Kurtosis", d: "A measure of the 'tailedness' of a probability distribution. High kurtosis means more extreme events (fat tails) than a normal distribution predicts." }
      ]},
      { letter: "L", items: [
          { t: "Latency", d: "The time delay between initiating an action and its execution. In HFT, measured in microseconds or nanoseconds." },
          { t: "Leverage", d: "Using borrowed capital to increase position size and amplify returns (and losses). A 2:1 leverage means you control $2 of assets for every $1 of capital." },
          { t: "Limit Order", d: "An order to buy or sell at a specific price or better. Provides price certainty but no guarantee of execution." },
          { t: "Liquidity", d: "The ease with which an asset can be bought or sold without significantly affecting its price. High liquidity means tight spreads and large available volumes." },
          { t: "Long Position", d: "Buying a security with the expectation that its price will rise. Profits from upward price movement." },
          { t: "Look-Ahead Bias", d: "A backtesting error where future information is inadvertently used in past decisions, creating unrealistically good results." }
      ]},
      { letter: "M", items: [
          { t: "Machine Learning", d: "Algorithms that learn patterns from data without being explicitly programmed. Used increasingly in quantitative trading for pattern recognition and prediction." },
          { t: "Market Impact", d: "The effect of your trading activity on market prices. Large orders tend to move prices against you." },
          { t: "Market Maker", d: "A firm or individual that continuously quotes both buy and sell prices, providing liquidity and profiting from the bid-ask spread." },
          { t: "Market Order", d: "An order to buy or sell immediately at the best available current price. Guarantees execution but not price." },
          { t: "Mean Reversion", d: "The tendency of prices to return to their average or historical mean after extreme moves." },
          { t: "Momentum", d: "The tendency of assets that have performed well (or poorly) to continue performing well (or poorly) in the near term." },
          { t: "Monte Carlo Simulation", d: "A computational technique that runs thousands of simulations with randomized variables to assess risk and model uncertainty." }
      ]},
      { letter: "O", items: [
          { t: "Order Book", d: "A real-time list of buy and sell orders for a security, organized by price level. Shows market depth and liquidity." },
          { t: "Out-of-Sample Data", d: "Historical data not used in strategy development, reserved for testing to validate that the strategy generalizes beyond the training period." },
          { t: "Overfitting", d: "When a model is too closely tailored to historical data, learning noise rather than signal, resulting in poor performance on new data." },
          { t: "Overweight", d: "Holding a larger allocation to an asset than its benchmark weight. Opposite of underweight." }
      ]},
      { letter: "P", items: [
          { t: "P&L (Profit and Loss)", d: "The net profit or loss from trading activity over a specific period." },
          { t: "Pairs Trading", d: "A market-neutral strategy that simultaneously buys an undervalued security and shorts an overvalued security from the same sector, profiting from convergence." },
          { t: "Paper Trading", d: "Simulating trades with fake money in real market conditions to test a strategy without risking capital." },
          { t: "Portfolio Optimization", d: "The process of selecting the best portfolio allocation to maximize returns for a given risk level or minimize risk for a given return target." },
          { t: "Position Sizing", d: "Determining how much capital to allocate to each trade based on risk tolerance, strategy edge, and portfolio constraints." },
          { t: "Premium", d: "The price of an option, or the amount paid above face value for a bond or security." }
      ]},
      { letter: "Q", items: [
          { t: "Quantitative Analysis", d: "Using mathematical and statistical methods to analyze financial markets and securities." },
          { t: "Quote", d: "The current bid and ask prices for a security." }
    ]},
    { letter: "R", items: [
        { t: "Regime Change", d: "A fundamental shift in market behavior or characteristics, such as moving from low to high volatility or from trending to range-bound conditions." },
        { t: "Regression", d: "A statistical method for modeling relationships between variables. Linear regression is commonly used in factor models." },
        { t: "Reversion to the Mean", d: "See Mean Reversion." },
        { t: "Risk Management", d: "The process of identifying, assessing, and controlling threats to trading capital through position sizing, diversification, and hedging." },
        { t: "Risk-Adjusted Return", d: "Returns measured relative to the risk taken. Sharpe ratio and Sortino ratio are common measures." },
        { t: "RSI (Relative Strength Index)", d: "A momentum oscillator ranging from 0-100 that identifies overbought (>70) and oversold (<30) conditions." }
    ]},
    { letter: "S", items: [
        { t: "Scalping", d: "A strategy involving very frequent trades that aim to profit from small price movements, typically holding positions for seconds to minutes." },
        { t: "Sharpe Ratio", d: "A measure of risk-adjusted return calculated as (return - risk-free rate) / standard deviation. Higher is better; above 1.0 is generally considered good." },
        { t: "Short Position", d: "Selling a borrowed security with the expectation of buying it back at a lower price. Profits from downward price movement." },
        { t: "Slippage", d: "The difference between expected trade price and actual execution price, typically due to market movement or liquidity constraints." },
        { t: "SMA (Simple Moving Average)", d: "The average price over a specified number of periods, giving equal weight to each period." },
        { t: "Sortino Ratio", d: "Similar to Sharpe ratio but only considers downside volatility, treating upside volatility as desirable rather than risky." },
        { t: "Spread Trading", d: "Trading the price difference between related instruments, such as calendar spreads (different expiration dates) or inter-market spreads." },
        { t: "Statistical Arbitrage (Stat Arb)", d: "Strategies that exploit statistical mispricings between related securities, often involving mean reversion or correlation breakdowns." },
        { t: "Stop Loss", d: "An order that automatically closes a position when price reaches a specified level to limit losses." },
        { t: "Survivorship Bias", d: "A backtesting error that occurs when historical data only includes securities that still exist, excluding those that failed or were delisted." },
        { t: "Swing Trading", d: "A strategy that holds positions for several days to weeks to capture short- to medium-term trends." }
    ]},
    { letter: "T", items: [
        { t: "Technical Analysis", d: "The study of historical price and volume data to identify patterns and predict future movements." },
        { t: "Theta", d: "The rate of time decay in an option's value. All options lose value as expiration approaches, all else equal." },
        { t: "Tick", d: "The minimum price movement of a security. Tick data is the highest resolution price data available." },
        { t: "Time Series", d: "A sequence of data points ordered in time, such as historical prices. Central to quantitative analysis." },
        { t: "Transaction Cost", d: "All costs associated with trading including commissions, exchange fees, bid-ask spreads, slippage, and market impact." },
        { t: "Trend Following", d: "A strategy that identifies and trades in the direction of sustained price movements." }
    ]},
    { letter: "U", items: [
        { t: "Underweight", d: "Holding a smaller allocation to an asset than its benchmark weight. Opposite of overweight." }
    ]},
    { letter: "V", items: [
        { t: "VaR (Value at Risk)", d: "A statistical measure of potential portfolio loss over a specified time period at a given confidence level." },
        { t: "Vega", d: "An option Greek measuring sensitivity to changes in implied volatility." },
        { t: "Volatility", d: "The degree of price variation over time. Higher volatility means larger and more frequent price swings." },
        { t: "Volume", d: "The number of shares or contracts traded during a specific period. Often used as a measure of interest and liquidity." },
        { t: "VWAP (Volume-Weighted Average Price)", d: "The average price weighted by volume, often used as an execution benchmark or trading signal." }
    ]},
    { letter: "W", items: [
        { t: "Walk-Forward Analysis", d: "A more robust backtesting method that repeatedly optimizes on a training period and tests on the subsequent period, simulating how a strategy would actually be used." },
        { t: "Wash Trading", d: "Illegal practice of simultaneously buying and selling the same security to create false activity. Prohibited in regulated markets." },
        { t: "Whipsaw", d: "When a security's price rapidly reverses direction, causing losses for trend followers or triggering stop losses prematurely." }
    ]},
    { letter: "Z", items: [
        { t: "Z-Score", d: "A statistical measure indicating how many standard deviations a data point is from the mean. Used in mean reversion strategies to identify extreme deviations." }
    ]}
  ];

  const strategyTypes = [
    { name: "Momentum", desc: "Buy winners, sell losers" },
    { name: "Mean Reversion", desc: "Buy oversold, sell overbought" },
    { name: "Arbitrage", desc: "Exploit price discrepancies" },
    { name: "Market Making", desc: "Provide liquidity, earn spread" },
    { name: "Trend Following", desc: "Trade directional moves" },
    { name: "Statistical Arbitrage", desc: "Pairs trading, correlation strategies" },
    { name: "Factor Investing", desc: "Exposure to value, size, quality factors" },
    { name: "Market Neutral", desc: "Long and short positions that offset market risk" }
  ];

  const filteredTerms = terms.map(group => ({
    ...group,
    items: group.items.filter(item => 
      item.t.toLowerCase().includes(searchTerm.toLowerCase()) || 
      item.d.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(group => group.items.length > 0);

  return (
    <div className="space-y-12 max-w-5xl mx-auto p-2 pb-24">
      
      {/* Search Header */}
      <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 p-8 rounded-2xl border border-blue-500/20 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="relative z-10">
          <h2 className="text-3xl font-bold text-white mb-4">The Quant Dictionary</h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Essential terminology for Quantitative & Algorithmic Trading. From "Alpha" to "Z-Score".
          </p>
          
          <div className="relative max-w-xl mx-auto">
            <input
              type="text"
              placeholder="Search term or definition..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-black/60 border border-blue-500/30 rounded-full px-6 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-lg"
            />
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" size={24} />
          </div>
        </div>
      </div>

      {/* Quick Reference Section */}
      {searchTerm === "" && (
        <section className="space-y-4">
           <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" />
              Quick Reference: Strategy Types
           </h3>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {strategyTypes.map((strat, i) => (
                  <div key={i} className="bg-gray-900/40 border border-white/5 p-4 rounded-xl hover:bg-white/5 transition-all">
                      <div className="font-bold text-blue-300 mb-1">{strat.name}</div>
                      <div className="text-xs text-gray-400">{strat.desc}</div>
                  </div>
              ))}
           </div>
        </section>
      )}

      {/* Dictionary Content */}
      <div className="space-y-12">
        {filteredTerms.length > 0 ? (
          filteredTerms.map((group) => (
            <div key={group.letter} className="relative">
              {/* Sticky Letter Header */}
              <div className="sticky top-0 z-20 bg-black/90 backdrop-blur border-b border-white/10 py-2 mb-6">
                <span className="text-4xl font-bold text-blue-500/50">{group.letter}</span>
              </div>
              
              <div className="grid gap-6">
                {group.items.map((item, i) => (
                  <div key={i} className="group bg-gray-900/20 rounded-xl p-6 border border-white/5 hover:border-blue-500/30 transition-all">
                    <h4 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                      {item.t}
                    </h4>
                    <p className="text-gray-300 leading-relaxed text-sm md:text-base">
                      {item.d}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
           <div className="text-center py-20">
              <div className="text-gray-500 mb-2">No terms found for "{searchTerm}"</div>
              <button onClick={() => setSearchTerm("")} className="text-blue-400 hover:underline">Clear Search</button>
           </div>
        )}
      </div>

    </div>
  );
};

// ... (LessonEcosystem and LessonFunding remain unchanged, assumed to be defined previously or above) ...

const LessonEcosystem = () => {
  const sections = [
    {
      title: "1. The Core: MetaTrader 5",
      subtitle: "The Engine",
      icon: Layers,
      color: "blue",
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            This is where the rubber meets the road. It connects to the broker, receives price ticks, and executes orders.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 p-4 rounded-lg">
                <h5 className="text-blue-400 font-bold mb-1">Why MT5 &gt; MT4?</h5>
                <p className="text-xs text-gray-400">MT4 is a 32-bit dinosaur. MT5 is a 64-bit multi-threaded beast with native Python integration.</p>
            </div>
            <div className="bg-white/5 p-4 rounded-lg">
                <h5 className="text-green-400 font-bold mb-1">The "Data Folder"</h5>
                <p className="text-xs text-gray-400">File -&gt; Open Data Folder. Treat this as your sanctuary. This is where your robots live.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "2. The Workshop: MetaEditor",
      subtitle: "The IDE",
      icon: Code,
      color: "yellow",
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            The Integrated Development Environment where we write MQL5 code.
          </p>
          <div className="bg-yellow-900/20 border border-yellow-500/30 p-4 rounded-lg flex items-center gap-4">
            <div className="bg-yellow-500/20 p-2 rounded-full"><Zap size={20} className="text-yellow-400"/></div>
            <div>
                <h5 className="text-white font-bold">Key Feature: The Debugger</h5>
                <p className="text-sm text-gray-400">Freeze time (pause the robot) and inspect variables tick-by-tick.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. The Brain: Python & Jupyter",
      subtitle: "Strategy Discovery",
      icon: TrendingUp,
      color: "green",
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            You don't guess strategies. You export 10 years of data found in MT5 to Python.
          </p>
          <ul className="text-sm text-gray-400 list-disc list-inside space-y-1">
            <li>Use <code className="text-green-400">pandas</code> and <code className="text-green-400">numpy</code> to find statistical edges.</li>
            <li>Write the MQL5 bot to trade that edge.</li>
            <li>Use the <strong>MetaTrader5 Python library</strong> to make them talk.</li>
          </ul>
        </div>
      )
    },
    {
      title: "4. The Heartbeat: The VPS",
      subtitle: "Keeping the Lights On",
      icon: Database, // Assuming Database or Server icon, using Database as proxy if Server not imported, will check imports
      color: "purple",
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">
            You cannot run a robot on your home laptop. WiFi drops and Windows updates = lost money.
          </p>
          <div className="bg-purple-900/20 border border-purple-500/30 p-4 rounded-lg">
            <h5 className="text-white font-bold mb-2">The Quant Standard</h5>
            <p className="text-sm text-gray-400">
               Rent a slice of a server (VPS) in the same data center as your broker (e.g., London Equinix LD4).
               <br/><span className="text-purple-400 font-bold">Target: &lt; 2ms latency.</span>
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-12">
      <div className="grid grid-cols-1 gap-6">
        {sections.map((section, idx) => (
            <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`bg-gray-900/50 border border-${section.color}-500/20 p-6 rounded-xl hover:border-${section.color}-500/50 transition-colors`}
            >
                <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl bg-${section.color}-500/10`}>
                        <section.icon size={24} className={`text-${section.color}-400`} />
                    </div>
                    <div className="flex-1">
                        <div className="flex justify-between items-start">
                            <h3 className="text-xl font-bold text-white mb-1">{section.title}</h3>
                            <span className={`text-xs font-mono uppercase px-2 py-1 rounded bg-${section.color}-500/10 text-${section.color}-400`}>{section.subtitle}</span>
                        </div>
                        <div className="mt-4">
                            {section.content}
                        </div>
                    </div>
                </div>
            </motion.div>
        ))}
      </div>

      {/* Student Action Plan */}
      <div className="border-t border-white/10 pt-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Terminal className="text-blue-500" /> Student Action Plan
        </h2>
        <div className="bg-black/40 p-6 rounded-2xl border border-white/10 space-y-4">
            <p className="text-gray-400 italic mb-4">Before writing code in Module 2, you must have the environment ready.</p>
            
            <div className="space-y-3">
                <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg border border-white/5">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">1</div>
                    <div>
                        <h5 className="font-bold text-white">Download & Install</h5>
                        <p className="text-xs text-gray-400">Get MT5 from your broker or MetaQuotes.</p>
                    </div>
                </div>
                <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg border border-white/5">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">2</div>
                    <div>
                        <h5 className="font-bold text-white">The "Hello" Check</h5>
                        <p className="text-xs text-gray-400">Open MetaEditor (Press F4 in MT5).</p>
                    </div>
                </div>
                <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg border border-white/5">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">3</div>
                    <div>
                        <h5 className="font-bold text-white">Locate the Logs</h5>
                        <p className="text-xs text-gray-400">Find the "Journal" tab. This is your Black Box recorder.</p>
                    </div>
                </div>
            </div>
            
            <div className="mt-6 p-4 bg-green-900/20 border border-green-500/30 rounded-xl text-center">
                <p className="text-green-400 font-bold">
                    Strategic Checkpoint Reached! <br/>
                    <span className="text-sm font-normal text-gray-300">You now have the Mindset, Vocabulary, and Tools.</span>
                </p>
            </div>
        </div>
      </div>
    </div>
  );
};

const LessonFunding = () => {
    const [activeTab, setActiveTab] = useState('fee');

    return (
        <div className="space-y-8">
            {/* Intro */}
            <div className="bg-gradient-to-r from-green-900/20 to-blue-900/20 p-6 rounded-2xl border border-green-500/20">
                <h3 className="text-2xl font-bold mb-2">Two Paths to Capital</h3>
                 <p className="text-gray-300">
                    The retail trading landscape has split. You must choose your battlefield wisely.
                </p>
            </div>

            {/* Tab Navigation */}
            <div className="flex bg-black/40 p-1 rounded-xl">
                 <button 
                    onClick={() => setActiveTab('fee')}
                    className={`flex-1 py-3 px-6 rounded-lg text-sm font-bold transition-all ${activeTab === 'fee' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                 >
                    1. Fee-Based (Retail Prop)
                 </button>
                 <button 
                    onClick={() => setActiveTab('merit')}
                    className={`flex-1 py-3 px-6 rounded-lg text-sm font-bold transition-all ${activeTab === 'merit' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
                 >
                    2. Institutional (The Goal)
                 </button>
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
                {activeTab === 'fee' && (
                    <motion.div key="fee" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                        <div className="bg-blue-900/10 border border-blue-500/20 p-6 rounded-xl">
                            <h4 className="text-xl font-bold text-blue-400 mb-4 flex items-center gap-2">
                                <Zap className="text-yellow-400" size={20}/> The "Quick Path"
                            </h4>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <h5 className="font-bold text-white mb-2">The Model</h5>
                                    <p className="text-sm text-gray-400 mb-4">
                                        You pay an evaluation fee (e.g., $100-$500). If you pass their rules (profit targets, drawdown), you get a funded account.
                                    </p>
                                    <div className="flex gap-2 text-xs">
                                        <span className="bg-white/10 px-2 py-1 rounded">FTMO</span>
                                        <span className="bg-white/10 px-2 py-1 rounded">Apex</span>
                                        <span className="bg-white/10 px-2 py-1 rounded">TopStep</span>
                                    </div>
                                </div>
                                <div className="bg-black/20 p-4 rounded-lg">
                                    <h5 className="font-bold text-gray-300 mb-2">The Reality</h5>
                                    <ul className="text-sm text-gray-400 space-y-2">
                                        <li className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500"/> Low barrier to entry.</li>
                                        <li className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500"/> Gamified learning.</li>
                                        <li className="flex items-center gap-2"><div className="w-3.5 h-3.5 rounded-full border border-red-500 flex items-center justify-center text-[10px] text-red-500">!</div> High failure rate.</li>
                                        <li className="flex items-center gap-2"><div className="w-3.5 h-3.5 rounded-full border border-red-500 flex items-center justify-center text-[10px] text-red-500">!</div> You are the customer.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {activeTab === 'merit' && (
                    <motion.div key="merit" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                         <div className="bg-purple-900/10 border border-purple-500/20 p-6 rounded-xl">
                            <h4 className="text-xl font-bold text-purple-400 mb-4 flex items-center gap-2">
                                <DollarSign className="text-green-400" size={20}/> The "Career Path"
                            </h4>
                            <p className="text-gray-300 mb-6">
                                The Model: Crowdsourced Hedge Funds. They don't sell "challenges". They license your "Alpha" (Intellectual Property).
                            </p>

                            <div className="space-y-4">
                                {/* Category A */}
                                <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                                    <h5 className="font-bold text-white mb-2">Category A: Submit Code (Crowdsourced Alpha)</h5>
                                    <p className="text-sm text-gray-400 mb-4">You provide the logic; they provide the capital.</p> 
                                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                                        <div className="bg-black/20 p-3 rounded-lg">
                                            <strong className="text-purple-300 block mb-1">QuantConnect</strong>
                                            <span className="text-gray-500">Write Python/C# using LEAN engine. High score = Hedge funds license your signal.</span>
                                        </div>
                                        <div className="bg-black/20 p-3 rounded-lg">
                                            <strong className="text-purple-300 block mb-1">Numerai</strong>
                                            <span className="text-gray-500">Data Science HF. Predict on obfuscated data. Stake crypto (NMR) on models.</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Category B */}
                                <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                                    <h5 className="font-bold text-white mb-2">Category B: Bring Your Own Record</h5>
                                    <p className="text-sm text-gray-400 mb-4">You trade your own live account; they allocate capital to you.</p> 
                                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                                        <div className="bg-black/20 p-3 rounded-lg">
                                            <strong className="text-green-300 block mb-1">Darwinex</strong>
                                            <span className="text-gray-500">Wraps your strategy into an asset (DARWIN). Investors buy it, you get 15% fees.</span>
                                        </div>
                                        <div className="bg-black/20 p-3 rounded-lg">
                                            <strong className="text-green-300 block mb-1">FundSeeder</strong>
                                            <span className="text-gray-500">Link broker API. High rank = Direct seeding intro.</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Non-Negotiable Skills */}
                        <div className="p-4 rounded-xl border border-white/10 bg-white/5">
                            <h5 className="font-bold text-white mb-3">To play in the Major Leagues, you need:</h5>
                            <div className="flex flex-wrap gap-4 text-sm">
                                <span className="px-3 py-1 bg-white/10 rounded-full text-gray-300">Strict Backtesting (Out-of-Sample)</span>
                                <span className="px-3 py-1 bg-white/10 rounded-full text-gray-300">Sharpe Ratio &gt; 1.5</span>
                                <span className="px-3 py-1 bg-white/10 rounded-full text-gray-300">Uncorrelated Alpha</span>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Comparison Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs text-gray-400 uppercase bg-white/5">
                        <tr>
                            <th className="px-4 py-3 rounded-l-lg">Feature</th>
                            <th className="px-4 py-3">Retail Prop (Fee)</th>
                            <th className="px-4 py-3 rounded-r-lg">Institutional (Merit)</th>
                        </tr>
                    </thead>
                    <tbody className="text-gray-300">
                        <tr className="border-b border-white/5">
                            <td className="px-4 py-3 font-medium text-white">Barrier to Entry</td>
                            <td className="px-4 py-3 text-green-400">Low (Pay ~$100)</td>
                            <td className="px-4 py-3 text-red-400">High (Code/Track Record)</td>
                        </tr>
                        <tr className="border-b border-white/5">
                            <td className="px-4 py-3 font-medium text-white">Risk Model</td>
                            <td className="px-4 py-3">Demo, Gamified</td>
                            <td className="px-4 py-3">Live Risk, Real Alpha</td>
                        </tr>
                         <tr className="border-b border-white/5">
                            <td className="px-4 py-3 font-medium text-white">Revenue Source</td>
                            <td className="px-4 py-3">Challenge Fees</td>
                            <td className="px-4 py-3">Performance Fees</td>
                        </tr>
                         <tr className="border-b border-white/5">
                            <td className="px-4 py-3 font-medium text-white">Sustainability</td>
                            <td className="px-4 py-3 text-red-400">Low (Churn & Burn)</td>
                            <td className="px-4 py-3 text-green-400">High (Career Building)</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Instructor's Rec */}
            <div className="border border-blue-500/30 bg-blue-900/10 p-6 rounded-xl">
                 <h4 className="font-bold text-blue-400 mb-4">Instructor's Recommendation</h4>
                 <div className="space-y-3 text-sm">
                    <p className="text-gray-300"><strong className="text-white">If you are a Coder:</strong> Start with <span className="text-purple-400">QuantConnect</span>. Safest path to license IP.</p>
                    <p className="text-gray-300"><strong className="text-white">If you are a Data Scientist:</strong> Start with <span className="text-purple-400">Numerai</span>. Pure math, no market microstructure needed.</p>
                    <p className="text-gray-300"><strong className="text-white">If you have Capital:</strong> Start with <span className="text-green-400">Darwinex</span>. Legal structure to scale AUM.</p>
                 </div>
            </div>
            
             <div className="text-center p-8 mt-8">
                 <p className="text-xl text-gray-400">
                    This concludes the Intro Course.<br/>
                    <span className="text-white font-bold">You now see the full map.</span>
                 </p>
            </div>
        </div>
    );
};

const LessonSetup = ({ checklist, updateChecklist }) => {
    // Windows-Only Algo Lab Checklist
    const phases = [
        {
            title: "Phase 1: The Platform (The \"Car\")",
            description: "Get the vehicle ready first.",
            items: [
                { id: "mt5", label: "Install MetaTrader 5 (Windows Version)", note: "Must be the desktop version, not WebTrader." },
                { id: "demo", label: "Open a 'Demo' Account (Raw Spread)", note: "Save your Login ID, Password, and Server Name." },
                { id: "auto_trading", label: "Enable 'Auto-Trading' in Toolbar", note: "Ensure the button turns green." }
            ]
        },
        {
            title: "Phase 2: The Engine (The \"Driver\")",
            description: "Setting up the code environment.",
            items: [
                { id: "vscode", label: "Install VS Code" },
                { id: "python", label: "Install Python for Windows (64-bit)", note: "CRITICAL: Must be 64-bit. Check 'Add to PATH'." },
                { id: "connector", label: "Install Connector Library", note: "Run: pip install MetaTrader5" }
            ]
        },
        {
            title: "Phase 3: The Verification (The \"Test Drive\")",
            description: "Confirm the brain is talking to the body.",
            items: [
                { 
                    id: "data_folder", 
                    label: "Locate 'Data Folder' in MT5", 
                    note: "Run this script in VS Code to print the path:",
                    code: `import MetaTrader5 as mt5\n\nif not mt5.initialize():\n    print("initialize() failed")\nelse:\n    print(mt5.terminal_info().data_path)`
                }
            ]
        }
    ];

    // Check if the user is potentially on a non-Windows machine (rudimentary check)
    const isMacOrLinux = navigator.platform.toLowerCase().includes('mac') || navigator.platform.toLowerCase().includes('linux');

    return (
        <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-2 text-blue-400">
                    <Terminal size={20} />
                    <span className="font-mono text-xs tracking-wider uppercase">Lesson 1.7</span>
                </div>
                <h1 className="text-4xl font-bold text-white mb-4">Preparing Your Lab</h1>
                <p className="text-xl text-gray-400">The "Windows-Only" Algo Lab Checklist</p>
            </div>

            {isMacOrLinux && (
                <div className="bg-red-900/20 border-l-4 border-red-500 p-4 rounded-r-lg mb-6">
                    <div className="flex items-start gap-3">
                        <div className="bg-red-500/10 p-2 rounded-full">
                            <Lock size={20} className="text-red-500" />
                        </div>
                        <div>
                            <h3 className="text-red-400 font-bold">Operating System Warning</h3>
                            <p className="text-sm text-red-200 mt-1">
                                You appear to be on a Mac or Linux machine. 
                                <span className="font-bold underline ml-1">pip install MetaTrader5 will fail.</span>
                            </p>
                            <p className="text-xs text-red-300 mt-2">
                                This setup only works natively on a Windows PC (or a Windows VPS).
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-8">
                {phases.map((phase, index) => (
                    <div key={index} className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
                        <div className="mb-4">
                            <h3 className="text-xl font-bold text-white mb-1">{phase.title}</h3>
                            <p className="text-sm text-gray-400 italic">{phase.description}</p>
                        </div>
                        <div className="space-y-3">
                            {phase.items.map((item) => (
                                <div 
                                    key={item.id} 
                                    onClick={() => updateChecklist(item.id, !checklist[item.id])}
                                    className={`relative p-4 rounded-xl border transition-all cursor-pointer select-none group
                                        ${checklist[item.id] ? 'bg-green-500/10 border-green-500/50' : 'bg-white/5 border-white/10 hover:bg-white/10'}
                                    `}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`mt-0.5 w-6 h-6 rounded-full border-2 flex shrink-0 items-center justify-center transition-colors
                                            ${checklist[item.id] ? 'bg-green-500 border-green-500' : 'border-gray-500 group-hover:border-gray-400'}
                                        `}>
                                            {checklist[item.id] && <CheckCircle size={14} className="text-white" />}
                                        </div>
                                        <div className="flex-1">
                                            <span className={`font-medium block ${checklist[item.id] ? 'text-white line-through decoration-white/30' : 'text-gray-200'}`}>
                                                {item.label}
                                            </span>
                                            {item.note && (
                                                <p className={`text-xs mt-1 ${checklist[item.id] ? 'text-gray-500' : 'text-blue-400'}`}>
                                                    {item.note}
                                                </p>
                                            )}
                                            {item.code && !checklist[item.id] && (
                                                <div 
                                                    className="mt-3 bg-black/50 p-3 rounded border border-white/10 font-mono text-xs text-green-400 overflow-x-auto relative group/code"
                                                    onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(item.code); }}
                                                >
                                                    <pre>{item.code}</pre>
                                                    <div className="absolute top-2 right-2 opacity-0 group-hover/code:opacity-100 transition-opacity bg-gray-800 text-white text-[10px] px-2 py-1 rounded">
                                                        Click to Copy
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {Object.keys(checklist).length > 0 && Object.values(checklist).filter(Boolean).length >= 7 && (
                <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-6 bg-green-500/20 text-green-400 rounded-xl text-center font-bold border border-green-500/30 backdrop-blur-sm"
                >
                    <div className="text-2xl mb-2">🎉</div>
                    You are ready for Module 1 of MQL5 Masterclass!
                </motion.div>
            )}
        </div>
    )
};



// Lesson 1.7b: The Rosetta Stone (Python vs MQL5)
const LessonRosettaStone = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-purple-400">
            <BookOpen size={20} />
            <span className="font-mono text-xs tracking-wider uppercase">Lesson 1.7b</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">Python vs. MQL5: The Rosetta Stone</h1>
        <p className="text-xl text-gray-400">Learning the "Grammar" of Algorithmic Trading.</p>
      </div>

      <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10 mb-8">
        <h2 className="text-xl font-bold text-white mb-2">Why this matters?</h2>
        <p className="text-gray-300">
            We won't waste time on "Hello World". We focus only on the code you need to place a trade.
            You'll see that <strong className="text-green-400">MQL5 (The Robot)</strong> is just <strong className="text-yellow-400">Python (The Analyst)</strong> with stricter grammar.
        </p>
      </div>

      {/* Comparison Table Component */}
      {[
        {
            title: "1. Variables (The Containers)",
            desc: "You need a place to store Price, Lot Size, and Symbol name.",
            diff: "Python guesses the type. MQL5 needs you to be specific (Strict Typing).",
            task: "Store the price of Gold (2050.50) and name 'XAUUSD'.",
            py: `price = 2050.50
symbol = "XAUUSD"
lots = 0.1

# Python is clean. No semicolons.`,
            mql: `double price = 2050.50; // 'double' means decimal number
string symbol = "XAUUSD"; // 'string' means text
double lots = 0.1;

// MQL5 is strict. You MUST use semicolons ;`
        },
        {
            title: "2. Arrays & Lists (The Candle History)",
            desc: "A single variable holds current price. A list holds the last 100 prices.",
            diff: "Python uses 'Lists' (flexible). MQL5 uses 'Arrays' (fast but rigid).",
            task: "Create a list of the last 3 closing prices.",
            py: `prices = [1.0500, 1.0510, 1.0520]

# Access the newest (last one)
current = prices[-1]`,
            mql: `double prices[3]; // Define size first

prices[0] = 1.0520; // [0] is usually current
prices[1] = 1.0510;
prices[2] = 1.0500;

double current = prices[0];`
        },
        {
            title: "3. Logic (The Decision Maker)",
            desc: "The 'If This, Then That' logic.",
            diff: "Python relies on indentation. MQL5 relies on curly braces { }.",
            task: "If current price > old price, print 'Green Candle'.",
            py: `if current_price > old_price:
    print("Green Candle")
else:
    print("Red Candle")`,
            mql: `if(currentPrice > oldPrice)
{
    Print("Green Candle");
}
else
{
    Print("Red Candle");
}`
        },
        {
            title: "4. Loops (The Scanner)",
            desc: "Check the last 100 candles. You need to 'Loop' through history.",
            diff: "Iterating through data to find patterns.",
            task: "Print the price of the last 5 candles.",
            py: `for i in range(5):
    print(f"Candle {i}: {prices[i]}")`,
            mql: `for(int i=0; i<5; i++)
{
    Print("Candle ", i, ": ", prices[i]);
}`
        },
        {
            title: "5. Functions (The Strategy Tools)",
            desc: "Don't write 'Calculate RSI' every time. Wrap it in a function.",
            diff: "Reusable blocks of code.",
            task: "Create a function that calculates Spread (Ask - Bid).",
            py: `def get_spread(ask, bid):
    return ask - bid

# Usage
s = get_spread(1.0502, 1.0500)`,
            mql: `double GetSpread(double ask, double bid)
{
    return ask - bid;
}

// Usage
double s = GetSpread(1.0502, 1.0500);`
        }
      ].map((item, idx) => (
        <div key={idx} className="space-y-4 pt-8 border-t border-white/10">
            <h3 className="text-2xl font-bold text-white">{item.title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-2">
                <div className="bg-gray-800 p-3 rounded">
                    <strong className="text-blue-400 block">Concept:</strong> {item.desc}
                </div>
                <div className="bg-gray-800 p-3 rounded">
                    <strong className="text-purple-400 block">Difference:</strong> {item.diff}
                </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Python Column */}
                <div className="bg-yellow-900/10 border border-yellow-500/20 rounded-xl overflow-hidden">
                    <div className="bg-yellow-500/10 p-2 border-b border-yellow-500/20 flex justify-between items-center">
                        <span className="text-yellow-400 font-bold text-xs uppercase tracking-wider">Python (The Analyst)</span>
                        <Terminal size={14} className="text-yellow-400"/>
                    </div>
                    <div className="p-4 overflow-x-auto">
                        <pre className="font-mono text-xs text-yellow-100/90 whitespace-pre-wrap">{item.py}</pre>
                    </div>
                </div>

                {/* MQL5 Column */}
                <div className="bg-blue-900/10 border border-blue-500/20 rounded-xl overflow-hidden">
                    <div className="bg-blue-500/10 p-2 border-b border-blue-500/20 flex justify-between items-center">
                        <span className="text-blue-400 font-bold text-xs uppercase tracking-wider">MQL5 (The Robot)</span>
                        <Code size={14} className="text-blue-400"/>
                    </div>
                    <div className="p-4 overflow-x-auto">
                        <pre className="font-mono text-xs text-blue-100/90 whitespace-pre-wrap">{item.mql}</pre>
                    </div>
                </div>
            </div>
        </div>
      ))}

      {/* Challenge Section */}
      <div className="mt-12 bg-gradient-to-r from-green-900/20 to-blue-900/20 p-8 rounded-xl border border-green-500/20">
        <h2 className="text-2xl font-bold text-white mb-4">6. The "Hello Money" Challenge</h2>
        <p className="text-gray-300 mb-6">
            Write a script that defines Balance ($10,000) and Risk % (1%), calculates the risk amount, and prints it.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
                <h4 className="font-bold text-yellow-400 mb-2">Python Solution</h4>
                <div className="bg-black/50 p-4 rounded border border-yellow-500/20 max-h-40 overflow-y-auto">
<pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap">{`balance = 10000.00
risk_percent = 0.01

risk_amount = balance * risk_percent

print(f"Account: \${balance} | Risking: \${risk_amount}")`}</pre>
                </div>
            </div>
            <div>
                <h4 className="font-bold text-blue-400 mb-2">MQL5 Solution</h4>
                <div className="bg-black/50 p-4 rounded border border-blue-500/20 max-h-40 overflow-y-auto">
<pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap">{`void OnStart()
{
   double balance = 10000.00;
   double riskPercent = 0.01;
   
   double riskAmount = balance * riskPercent;
   
   Print("Account: $", balance, " | Risking: $", riskAmount);
}`}</pre>
                </div>
            </div>
        </div>
      </div>

    </div>
);

// --- MODULE 2: The Execution Engine (MQL5 Mastery) ---

// Lesson 2.1: The OrderSend Wrapper (Safe Execution)
const LessonOrderWrapper = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
        <div className="mb-8">
            <div className="flex items-center gap-2 mb-2 text-red-400">
                <Shield size={20} />
                <span className="font-mono text-xs tracking-wider uppercase">Module 2.1</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">The "OrderSend" Wrapper</h1>
            <p className="text-xl text-gray-400">Raw execution is dangerous. We wrap it in safety logic.</p>
        </div>

        <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
            <h2 className="text-2xl font-bold text-white mb-4">Why Wrapper?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-900/10 border border-red-500/20 p-4 rounded-xl">
                    <strong className="text-red-400 block mb-1">Raw Trade.Buy()</strong>
                    <ul className="text-sm text-gray-400 list-disc pl-4 space-y-1">
                        <li>Fails on "Requotes" (Busy Market)</li>
                        <li>Fails if Spread &gt; 100 pips (News)</li>
                        <li>No Error Logging (Blind Failure)</li>
                    </ul>
                </div>
                <div className="bg-green-900/10 border border-green-500/20 p-4 rounded-xl">
                    <strong className="text-green-400 block mb-1">Our "OpenBuy()" Wrapper</strong>
                    <ul className="text-sm text-gray-400 list-disc pl-4 space-y-1">
                        <li><strong>Retry Logic:</strong> Tries 3 times if busy.</li>
                        <li><strong>Spread Filter:</strong> Blocks trade if spread is toxic.</li>
                        <li><strong>Logging:</strong> Prints exact error ID to Journal.</li>
                    </ul>
                </div>
            </div>
        </div>

        <div className="bg-black/50 p-4 rounded-xl border border-gray-700 overflow-x-auto">
             <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-mono text-blue-400">Library/TradeWrapper.mqh</span>
             </div>
             <pre className="text-xs font-mono text-gray-300">
{`// The "Safe" Buy Function
void OpenBuy(double lots, double sl, double tp, string comment) {
   // 1. Refresh Rates (Critical)
   double ask = SymbolInfoDouble(_Symbol, SYMBOL_ASK);
   double bid = SymbolInfoDouble(_Symbol, SYMBOL_BID);

   // 2. Spread Protection
   if((ask - bid) > Point() * 50) {
      Print("Spread too high! Execution Aborted.");
      return; 
   }

   // 3. Execution with Retry Logic
   for(int i=0; i<3; i++) {
       if(trade.Buy(lots, _Symbol, ask, sl, tp, comment)) {
           Print("Order Opened! Ticket: ", trade.ResultOrder());
           break; // Success
       } else {
           Print("Error: ", GetLastError(), ". Retrying...");
           Sleep(100); // Wait 100ms
       }
   }
}`}
             </pre>
        </div>
    </div>
);

// Lesson 2.2: Event-Driven Architecture
const LessonEvents = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
        <div className="mb-8">
            <div className="flex items-center gap-2 mb-2 text-yellow-400">
                <Zap size={20} />
                <span className="font-mono text-xs tracking-wider uppercase">Module 2.2</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Event-Driven Architecture</h1>
            <p className="text-xl text-gray-400">The 3 Heartbeats of your Trading Bot.</p>
        </div>

        <div className="grid grid-cols-1 gap-6">
            <div className="bg-blue-900/10 p-6 rounded-xl border border-blue-500/20">
                <h3 className="text-xl font-bold text-blue-400 mb-2">1. OnTick() - The Sprinter</h3>
                <p className="text-sm text-gray-300 mb-2">Triggers every time price moves.</p>
                <div className="bg-black/40 p-3 rounded font-mono text-xs text-gray-400">
                    Use for: Entry Logic, Trailing Stop updates. <br/>
                    <span className="text-red-400">Danger: Heavy math here freezes the terminal.</span>
                </div>
            </div>

            <div className="bg-purple-900/10 p-6 rounded-xl border border-purple-500/20">
                <h3 className="text-xl font-bold text-purple-400 mb-2">2. OnTimer() - The Marathon Runner</h3>
                <p className="text-sm text-gray-300 mb-2">Triggers every X seconds (e.g., 1 second).</p>
                <div className="bg-black/40 p-3 rounded font-mono text-xs text-gray-400">
                    Use for: Dashboard Updates, Server Connection Checks, Telegram Alerts.
                </div>
            </div>

            <div className="bg-orange-900/10 p-6 rounded-xl border border-orange-500/20">
                <h3 className="text-xl font-bold text-orange-400 mb-2">3. OnTradeTransaction() - The Watchdog</h3>
                <p className="text-sm text-gray-300 mb-2">Triggers when the Server reports a change.</p>
                <div className="bg-black/40 p-3 rounded font-mono text-xs text-gray-400">
                    Use for: Detecting "Limit Order Filled", "Stop Loss Hit". <br/>
                    <strong className="text-white">Crucial for Async Logic.</strong>
                </div>
            </div>
        </div>
    </div>
);

// Lesson 2.3: Project - Trade Manager
const LessonTradeManager = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
        <div className="mb-8">
            <div className="flex items-center gap-2 mb-2 text-green-400">
                <Terminal size={20} />
                <span className="font-mono text-xs tracking-wider uppercase">Project 2</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Build: The Trade Manager</h1>
            <p className="text-xl text-gray-400">Your first utility. It manages potential human errors.</p>
        </div>

        <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10 mb-8">
            <h2 className="text-xl font-bold text-white mb-4">The Brief</h2>
            <ul className="space-y-4 text-gray-300">
                <li className="flex items-start gap-3">
                     <CheckCircle className="text-green-500 shrink-0 mt-1" size={18} />
                     <span><strong>Auto-StopLoss:</strong> Detect manual trades immediately and add a 20 pip SL if missing.</span>
                </li>
                <li className="flex items-start gap-3">
                     <CheckCircle className="text-green-500 shrink-0 mt-1" size={18} />
                     <span><strong>Breakeven Trigger:</strong> If Profit &gt; 15 pips, move SL to Entry + 1 pip.</span>
                </li>
            </ul>
        </div>

        <div className="bg-black/50 p-4 rounded-xl border border-gray-700">
            <h3 className="text-sm font-bold text-gray-400 mb-2">Hint: The Event Handler</h3>
            <pre className="text-xs font-mono text-gray-300 overflow-x-auto">
{`void OnTradeTransaction(const MqlTradeTransaction &trans, ...) {
    
    // Check if a new deal was added to history (Order Filled)
    if(trans.type == TRADE_TRANSACTION_DEAL_ADD) {
        
        ulong ticket = trans.order;
        if(OrderSelect(ticket)) {
            // Check if SL is zero
            if(OrderGetDouble(ORDER_SL) == 0) {
                ApplyStopLoss(ticket); // Your custom function
            }
        }
    }
}`}
            </pre>
        </div>
    </div>
);

// --- MODULE 3: The Research Lab (Python & Data Science) ---

// Lesson 3.1: Vectorized Backtesting (Speed)
const LessonVectorTesting = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
        <div className="mb-8">
            <div className="flex items-center gap-2 mb-2 text-blue-400">
                <FastForward size={20} />
                <span className="font-mono text-xs tracking-wider uppercase">Module 3.1</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Vectorized Backtesting</h1>
            <p className="text-xl text-gray-400">Testing 10 years of data in 0.5 seconds.</p>
        </div>

         <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
                <h3 className="text-lg font-bold text-gray-400 mb-2">The Old Way (Event Loop)</h3>
                <p className="text-sm text-gray-500 mb-4">Iterating through every candle one by one. Slow.</p>
                <pre className="bg-black/50 p-3 rounded text-xs text-gray-400">
{`for i in range(len(prices)):
   if prices[i] > ma[i]:
      buy()`}
                </pre>
            </div>
            <div className="bg-blue-900/10 p-6 rounded-xl border border-blue-500/20">
                <h3 className="text-lg font-bold text-blue-400 mb-2">The Vector Way (Numpy)</h3>
                <p className="text-sm text-gray-300 mb-4">Calculate entire columns at once. Instant.</p>
                <pre className="bg-black/50 p-3 rounded text-xs text-blue-300">
{`signals = prices > ma
entries = signals.vbt.signals.fshift(1)`}
                </pre>
            </div>
        </div>

        <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
             <h2 className="text-xl font-bold text-white mb-4">Introducing vectorbt</h2>
             <p className="text-gray-300 mb-4">
                We use the `vectorbt` library. It allows you to test thousands of parameter combinations instantly.
             </p>
             <div className="bg-black/50 p-4 rounded-xl border border-gray-700">
                <pre className="text-xs font-mono text-gray-300 overflow-x-auto">
{`import vectorbt as vbt

# 1. Download Data
price = vbt.YFData.download('EURUSD=X').get('Close')

# 2. Run Strategy (RSI)
entries = vbt.RSI.run(price, 14).rsi_below(30)
exits = vbt.RSI.run(price, 14).rsi_above(70)

# 3. Simulate
portfolio = vbt.Portfolio.from_signals(price, entries, exits)

# 4. Analyze
print(portfolio.total_return())`}
                </pre>
             </div>
        </div>
    </div>
);

// Lesson 3.2: Stationarity & Filters
const LessonStationarity = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
        <div className="mb-8">
            <div className="flex items-center gap-2 mb-2 text-purple-400">
                <Activity size={20} />
                <span className="font-mono text-xs tracking-wider uppercase">Module 3.2</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">The Quant's Filter: Stationarity</h1>
            <p className="text-xl text-gray-400">Why your Mean Reversion bot dies in a Trend.</p>
        </div>

        <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10 mb-8">
            <h2 className="text-xl font-bold text-white mb-2">The ADF Test</h2>
            <p className="text-gray-300 mb-4">
                The Augmented Dickey-Fuller test tells us if a time series is "Stationary" (Mean Reverting) or "Non-Stationary" (Trending/Random Walk).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="bg-green-900/10 p-4 rounded-lg border border-green-500/20">
                    <strong className="text-green-400">P-Value &lt; 0.05</strong>
                    <p className="text-xs text-gray-400">Stationary. Safe for Mean Reversion (RSI, Bollinger Bands).</p>
                 </div>
                 <div className="bg-red-900/10 p-4 rounded-lg border border-red-500/20">
                    <strong className="text-red-400">P-Value &gt; 0.05</strong>
                    <p className="text-xs text-gray-400">Non-Stationary. Use Trend Following (MA, Breakout).</p>
                 </div>
            </div>
        </div>
    </div>
);

// Lesson 3.3: Project - Strategy Validator
const LessonStrategyValidator = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
        <div className="mb-8">
             <div className="flex items-center gap-2 mb-2 text-green-400">
                <Target size={20} />
                <span className="font-mono text-xs tracking-wider uppercase">Project 3</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Build: The Strategy Validator</h1>
            <p className="text-xl text-gray-400">A Python script that grades your idea before you code MQL5.</p>
        </div>

        <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">The Workflow</h2>
            <ol className="list-decimal pl-5 space-y-2 text-gray-300">
                <li><strong>Fetch Data:</strong> Pull 5 years of H1 candles from MT5.</li>
                <li><strong>Run ADF Test:</strong> Check if eligible for the strategy type.</li>
                <li><strong>Vector Backtest:</strong> Run the logic.</li>
                <li><strong>Monte Carlo:</strong> Shuffle the trades. Does it still survive?</li>
                <li><strong>Output:</strong> Pass/Fail Report.</li>
            </ol>
        </div>
    </div>
);

// --- MODULE 4: The AI Edge (Machine Learning & LLMs) ---

// Lesson 4.1: Sentiment Analysis (FinBERT)
const LessonSentiment = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
        <div className="mb-8">
            <div className="flex items-center gap-2 mb-2 text-pink-400">
                <Globe size={20} />
                <span className="font-mono text-xs tracking-wider uppercase">Module 4.1</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Sentiment Analysis with FinBERT</h1>
            <p className="text-xl text-gray-400">Reading the market's mood from News headlines.</p>
        </div>

        <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10 mb-8">
            <h2 className="text-xl font-bold text-white mb-4">FinBERT vs. Regular BERT</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div className="bg-gray-800 p-4 rounded-xl opacity-50">
                    <strong className="text-gray-400 block mb-1">Standard BERT</strong>
                    <p className="text-sm italic">"The company cut costs." &rarr; Negative/Neutral</p>
                 </div>
                 <div className="bg-pink-900/20 p-4 rounded-xl border border-pink-500/20">
                    <strong className="text-pink-400 block mb-1">FinBERT (Financial)</strong>
                    <p className="text-sm italic">"The company cut costs." &rarr; <span className="text-green-400 font-bold">POSITIVE</span> (Higher Margins)</p>
                 </div>
            </div>
            <p className="text-xs text-gray-400 mt-4">Context is King. FinBERT is trained on financial news, not Wikipedia.</p>
        </div>
    </div>
);

// Lesson 4.2: LLMs in Trading (Gemini)
const LessonLLMs = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
        <div className="mb-8">
            <div className="flex items-center gap-2 mb-2 text-cyan-400">
                <Cpu size={20} />
                <span className="font-mono text-xs tracking-wider uppercase">Module 4.2</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">LLMs: The "Why" Engine</h1>
            <p className="text-xl text-gray-400">Using Gemini/GPT to explain market moves, not just predict them.</p>
        </div>

        <div className="bg-cyan-900/10 p-6 rounded-xl border border-cyan-500/20 mb-8">
            <h3 className="text-xl font-bold text-cyan-400 mb-2">The Setup</h3>
            <p className="text-gray-300 mb-4">Pass the raw news headlines to the LLM and ask for a summary.</p>
            <pre className="bg-black/50 p-4 rounded-xl text-xs text-cyan-200 overflow-x-auto">
{`prompt = """
Analyze these headlines for EURUSD impact:
1. ECB hikes rates by 0.50%.
2. US GDP grows faster than expected.
3. German factory orders collapse.

Rate Sentiment from -1 to +1.
"""
response = gemini.generate_content(prompt)`}
            </pre>
        </div>
    </div>
);

// Lesson 4.3: Project - AI Sentiment Dashboard
const LessonAIDashboard = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
        <div className="mb-8">
             <div className="flex items-center gap-2 mb-2 text-green-400">
                <Target size={20} />
                <span className="font-mono text-xs tracking-wider uppercase">Project 4</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Build: The AI Sentiment Dashboard</h1>
            <p className="text-xl text-gray-400">Visualizing the "Fear & Greed" of the institutional world.</p>
        </div>

        <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Project Goals</h2>
            <ul className="list-disc pl-5 space-y-2 text-gray-300">
                <li><strong>Source:</strong> Scrape ForexFactory or Investing.com headlines.</li>
                <li><strong>Model:</strong> Feed into FinBERT (HuggingFace) for immediate scoring.</li>
                <li><strong>Filter:</strong> Ignore "Neutral" fluff. Only count High Confidence &gt; 0.8.</li>
                <li><strong>Display:</strong> A Gauge Chart showing "Bullish" or "Bearish" bias for the day.</li>
            </ul>
        </div>
    </div>
);

// Lesson 1.8: Decoding Retail Patterns
const LessonRetailPatterns = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-blue-400">
          <Search size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson 1.8</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">Decoding Retail Patterns</h1>
        <p className="text-xl text-gray-400">Bridging the gap between "Trading Theory" and "Algorithmic Reality".</p>
      </div>

      <div className="bg-blue-900/20 p-4 rounded-lg border border-blue-500/30 mb-8">
        <p className="text-gray-300 text-sm">
            Before we can tell our bot to "Trade a Head and Shoulders," we first need to understand what that actually means—and how to translate a visual shape on a chart into raw data our code can read.
        </p>
      </div>
  
      {/* Part 1: The Retail Playbook */}
      <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
        <h2 className="text-2xl font-bold text-white mb-6">Part 1: The "Retail" Playbook (Theory)</h2>
        <p className="text-gray-300 mb-8">
            Retail traders trade on human psychology. When thousands of traders see the same shape, they react the same way.
        </p>
  
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Reversal Patterns */}
            <div className="space-y-6">
                <h3 className="text-red-400 font-bold flex items-center gap-2 border-b border-red-500/20 pb-2">
                    <TrendingUp size={18} className="rotate-180" /> 1. Reversal Patterns (Trend Killers)
                </h3>
  
                {/* Head and Shoulders Viz */}
                <div className="bg-black/40 p-4 rounded-lg border border-red-500/20 hover:border-red-500/50 transition-colors">
                    <h4 className="font-bold text-gray-200 text-sm mb-2">Head and Shoulders (Bearish)</h4>
                    <div className="h-32 w-full relative mb-3">
                        <svg className="w-full h-full" viewBox="0 0 200 100" preserveAspectRatio="none">
                            <path d="M10,80 L30,50 L50,80 L80,20 L110,80 L130,50 L150,80" stroke="#f87171" strokeWidth="2" fill="none" />
                            <line x1="10" y1="80" x2="190" y2="80" stroke="white" strokeWidth="1" strokeDasharray="4,4" />
                            <path d="M150,80 L170,100" stroke="#ef4444" strokeWidth="2" markerEnd="url(#arrowdown)" />
                            <text x="30" y="45" fill="gray" fontSize="8" textAnchor="middle">Shoulder</text>
                            <text x="80" y="15" fill="white" fontSize="8" textAnchor="middle">HEAD</text>
                            <text x="130" y="45" fill="gray" fontSize="8" textAnchor="middle">Shoulder</text>
                            <text x="170" y="90" fill="gray" fontSize="8" textAnchor="start">Break</text>
                        </svg>
                    </div>
                    <ul className="text-xs text-gray-400 space-y-1 list-disc pl-4">
                        <li><strong>Logic:</strong> Buyers tried 3 times. Middle attempt passed, final failed. Trend is dead.</li>
                        <li><strong>Success Rate:</strong> <span className="text-green-400 font-bold">81%</span> for H&S, <span className="text-green-400 font-bold">90%</span> for Inverse H&S.</li>
                    </ul>
                </div>
  
                {/* Double Bottom Viz */}
                <div className="bg-black/40 p-4 rounded-lg border border-green-500/20 hover:border-green-500/50 transition-colors">
                    <h4 className="font-bold text-gray-200 text-sm mb-2">Double Bottom (Bullish "W")</h4>
                    <div className="h-24 w-full relative mb-3">
                         <svg className="w-full h-full" viewBox="0 0 200 80" preserveAspectRatio="none">
                            <path d="M20,10 L50,70 L80,30 L110,70 L140,10" stroke="#4ade80" strokeWidth="2" fill="none" />
                            <line x1="20" y1="70" x2="180" y2="70" stroke="gray" strokeWidth="1" strokeDasharray="2,2" />
                            <text x="50" y="78" fill="white" fontSize="8" textAnchor="middle">Bottom 1</text>
                            <text x="110" y="78" fill="white" fontSize="8" textAnchor="middle">Bottom 2</text>
                        </svg>
                    </div>
                    <ul className="text-xs text-gray-400 space-y-1 list-disc pl-4">
                        <li><strong>Logic:</strong> Market "tested" the floor twice and refused to go lower.</li>
                        <li><strong>Success Rate:</strong> ~<span className="text-green-400 font-bold">81%</span>. Classic "Buy" signal.</li>
                    </ul>
                </div>
            </div>
  
            {/* Continuation Patterns */}
            <div className="space-y-6">
                <h3 className="text-green-400 font-bold flex items-center gap-2 border-b border-green-500/20 pb-2">
                     <Play size={18} /> 2. Continuation Patterns (Trend Pauses)
                </h3>
  
                {/* Ascending Triangle Viz */}
                <div className="bg-black/40 p-4 rounded-lg border border-green-500/20 hover:border-green-500/50 transition-colors">
                    <h4 className="font-bold text-gray-200 text-sm mb-2">Ascending Triangle</h4>
                    <div className="h-32 w-full relative mb-3">
                        <svg className="w-full h-full" viewBox="0 0 200 100" preserveAspectRatio="none">
                            <line x1="50" y1="20" x2="180" y2="20" stroke="white" strokeWidth="2" />
                            <line x1="50" y1="90" x2="150" y2="20" stroke="gray" strokeWidth="1" strokeDasharray="4,4" />
                            <path d="M50,90 L70,20 L90,60 L110,20 L130,40 L150,20 L180,10" stroke="#4ade80" strokeWidth="2" fill="none" />
                            <text x="100" y="15" fill="gray" fontSize="8" textAnchor="middle">Resistance (Flat)</text>
                        </svg>
                    </div>
                     <ul className="text-xs text-gray-400 space-y-1 list-disc pl-4">
                        <li><strong>Logic:</strong> Buyers pushing lows higher. Squeeze into resistance.</li>
                        <li><strong>Success Rate:</strong> ~<span className="text-green-400 font-bold">83%</span> for breaking upward.</li>
                    </ul>
                </div>
  
                {/* Flag Viz */}
                <div className="bg-black/40 p-4 rounded-lg border border-green-500/20 hover:border-green-500/50 transition-colors">
                    <h4 className="font-bold text-gray-200 text-sm mb-2">Flags & Pennants</h4>
                    <div className="h-24 w-full relative mb-3">
                         <svg className="w-full h-full" viewBox="0 0 200 80" preserveAspectRatio="none">
                            <line x1="20" y1="70" x2="50" y2="10" stroke="#4ade80" strokeWidth="2" />
                            <line x1="50" y1="10" x2="100" y2="30" stroke="white" strokeWidth="1" />
                            <line x1="50" y1="30" x2="100" y2="50" stroke="white" strokeWidth="1" />
                            <path d="M100,40 L130,5" stroke="#4ade80" strokeWidth="2" strokeDasharray="2,2" />
                            <text x="35" y="40" fill="gray" fontSize="8" rotate="-60">Pole</text>
                            <text x="80" y="25" fill="gray" fontSize="8">Flag</text>
                        </svg>
                    </div>
                     <ul className="text-xs text-gray-400 space-y-1 list-disc pl-4">
                        <li><strong>Logic:</strong> A "Power Rest" after a sprint.</li>
                        <li><strong>Success Rate:</strong> <span className="text-green-400 font-bold">75-80%</span> in strong trends.</li>
                    </ul>
                </div>
            </div>
        </div>
      </div>

      {/* Part 2: The Translator */}
      <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h2 className="text-2xl font-bold text-white mb-6">Part 2: The "Translator" (How Code Sees)</h2>
        <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-1 space-y-4">
                <p className="text-gray-300">
                    This is the most critical concept. To automate strategies, we don't use "vision"—we use <span className="text-blue-400 font-bold">Data Extraction</span>.
                </p>
                <div className="bg-black/40 p-4 rounded border-l-4 border-blue-500">
                    <span className="block text-gray-400 text-xs uppercase mb-1">The Human Eye Sees</span>
                    <strong className="text-white text-lg">"A Double Top"</strong>
                </div>
                <div className="flex justify-center">
                    <div className="h-8 w-0.5 bg-gray-600"></div>
                </div>
                <div className="bg-black/40 p-4 rounded border-l-4 border-purple-500 font-mono">
                    <span className="block text-gray-400 text-xs uppercase mb-1">The Python/MQL5 Code Sees</span>
                    <strong className="text-purple-300 text-sm">High[10] == 1.0500 && High[20] == 1.0500</strong>
                </div>
            </div>
            <div className="flex-1 bg-blue-900/10 p-6 rounded-xl border border-blue-500/20">
                <h3 className="font-bold text-blue-400 mb-2">The Mission</h3>
                <p className="text-sm text-gray-300">
                    We must pull the raw Open, High, Low, and Close prices from the broker so our code can "measure" the shapes.
                </p>
            </div>
        </div>
      </div>
  
      {/* Part 3: The Plumbing */}
      <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h2 className="text-2xl font-bold text-white mb-6">Part 3: Fetching Data (The "How-To")</h2>
        
        {/* Master Template Callout */}
        <div className="bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500 mb-6">
          <strong className="text-blue-400 block mb-2">📋 Using Your Master Template</strong>
          <p className="text-gray-300 text-sm">
            When coding in MQL5, you'll add these snippets to your <strong>MasterTemplate.mq5</strong> (from Lesson P.1). 
            The data fetching code goes in the <strong>Helper Functions</strong> section, and you call it from <code className="bg-black/50 px-2 py-1 rounded">OnTick()</code>.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Python Column */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 text-yellow-400">
                    <Code size={20} />
                    <h3 className="font-bold">A. Python (The "Analyst")</h3>
                </div>
                <p className="text-xs text-gray-400">Best for: Complex analysis, finding patterns in history.</p>
                <div className="bg-black/50 p-4 rounded-lg border border-yellow-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-80 relative group">
                    <button 
                        onClick={() => navigator.clipboard.writeText(`import MetaTrader5 as mt5\nimport pandas as pd\n\n# 1. Initialize\nif not mt5.initialize():\n    print("Connection failed", mt5.last_error())\n    quit()\n\n# 2. Define parameters\nsymbol = "EURUSD"\ntimeframe = mt5.TIMEFRAME_H1\nnum_candles = 500\n\n# 3. Fetch data\nrates = mt5.copy_rates_from_pos(symbol, timeframe, 0, num_candles)\nmt5.shutdown()\n\n# 4. Convert to DataFrame\ndf = pd.DataFrame(rates)\ndf['time'] = pd.to_datetime(df['time'], unit='s')\n\nprint("Data fetched successfully!")\nprint(df.tail())`)}
                        className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <span className="text-[10px] text-white">Copy</span>
                    </button>
<pre>{`import MetaTrader5 as mt5
import pandas as pd

# 1. Initialize
if not mt5.initialize():
    print("Connection failed", mt5.last_error())
    quit()

# 2. Define parameters
symbol = "EURUSD"
timeframe = mt5.TIMEFRAME_H1
num_candles = 500

# 3. Fetch data
rates = mt5.copy_rates_from_pos(symbol, timeframe, 0, num_candles)
mt5.shutdown()

# 4. Convert to DataFrame
df = pd.DataFrame(rates)
df['time'] = pd.to_datetime(df['time'], unit='s')

print("Data fetched successfully!")
print(df.tail())`}</pre>
                </div>
            </div>
  
            {/* MQL5 Column */}
             <div className="space-y-4">
                <div className="flex items-center gap-2 text-blue-400">
                    <Database size={20} />
                    <h3 className="font-bold">B. MQL5 (The "Executor")</h3>
                </div>
                 <p className="text-xs text-gray-400">Best for: High-speed live execution inside the terminal.</p>
                <div className="bg-black/50 p-4 rounded-lg border border-blue-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-80 relative group">
                    <button 
                        onClick={() => navigator.clipboard.writeText(`void OnStart()\n{\n   // 1. Define arrays\n   double High[];\n   double Low[];\n\n   // 2. Sort arrays (0 = Current)\n   ArraySetAsSeries(High, true);\n   ArraySetAsSeries(Low, true);\n\n   // 3. Fetch data\n   int copiedHigh = CopyHigh(_Symbol, _Period, 0, 500, High);\n   int copiedLow  = CopyLow(_Symbol, _Period, 0, 500, Low);\n\n   // 4. Verify\n   if(copiedHigh > 0 && copiedLow > 0)\n   {\n      Print("Success! Fetched ", copiedHigh, " candles.");\n      Print("Current High: ", High[0]);\n      Print("Previous High: ", High[1]);\n   }\n}`)}
                        className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <span className="text-[10px] text-white">Copy</span>
                    </button>
<pre>{`void OnStart()
{
   // 1. Define arrays
   double High[];
   double Low[];

   // 2. Sort arrays (0 = Current)
   ArraySetAsSeries(High, true);
   ArraySetAsSeries(Low, true);

   // 3. Fetch data
   int copiedHigh = CopyHigh(_Symbol, _Period, 0, 500, High);
   int copiedLow  = CopyLow(_Symbol, _Period, 0, 500, Low);

   // 4. Verify
   if(copiedHigh > 0 && copiedLow > 0)
   {
      Print("Success! Fetched ", copiedHigh, " candles.");
      Print("Current High: ", High[0]);
      Print("Previous High: ", High[1]);
   }
}`}</pre>
                </div>
            </div>
        </div>
      </div>

      {/* Part 4: The Next Step */}
      <div className="bg-green-900/10 p-6 rounded-xl border border-green-500/20">
        <h3 className="text-xl font-bold text-green-400 mb-4">Part 4: Your Homework</h3>
        <ul className="space-y-4">
            <li className="flex gap-3">
                <div className="bg-green-500/20 p-2 rounded-full h-fit">
                    <Terminal size={16} className="text-green-400" />
                </div>
                <div>
                    <strong className="text-white block">Run the Code</strong>
                    <span className="text-gray-400 text-sm">Run the Python script above in VS Code and verify it prints a table of data (Open, High, Low, Close).</span>
                </div>
            </li>
            <li className="flex gap-3">
                <div className="bg-green-500/20 p-2 rounded-full h-fit">
                    <Target size={16} className="text-green-400" />
                </div>
                <div>
                    <strong className="text-white block">The Challenge</strong>
                    <span className="text-gray-400 text-sm">Look at the printed numbers. Can you find two "High" numbers that are almost exactly the same? That is the raw math behind a Double Top.</span>
                </div>
            </li>
        </ul>
      </div>

    </div>
);

// Lesson 1.9: Coding the Pattern Scanner
const LessonPatternScanner = ({ updateLesson, lessons }) => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-blue-400">
          <Code size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson 1.9</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">Coding the "Double Bottom" Scanner</h1>
        <p className="text-xl text-gray-400">Building your first Pattern Scanner: The Challenge of "Close Enough".</p>
      </div>
  
      {/* Prerequisites Callout */}
      <div className="bg-yellow-500/10 p-4 rounded-lg border-l-4 border-yellow-500 mb-6">
        <strong className="text-yellow-400 block mb-1">📦 Before You Start:</strong>
        <p className="text-gray-300 text-sm">
            Make sure you've completed <strong>Lesson 1.7</strong> and have MT5 + Python installed. 
            This lesson assumes you have a working development environment.
        </p>
      </div>
  
      {/* Master Template Callout */}
      <div className="bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500 mb-6">
        <strong className="text-blue-400 block mb-2">🔗 Using the Master Template (P.1)</strong>
        <p className="text-gray-300 text-sm mb-2">
            For the MQL5 implementation below, open your <strong>MasterTemplate.mq5</strong> and:
        </p>
        <ol className="text-gray-300 text-sm space-y-1 list-decimal pl-5">
          <li>Save it as <code className="bg-black/50 px-2 py-1 rounded">DoubleBottomScanner.mq5</code></li>
          <li>Add the pattern detection function to the <strong>Helper Functions</strong> section</li>
          <li>Call it from <code className="bg-black/50 px-2 py-1 rounded">OnTick()</code> after the safety checks</li>
        </ol>
      </div>
  
      {/* Intro: The Close Enough Challenge */}
      <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
        <h2 className="text-2xl font-bold text-white mb-4">The Challenge: "Close Enough"</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
                <p className="text-gray-300 mb-4">
                    In a textbook, a Double Bottom looks like a perfect "W" where the bottoms are at the <strong>exact same price</strong>.
                    <br/><br/>
                    <span className="text-red-400">Reality:</span> The market is messy. Bottom 1 might be 1.0502 and Bottom 2 might be 1.0505.
                </p>
                <div className="bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500">
                    <strong className="block text-blue-400 mb-1">Algo Lesson #1</strong>
                    We must program a <span className="text-white font-bold">"Tolerance Zone"</span> (margin of error). We tell Python: "Find two lows within 5 pips of each other."
                </div>
            </div>
            
            {/* Visual Tolerance Viz */}
            <div className="bg-black/40 p-6 rounded-xl border border-white/10 flex flex-col items-center justify-center">
                <div className="relative w-full h-32">
                    <svg className="w-full h-full" viewBox="0 0 200 100">
                        {/* Tolerance Zone */}
                        <rect x="0" y="65" width="200" height="10" fill="#3b82f6" fillOpacity="0.2" />
                        <line x1="0" y1="65" x2="200" y2="65" stroke="#3b82f6" strokeWidth="1" strokeDasharray="4,4" />
                        <line x1="0" y1="75" x2="200" y2="75" stroke="#3b82f6" strokeWidth="1" strokeDasharray="4,4" />
                        <text x="180" y="60" fill="#3b82f6" fontSize="8" textAnchor="end">Tolerance (5 Pips)</text>

                        {/* Price Action */}
                        <path d="M10,20 L40,70 L80,30 L120,72 L150,10" stroke="#4ade80" strokeWidth="2" fill="none" />
                        
                        {/* Points */}
                        <circle cx="40" cy="70" r="3" fill="#ef4444" />
                        <circle cx="120" cy="72" r="3" fill="#ef4444" />
                        
                        <text x="40" y="85" fill="gray" fontSize="8" textAnchor="middle">1.0502</text>
                        <text x="120" y="87" fill="gray" fontSize="8" textAnchor="middle">1.0505</text>
                        
                        {/* Checkmark */}
                        <circle cx="160" cy="40" r="12" fill="#22c55e" fillOpacity="0.2" />
                        <path d="M154,40 L158,44 L166,36" stroke="#22c55e" strokeWidth="2" fill="none" />
                        <text x="160" y="60" fill="#22c55e" fontSize="10" fontWeight="bold" textAnchor="middle">VALID MATCH</text>
                    </svg>
                </div>
                <p className="text-xs text-gray-500 mt-2">Visually imperfect, but mathematically valid within tolerance.</p>
            </div>
        </div>
      </div>
  
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Part 1: Python Scanner */}
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-yellow-400">
                <Terminal size={20} />
                <h3 className="font-bold text-lg">Part 1: The Python Scanner (Analyst)</h3>
            </div>
            <p className="text-sm text-gray-400">Looks back at 500 hours of history to find every occurrences.</p>
            
            <div className="bg-black/50 p-4 rounded-lg border border-yellow-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-96 relative group">
                <button 
                    onClick={() => navigator.clipboard.writeText(`import MetaTrader5 as mt5\nimport pandas as pd\n\nSYMBOL = "EURUSD"\nTIMEFRAME = mt5.TIMEFRAME_H1\nLOOKBACK = 500\nTOLERANCE_PIPS = 5\n\nif not mt5.initialize(): quit()\n\nrates = mt5.copy_rates_from_pos(SYMBOL, TIMEFRAME, 0, LOOKBACK)\ndf = pd.DataFrame(rates)\ndf['time'] = pd.to_datetime(df['time'], unit='s')\n\nprint(f"Scanning {SYMBOL} for Double Bottoms...")\n\nfor i in range(2, len(df) - 2):\n    bottom1 = df.iloc[i]['low']\n    \n    for j in range(i + 5, len(df) - 1):\n        bottom2 = df.iloc[j]['low']\n        diff_pips = abs(bottom1 - bottom2) * 10000\n        \n        if diff_pips <= TOLERANCE_PIPS:\n            middle_peak = df.iloc[i:j]['high'].max()\n            \n            if middle_peak > (bottom1 + 0.0020):\n                print(f"✅ Found! {df.iloc[j]['time']} | Diff: {diff_pips:.1f} pips")\n                break\nmt5.shutdown()`)}
                    className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <span className="text-[10px] text-white">Copy</span>
                </button>
<pre>{`import MetaTrader5 as mt5
import pandas as pd

SYMBOL = "EURUSD"
TIMEFRAME = mt5.TIMEFRAME_H1
LOOKBACK = 500
TOLERANCE_PIPS = 5  # Margin of Error

if not mt5.initialize(): quit()

rates = mt5.copy_rates_from_pos(
    SYMBOL, TIMEFRAME, 0, LOOKBACK
)
df = pd.DataFrame(rates)
df['time'] = pd.to_datetime(df['time'], unit='s')

print(f"Scanning {SYMBOL}...")

for i in range(2, len(df) - 2):
    bottom1 = df.iloc[i]['low']
    
    # Look forward for match
    for j in range(i + 5, len(df) - 1):
        bottom2 = df.iloc[j]['low']
        diff_pips = abs(bottom1 - bottom2) * 10000
        
        # CHECK 1: Close Enough?
        if diff_pips <= TOLERANCE_PIPS:
            # CHECK 2: Is there a "W" peak?
            middle_peak = df.iloc[i:j]['high'].max()
            
            if middle_peak > (bottom1 + 0.0020):
                print(f"✅ Found! {df.iloc[j]['time']}")
                break
mt5.shutdown()`}</pre>
            </div>
        </div>

        {/* Part 2: MQL5 Hunter */}
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-blue-400">
                <Zap size={20} />
                <h3 className="font-bold text-lg">Part 2: The MQL5 Hunter (Live)</h3>
            </div>
            <p className="text-sm text-gray-400">Checks "Live" on every new tick/candle. No loop needed.</p>
            
            <div className="bg-black/50 p-4 rounded-lg border border-blue-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-96 relative group">
                 <button 
                    onClick={() => navigator.clipboard.writeText(`void OnTick()\n{\n   double Lows[];\n   ArraySetAsSeries(Lows, true);\n   CopyLow(_Symbol, _Period, 0, 30, Lows);\n\n   double tolerance = 50 * _Point; // 5 Pips\n   double currentBottom = Lows[1];\n\n   for(int i = 5; i < 30; i++)\n   {\n      double pastBottom = Lows[i];\n      \n      if(MathAbs(currentBottom - pastBottom) < tolerance)\n      {\n         Print("Potential Double Bottom!");\n         Print("Current: ", currentBottom, " Match: ", pastBottom);\n         break;\n      }\n   }\n}`)}
                    className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <span className="text-[10px] text-white">Copy</span>
                </button>
<pre>{`// Inside Expert Advisor
void OnTick()
{
   // 1. Get recent Lows
   double Lows[];
   ArraySetAsSeries(Lows, true);
   CopyLow(_Symbol, _Period, 0, 30, Lows);

   // 2. Define Tolerance (5 Pips)
   double tolerance = 50 * _Point;

   // 3. Check Current vs Past
   double currentBottom = Lows[1];

   for(int i = 5; i < 30; i++)
   {
      double pastBottom = Lows[i];
      
      // The Math: abs(A - B) < Tolerance
      if(MathAbs(currentBottom - pastBottom) < tolerance)
      {
         Print("Potential Double Bottom!");
         Print("Current: ", currentBottom); 
         break;
      }
   }
}`}</pre>
            </div>
        </div>
      </div>
  
      <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 p-6 rounded-xl border border-blue-500/20 mt-8">
        <h3 className="text-xl font-bold text-white mb-2">Key Takeaway</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <strong className="text-yellow-400 block mb-1">Python is for Backtesting</strong>
                Scan 5 years of history in seconds to see <em>how often</em> the pattern works (verifying that 81% stat).
            </div>
            <div>
                <strong className="text-blue-400 block mb-1">MQL5 is for Live Trading</strong>
                Sits and waits for the exact moment the condition becomes true to fire the trade.
            </div>
        </div>
        
        <div className="mt-6 pt-6 border-t border-white/10 flex justify-between items-center">
            <span className="text-gray-400 text-sm">Next: Does this pattern actually make money?</span>
            <button 
                onClick={() => {
                    const lessonIndex = lessons.findIndex(l => l.id === '1.10');
                    if(lessonIndex !== -1) updateLesson(lessonIndex);
                }}
                className="px-4 py-2 bg-white text-black font-bold rounded hover:bg-gray-200 transition"
            >
                Proceed to Lesson 1.10
            </button>
        </div>
      </div>

    </div>
);

// Lesson 1.10: Backtesting the Pattern
const LessonBacktesting = ({ updateLesson, lessons }) => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-green-400">
          <Target size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson 1.10</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">Backtesting: Does the "W" Actually Pay?</h1>
        <p className="text-xl text-gray-400">Moving from "Finding Patterns" to "Simulating Trades" (Paper Trading).</p>
      </div>
  
      {/* Intro: The Reality Check */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div>
            <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10 h-full">
                <h2 className="text-2xl font-bold text-white mb-4">The "Paper Trading" Script</h2>
                <p className="text-gray-300 mb-4">
                    We are going to upgrade your Python scanner. Instead of just printing "Found one!", it will <strong className="text-blue-400">simulate a trade</strong> using historical data.
                </p>
                <ul className="space-y-2 text-sm text-gray-400">
                    <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                        <span className="text-green-300 font-bold">Entry:</span> Buy immediately when detected.
                    </li>
                    <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                        <span className="text-red-300 font-bold">Exit:</span> Set Target (+20 pips) & Stop Loss (-10 pips).
                    </li>
                    <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                        <span className="text-yellow-300 font-bold">Result:</span> Track Wins, Losses, and Final Balance.
                    </li>
                </ul>
            </div>
        </div>
            
        {/* Simulation Viz */}
        <div className="bg-black/40 p-6 rounded-xl border border-white/10 flex flex-col items-center justify-center">
             <div className="relative w-full h-40">
                <svg className="w-full h-full" viewBox="0 0 200 100">
                    {/* Price Action */}
                    <path d="M10,20 L40,70 L80,30 L120,70 L130,50 L140,40 L160,80 L180,20" stroke="#4ade80" strokeWidth="2" fill="none" />
                    
                    {/* Double Bottom Markers */}
                    <circle cx="40" cy="70" r="3" fill="#ef4444" />
                    <circle cx="120" cy="70" r="3" fill="#ef4444" />
                    <text x="80" y="85" fill="gray" fontSize="8" textAnchor="middle">Pattern Found</text>

                    {/* ENTRY */}
                    <line x1="120" y1="10" x2="120" y2="90" stroke="white" strokeWidth="1" strokeDasharray="2,2" />
                    <text x="120" y="10" fill="white" fontSize="8" textAnchor="middle">ENTRY</text>

                    {/* TP/SL Lines */}
                    <line x1="120" y1="30" x2="180" y2="30" stroke="#22c55e" strokeWidth="1" strokeDasharray="4,4" />
                    <text x="190" y="32" fill="#22c55e" fontSize="8">TP</text>

                    <line x1="120" y1="80" x2="160" y2="80" stroke="#ef4444" strokeWidth="1" strokeDasharray="4,4" />
                    <text x="170" y="82" fill="#ef4444" fontSize="8">SL</text>

                    {/* Outcome */}
                    <circle cx="180" cy="20" r="4" fill="#22c55e" />
                    <text x="180" y="12" fill="#22c55e" fontSize="10" fontWeight="bold" textAnchor="middle">WIN ($200)</text>
                </svg>
            </div>
            <p className="text-xs text-gray-500 mt-2">The script "fast forwards" to see if price hit TP or SL first.</p>
        </div>
      </div>
  
      {/* The Code */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-yellow-400">
            <Terminal size={20} />
            <h3 className="font-bold text-lg">The Backtester Script</h3>
        </div>
        <p className="text-sm text-gray-400">Create <code className="text-white bg-gray-800 px-1 py-0.5 rounded">backtest_double_bottom.py</code> and run this.</p>
        
        <div className="bg-black/50 p-4 rounded-lg border border-yellow-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-96 relative group">
            <button 
                onClick={() => navigator.clipboard.writeText(`import MetaTrader5 as mt5\nimport pandas as pd\n\n# --- Configuration ---\nSYMBOL = "EURUSD"\nTIMEFRAME = mt5.TIMEFRAME_H1\nLOOKBACK = 1000\nTOLERANCE_PIPS = 5\nTP_PIPS = 20\nSL_PIPS = 10\n\nif not mt5.initialize(): quit()\nrates = mt5.copy_rates_from_pos(SYMBOL, TIMEFRAME, 0, LOOKBACK)\nmt5.shutdown()\n\ndf = pd.DataFrame(rates)\ndf['time'] = pd.to_datetime(df['time'], unit='s')\n\nbalance = 10000\nwins = 0\nlosses = 0\n\nprint(f"--- Starting Backtest on {SYMBOL} ---")\n\nfor i in range(2, len(df) - 50):\n    bottom1 = df.iloc[i]['low']\n    found_pattern = False\n    entry_index = 0\n    \n    for j in range(i + 5, i + 50):\n        bottom2 = df.iloc[j]['low']\n        diff_pips = abs(bottom1 - bottom2) * 10000\n        \n        if diff_pips <= TOLERANCE_PIPS:\n            middle_peak = df.iloc[i:j]['high'].max()\n            if middle_peak > (bottom1 + 0.0015):\n                found_pattern = True\n                entry_index = j\n                break\n    \n    if found_pattern:\n        entry_price = df.iloc[entry_index]['close']\n        tp_price = entry_price + (TP_PIPS * 0.0001)\n        sl_price = entry_price - (SL_PIPS * 0.0001)\n        \n        outcome = "PENDING"\n        for k in range(entry_index + 1, entry_index + 24):\n            high = df.iloc[k]['high']\n            low = df.iloc[k]['low']\n            \n            if low <= sl_price:\n                balance -= 100\n                losses += 1\n                outcome = "LOSS"\n                break\n            elif high >= tp_price:\n                balance += 200\n                wins += 1\n                outcome = "WIN"\n                break\n        \n        if outcome != "PENDING":\n             i = entry_index \n\nprint("-" * 30)\nprint(f"Wins: {wins} | Losses: {losses}")\nif (wins + losses) > 0:\n    print(f"Win Rate: {(wins / (wins + losses)) * 100:.1f}%")\nprint(f"Final Balance: \${balance}")`)}
                className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
                <span className="text-[10px] text-white">Copy</span>
            </button>
<pre>{`import MetaTrader5 as mt5
import pandas as pd

# Configuration
SYMBOL = "EURUSD"
TIMEFRAME = mt5.TIMEFRAME_H1
LOOKBACK = 1000            
TOLERANCE_PIPS = 5         
TP_PIPS = 20               # Target (+200 profit)
SL_PIPS = 10               # Risk (-100 loss)

if not mt5.initialize(): quit()
rates = mt5.copy_rates_from_pos(SYMBOL, TIMEFRAME, 0, LOOKBACK)
mt5.shutdown()

df = pd.DataFrame(rates)
df['time'] = pd.to_datetime(df['time'], unit='s')

balance = 10000
wins = 0
losses = 0

print(f"--- Starting Backtest on {SYMBOL} ---")

for i in range(2, len(df) - 50):
    bottom1 = df.iloc[i]['low']
    found_pattern = False
    
    # Check for Double Bottom
    for j in range(i + 5, i + 50):
        bottom2 = df.iloc[j]['low']
        diff_pips = abs(bottom1 - bottom2) * 10000
        
        if diff_pips <= TOLERANCE_PIPS:
            middle_peak = df.iloc[i:j]['high'].max()
            if middle_peak > (bottom1 + 0.0015):
                found_pattern = True
                entry_index = j
                break
    
    if found_pattern:
        # --- SIMULATE TRADE ---
        entry_price = df.iloc[entry_index]['close']
        tp_price = entry_price + (TP_PIPS * 0.0001)
        sl_price = entry_price - (SL_PIPS * 0.0001)
        
        outcome = "PENDING"
        # Look forward 24 hours
        for k in range(entry_index + 1, entry_index + 24):
            future_high = df.iloc[k]['high']
            future_low = df.iloc[k]['low']
            
            if future_low <= sl_price:
                balance -= 100
                losses += 1
                outcome = "LOSS"
                break
            elif future_high >= tp_price:
                balance += 200
                wins += 1
                outcome = "WIN"
                break
        
        # Skip ahead if trade finished
        if outcome != "PENDING":
             i = entry_index 

print("-" * 30)
print(f"Wins: {wins} | Losses: {losses}")
if (wins + losses) > 0:
    print(f"Win Rate: {(wins / (wins + losses)) * 100:.1f}%")
print(f"Final Balance: \${balance}")`}</pre>
        </div>
      </div>

      {/* How to Read Results */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-6 rounded-xl border border-white/10">
        <h3 className="text-xl font-bold text-white mb-4">How to Read Your Results</h3>
        <div className="space-y-4">
            <div className="flex gap-4 items-start">
                <div className="bg-green-500/20 p-2 rounded shrink-0">
                    <CheckCircle className="text-green-400" size={20} />
                </div>
                <div>
                    <strong className="text-white block">Win Rate ~80%?</strong>
                    <span className="text-gray-400 text-sm">The pattern is robust! This matches the statistics.</span>
                </div>
            </div>
            <div className="flex gap-4 items-start">
                <div className="bg-yellow-500/20 p-2 rounded shrink-0">
                    <AlertTriangle className="text-yellow-400" size={20} />
                </div>
                <div>
                    <strong className="text-white block">Win Rate ~50%?</strong>
                    <span className="text-gray-400 text-sm">It's a coin flip. The pattern isn't enough on its own. You need <strong className="text-white">Filters</strong> (like Volume or RSI).</span>
                </div>
            </div>
             <div className="flex gap-4 items-start">
                <div className="bg-red-500/20 p-2 rounded shrink-0">
                    <XCircle className="text-red-400" size={20} />
                </div>
                <div>
                    <strong className="text-white block">Balance Negative?</strong>
                    <span className="text-gray-400 text-sm">You are losing money. The "Ugly Ws" are tricking the bot.</span>
                </div>
            </div>
        </div>

         <div className="mt-8 pt-6 border-t border-white/10">
            <h4 className="font-bold text-blue-400 mb-2">The Algorithm's Dilemma</h4>
            <p className="text-gray-300 text-sm italic">
                "A pure code scanner finds every ugly 'W' shape. A human trader filters out the ugly ones. Your next challenge is to teach the code to filter them out."
            </p>
         </div>

         <div className="mt-6 flex justify-end">
            <button 
                onClick={() => {
                    const lessonIndex = lessons.findIndex(l => l.id === '1.11');
                    if(lessonIndex !== -1) updateLesson(lessonIndex);
                }}
                className="px-6 py-3 bg-blue-600 text-white font-bold rounded hover:bg-blue-500 transition shadow-lg shadow-blue-900/50"
            >
                Next: Adding Filters (RSI)
            </button>
         </div>
      </div>

    </div>
);


// Lesson 1.11: The "Smart" Trader (Filters)
const LessonSmartTrader = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-purple-400">
          <Filter size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson 1.11</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">The "Smart" Trader: Filtering & Execution</h1>
        <p className="text-xl text-gray-400">Making the bot smarter with RSI and going live with MQL5.</p>
      </div>
  
      {/* Intro: The Filter Logic */}
      <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
        <h2 className="text-2xl font-bold text-white mb-4">The Filter Logic</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
                <p className="text-gray-300 mb-4">
                    Our Backtest showed that a raw "Double Bottom" scanner picks up a lot of noise.
                    <br/><br/>
                    <strong className="text-purple-400">The Fix:</strong> We only trade "High Quality" signals.
                    We add a rule: <strong className="text-white">Only buy if RSI {"<"} 40.</strong>
                </p>
                <div className="bg-purple-900/20 p-4 rounded-lg border-l-4 border-purple-500">
                    <strong className="block text-purple-400 mb-1">Logic:</strong>
                    "A W-shape is good. A W-shape when sellers are exhausted (Oversold) is <em className="text-white">excellent</em>."
                </div>
            </div>
            
            {/* Filter Viz */}
            <div className="bg-black/40 p-6 rounded-xl border border-white/10 flex flex-col items-center justify-center">
                <div className="relative w-full h-32">
                    <svg className="w-full h-full" viewBox="0 0 200 100">
                        {/* Price */}
                        <path d="M10,20 L40,60 L80,30 L120,60 L150,10" stroke="#4ade80" strokeWidth="2" fill="none" />
                        
                        {/* RSI Graph */}
                        <line x1="0" y1="80" x2="200" y2="80" stroke="gray" strokeWidth="1" strokeDasharray="2,2" />
                        <text x="190" y="75" fill="gray" fontSize="8" textAnchor="end">RSI 40</text>
                        
                        <path d="M10,70 L40,85 L80,60 L120,90 L150,50" stroke="#a855f7" strokeWidth="2" fill="none" />
                        
                        {/* Signal Markers */}
                        <circle cx="40" cy="60" r="3" fill="gray" />
                        <line x1="40" y1="60" x2="40" y2="85" stroke="gray" strokeWidth="1" strokeDasharray="2,2" />
                        <text x="40" y="50" fill="gray" fontSize="8" textAnchor="middle">Ignored</text>
                        
                        <circle cx="120" cy="60" r="4" fill="#22c55e" />
                        <line x1="120" y1="60" x2="120" y2="90" stroke="#22c55e" strokeWidth="1" />
                        <text x="120" y="50" fill="#22c55e" fontSize="10" fontWeight="bold" textAnchor="middle">TRADE!</text>
                        <text x="120" y="98" fill="#a855f7" fontSize="8" textAnchor="middle">RSI Oversold</text>
                    </svg>
                </div>
            </div>
        </div>
      </div>
  
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        {/* Option 1: Python Smart Filter */}
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-yellow-400">
                <Terminal size={20} />
                <h3 className="font-bold text-lg">Option 1: The "Smart" Filter (Python)</h3>
            </div>
            <p className="text-sm text-gray-400">Manually calculating RSI to filter the loop.</p>
            
            <div className="bg-black/50 p-4 rounded-lg border border-yellow-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-96 relative group">
                <button 
                    onClick={() => navigator.clipboard.writeText(`import MetaTrader5 as mt5\nimport pandas as pd\nimport numpy as np\n\nmt5.initialize()\nrates = mt5.copy_rates_from_pos("EURUSD", mt5.TIMEFRAME_H1, 0, 1000)\nmt5.shutdown()\ndf = pd.DataFrame(rates)\n\n# --- RSI CALCULATION ---\nchange = df['close'].diff()\ngain = change.where(change > 0, 0)\nloss = -change.where(change < 0, 0)\navg_gain = gain.rolling(window=14).mean()\navg_loss = loss.rolling(window=14).mean()\nrs = avg_gain / avg_loss\ndf['rsi'] = 100 - (100 / (1 + rs))\n\nprint("Scanning with SMART Filter...")\n\nfor i in range(2, len(df) - 50):\n    bottom1 = df.iloc[i]['low']\n    \n    for j in range(i + 5, i + 50):\n        bottom2 = df.iloc[j]['low']\n        diff_pips = abs(bottom1 - bottom2) * 10000\n        \n        if diff_pips <= 5:\n            # FILTER: RSI Check\n            current_rsi = df.iloc[j]['rsi']\n            if current_rsi < 40:\n                print(f"✅ HIGH QUALITY! RSI: {current_rsi:.2f}")\n                break`)}
                    className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <span className="text-[10px] text-white">Copy</span>
                </button>
<pre>{`# Python Smart Scanner
... (Fetch Data per usual) ...

# --- RSI CALCULATION ---
# Manual math so no external libs needed
change = df['close'].diff()
gain = change.where(change > 0, 0)
loss = -change.where(change < 0, 0)

avg_gain = gain.rolling(window=14).mean()
avg_loss = loss.rolling(window=14).mean()

rs = avg_gain / avg_loss
df['rsi'] = 100 - (100 / (1 + rs))

print("Scanning with SMART Filter...")

for i in range(2, len(df) - 50):
    bottom1 = df.iloc[i]['low']
    
    for j in range(i + 5, i + 50):
        bottom2 = df.iloc[j]['low']
        diff = abs(bottom1 - bottom2) * 10000
        
        if diff <= 5: # Found "W"
            
            # FILTER: RSI Check
            current_rsi = df.iloc[j]['rsi']
            
            if current_rsi < 40:
                print(f"✅ HIGH QUALITY! RSI: {current_rsi:.2f}")
                break`}</pre>
            </div>
        </div>

        {/* Option 2: MQL5 Live Execution */}
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-blue-400">
                <Zap size={20} />
                <h3 className="font-bold text-lg">Option 2: Going Live (MQL5 EA)</h3>
            </div>
            <p className="text-sm text-gray-400">Complete Expert Advisor code. Copy into MetaEditor.</p>
            
            <div className="bg-black/50 p-4 rounded-lg border border-blue-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-96 relative group">
                 <button 
                    onClick={() => navigator.clipboard.writeText(`#include <Trade\\Trade.mqh>\nCTrade trade;\nint rsiHandle;\n\nint OnInit()\n{\n   rsiHandle = iRSI(_Symbol, _Period, 14, PRICE_CLOSE);\n   return(INIT_SUCCEEDED);\n}\n\nvoid OnTick()\n{\n   // 1. Get Data\n   double Lows[]; ArraySetAsSeries(Lows, true);\n   CopyLow(_Symbol, _Period, 0, 30, Lows);\n\n   double RsiValues[]; ArraySetAsSeries(RsiValues, true);\n   CopyBuffer(rsiHandle, 0, 0, 3, RsiValues);\n\n   double currentLow = Lows[1];\n   double currentRSI = RsiValues[1];\n\n   // FILTER: Check RSI\n   if(currentRSI > 40) return;\n\n   // SCAN: Check Double Bottom\n   for(int i = 5; i < 25; i++)\n   {\n      if(MathAbs(currentLow - Lows[i]) < 50 * _Point)\n      {\n         // EXECUTION\n         if(PositionsTotal() == 0)\n         {\n            double ask = SymbolInfoDouble(_Symbol, SYMBOL_ASK);\n            trade.Buy(0.01, _Symbol, ask, \n               currentLow - 200*_Point, \n               ask + 400*_Point, \n               "Smart Double Bottom");\n         }\n         break;\n      }\n   }\n}`)}
                    className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <span className="text-[10px] text-white">Copy</span>
                </button>
<pre>{`#include <Trade\\Trade.mqh>
CTrade trade;
int rsiHandle;

int OnInit()
{
   // Setup Indicator
   rsiHandle = iRSI(_Symbol, _Period, 14, PRICE_CLOSE);
   return(INIT_SUCCEEDED);
}

void OnTick()
{
   // 1. Get Data
   double Lows[]; ArraySetAsSeries(Lows, true);
   CopyLow(_Symbol, _Period, 0, 30, Lows);

   double Rsi[]; ArraySetAsSeries(Rsi, true);
   CopyBuffer(rsiHandle, 0, 0, 3, Rsi);

   double currentLow = Lows[1];
   double currentRSI = Rsi[1];

   // FILTER: Check RSI (Must be < 40)
   if(currentRSI > 40) return;

   // SCAN: Check Double Bottom
   for(int i = 5; i < 25; i++)
   {
      if(MathAbs(currentLow - Lows[i]) < 50 * _Point)
      {
         // EXECUTION
         if(PositionsTotal() == 0)
         {
            double ask = SymbolInfoDouble(_Symbol, SYMBOL_ASK);
            
            // Buy with SL/TP
            trade.Buy(0.01, _Symbol, ask, 
               currentLow - 200*_Point, 
               ask + 400*_Point, 
               "Smart Double Bottom");
         }
         break;
      }
   }
}`}</pre>
            </div>
        </div>
      </div>
  
      <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 p-6 rounded-xl border border-purple-500/20 mt-8">
        <h3 className="text-xl font-bold text-white mb-2">Congratulations!</h3>
        <p className="text-gray-300 mb-4">
            You have now built a full <strong>Algorithmic Trading Pipeline</strong>:
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="bg-black/30 p-3 rounded border border-white/10">
                <span className="block text-gray-500 text-xs">Infrastructure</span>
                <strong className="text-white">MT5 Setup</strong>
            </div>
             <div className="bg-black/30 p-3 rounded border border-white/10">
                <span className="block text-gray-500 text-xs">Theory</span>
                <strong className="text-green-400">Retail Patterns</strong>
            </div>
             <div className="bg-black/30 p-3 rounded border border-white/10">
                <span className="block text-gray-500 text-xs">Analysis</span>
                <strong className="text-yellow-400">Python Scanner</strong>
            </div>
             <div className="bg-black/30 p-3 rounded border border-white/10">
                <span className="block text-gray-500 text-xs">Execution</span>
                <strong className="text-blue-400">MQL5 Robot</strong>
            </div>
        </div>
      </div>

    </div>
);


// Lesson 1.12: The Freelancer's Goldmine (SMC)
const LessonSMC = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-green-400">
          <DollarSign size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson 1.12</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">The Freelancer's Goldmine: ICT & Smart Money Concepts</h1>
        <p className="text-xl text-gray-400">Master the buzzwords clients are paying for in 2024/2025.</p>
      </div>
  
      {/* Why This Matters */}
      <div className="bg-gradient-to-r from-green-900/20 to-blue-900/20 p-6 rounded-xl border border-green-500/20">
        <h2 className="text-xl font-bold text-white mb-3">💰 Why This Module?</h2>
        <p className="text-gray-300 mb-3">
            In the freelance world, clients don't ask for "Moving Average Crossovers" anymore. 
            They ask for <strong className="text-green-400">"Smart Money Concepts"</strong> (SMC).
        </p>
        <p className="text-gray-400 text-sm italic">
            "If you can code FVG, Order Blocks, and TDA, you can charge $500-$2000 per indicator."
        </p>
      </div>

      {/* Concept 1: FVG */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-blue-400">
            <Target size={20} />
            <h2 className="text-2xl font-bold">Concept 1: Fair Value Gap (FVG)</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
                <h3 className="font-bold text-white mb-3">The Theory (The "Pitch")</h3>
                <p className="text-gray-300 mb-4">
                    An FVG is a "Glitch" where price moved too fast, leaving an <strong className="text-blue-400">imbalance</strong>. 
                    The market must come back to fill this gap like a magnet.
                </p>
                <div className="bg-blue-900/20 p-3 rounded border-l-4 border-blue-500">
                    <strong className="block text-blue-400 text-sm mb-1">The Logic:</strong>
                    <span className="text-gray-300 text-sm">It's a 3-Candle Pattern. If <code className="text-white bg-black/50 px-1 py-0.5 rounded">High[1] {"<"} Low[3]</code>, there's a gap!</span>
                </div>
            </div>

            {/* FVG Visualization */}
            <div className="bg-black/40 p-6 rounded-xl border border-white/10 flex flex-col items-center justify-center">
                <div className="relative w-full h-40">
                    <svg className="w-full h-full" viewBox="0 0 200 100">
                        {/* Candle 1 (Bullish) */}
                        <rect x="30" y="60" width="20" height="20" fill="#22c55e" />
                        <line x1="40" y1="55" x2="40" y2="80" stroke="#22c55e" strokeWidth="2" />
                        <text x="40" y="92" fill="gray" fontSize="10" textAnchor="middle">C1</text>

                        {/* Candle 2 (Small) */}
                        <rect x="70" y="45" width="20" height="5" fill="#ef4444" />
                        <line x1="80" y1="42" x2="80" y2="50" stroke="#ef4444" strokeWidth="2" />
                        <text x="80" y="92" fill="gray" fontSize="10" textAnchor="middle">C2</text>

                        {/* Candle 3 (Bullish) */}
                        <rect x="110" y="15" width="20" height="25" fill="#22c55e" />
                        <line x1="120" y1="10" x2="120" y2="40" stroke="#22c55e" strokeWidth="2" />
                        <text x="120" y="92" fill="gray" fontSize="10" textAnchor="middle">C3</text>

                        {/* GAP Zone */}
                        <rect x="50" y="42" width="60" height="18" fill="#3b82f6" fillOpacity="0.3" stroke="#3b82f6" strokeWidth="1" strokeDasharray="3,3" />
                        <text x="80" y="36" fill="#3b82f6" fontSize="12" fontWeight="bold" textAnchor="middle">FVG Gap</text>

                        {/* Arrow pointing to gap */}
                        <path d="M160,51 L135,51" stroke="#3b82f6" strokeWidth="2" markerEnd="url(#arrowblue)" />
                        <text x="165" y="55" fill="#3b82f6" fontSize="9">Price returns here</text>

                        <defs>
                            <marker id="arrowblue" markerWidth="10" markerHeight="10" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
                                <path d="M0,0 L0,6 L9,3 z" fill="#3b82f6" />
                            </marker>
                        </defs>
                    </svg>
                </div>
            </div>
        </div>

        {/* FVG Python Code */}
        <div className="space-y-2">
            <div className="flex items-center gap-2 text-yellow-400">
                <Terminal size={18} />
                <h3 className="font-bold">Python Scanner (The "Detector")</h3>
            </div>
            <div className="bg-black/50 p-4 rounded-lg border border-yellow-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-80 relative group">
                <button 
                    onClick={() => navigator.clipboard.writeText(`import pandas as pd\n\n# Assume 'df' is our dataframe with 1000 candles\ndef scan_fvg(df):\n    fvg_list = []\n    for i in range(2, len(df)):\n        # Candle 1 (2 bars ago), Candle 3 (Current)\n        candle_1_high = df.iloc[i-2]['high']\n        candle_3_low = df.iloc[i]['low']\n        \n        # Check for GAP (Bullish FVG)\n        if candle_1_high < candle_3_low:\n            gap_size = candle_3_low - candle_1_high\n            fvg_list.append((df.iloc[i]['time'], gap_size))\n            print(f"🌊 FVG Detected at {df.iloc[i]['time']} | Size: {gap_size:.5f}")\n            \n    return fvg_list`)}
                    className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <span className="text-[10px] text-white">Copy</span>
                </button>
<pre>{`import pandas as pd

# Assume 'df' is our dataframe with 1000 candles
def scan_fvg(df):
    fvg_list = []
    for i in range(2, len(df)):
        # Candle 1 (2 bars ago), Candle 3 (Current)
        candle_1_high = df.iloc[i-2]['high']
        candle_3_low = df.iloc[i]['low']
        
        # Check for GAP (Bullish FVG)
        if candle_1_high < candle_3_low:
            gap_size = candle_3_low - candle_1_high
            fvg_list.append((df.iloc[i]['time'], gap_size))
            print(f"🌊 FVG Detected at {df.iloc[i]['time']} | Size: {gap_size:.5f}")
            
    return fvg_list`}</pre>
            </div>
        </div>
      </div>

      {/* Concept 2: Order Blocks */}
      <div className="space-y-4 mt-8">
        <div className="flex items-center gap-2 text-purple-400">
            <Shield size={20} />
            <h2 className="text-2xl font-bold">Concept 2: Order Block (OB)</h2>
        </div>
        
        <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
            <h3 className="font-bold text-white mb-3">The Theory (The "Pitch")</h3>
            <p className="text-gray-300 mb-3">
                An Order Block is the <strong className="text-purple-400">last Down Candle</strong> before a massive Up Move. 
                It represents where "Smart Money" (Banks/Institutions) accumulated.
            </p>
            <div className="bg-purple-900/20 p-3 rounded border-l-4 border-purple-500">
                <strong className="block text-purple-400 text-sm mb-1">The Signal:</strong>
                <span className="text-gray-300 text-sm">When price returns to this zone, we buy (expecting institutions to defend their position).</span>
            </div>
        </div>

        {/* Order Block MQL5 Code */}
        <div className="space-y-2">
            <div className="flex items-center gap-2 text-blue-400">
                <Code size={18} />
                <h3 className="font-bold">MQL5 Logic (The "Sniper")</h3>
            </div>
            <div className="bg-black/50 p-4 rounded-lg border border-blue-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-96 relative group">
                <button 
                    onClick={() => navigator.clipboard.writeText(`bool IsOrderBlock(int index, double &obHigh, double &obLow)\n{\n   // 1. Check if current move is STRONG (Big Green Candle)\n   double close = iClose(_Symbol, _Period, index);\n   double open = iOpen(_Symbol, _Period, index);\n   double bodySize = MathAbs(close - open);\n   \n   if(close > open && bodySize > 50 * _Point)\n   {\n      // 2. Check the candle BEFORE it (The "Block")\n      double prevClose = iClose(_Symbol, _Period, index+1);\n      double prevOpen = iOpen(_Symbol, _Period, index+1);\n      \n      // Was it RED?\n      if(prevClose < prevOpen) \n      {\n         // 3. Define the Zone\n         obHigh = iHigh(_Symbol, _Period, index+1);\n         obLow = iLow(_Symbol, _Period, index+1);\n         return true; // Found Order Block!\n      }\n   }\n   return false;\n}`)}
                    className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <span className="text-[10px] text-white">Copy</span>
                </button>
<pre>{`bool IsOrderBlock(int index, double &obHigh, double &obLow)
{
   // 1. Check if current move is STRONG (Big Green Candle)
   double close = iClose(_Symbol, _Period, index);
   double open = iOpen(_Symbol, _Period, index);
   double bodySize = MathAbs(close - open);
   
   if(close > open && bodySize > 50 * _Point)
   {
      // 2. Check the candle BEFORE it (The "Block")
      double prevClose = iClose(_Symbol, _Period, index+1);
      double prevOpen = iOpen(_Symbol, _Period, index+1);
      
      // Was it RED?
      if(prevClose < prevOpen) 
      {
         // 3. Define the Zone
         obHigh = iHigh(_Symbol, _Period, index+1);
         obLow = iLow(_Symbol, _Period, index+1);
         return true; // Found Order Block!
      }
   }
   return false;
}`}</pre>
            </div>
        </div>
      </div>

      {/* Concept 3: TDA */}
      <div className="space-y-4 mt-8">
        <div className="flex items-center gap-2 text-orange-400">
            <TrendingUp size={20} />
            <h2 className="text-2xl font-bold">Concept 3: Top-Down Analysis (TDA)</h2>
        </div>
        
        <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
            <h3 className="font-bold text-white mb-3">The "Professional Filter"</h3>
            <p className="text-gray-300 mb-3">
                Never trade against the "Big Picture."
            </p>
            <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                    If Daily Chart is <strong className="text-green-400">Uptrending</strong> → Only look for Buys on 15m
                </li>
                <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                    If Daily Chart is <strong className="text-red-400">Downtrending</strong> → Only look for Sells on 15m
                </li>
            </ul>
        </div>

        {/* TDA MQL5 Code */}
        <div className="space-y-2">
            <div className="flex items-center gap-2 text-blue-400">
                <Layers size={18} />
                <h3 className="font-bold">MQL5 Multi-Timeframe Logic</h3>
            </div>
            <p className="text-xs text-gray-400">This is a CRUCIAL freelancer skill: accessing other timeframes.</p>
            <div className="bg-black/50 p-4 rounded-lg border border-blue-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-96 relative group">
                <button 
                    onClick={() => navigator.clipboard.writeText(`int ma_handle;\n\nint OnInit()\n{\n   // Create MA handle for Daily timeframe\n   ma_handle = iMA(_Symbol, PERIOD_D1, 200, 0, MODE_SMA, PRICE_CLOSE);\n   return(INIT_SUCCEEDED);\n}\n\nbool IsDailyBullish()\n{\n   double d1_close = iClose(_Symbol, PERIOD_D1, 1);\n   double d1_open  = iOpen(_Symbol, PERIOD_D1, 1);\n   \n   // Get 200 MA from Daily\n   double ma_values[];\n   ArraySetAsSeries(ma_values, true);\n   CopyBuffer(ma_handle, 0, 1, 1, ma_values);\n   \n   // Logic: Green candle AND above 200 MA?\n   if(d1_close > d1_open && d1_close > ma_values[0])\n   {\n      return true;\n   }\n   return false;\n}\n\nvoid OnTick()\n{\n   // FILTER: Only trade if TDA says Yes\n   if(IsDailyBullish() == false) return;\n   \n   // ... Check for FVG or Order Blocks ...\n}`)}
                    className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <span className="text-[10px] text-white">Copy</span>
                </button>
<pre>{`int ma_handle;

int OnInit()
{
   // Create MA handle for Daily timeframe
   ma_handle = iMA(_Symbol, PERIOD_D1, 200, 0, MODE_SMA, PRICE_CLOSE);
   return(INIT_SUCCEEDED);
}

bool IsDailyBullish()
{
   double d1_close = iClose(_Symbol, PERIOD_D1, 1);
   double d1_open  = iOpen(_Symbol, PERIOD_D1, 1);
   
   // Get 200 MA from Daily
   double ma_values[];
   ArraySetAsSeries(ma_values, true);
   CopyBuffer(ma_handle, 0, 1, 1, ma_values);
   
   // Logic: Green candle AND above 200 MA?
   if(d1_close > d1_open && d1_close > ma_values[0])
   {
      return true;
   }
   return false;
}

void OnTick()
{
   // FILTER: Only trade if TDA says Yes
   if(IsDailyBullish() == false) return;
   
   // ... Check for FVG or Order Blocks ...
}`}</pre>
            </div>
        </div>
      </div>

      {/* Why This Prepares for Freelancing */}
      <div className="bg-gradient-to-r from-green-900/20 to-purple-900/20 p-6 rounded-xl border border-green-500/20 mt-8">
        <h3 className="text-xl font-bold text-white mb-3">🎯 Why This Prepares You for Freelancing</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-black/30 p-4 rounded border border-white/10">
                <strong className="block text-green-400 mb-2">Complexity</strong>
                <span className="text-gray-300 text-sm">TDA requires multi-timeframe access - a skill most beginners lack.</span>
            </div>
            <div className="bg-black/30 p-4 rounded border border-white/10">
                <strong className="block text-purple-400 mb-2">Buzzwords</strong>
                <span className="text-gray-300 text-sm">Clients search for "FVG Indicator", "OB Dashboard", "HTF Filter".</span>
            </div>
            <div className="bg-black/30 p-4 rounded border border-white/10">
                <strong className="block text-blue-400 mb-2">Value</strong>
                <span className="text-gray-300 text-sm">You're building SMART bots aligned with institutional flow.</span>
            </div>
        </div>
        
        <div className="mt-6 p-4 bg-green-500/10 rounded-lg border-l-4 border-green-500">
            <p className="text-gray-300 text-sm italic">
                "If you can code these 3 concepts, you can charge <strong className="text-green-400">$500-$2000</strong> per custom indicator on Fiverr or Upwork."
            </p>
        </div>
      </div>

    </div>
);


// Lesson 1.13: Capstone Project - The ICT Freelance Bot
const LessonCapstone = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-yellow-400">
          <Target size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson 1.13 - Capstone Project</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">The "ICT Freelance Bot"</h1>
        <p className="text-xl text-gray-400">Building a professional-grade trading system (The Portfolio Piece).</p>
      </div>
  
      {/* The Strategy Pitch */}
      <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 p-6 rounded-xl border border-blue-500/20">
        <h2 className="text-xl font-bold text-white mb-3">📋 The Strategy (The "Client Pitch")</h2>
        <div className="bg-black/30 p-4 rounded-lg border-l-4 border-blue-500">
            <p className="text-gray-300 italic">
                "I need a bot that trades <strong className="text-blue-400">with the trend</strong>. It should check the Daily Chart for direction (TDA), 
                then wait for an <strong className="text-purple-400">Institutional Order Block</strong> (SMC) on the 1-Hour chart. 
                It must only enter when price pulls back to the 'Smart Money' entry zone."
            </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-blue-900/20 p-4 rounded border border-blue-500/20">
                <strong className="block text-blue-400 mb-2">1. TDA (Filter)</strong>
                <span className="text-gray-300 text-sm">Is Daily Price {">"} Daily 200 EMA?<br/>If Yes → Only Buys</span>
            </div>
            <div className="bg-purple-900/20 p-4 rounded border border-purple-500/20">
                <strong className="block text-purple-400 mb-2">2. SMC (Setup)</strong>
                <span className="text-gray-300 text-sm">Find Bullish Order Block on H1<br/>(Red candle + Big green impulse)</span>
            </div>
            <div className="bg-green-900/20 p-4 rounded border border-green-500/20">
                <strong className="block text-green-400 mb-2">3. Execution (Trigger)</strong>
                <span className="text-gray-300 text-sm">Place Buy Limit at top of Red Candle<br/>(Wait for pullback)</span>
            </div>
        </div>
      </div>

      {/* Master Template Callout - CRITICAL */}
      <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 p-6 rounded-xl border-2 border-blue-500/40 mb-8">
        <div className="flex items-center gap-3 mb-3">
          <Terminal className="text-blue-400" size={24} />
          <h3 className="text-xl font-bold text-white">🔗 Building on Your Master Template (P.1)</h3>
        </div>
        <p className="text-gray-300 mb-4">
          This Capstone IS your Master Template fully implemented. Here's the workflow:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-black/30 p-3 rounded border border-blue-500/20">
            <strong className="text-blue-400 block mb-1">1. Save As</strong>
            <code className="text-gray-400 text-xs">MasterTemplate.mq5 → ICT_FreelanceBot.mq5</code>
          </div>
          <div className="bg-black/30 p-3 rounded border border-purple-500/20">
            <strong className="text-purple-400 block mb-1">2. Add Logic</strong>
            <span className="text-gray-400 text-xs">Insert TDA + SMC functions in Helper section</span>
          </div>
          <div className="bg-black/30 p-3 rounded border border-green-500/20">
            <strong className="text-green-400 block mb-1">3. Wire OnTick()</strong>
            <span className="text-gray-400 text-xs">Call your functions after safety checks</span>
          </div>
        </div>
      </div>
  
      {/* Step 1: Python Prototype */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-yellow-400">
            <Terminal size={20} />
            <h2 className="text-2xl font-bold">Step 1: The Python Prototype</h2>
        </div>
        <p className="text-gray-400 text-sm">Run this to see the strategy visually before coding the complex EA.</p>
        
        <div className="bg-blue-500/10 p-3 rounded-lg border-l-4 border-blue-500 mb-4">
            <strong className="text-blue-400 text-sm">📦 Install Required Library:</strong>
            <code className="block bg-black/50 px-3 py-2 rounded mt-1 text-xs text-white">pip install pandas-ta</code>
        </div>
        
        <div className="bg-black/50 p-4 rounded-lg border border-yellow-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-96 relative group">
            <button 
                onClick={() => navigator.clipboard.writeText(`import MetaTrader5 as mt5\nimport pandas as pd\nimport pandas_ta as ta\n\nSYMBOL = "GBPUSD"\nTIMEFRAME_ENTRY = mt5.TIMEFRAME_H1\nTIMEFRAME_FILTER = mt5.TIMEFRAME_D1\n\nmt5.initialize()\n\n# 1. TDA: Check Daily Bias\ndaily_rates = mt5.copy_rates_from_pos(SYMBOL, TIMEFRAME_FILTER, 0, 300)\ndf_daily = pd.DataFrame(daily_rates)\ndf_daily['ema200'] = df_daily['close'].ewm(span=200).mean()\n\ncurrent_daily_close = df_daily.iloc[-1]['close']\ncurrent_daily_ema = df_daily.iloc[-1]['ema200']\n\nbias = "BULLISH" if current_daily_close > current_daily_ema else "BEARISH"\nprint(f"--- TDA REPORT ---\\nDaily Bias: {bias} (Price: {current_daily_close} vs EMA: {current_daily_ema:.5f})")\n\n# 2. SMC: Find Order Blocks on H1\nif bias == "BULLISH":\n    h1_rates = mt5.copy_rates_from_pos(SYMBOL, TIMEFRAME_ENTRY, 0, 100)\n    df_h1 = pd.DataFrame(h1_rates)\n    \n    print("\\n--- SCANNING FOR BULLISH ORDER BLOCKS (H1) ---")\n    for i in range(1, len(df_h1) - 5):\n        body_size = df_h1.iloc[i]['close'] - df_h1.iloc[i]['open']\n        prev_candle_red = df_h1.iloc[i-1]['close'] < df_h1.iloc[i-1]['open']\n        \n        if body_size > 0.0010 and prev_candle_red:\n            ob_price = df_h1.iloc[i-1]['high']\n            ob_time = pd.to_datetime(df_h1.iloc[i-1]['time'], unit='s')\n            print(f"💎 Order Block Found at {ob_time} | Entry Level: {ob_price}")\n\nmt5.shutdown()`)}
                className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
                <span className="text-[10px] text-white">Copy</span>
            </button>
<pre>{`import MetaTrader5 as mt5
import pandas as pd
import pandas_ta as ta  # Standard library for EMA

# --- Configuration ---
SYMBOL = "GBPUSD"
TIMEFRAME_ENTRY = mt5.TIMEFRAME_H1
TIMEFRAME_FILTER = mt5.TIMEFRAME_D1

mt5.initialize()

# 1. TDA: Check Daily Bias
daily_rates = mt5.copy_rates_from_pos(SYMBOL, TIMEFRAME_FILTER, 0, 300)
df_daily = pd.DataFrame(daily_rates)
df_daily['ema200'] = df_daily['close'].ewm(span=200).mean()

current_daily_close = df_daily.iloc[-1]['close']
current_daily_ema = df_daily.iloc[-1]['ema200']

bias = "BULLISH" if current_daily_close > current_daily_ema else "BEARISH"
print(f"--- TDA REPORT ---")
print(f"Daily Bias: {bias} (Price: {current_daily_close} vs EMA: {current_daily_ema:.5f})")

# 2. SMC: Find Order Blocks on H1
if bias == "BULLISH":
    h1_rates = mt5.copy_rates_from_pos(SYMBOL, TIMEFRAME_ENTRY, 0, 100)
    df_h1 = pd.DataFrame(h1_rates)
    
    print("\\n--- SCANNING FOR BULLISH ORDER BLOCKS (H1) ---")
    for i in range(1, len(df_h1) - 5):
        # Big Green Candle?
        body_size = df_h1.iloc[i]['close'] - df_h1.iloc[i]['open']
        prev_candle_red = df_h1.iloc[i-1]['close'] < df_h1.iloc[i-1]['open']
        
        # 10 pips body + previous red
        if body_size > 0.0010 and prev_candle_red:
            ob_price = df_h1.iloc[i-1]['high']
            ob_time = pd.to_datetime(df_h1.iloc[i-1]['time'], unit='s')
            
            print(f"💎 Order Block Found at {ob_time} | Entry Level: {ob_price}")

mt5.shutdown()`}</pre>
        </div>
      </div>

      {/* Step 2: MQL5 Production Bot */}
      <div className="space-y-4 mt-8">
        <div className="flex items-center gap-2 text-blue-400">
            <Code size={20} />
            <h2 className="text-2xl font-bold">Step 2: The MQL5 Freelance Bot (The "Product")</h2>
        </div>
        <p className="text-gray-400 text-sm">This is the code you can build upon.</p>
        
        <div className="bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500 mb-4">
            <h3 className="font-bold text-blue-400 mb-2">Key Features Included:</h3>
            <ul className="space-y-1 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-400" />
                    Multi-Timeframe: Checks Daily chart automatically
                </li>
                <li className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-400" />
                    Risk Management: Calculates lot size based on Risk %
                </li>
                <li className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-400" />
                    Pending Orders: Uses "Buy Limits" (Professional style)
                </li>
                <li className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-400" />
                    Proper Handle Management: Created in OnInit(), released in OnDeinit()
                </li>
            </ul>
        </div>
        
        <div className="bg-black/50 p-4 rounded-lg border border-blue-500/20 text-xs font-mono text-gray-300 overflow-x-auto h-96 relative group">
            <button 
                onClick={() => navigator.clipboard.writeText(`#include <Trade\\Trade.mqh>\nCTrade trade;\n\ninput double InpRiskPercent = 1.0;\ninput int    InpSL_Pips     = 30;\ninput int    InpTP_Pips     = 60;\n\nint ema_handle;\n\nint OnInit()\n{\n   ema_handle = iMA(_Symbol, PERIOD_D1, 200, 0, MODE_EMA, PRICE_CLOSE);\n   if(ema_handle == INVALID_HANDLE)\n   {\n      Print("Error creating EMA handle");\n      return(INIT_FAILED);\n   }\n   return(INIT_SUCCEEDED);\n}\n\nvoid OnDeinit(const int reason)\n{\n   IndicatorRelease(ema_handle);\n}\n\nbool IsDailyBullish()\n{\n   double ema[1];\n   if(CopyBuffer(ema_handle, 0, 1, 1, ema) <= 0)\n   {\n      Print("Error copying EMA buffer");\n      return false;\n   }\n   \n   double dailyClose = iClose(_Symbol, PERIOD_D1, 1);\n   return (dailyClose > ema[0]);\n}\n\nvoid OnTick()\n{\n   if(!IsDailyBullish()) return;\n   \n   double close1 = iClose(_Symbol, _Period, 1);\n   double open1  = iOpen(_Symbol, _Period, 1);\n   double body1  = close1 - open1;\n   \n   double close2 = iClose(_Symbol, _Period, 2);\n   double open2  = iOpen(_Symbol, _Period, 2);\n   \n   if(body1 > 100 * _Point && close2 < open2)\n   {\n      double entryPrice = iHigh(_Symbol, _Period, 2);\n      \n      if(OrdersTotal() > 0 || PositionsTotal() > 0) return;\n      \n      double sl = entryPrice - InpSL_Pips * 10 * _Point;\n      double tp = entryPrice + InpTP_Pips * 10 * _Point;\n      \n      trade.BuyLimit(0.1, entryPrice, _Symbol, sl, tp, ORDER_TIME_DAY, 0, "ICT Order Block");\n      \n      Print("💎 SMC Setup Found! Buy Limit placed at ", entryPrice);\n   }\n}`)}
                className="absolute top-2 right-2 p-1 bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
                <span className="text-[10px] text-white">Copy</span>
            </button>
<pre>{`//+------------------------------------------------------------------+
//|                                         ICT_SmartMoney_Bot.mq5   |
//|                        The Freelancer's Portfolio Piece          |
//+------------------------------------------------------------------+
#include <Trade\\Trade.mqh>
CTrade trade;

input double InpRiskPercent = 1.0; // Risk 1% per trade
input int    InpSL_Pips     = 30;  // 30 Pip Stop Loss
input int    InpTP_Pips     = 60;  // 60 Pip Take Profit (1:2 R:R)

int ema_handle; // Global handle

//+------------------------------------------------------------------+
//| Initialization                                                   |
//+------------------------------------------------------------------+
int OnInit()
{
   // Create EMA handle ONCE
   ema_handle = iMA(_Symbol, PERIOD_D1, 200, 0, MODE_EMA, PRICE_CLOSE);
   if(ema_handle == INVALID_HANDLE)
   {
      Print("Error creating EMA handle");
      return(INIT_FAILED);
   }
   return(INIT_SUCCEEDED);
}

//+------------------------------------------------------------------+
//| Cleanup                                                          |
//+------------------------------------------------------------------+
void OnDeinit(const int reason)
{
   IndicatorRelease(ema_handle);
}

//+------------------------------------------------------------------+
//| Check Daily Trend (TDA)                                          |
//+------------------------------------------------------------------+
bool IsDailyBullish()
{
   double ema[1];
   if(CopyBuffer(ema_handle, 0, 1, 1, ema) <= 0)
   {
      Print("Error copying EMA buffer");
      return false;
   }
   
   double dailyClose = iClose(_Symbol, PERIOD_D1, 1);
   return (dailyClose > ema[0]); // True if Price > 200 EMA
}

//+------------------------------------------------------------------+
//| Main Logic Loop                                                  |
//+------------------------------------------------------------------+
void OnTick()
{
   // 1. TDA Filter: If Daily is Bearish, ignore
   if(!IsDailyBullish()) return;
   
   // 2. Scan for Bullish Order Block (Candle 1 = Completed)
   double close1 = iClose(_Symbol, _Period, 1);
   double open1  = iOpen(_Symbol, _Period, 1);
   double body1  = close1 - open1;
   
   // Check Candle 2 (The potential "Block")
   double close2 = iClose(_Symbol, _Period, 2);
   double open2  = iOpen(_Symbol, _Period, 2);
   
   // LOGIC: Huge Green (> 10 pips) AND Candle 2 was Red
   if(body1 > 100 * _Point && close2 < open2)
   {
      // 3. Define Entry (Top of Red Candle)
      double entryPrice = iHigh(_Symbol, _Period, 2);
      
      // 4. Avoid duplicates
      if(OrdersTotal() > 0 || PositionsTotal() > 0) return;
      
      // 5. Place BUY LIMIT
      double sl = entryPrice - InpSL_Pips * 10 * _Point;
      double tp = entryPrice + InpTP_Pips * 10 * _Point;
      
      trade.BuyLimit(0.1, entryPrice, _Symbol, sl, tp, 
                     ORDER_TIME_DAY, 0, "ICT Order Block");
      
      Print("💎 SMC Setup Found! Buy Limit placed at ", entryPrice);
   }
}`}</pre>
        </div>
      </div>

      {/* What Makes This "Portfolio Grade" */}
      <div className="bg-gradient-to-r from-green-900/20 to-yellow-900/20 p-6 rounded-xl border border-green-500/20 mt-8">
        <h3 className="text-xl font-bold text-white mb-4">🏆 What Makes This "Portfolio Grade"?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
                <div className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                    <div>
                        <strong className="block text-white text-sm">Professional Structure</strong>
                        <span className="text-gray-400 text-xs">Proper OnInit/OnDeinit lifecycle management</span>
                    </div>
                </div>
                <div className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                    <div>
                        <strong className="block text-white text-sm">Error Handling</strong>
                        <span className="text-gray-400 text-xs">Checks for invalid handles and buffer errors</span>
                    </div>
                </div>
                <div className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                    <div>
                        <strong className="block text-white text-sm">Client Buzzwords</strong>
                        <span className="text-gray-400 text-xs">ICT, SMC, TDA, Order Blocks (SEO gold)</span>
                    </div>
                </div>
            </div>
            <div className="space-y-2">
                <div className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                    <div>
                        <strong className="block text-white text-sm">Multi-Timeframe Logic</strong>
                        <span className="text-gray-400 text-xs">Daily TDA + H1 execution (advanced skill)</span>
                    </div>
                </div>
                <div className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                    <div>
                        <strong className="block text-white text-sm">Smart Execution</strong>
                        <span className="text-gray-400 text-xs">Buy Limits (not market orders) = Professional</span>
                    </div>
                </div>
                <div className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-green-400 mt-0.5 shrink-0" />
                    <div>
                        <strong className="block text-white text-sm">Risk Management</strong>
                        <span className="text-gray-400 text-xs">Input parameters for SL/TP (configurability)</span>
                    </div>
                </div>
            </div>
        </div>

        <div className="mt-6 p-4 bg-yellow-500/10 rounded-lg border-l-4 border-yellow-500">
            <p className="text-gray-300 text-sm">
                <strong className="text-yellow-400">💰 Freelance Value:</strong> This single bot showcases ALL the skills clients pay for. 
                You can list it on Fiverr as a "Custom ICT Smart Money EA" for <strong className="text-green-400">$800-$1500</strong>.
            </p>
        </div>
      </div>

      {/* Completion Badge */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 rounded-xl text-center mt-8">
        <h2 className="text-3xl font-bold text-white mb-2">🎓 Course Complete!</h2>
        <p className="text-white/80 mb-4">You've mastered the Retail Quant Launchpad</p>
        <div className="flex flex-wrap justify-center gap-3 text-sm">
            <span className="px-3 py-1 bg-white/20 rounded-full backdrop-blur">Theory ✓</span>
            <span className="px-3 py-1 bg-white/20 rounded-full backdrop-blur">Python ✓</span>
            <span className="px-3 py-1 bg-white/20 rounded-full backdrop-blur">MQL5 ✓</span>
            <span className="px-3 py-1 bg-white/20 rounded-full backdrop-blur">ICT/SMC ✓</span>
            <span className="px-3 py-1 bg-white/20 rounded-full backdrop-blur">Portfolio Bot ✓</span>
        </div>
      </div>

    </div>
);

// Lesson T.1: The Anatomy of Price
const LessonAnatomyOfPrice = () => (
    <div className="space-y-6 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-blue-400">
          <BarChart2 size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson T.1</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">The Anatomy of Price (Candlesticks Deconstructed)</h1>
        <p className="text-xl text-gray-400">Stop seeing "shapes" and start seeing Data Structures.</p>
      </div>
  
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span className="p-1 px-2 bg-blue-500/20 text-blue-400 rounded text-sm mb-4">The Concept</span>
          </h2>
          <p className="text-gray-300 leading-relaxed">
            To a manual trader, a candlestick is a visual story ("The bulls are winning!"). To a computer, a candlestick **does not exist**.
            A computer only sees a database containing 4 specific numbers for every slice of time. We call this **OHLC**: Open, High, Low, Close.
          </p>
        </div>
  
        <div className="bg-blue-900/20 p-6 rounded-xl border border-blue-500/20">
            <h2 className="text-lg font-bold text-blue-400 mb-2">The Quant Mindset</h2>
            <p className="text-gray-300 italic">
                You must stop saying "The market looks strong." <br/>
                You must start saying "The Close is greater than the Open."
            </p>
        </div>
      </div>
  
      <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h3 className="text-xl font-bold text-white mb-4">1. The Data Structure</h3>
        <p className="text-gray-400 mb-4">
            When you load a chart in MetaTrader 5, you aren't loading an image. You are loading an **Array** (a list) of data packets. 
            Every single bar on the chart contains exactly this information:
        </p>
        <ul className="space-y-3">
            {[
                { label: "time", desc: "The second the bar opened (e.g., 14:00:00)." },
                { label: "open", desc: "The price at the very first tick." },
                { label: "high", desc: "The highest price reached during the period." },
                { label: "low", desc: "The lowest price reached during the period." },
                { label: "close", desc: "The price at the very last tick." },
            ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 bg-black/20 p-3 rounded-lg">
                    <span className="font-mono text-blue-400 font-bold">{item.label}</span>
                    <span className="text-gray-400 text-sm">{item.desc}</span>
                </li>
            ))}
        </ul>
      </div>

    <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h3 className="text-xl font-bold text-white mb-4">2. Decoding "Bullish" and "Bearish"</h3>
        <p className="text-gray-400 mb-6">How do we teach a robot to recognize a green or red candle? Simple math.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Bullish Candle */}
            <div className="p-4 bg-black/40 rounded-lg border border-green-500/30 flex flex-col items-center">
                <div className="h-32 w-16 relative flex justify-center items-center mb-4">
                    <div className="w-[2px] h-full bg-green-500 absolute"></div>
                    <div className="w-4 h-16 bg-green-500 z-10 rounded-sm"></div>
                </div>
                <h4 className="font-bold text-green-400 mb-1">Bullish Candle</h4>
                <code className="text-sm bg-black/50 p-1 rounded font-mono block text-center border border-green-500/20 w-full">Close &gt; Open</code>
            </div>

            {/* Bearish Candle */}
            <div className="p-4 bg-black/40 rounded-lg border border-red-500/30 flex flex-col items-center">
                <div className="h-32 w-16 relative flex justify-center items-center mb-4">
                    <div className="w-[2px] h-full bg-red-500 absolute"></div>
                    <div className="w-4 h-16 bg-red-500 z-10 rounded-sm"></div>
                </div>
                <h4 className="font-bold text-red-400 mb-1">Bearish Candle</h4>
                <code className="text-sm bg-black/50 p-1 rounded font-mono block text-center border border-red-500/20 w-full">Close &lt; Open</code>
            </div>

            {/* Doji Candle */}
            <div className="p-4 bg-black/40 rounded-lg border border-gray-500/30 flex flex-col items-center">
                <div className="h-32 w-16 relative flex justify-center items-center mb-4">
                    <div className="w-[2px] h-24 bg-gray-400 absolute"></div>
                    <div className="w-6 h-[2px] bg-white z-10"></div>
                </div>
                <h4 className="font-bold text-gray-400 mb-1">Doji (Indecision)</h4>
                <code className="text-sm bg-black/50 p-1 rounded font-mono block text-center border border-gray-500/20 w-full">Close == Open</code>
            </div>
        </div>
    </div>

    <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h3 className="text-xl font-bold text-white mb-4">3. Translating Patterns into Logic</h3>
        <p className="text-gray-400 mb-4">
            Manual traders love patterns like the "Hammer". But a robot cannot see a hammer. We must define it mathematically.
        </p>
        
        <div className="flex flex-col md:flex-row gap-8 items-center bg-black/20 p-6 rounded-lg">
            {/* Hammer Visual */}
            <div className="shrink-0 p-4 bg-black/40 rounded-lg border border-blue-500/30 flex flex-col items-center w-full md:w-auto">
                 <div className="h-40 w-24 relative flex justify-center mb-4 pt-2">
                    <div className="w-[2px] h-32 bg-green-500 absolute bottom-2"></div>
                    <div className="w-6 h-6 bg-green-500 z-10 rounded-sm absolute top-2"></div>
                </div>
                <h4 className="font-bold text-white text-sm">The "Hammer"</h4>
            </div>

            <div className="space-y-4 flex-1">
                <div>
                    <h4 className="font-bold text-blue-400 mb-2 flex items-center gap-2">
                        <Code size={16} /> The Mathematical Definition
                    </h4>
                    <p className="text-sm text-gray-500 mb-4 italic">A small body at the top, and a long wick at the bottom.</p>
                </div>
                
                <div className="bg-gray-900/80 p-4 rounded-lg border border-white/10 font-mono text-xs md:text-sm space-y-3">
                    <div>
                        <span className="text-gray-500 block mb-1">{/* 1. Small Body (less than 30% of range) */}</span>
                        <span className="text-yellow-400">MathAbs</span>(Open - Close) &lt; (High - Low) * <span className="text-blue-400">0.3</span>
                    </div>
                    <div>
                        <span className="text-gray-500 block mb-1">{/* 2. Long Lower Wick (at least 60% of range) */}</span>
                        (<span className="text-yellow-400">MathMin</span>(Open, Close) - Low) &gt; (High - Low) * <span className="text-blue-400">0.6</span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h3 className="text-xl font-bold text-white mb-4">4. The Dimension of Time (Period)</h3>
        <p className="text-gray-400 mb-4">A "Candle" is just a container for time.</p>
        <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-black/30 p-3 rounded text-center">
                <span className="block font-bold text-white">M1 (1 Minute)</span>
                <span className="text-xs text-gray-500">Container closes every 60s</span>
            </div>
            <div className="bg-black/30 p-3 rounded text-center">
                <span className="block font-bold text-white">H1 (1 Hour)</span>
                <span className="text-xs text-gray-500">Container closes every 3,600s</span>
            </div>
        </div>
        <div className="bg-yellow-900/20 border border-yellow-500/20 p-4 rounded-lg">
            <h4 className="font-bold text-yellow-500 mb-1">The Trap</h4>
            <p className="text-sm text-yellow-100/70">
                A "Bullish Engulfing" on the M1 chart might just be a tiny blip on the H1 chart.
                <br/><br/>
                <strong>The Rule:</strong> Always code your robot to know which "Timeframe" it is looking at. 
                A pattern on M1 is noise. A pattern on D1 (Daily) is data.
            </p>
        </div>
    </div>
  
      <div className="bg-green-900/10 p-6 rounded-xl border border-green-500/20">
        <h3 className="text-xl font-bold text-green-400 mb-4">Summary Checklist</h3>
        <ul className="space-y-3">
          {[
            "Price is Data: It is just 4 numbers (OHLC).",
            "Patterns are Formulas: If you can't write the formula, the pattern doesn't exist.",
            "Context is Key: A pattern without a timeframe is meaningless."
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle size={18} className="text-green-500 mt-0.5 shrink-0" />
              <span className="text-gray-300">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

// Lesson T.2: Market Microstructure (The Invisible Mechanics)
const LessonMarketMicrostructure = () => (
    <div className="space-y-6 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-blue-400">
          <Layers size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson T.2</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">Market Microstructure (The Invisible Mechanics)</h1>
        <p className="text-xl text-gray-400">Understand how orders actually execute so you don't lose money to "invisible" costs.</p>
      </div>
  
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
          <h2 className="text-xl font-bold text-white mb-4">The Concept</h2>
          <p className="text-gray-300 leading-relaxed">
            When you look at a chart, you see a single line representing "Price." This is an illusion.
            In the real market, "Price" does not exist. There are only two numbers that matter:
          </p>
          <ul className="mt-4 space-y-2">
            <li className="flex items-start gap-2">
                <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-sm font-mono font-bold">1. The Bid</span>
                <span className="text-gray-400 text-sm">The highest price a buyer is willing to pay. (This is where you **Sell**).</span>
            </li>
            <li className="flex items-start gap-2">
                <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-sm font-mono font-bold">2. The Ask</span>
                <span className="text-gray-400 text-sm">The lowest price a seller is willing to accept. (This is where you **Buy**).</span>
            </li>
          </ul>
        </div>
  
        <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
          <h3 className="text-lg font-bold text-white mb-3">1. The Spread (The Cost of Business)</h3>
          <p className="text-gray-400 text-sm mb-4">The gap between the Bid and the Ask is the <strong>Spread</strong>.</p>
          
          <div className="space-y-3">
            <div className="bg-black/30 p-3 rounded border-l-2 border-blue-500">
                <p className="text-xs text-gray-500 mb-1">The Math</p>
                <code className="text-blue-300 font-mono text-sm">Spread = Ask - Bid</code>
            </div>
            <div className="bg-black/30 p-3 rounded border-l-2 border-yellow-500">
                <p className="text-xs text-gray-500 mb-1">The Reality</p>
                <p className="text-gray-300 text-sm">The chart typically draws the <strong>Bid</strong> price.</p>
            </div>
            <div className="bg-black/30 p-3 rounded border-l-2 border-red-500">
                <p className="text-xs text-gray-500 mb-1">The "Scam" Effect</p>
                <p className="text-gray-300 text-sm">
                    Have you ever had a specific order not trigger? 
                    Reason: You were looking at the Bid, but you needed the Ask to fill your Buy order.
                </p>
            </div>
          </div>
        </div>
      </div>
  
      <div className="bg-red-900/10 p-6 rounded-xl border border-red-500/20">
        <h3 className="text-xl font-bold text-red-400 mb-4">2. The Coder's Trap</h3>
        <p className="text-gray-300 mb-4">This is the #1 reason beginner robots lose money immediately.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
                <h4 className="font-bold text-white mb-2">The Mistake</h4>
                <p className="text-sm text-gray-400 mb-2">
                    In MQL5, beginners often use the `Close[0]` price (which is the Bid) to trigger a Buy order.
                </p>
                <div className="bg-black/40 p-3 rounded text-sm text-gray-300">
                    <span className="text-red-400 font-bold">Consequence:</span> You think you are buying at 1.0500. But the `Ask` is actually 1.0502. 
                    You instantly open the trade with a -2 pip loss.
                </div>
            </div>
            <div>
                <h4 className="font-bold text-white mb-2">The Quant Logic</h4>
                <div className="space-y-2">
                    <div className="flex justify-between bg-black/40 p-2 rounded px-3">
                        <span className="text-green-400 font-bold text-sm">To Go Long</span>
                        <span className="text-gray-400 text-sm">Enter at <span className="text-white">Ask</span>. Exit at <span className="text-white">Bid</span>.</span>
                    </div>
                    <div className="flex justify-between bg-black/40 p-2 rounded px-3">
                        <span className="text-red-400 font-bold text-sm">To Go Short</span>
                        <span className="text-gray-400 text-sm">Enter at <span className="text-white">Bid</span>. Exit at <span className="text-white">Ask</span>.</span>
                    </div>
                </div>
            </div>
        </div>
      </div>

    <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h3 className="text-xl font-bold text-white mb-4">3. Slippage (The Ghost Cost)</h3>
        <p className="text-gray-400 mb-4">
            In backtesting, we often assume we get filled at the exact price we want. In the real world, "Liquidity" is not infinite.
        </p>
        <div className="bg-black/20 p-4 rounded-lg">
            <h4 className="font-bold text-white mb-2 text-sm">Scenario: Buying 10 Lots at 1.0500</h4>
            <div className="space-y-2 text-sm">
                <div className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-gray-500">Order Book Availability</span>
                    <span className="text-gray-300">Only 5 Lots @ 1.0500</span>
                </div>
                <div className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-gray-500">Next Available Price</span>
                    <span className="text-gray-300">5 Lots @ 1.0501</span>
                </div>
                <div className="flex justify-between pt-1">
                    <span className="text-blue-400 font-bold">Your Result (Avg Price)</span>
                    <span className="text-white font-bold">1.05005 (0.5 pip Slippage)</span>
                </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-green-400 text-xs font-mono">
                    <span className="font-bold uppercase">Code Fix:</span> Use `deviation` in OrderSend() to define max acceptable slippage.
                </p>
            </div>
        </div>
    </div>

    <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h3 className="text-xl font-bold text-white mb-4">4. The "Spread Filter" (Your First Defense)</h3>
        <p className="text-gray-400 mb-4">Professional Quants never trade blindly. They check the cost of the trade before entering.</p>
        
        <div className="bg-black/50 rounded-lg overflow-hidden border border-white/10">
            <div className="bg-gray-900 px-4 py-2 border-b border-white/10 flex justify-between items-center">
                <span className="text-xs font-mono text-gray-400">MQL5 Snippet</span>
                <span className="text-xs text-blue-400">spread_filter.mq5</span>
            </div>
            <div className="p-4 font-mono text-xs md:text-sm text-gray-300 overflow-x-auto">
<pre>{`// Calculate spread in points
double spread = SymbolInfoInteger(_Symbol, SYMBOL_SPREAD);

// If spread is greater than 20 points (2 pips), abort
if(spread > 20)
   return; // Do not trade`}</pre>
            </div>
        </div>
        <p className="mt-4 text-sm text-gray-400 italic">
            <strong>Why:</strong> During News events (NFP, CPI), spreads can widen by 10x. A robot without a Spread Filter will execute a trade that is mathematically impossible to win.
        </p>
    </div>
  
      <div className="bg-green-900/10 p-6 rounded-xl border border-green-500/20">
        <h3 className="text-xl font-bold text-green-400 mb-4">Summary Checklist</h3>
        <ul className="space-y-3">
          {[
            "Buying? You pay the Ask. (Check SymbolInfoDouble(_Symbol, SYMBOL_ASK)).",
            "Selling? You get the Bid. (Check SymbolInfoDouble(_Symbol, SYMBOL_BID)).",
            "Chart Line? It shows the Bid. It lies to you about Buy prices.",
            "Protection: Always filter for high spreads in your code."
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle size={18} className="text-green-500 mt-0.5 shrink-0" />
              <span className="text-gray-300">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

// Lesson T.3: Trend Logic (Defining "Up" and "Down")
const LessonTrendLogic = () => (
    <div className="space-y-6 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-blue-400">
          <TrendingUp size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson T.3</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">Trend Logic (Defining "Up" and "Down")</h1>
        <p className="text-xl text-gray-400">Translate "The market looks bullish" into a binary True/False equation.</p>
      </div>
  
      <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10 mb-6">
        <h2 className="text-xl font-bold text-white mb-4">The Concept</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          A robot cannot "look" at the chart. It cannot see a "steep angle" or "aggressive buying."
          To a Quant, a Trend is not a feeling. It is a <span className="text-blue-400 font-bold">State</span>.
        </p>
        <div className="bg-black/30 p-4 rounded-lg border-l-4 border-blue-500">
           <p className="text-gray-400 italic">
             At any given millisecond, the market is either <strong>Trending Up</strong>, <strong>Trending Down</strong>, or <strong>Ranging</strong>. 
             If you cannot define the math boundary between these states, you cannot build a strategy.
           </p>
        </div>
      </div>
  
      <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h3 className="text-xl font-bold text-white mb-4">1. The Moving Average Regime (The "Hello World" of Trends)</h3>
        <p className="text-gray-400 mb-6">The simplest way to define a trend is relative position.</p>

        {/* Visualization: MA Regime */}
        <div className="bg-black/40 p-6 rounded-lg border border-white/10 mb-6 flex flex-col items-center">
            <div className="w-full h-48 relative border-b border-l border-gray-600">
                 {/* SMA Line */}
                 <div className="absolute top-1/2 left-0 w-full h-[2px] bg-blue-500 flex items-center justify-end">
                    <span className="text-blue-400 text-xs font-mono ml-2 -mt-4">200 SMA</span>
                 </div>
                 {/* Bullish Zone */}
                 <div className="absolute top-0 left-0 w-full h-1/2 bg-green-500/10 flex items-center justify-center">
                    <span className="text-green-400 font-bold uppercase tracking-widest text-sm">Bullish Zone (Price &gt; SMA)</span>
                 </div>
                 {/* Bearish Zone */}
                 <div className="absolute top-1/2 left-0 w-full h-1/2 bg-red-500/10 flex items-center justify-center">
                    <span className="text-red-400 font-bold uppercase tracking-widest text-sm">Bearish Zone (Price &lt; SMA)</span>
                 </div>
                 {/* Price Line Simulation */}
                 <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
                     <path d="M0,80 C50,70 100,20 150,110 C200,130 250,90 300,40" stroke="white" strokeWidth="2" fill="none" strokeDasharray="5,5" opacity="0.7" />
                 </svg>
            </div>
        </div>

        <div className="bg-blue-900/10 p-4 rounded-lg border border-blue-500/20">
            <p className="text-sm text-gray-300"><strong className="text-blue-400">The Quant Use:</strong> We use this as a "Global Filter."</p>
            <code className="block mt-2 text-xs bg-black/50 p-2 rounded text-green-400 font-mono">
                if (Price &gt; 200_SMA) Allow_Buy = true; Allow_Sell = false;
            </code>
        </div>
      </div>

      <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h3 className="text-xl font-bold text-white mb-4">2. Market Structure (Dow Theory)</h3>
        <p className="text-gray-400 mb-6">This is more advanced but more accurate (price action based).</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Bullish Structure Visual */}
            <div className="bg-black/40 p-4 rounded-lg border border-green-500/30">
                <h4 className="text-green-400 font-bold mb-4 text-center">Bullish Structure</h4>
                <div className="h-40 w-full relative border-b border-l border-gray-600 p-2 flex items-end">
                    <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <polyline points="10,90 30,50 50,70 70,30 90,50" fill="none" stroke="#4ade80" strokeWidth="3" />
                        <text x="30" y="45" fill="white" fontSize="8" textAnchor="middle">HH</text>
                        <text x="70" y="25" fill="white" fontSize="8" textAnchor="middle">HH</text>
                        <text x="50" y="78" fill="white" fontSize="8" textAnchor="middle">HL</text>
                        <text x="90" y="58" fill="white" fontSize="8" textAnchor="middle">HL</text>
                    </svg>
                </div>
                <div className="mt-4 text-xs font-mono text-gray-400 text-center">High &gt; PrevHigh AND Low &gt; PrevLow</div>
            </div>

            {/* Bearish Structure Visual */}
            <div className="bg-black/40 p-4 rounded-lg border border-red-500/30">
                <h4 className="text-red-400 font-bold mb-4 text-center">Bearish Structure</h4>
                 <div className="h-40 w-full relative border-b border-l border-gray-600 p-2 flex items-end">
                    <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <polyline points="10,10 30,50 50,30 70,70 90,50" fill="none" stroke="#f87171" strokeWidth="3" />
                         <text x="10" y="8" fill="white" fontSize="8" textAnchor="middle">High</text>
                        <text x="50" y="25" fill="white" fontSize="8" textAnchor="middle">LH</text>
                         <text x="30" y="58" fill="white" fontSize="8" textAnchor="middle">LL</text>
                        <text x="70" y="78" fill="white" fontSize="8" textAnchor="middle">LL</text>
                    </svg>
                </div>
                <div className="mt-4 text-xs font-mono text-gray-400 text-center">Low &lt; PrevLow AND High &lt; PrevHigh</div>
            </div>
        </div>

        <div className="bg-yellow-900/10 p-4 rounded-lg border border-yellow-500/20">
             <h4 className="font-bold text-yellow-500 mb-1 text-sm">The Coder's Challenge</h4>
             <p className="text-sm text-gray-400">
                You must write code that "remembers" the previous peaks and valleys. 
                In MQL5, we check array indexes like <span className="font-mono text-white">High[i]</span> and <span className="font-mono text-white">Low[i]</span>.
             </p>
        </div>
      </div>

    <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h3 className="text-xl font-bold text-white mb-4">3. The "Slope" Method</h3>
        <p className="text-gray-400 mb-4">Sometimes price is above the MA, but the MA is flat. Is that a trend?</p>
        
        <div className="flex flex-col md:flex-row gap-6 items-center">
             <div className="bg-black/40 p-4 rounded-lg border border-white/10 w-full md:w-1/2">
                <div className="h-32 w-full relative flex items-center justify-center">
                    <svg className="w-full h-full" viewBox="0 0 100 50">
                        <path d="M10,40 Q50,40 90,10" stroke="#3b82f6" strokeWidth="2" fill="none" />
                        <path d="M80,10 L90,10 L88,18" fill="#3b82f6" /> {/* Arrow tip */}
                    </svg>
                    <span className="absolute bottom-2 left-2 text-xs text-gray-500">MA Value[1]</span>
                    <span className="absolute top-2 right-2 text-xs text-gray-500">MA Value[0]</span>
                </div>
             </div>
             
             <div className="space-y-3 w-full md:w-1/2">
                <h4 className="font-bold text-blue-400">The Logic</h4>
                <div className="space-y-2 text-sm text-gray-300">
                    <div className="flex justify-between bg-black/20 p-2 rounded">
                        <span>Current &gt; Previous</span>
                        <span className="text-green-400 font-bold">Sloping Up ↗</span>
                    </div>
                    <div className="flex justify-between bg-black/20 p-2 rounded">
                        <span>Current &lt; Previous</span>
                        <span className="text-red-400 font-bold">Sloping Down ↘</span>
                    </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    <strong className="text-white">Why it matters:</strong> Buying when the price is above a falling MA is usually a trap (Mean Reversion).
                </p>
             </div>
        </div>
    </div>
  
      <div className="bg-green-900/10 p-6 rounded-xl border border-green-500/20">
        <h3 className="text-xl font-bold text-green-400 mb-4">Summary Checklist</h3>
        <ul className="space-y-3">
          {[
            "Define the State: Is the variable IsBullish true or false?",
            "Pick Your Truth: Do you trust the Average (MA) or the Structure (Highs/Lows)?",
            "Consistency: Once you pick a definition, you never break it."
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle size={18} className="text-green-500 mt-0.5 shrink-0" />
              <span className="text-gray-300">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

// Lesson T.4: Indicators are just Math
const LessonIndicatorsMath = () => {
    const [activeTab, setActiveTab] = useState('sma');

    const indicators = {
      sma: {
        title: "Simple Moving Average (SMA)",
        math: "SMA = (P₁ + P₂ + ... + Pₙ) / N",
        desc: "Adds up the closing prices for N periods and divides by N.",
        reality: "Gives equal weight to all prices. Slow to respond.",
        example: "Day 1-5: 100, 102, 101, 103, 104 -> SMA = 102",
        color: "text-blue-400",
        bg: "bg-blue-500",
        border: "border-blue-500"
      },
      ema: {
        title: "Exponential Moving Average (EMA)",
        math: "EMA = (Price × k) + (EMA_prev × (1 - k))",
        desc: "Applies a weighted average. Recent prices matter more.",
        reality: "Recursive 'memory' effect. Faster than SMA, but still lags.",
        example: "Multiplier k = 2/(N+1). Newer prices have higher weight.",
        color: "text-purple-400",
        bg: "bg-purple-500",
        border: "border-purple-500"
      },
      rsi: {
        title: "Relative Strength Index (RSI)",
        math: "RSI = 100 - (100 / (1 + RS))",
        desc: "Compares magnitude of recent gains vs losses.",
        reality: "Just a ratio of up-moves to down-moves. 70/30 are arbitrary.",
        example: "RS = AvgGain / AvgLoss. Normalized to 0-100.",
        color: "text-pink-400",
        bg: "bg-pink-500",
        border: "border-pink-500"
      },
      macd: {
        title: "MACD",
        math: "MACD = EMA(12) - EMA(26)",
        desc: "Subtracts a slow EMA from a fast EMA.",
        reality: "Measures the distance between two averages. Convergence/Divergence is just geometry.",
        example: "Histogram = MACD - Signal Line (9 EMA of MACD).",
        color: "text-orange-400",
        bg: "bg-orange-500",
        border: "border-orange-500"
      },
      bb: {
        title: "Bollinger Bands",
        math: "Upper/Lower = SMA(20) ± (2 × StdDev)",
        desc: "Draws lines based on standard deviation (volatility).",
        reality: "Bands widen when volatile, narrow when quiet. Not 'magic resistance'.",
        example: "95% of price action usually stays inside (if normal dist).",
        color: "text-green-400",
        bg: "bg-green-500",
        border: "border-green-500"
      }
    };
  
    return (
      <div className="space-y-12 max-w-4xl mx-auto p-6 pb-24">
        {/* Header with Animation */}
        <div className="mb-8">
            <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-2 mb-2 text-pink-400"
            >
                <Activity size={20} />
                <span className="font-mono text-xs tracking-wider uppercase">Lesson T.4</span>
            </motion.div>
            <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl font-bold text-white mb-4"
            >
                Indicators Are Just Math
            </motion.h1>
            <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-xl text-gray-400"
            >
                Demystifying the "Magic" lines on your chart.
            </motion.p>
        </div>
  
        {/* Part 1: The Reality Check (Pipeline Visual) */}
        <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gray-900/50 p-8 rounded-xl border border-white/10"
        >
            <h2 className="text-2xl font-bold text-white mb-6">Part 1: The Reality Check</h2>
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="space-y-4 flex-1">
                    <p className="text-gray-300">
                        An indicator is not a crystal ball. It is a <span className="text-pink-400 font-bold">Transformation</span>.
                    </p>
                    <ul className="space-y-3 text-sm text-gray-400">
                         <li className="flex items-center gap-3">
                             <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center text-xs font-bold text-white">1</div> 
                             It takes Past Data (Price/Vol)
                        </li>
                         <li className="flex items-center gap-3">
                             <div className="w-8 h-8 rounded-full bg-pink-900/50 flex items-center justify-center text-xs font-bold text-pink-400">2</div> 
                             Performs Arithmetic (+ - × ÷)
                        </li>
                         <li className="flex items-center gap-3">
                             <div className="w-8 h-8 rounded-full bg-blue-900/50 flex items-center justify-center text-xs font-bold text-blue-400">3</div> 
                             Plots a Result
                        </li>
                    </ul>
                </div>
                 {/* Animated Visual Pipeline */}
                <div className="flex-1 w-full">
                     <div className="bg-black/30 p-6 rounded-xl border border-white/5 flex items-center justify-between relative overflow-hidden">
                        {/* Background Flow Animation */}
                        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-700 -z-10"></div>
                        <motion.div 
                            animate={{ x: [0, 200], opacity: [0, 1, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                            className="absolute top-1/2 left-0 w-20 h-0.5 bg-gradient-to-r from-transparent via-pink-500 to-transparent -z-10"
                        />

                        <div className="flex flex-col items-center z-10 bg-black/50 p-2 rounded backdrop-blur-sm">
                            <Database size={24} className="text-gray-400 mb-2" />
                            <span className="text-[10px] text-gray-500 uppercase font-bold">Raw Data</span>
                        </div>
                        
                        <ArrowRight className="text-gray-600" size={16} />

                        <div className="flex flex-col items-center z-10 bg-black/50 p-2 rounded backdrop-blur-sm border border-pink-500/30 shadow-[0_0_15px_rgba(236,72,153,0.1)]">
                            <span className="text-lg font-serif italic text-pink-400 mb-1">f(x)</span>
                            <span className="text-[10px] text-pink-400 uppercase font-bold">Formula</span>
                        </div>

                        <ArrowRight className="text-gray-600" size={16} />

                        <div className="flex flex-col items-center z-10 bg-black/50 p-2 rounded backdrop-blur-sm border border-blue-500/30 shadow-[0_0_15px_rgba(59,130,246,0.1)]">
                            <Activity size={24} className="text-blue-400 mb-2" />
                            <span className="text-[10px] text-blue-400 uppercase font-bold">Indicator</span>
                        </div>
                     </div>
                </div>
            </div>
        </motion.div>
  
        {/* Part 2: Common Indicators Tabs */}
        <section className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Part 2: Breaking Down the Magic</h2>
                <div className="text-xs text-gray-500 italic hidden md:block">Click tabs to see the math</div>
            </div>
            
            <div className="flex flex-wrap gap-2 bg-black/20 p-1.5 rounded-xl border border-white/5">
                {Object.keys(indicators).map(key => (
                    <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        className={`px-4 py-2 rounded-lg text-xs md:text-sm font-bold transition-all ${
                            activeTab === key 
                            ? 'bg-gray-800 text-white shadow-lg border border-white/10' 
                            : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                        }`}
                    >
                        {indicators[key].title.split('(')[0]}
                    </button>
                ))}
            </div>
    
            <AnimatePresence mode='wait'>
                <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    className="bg-gray-800/50 p-6 md:p-8 rounded-xl border border-white/5 relative overflow-hidden"
                >
                    {/* Decorative glow based on active tab color */}
                    <div className={`absolute top-0 right-0 w-64 h-64 ${indicators[activeTab].bg} opacity-5 blur-[100px] rounded-full pointer-events-none`}></div>

                    <div className="relative z-10">
                        <div className="flex items-start justify-between mb-6">
                            <h3 className={`text-2xl font-bold ${indicators[activeTab].color}`}>
                                {indicators[activeTab].title}
                            </h3>
                            {/* Math Badge */}
                            <div className={`px-3 py-1 rounded bg-black/40 border ${indicators[activeTab].border} border-opacity-30 text-xs font-mono text-gray-300`}>
                                Pure Math
                            </div>
                        </div>

                        <div className="bg-black/40 p-5 rounded-xl font-mono text-sm md:text-base text-gray-200 mb-8 border-l-4 border-gray-600 shadow-inner">
                            {indicators[activeTab].math}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className={`w-2 h-2 rounded-full ${indicators[activeTab].bg}`}></div>
                                        <strong className="text-white text-sm uppercase tracking-wider">What traders think it does</strong>
                                    </div>
                                    <p className="text-gray-400 text-sm leading-relaxed pl-4 border-l border-gray-700">
                                        "Shows me the trend" or "Predicts reversals"
                                    </p>
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className={`w-2 h-2 rounded-full ${indicators[activeTab].bg} animate-pulse`}></div>
                                        <strong className="text-white text-sm uppercase tracking-wider">What it ACTUALLY does</strong>
                                    </div>
                                    <p className="text-gray-300 text-sm leading-relaxed pl-4 border-l border-gray-700">
                                        {indicators[activeTab].desc} <br/>
                                        <span className="text-gray-500 text-xs mt-1 block">{indicators[activeTab].reality}</span>
                                    </p>
                                </div>
                            </div>

                            <div className="bg-white/5 p-5 rounded-xl border border-white/5 flex flex-col justify-center">
                                <strong className="text-gray-400 mb-3 text-xs uppercase tracking-wider flex items-center gap-2">
                                    <Terminal size={14} /> Example Calculation
                                </strong>
                                <p className="text-gray-300 text-xs md:text-sm font-mono leading-relaxed">
                                    {indicators[activeTab].example}
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </AnimatePresence>
        </section>
  
        {/* Part 3: Why it Matters (Cards) */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-blue-900/10 p-6 rounded-xl border border-blue-500/20"
              >
                  <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">
                          <GitMerge size={20} />
                      </div>
                      <h3 className="text-lg font-bold text-blue-300">Redundancy Trap</h3>
                  </div>
                  <p className="text-sm text-gray-300 mb-4 leading-relaxed">
                      If you use <strong>RSI</strong>, <strong>Stochastic</strong>, and <strong>Williams %R</strong> together, you aren't getting 3 confirmations. You are analyzing "Momentum" three times with slightly different algebra.
                  </p>
                  <div className="text-xs text-blue-200 bg-blue-900/30 p-2 rounded">
                      <strong>Quant Rule:</strong> Choose ONE indicator per category (Trend, Momentum, Volatility).
                  </div>
              </motion.div>
  
               <motion.div 
                  whileHover={{ y: -5 }}
                  className="bg-orange-900/10 p-6 rounded-xl border border-orange-500/20"
              >
                  <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-orange-500/20 rounded-lg text-orange-400">
                          <History size={20} />
                      </div>
                      <h3 className="text-lg font-bold text-orange-300">The Lag Reality</h3>
                  </div>
                  <p className="text-sm text-gray-300 mb-4 leading-relaxed">
                      Math takes time. A 200 SMA is, by definition, 100 days old. It tells you the <span className="italic">average</span> history, not the future.
                  </p>
                   <div className="text-xs text-orange-200 bg-orange-900/30 p-2 rounded">
                      <strong>Quant Rule:</strong> Indicators describe the past. Only execution speed captures the present.
                  </div>
              </motion.div>
        </section>
  
          {/* Part 4: How Indicators Are Used to Build Predictive Models */}
          <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="border-t border-white/10 pt-12 mt-12"
          >
              <div className="mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-3">
                      <Brain className="text-cyan-400" size={32} />
                      How Indicators Are Used to Build Predictive Models
                  </h2>
                  <p className="text-xl text-gray-400">
                      From description to prediction: Understanding probabilistic trading
                  </p>
              </div>

              {/* The Core Concept */}
              <div className="bg-gradient-to-r from-cyan-900/20 to-purple-900/20 p-8 rounded-xl border border-cyan-500/20 mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">The Core Concept: From Description to Prediction</h3>
                  
                  <div className="mb-6">
                      <div className="bg-black/40 p-6 rounded-xl border border-white/5 mb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                              {/* Prophecy Side */}
                              <div className="relative group p-6 rounded-xl bg-gradient-to-br from-red-900/10 to-red-900/5 border border-red-500/20 text-center">
                                  <div className="absolute top-2 right-2">
                                      <XCircle className="text-red-500" size={20} />
                                  </div>
                                  <div className="w-16 h-16 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center ring-1 ring-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.2)]">
                                      <div className="text-3xl">🔮</div>
                                  </div>
                                  <h4 className="text-lg font-bold text-red-400 mb-2">Prophecy</h4>
                                  <div className="space-y-1 text-xs text-gray-400">
                                      <p>"Market WILL go up"</p>
                                      <p>Claims Certainty</p>
                                      <p>Magic / Guru</p>
                                      <p>Outcome: Disappointment</p>
                                  </div>
                              </div>

                              {/* Prediction Side */}
                              <div className="relative group p-6 rounded-xl bg-gradient-to-br from-cyan-900/10 to-cyan-900/5 border border-cyan-500/20 text-center">
                                  <div className="absolute top-2 right-2">
                                      <CheckCircle className="text-cyan-500" size={20} />
                                  </div>
                                  <div className="w-16 h-16 mx-auto mb-4 bg-cyan-500/10 rounded-full flex items-center justify-center ring-1 ring-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                                      <LineChart className="text-cyan-400" size={32} />
                                  </div>
                                  <h4 className="text-lg font-bold text-cyan-400 mb-2">Prediction</h4>
                                  <div className="space-y-1 text-xs text-gray-400">
                                      <p>"60% Chance of Up"</p>
                                      <p>Accepts Uncertainty</p>
                                      <p>Math / Statistics</p>
                                      <p>Outcome: Reliable Edge</p>
                                  </div>
                              </div>
                          </div>
                          
                          <div className="mt-4 flex justify-center">
                              <div className="bg-white/5 rounded-full px-4 py-1 flex items-center gap-2 border border-white/10">
                                  <Scale size={14} className="text-gray-400" />
                                  <span className="text-xs text-gray-300">Shift from deterministic to probabilistic thinking</span>
                              </div>
                          </div>
                      </div>
                  </div>

                  <div className="space-y-4 text-gray-300">
                      <p className="leading-relaxed">
                          If indicators only describe the past, how can we use them to predict the future? 
                          The answer lies in understanding what <span className="text-cyan-400 font-bold">"prediction"</span> actually means in trading contexts.
                      </p>

                      <div className="bg-black/30 p-6 rounded-xl border border-cyan-500/20">
                          <h4 className="font-bold text-cyan-400 mb-3">What Prediction Really Means</h4>
                          <p className="text-sm mb-4">
                              When we say an indicator or model is "predictive," we're not claiming it can see the future. Instead, we mean:
                          </p>
                          <div className="bg-cyan-900/20 p-4 rounded-lg mb-4">
                              <p className="text-cyan-200 italic">
                                  "It identifies conditions that have historically been followed by specific outcomes more often than random chance would suggest."
                              </p>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                              <div className="bg-red-900/20 p-4 rounded-lg border border-red-500/30">
                                  <div className="flex items-center gap-2 mb-2">
                                      <X className="text-red-400" size={20} />
                                      <strong className="text-red-400">Prophecy (Wrong)</strong>
                                  </div>
                                  <p className="text-sm text-gray-300">"The market will definitely go up tomorrow"</p>
                              </div>
                              <div className="bg-green-900/20 p-4 rounded-lg border border-green-500/30">
                                  <div className="flex items-center gap-2 mb-2">
                                      <CheckCircle className="text-green-400" size={20} />
                                      <strong className="text-green-400">Prediction (Correct)</strong>
                                  </div>
                                  <p className="text-sm text-gray-300">"When these conditions occur, market has historically gone up 60% of the time vs random 50%"</p>
                              </div>
                          </div>

                          <div className="mt-4 p-4 bg-yellow-900/20 border border-yellow-500/20 rounded">
                              <p className="text-sm text-yellow-200">
                                  <strong>The Edge:</strong> That 10% edge over randomness—even though you'll still be wrong 40% of the time—is enough to build profitable strategies with proper risk management.
                              </p>
                          </div>
                      </div>
                  </div>
              </div>

              {/* Part 1: How Indicators Become Predictive */}
              <div className="bg-gray-900/50 p-8 rounded-xl border border-white/10 mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Part 1: How Indicators Become Predictive Features</h3>
                  
                  <div className="mb-6">
                      <div className="bg-black/40 p-6 rounded-xl border border-white/5 mb-6 overflow-x-auto">
                          <div className="min-w-[600px] flex items-center justify-between gap-4 relative">
                              {/* Connecting Line */}
                              <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-blue-500/30 via-purple-500/30 to-green-500/30 -z-10"></div>
                              
                              {/* Step 1 */}
                              <div className="flex flex-col items-center gap-3 w-40 text-center group">
                                  <div className="w-12 h-12 rounded-xl bg-blue-900/40 border border-blue-500/30 flex items-center justify-center group-hover:scale-110 transition-transform bg-black z-10 shadow-[0_0_10px_rgba(59,130,246,0.2)]">
                                      <Activity className="text-blue-400" size={24} />
                                  </div>
                                  <div>
                                      <p className="text-blue-400 font-bold text-xs uppercase tracking-wider mb-1">Step 1</p>
                                      <p className="text-white font-bold text-sm">State</p>
                                      <p className="text-[10px] text-gray-400 mt-1">Mathematical Description</p>
                                  </div>
                              </div>

                              <ChevronRight className="text-gray-600 flex-shrink-0" size={20} />

                              {/* Step 2 */}
                              <div className="flex flex-col items-center gap-3 w-40 text-center group">
                                  <div className="w-12 h-12 rounded-xl bg-purple-900/40 border border-purple-500/30 flex items-center justify-center group-hover:scale-110 transition-transform bg-black z-10 shadow-[0_0_10px_rgba(168,85,247,0.2)]">
                                      <History className="text-purple-400" size={24} />
                                  </div>
                                  <div>
                                      <p className="text-purple-400 font-bold text-xs uppercase tracking-wider mb-1">Step 2</p>
                                      <p className="text-white font-bold text-sm">History</p>
                                      <p className="text-[10px] text-gray-400 mt-1">Pattern Analysis</p>
                                  </div>
                              </div>

                              <ChevronRight className="text-gray-600 flex-shrink-0" size={20} />

                              {/* Step 3 */}
                              <div className="flex flex-col items-center gap-3 w-40 text-center group">
                                  <div className="w-12 h-12 rounded-xl bg-cyan-900/40 border border-cyan-500/30 flex items-center justify-center group-hover:scale-110 transition-transform bg-black z-10 shadow-[0_0_10px_rgba(6,182,212,0.2)]">
                                      <Scale className="text-cyan-400" size={24} />
                                  </div>
                                  <div>
                                      <p className="text-cyan-400 font-bold text-xs uppercase tracking-wider mb-1">Step 3</p>
                                      <p className="text-white font-bold text-sm">Valid</p>
                                      <p className="text-[10px] text-gray-400 mt-1">Statistical Check</p>
                                  </div>
                              </div>

                              <ChevronRight className="text-gray-600 flex-shrink-0" size={20} />

                              {/* Step 4 */}
                              <div className="flex flex-col items-center gap-3 w-40 text-center group">
                                  <div className="w-12 h-12 rounded-xl bg-green-900/40 border border-green-500/30 flex items-center justify-center group-hover:scale-110 transition-transform bg-black z-10 shadow-[0_0_10px_rgba(34,197,94,0.2)]">
                                      <Target className="text-green-400" size={24} />
                                  </div>
                                  <div>
                                      <p className="text-green-400 font-bold text-xs uppercase tracking-wider mb-1">Step 4</p>
                                      <p className="text-white font-bold text-sm">Model</p>
                                      <p className="text-[10px] text-gray-400 mt-1">Predictive Rule</p>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>

                  <div className="space-y-6">
                      <div>
                          <h4 className="text-xl font-bold text-cyan-400 mb-4">The Pattern Recognition Framework</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500">
                                  <div className="flex items-center gap-2 mb-2">
                                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm">1</div>
                                      <h5 className="font-bold text-blue-300">Indicator as State Description</h5>
                                  </div>
                                  <p className="text-sm text-gray-400">An indicator describes the current "state" of the market mathematically. RSI = 25 means recent downs exceeded ups significantly.</p>
                              </div>

                              <div className="bg-purple-900/20 p-4 rounded-lg border-l-4 border-purple-500">
                                  <div className="flex items-center gap-2 mb-2">
                                      <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-sm">2</div>
                                      <h5 className="font-bold text-purple-300">Historical Pattern Analysis</h5>
                                  </div>
                                  <p className="text-sm text-gray-400">Examine what typically happened after the market entered this state. Did prices bounce? Continue falling? Move sideways?</p>
                              </div>

                              <div className="bg-cyan-900/20 p-4 rounded-lg border-l-4 border-cyan-500">
                                  <div className="flex items-center gap-2 mb-2">
                                      <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold text-sm">3</div>
                                      <h5 className="font-bold text-cyan-300">Statistical Validation</h5>
                                  </div>
                                  <p className="text-sm text-gray-400">Measure whether the pattern occurs more frequently than random chance and if the relationship is statistically significant.</p>
                              </div>

                              <div className="bg-green-900/20 p-4 rounded-lg border-l-4 border-green-500">
                                  <div className="flex items-center gap-2 mb-2">
                                      <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-sm">4</div>
                                      <h5 className="font-bold text-green-300">Model Creation</h5>
                                  </div>
                                  <p className="text-sm text-gray-400">If pattern is robust, create a rule: "When RSI &lt; 30, expect mean reversion with 58% probability over next 5 days."</p>
                              </div>
                          </div>
                      </div>

                      {/* Concrete Example */}
                      <div className="bg-black/40 p-6 rounded-xl border border-white/10">
                          <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                              <Terminal className="text-pink-400" size={20} />
                              Concrete Example: RSI as a Predictive Feature
                          </h4>
                          <div className="space-y-4 text-sm">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                      <p className="text-gray-400 mb-2"><strong className="text-white">The Math:</strong> RSI compares average gains to losses over 14 periods, scaled 0-100</p>
                                      <p className="text-gray-400 mb-2"><strong className="text-white">The Observation:</strong> When RSI drops below 30, prices often rise over next few days</p>
                                      <p className="text-gray-400"><strong className="text-white">The Question:</strong> Is this reliable enough to trade on?</p>
                                  </div>
                                  <div className="bg-cyan-900/20 p-4 rounded border border-cyan-500/30">
                                      <p className="text-cyan-200 font-bold mb-2">The Analysis:</p>
                                      <ul className="text-xs text-gray-300 space-y-1">
                                          <li>• Tested 10 years on S&P 500</li>
                                          <li>• Found 247 instances where RSI &lt; 30</li>
                                          <li>• In 156 cases (63%), price was higher 5 days later</li>
                                          <li>• Random expectation: ~50% up days</li>
                                          <li>• <span className="text-green-400 font-bold">13% edge above random!</span></li>
                                      </ul>
                                  </div>
                              </div>
                              <div className="bg-green-900/20 p-3 rounded border border-green-500/30">
                                  <p className="text-green-200"><strong>The Model:</strong> When RSI &lt; 30, buy and hold for 5 days. Expected win rate: 63%, edge: 13%</p>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>

              {/* Part 2: Single vs Multi-Indicator Models */}
              <div className="bg-gray-900/50 p-8 rounded-xl border border-white/10 mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Part 2: Single-Indicator vs. Multi-Indicator Models</h3>
                  
                  <div className="mb-6">
                      <div className="bg-black/40 p-6 rounded-xl border border-white/5 mb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                              {/* Single Indicator Gauge */}
                              <div className="flex flex-col items-center">
                                  <div className="relative w-48 h-24 overflow-hidden mb-2">
                                      {/* Gauge Background */}
                                      <div className="absolute top-0 left-0 w-48 h-48 rounded-full border-[12px] border-white/10 box-border"></div>
                                      {/* Gauge Fill (Partial) */}
                                      <div className="absolute top-0 left-0 w-48 h-48 rounded-full border-[12px] border-transparent border-t-yellow-500 border-r-yellow-500 box-border rotate-[225deg]" style={{ transform: 'rotate(-45deg)', clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)' }}></div>
                                      {/* Needle - pointing to ~58% */}
                                      <div className="absolute bottom-0 left-1/2 w-1 h-24 bg-red-400 origin-bottom transform -rotate-[20deg]" style={{ borderRadius: '4px' }}></div>
                                      <div className="absolute bottom-0 left-1/2 w-4 h-4 bg-white rounded-full transform -translate-x-1/2 translate-y-1/2"></div>
                                  </div>
                                  <div className="text-center">
                                      <h5 className="font-bold text-yellow-400 text-lg">58% Win Rate</h5>
                                      <p className="text-gray-400 text-xs">Single Indicator (RSI)</p>
                                      <p className="text-red-400 text-[10px] mt-1">Weak Edge</p>
                                  </div>
                              </div>

                              {/* Multi-Indicator Gauge */}
                              <div className="flex flex-col items-center">
                                  <div className="relative w-48 h-24 overflow-hidden mb-2">
                                      {/* Gauge Background */}
                                      <div className="absolute top-0 left-0 w-48 h-48 rounded-full border-[12px] border-white/10 box-border"></div>
                                      {/* Gauge Fill */}
                                      <div className="absolute top-0 left-0 w-48 h-48 rounded-full border-[12px] border-transparent border-t-green-500 border-r-green-500 box-border rotate-[225deg]" style={{ transform: 'rotate(45deg)', clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)' }}></div>
                                      {/* Needle - pointing to ~68% */}
                                      <div className="absolute bottom-0 left-1/2 w-1 h-24 bg-green-400 origin-bottom transform rotate-[25deg]" style={{ borderRadius: '4px' }}></div>
                                      <div className="absolute bottom-0 left-1/2 w-4 h-4 bg-white rounded-full transform -translate-x-1/2 translate-y-1/2"></div>
                                  </div>
                                  <div className="text-center">
                                      <h5 className="font-bold text-green-400 text-lg">68% Win Rate</h5>
                                      <p className="text-gray-400 text-xs">Multi-Indicator Model</p>
                                      <p className="text-green-400 text-[10px] mt-1 font-bold">Strong Edge</p>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Single-Indicator */}
                      <div className="bg-blue-900/10 p-6 rounded-xl border border-blue-500/20">
                          <h4 className="text-xl font-bold text-blue-400 mb-4">Single-Indicator Models</h4>
                          <p className="text-gray-300 text-sm mb-4">
                              The simplest predictive models—using one indicator to generate trading signals.
                          </p>
                          
                          <div className="bg-black/40 p-4 rounded mb-4">
                              <p className="text-blue-300 font-bold mb-2">Example: Moving Average Crossover</p>
                              <ul className="text-xs text-gray-400 space-y-1">
                                  <li>• <strong className="text-white">Rule:</strong> Buy when 50-day SMA crosses above 200-day SMA</li>
                                  <li>• <strong className="text-white">Logic:</strong> Short-term momentum overcame long-term trend</li>
                                  <li>• <strong className="text-white">Prediction:</strong> Historically followed by continued upward movement</li>
                              </ul>
                          </div>

                          <div className="bg-red-900/20 p-3 rounded border border-red-500/30">
                              <p className="text-xs text-red-200">
                                  <strong>Limitation:</strong> Single indicators rarely provide strong edge because they ignore too much information.
                              </p>
                          </div>
                      </div>

                      {/* Multi-Indicator */}
                      <div className="bg-purple-900/10 p-6 rounded-xl border border-purple-500/20">
                          <h4 className="text-xl font-bold text-purple-400 mb-4">Multi-Indicator Models</h4>
                          <p className="text-gray-300 text-sm mb-4">
                              More sophisticated models combining multiple indicators for richer market descriptions.
                          </p>
                          
                          <div className="bg-black/40 p-4 rounded mb-4">
                              <p className="text-purple-300 font-bold mb-2">Example: Combined Momentum & Volatility</p>
                              <ul className="text-xs text-gray-400 space-y-1">
                                  <li>• RSI &lt; 30 (oversold momentum)</li>
                                  <li>• BB width &gt; 2× average (high volatility)</li>
                                  <li>• Price touching lower Bollinger Band</li>
                                  <li className="text-purple-300 font-bold pt-2">→ Buy when all three conditions met</li>
                              </ul>
                          </div>

                          <div className="bg-green-900/20 p-3 rounded border border-green-500/30 space-y-1">
                              <p className="text-xs text-green-200">
                                  <strong>Results:</strong>
                              </p>
                              <p className="text-xs text-gray-300">RSI &lt; 30 alone: 58% win rate</p>
                              <p className="text-xs text-green-400 font-bold">All three together: 68% win rate ✓</p>
                          </div>
                      </div>
                  </div>

                  <div className="mt-6 bg-cyan-900/20 p-4 rounded-xl border border-cyan-500/30">
                      <p className="text-cyan-200 text-sm">
                          <strong>Why Combined Works Better:</strong> Each indicator captures different market aspects. Together, they describe a specific state: "Oversold, volatile, at extreme range" with more reliable predictive characteristics.
                      </p>
                  </div>
              </div>

              {/* Part 3: Machine Learning */}
              <div className="bg-gray-900/50 p-8 rounded-xl border border-white/10 mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Part 3: Machine Learning Approaches</h3>
                  
                  <div className="mb-6">
                      <div className="bg-black/40 p-6 rounded-xl border border-white/5 mb-6 overflow-x-auto">
                          <div className="min-w-[500px] flex items-center justify-center gap-12 relative py-4">
                              {/* Input Layer */}
                              <div className="flex flex-col gap-4">
                                  <div className="flex items-center gap-2">
                                      <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                                      <span className="text-xs text-cyan-300 font-mono">RSI</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                      <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                                      <span className="text-xs text-cyan-300 font-mono">MACD</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                      <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                                      <span className="text-xs text-cyan-300 font-mono">SMA</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                      <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                                      <span className="text-xs text-cyan-300 font-mono">Vol</span>
                                  </div>
                                  <p className="text-xs text-gray-400 mt-2 text-center">Inputs</p>
                              </div>

                              {/* Connections 1 */}
                              <div className="flex-1 h-32 relative">
                                  <svg className="absolute inset-0 w-full h-full text-white/10" style={{ overflow: 'visible' }}>
                                      <line x1="0" y1="10%" x2="100%" y2="20%" stroke="currentColor" strokeWidth="1" />
                                      <line x1="0" y1="10%" x2="100%" y2="50%" stroke="currentColor" strokeWidth="1" />
                                      <line x1="0" y1="35%" x2="100%" y2="20%" stroke="currentColor" strokeWidth="1" />
                                      <line x1="0" y1="35%" x2="100%" y2="80%" stroke="currentColor" strokeWidth="1" />
                                      <line x1="0" y1="60%" x2="100%" y2="50%" stroke="currentColor" strokeWidth="1" />
                                      <line x1="0" y1="60%" x2="100%" y2="80%" stroke="currentColor" strokeWidth="1" />
                                      <line x1="0" y1="85%" x2="100%" y2="20%" stroke="currentColor" strokeWidth="1" />
                                      <line x1="0" y1="85%" x2="100%" y2="80%" stroke="currentColor" strokeWidth="1" />
                                  </svg>
                              </div>

                              {/* Hidden Layer */}
                              <div className="flex flex-col gap-6">
                                  <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/50 flex items-center justify-center shadow-[0_0_10px_rgba(168,85,247,0.3)]">
                                      <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                                  </div>
                                  <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/50 flex items-center justify-center shadow-[0_0_10px_rgba(168,85,247,0.3)]">
                                      <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                                  </div>
                                  <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/50 flex items-center justify-center shadow-[0_0_10px_rgba(168,85,247,0.3)]">
                                      <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                                  </div>
                                  <p className="text-xs text-gray-400 mt-2 text-center absolute -bottom-8 w-24 -left-8">Hidden Logic</p>
                              </div>

                              {/* Connections 2 */}
                              <div className="flex-1 h-32 relative">
                                  <svg className="absolute inset-0 w-full h-full text-white/10" style={{ overflow: 'visible' }}>
                                      <line x1="0" y1="20%" x2="100%" y2="50%" stroke="currentColor" strokeWidth="1" />
                                      <line x1="0" y1="50%" x2="100%" y2="50%" stroke="currentColor" strokeWidth="1" />
                                      <line x1="0" y1="80%" x2="100%" y2="50%" stroke="currentColor" strokeWidth="1" />
                                  </svg>
                              </div>

                              {/* Output Layer */}
                              <div className="flex flex-col items-center gap-2">
                                  <div className="w-12 h-12 rounded-full bg-green-500/20 border border-green-500 flex items-center justify-center shadow-[0_0_15px_rgba(34,197,94,0.4)]">
                                      <Target className="text-green-400" size={24} />
                                  </div>
                                  <span className="text-xs text-green-300 font-bold uppercase tracking-wider">Prediction</span>
                                  <div className="bg-green-900/40 px-2 py-1 rounded text-[10px] text-green-200 border border-green-500/30">
                                      62% UP
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>

                  <p className="text-gray-300 mb-6">
                      Modern quantitative trading uses machine learning to discover relationships between indicators and future price movements that aren't obvious to human pattern recognition.
                  </p>

                  <div className="space-y-6">
                      {/* The Setup */}
                      <div className="bg-black/30 p-6 rounded-xl border border-white/10">
                          <h4 className="text-lg font-bold text-cyan-400 mb-4">How Machine Learning Uses Indicators</h4>
                          
                          <div className="space-y-4">
                              <div>
                                  <h5 className="font-bold text-white mb-2">Feature Engineering</h5>
                                  <p className="text-sm text-gray-400 mb-3">Each indicator becomes a "feature" in ML terms. You might create dozens or hundreds:</p>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                      {['RSI(14), RSI(7), RSI(21)', 'SMA(10), SMA(50), SMA(200)', 'MACD, Signal, Histogram', 'BB Position & Width', 'Volume Ratios', 'Price Momentum (Multi-TF)', 'Cross-Asset Correlations', 'Custom Indicators'].map((feature, idx) => (
                                          <div key={idx} className="bg-cyan-900/20 px-3 py-2 rounded text-xs text-cyan-200 border border-cyan-500/20">
                                              {feature}
                                          </div>
                                      ))}
                                  </div>
                              </div>

                              <div>
                                  <h5 className="font-bold text-white mb-2">The Training Process</h5>
                                  <p className="text-sm text-gray-400 mb-3">Feed historical data to ML algorithm with labels for what you want to predict:</p>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                      <div className="bg-purple-900/20 p-3 rounded border border-purple-500/20">
                                          <p className="text-xs text-purple-300 font-bold mb-1">Classification</p>
                                          <p className="text-xs text-gray-400">Will price be higher in 5 days? (Yes/No)</p>
                                      </div>
                                      <div className="bg-blue-900/20 p-3 rounded border border-blue-500/20">
                                          <p className="text-xs text-blue-300 font-bold mb-1">Regression</p>
                                          <p className="text-xs text-gray-400">How much will price change in % terms?</p>
                                      </div>
                                      <div className="bg-green-900/20 p-3 rounded border border-green-500/20">
                                          <p className="text-xs text-green-300 font-bold mb-1">Threshold</p>
                                          <p className="text-xs text-gray-400">Will price move more than 2%?</p>
                                      </div>
                                  </div>
                              </div>

                              <div>
                                  <h5 className="font-bold text-white mb-2">Pattern Discovery</h5>
                                  <div className="bg-gradient-to-r from-cyan-900/20 to-purple-900/20 p-4 rounded border border-cyan-500/20">
                                      <p className="text-sm text-gray-300 italic mb-2">
                                          The algorithm discovers which combinations of indicator values preceded outcomes you're predicting:
                                      </p>
                                      <div className="bg-black/40 p-3 rounded font-mono text-xs text-cyan-200">
                                          <p>"When RSI(14) is 35-45, MACD is positive, volume is 1.5× average, and S&P 500 is above its 200-day SMA, this stock tends to rise <span className="text-green-400 font-bold">65% of the time</span>."</p>
                                      </div>
                                      <p className="text-xs text-gray-400 mt-2">
                                          You might never have thought to test that specific combination manually!
                                      </p>
                                  </div>
                              </div>
                          </div>
                      </div>

                      {/* Example: Random Forest */}
                      <div className="bg-gradient-to-r from-green-900/10 to-blue-900/10 p-6 rounded-xl border border-green-500/20">
                          <h4 className="text-lg font-bold text-green-400 mb-4">Example: Random Forest for Direction Prediction</h4>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                  <p className="text-sm font-bold text-white mb-2">Input Features:</p>
                                  <ul className="text-xs text-gray-400 space-y-1">
                                      <li>• RSI(14)</li>
                                      <li>• MACD value</li>
                                      <li>• Distance from 50-day SMA (%)</li>
                                      <li>• Bollinger Band percentile</li>
                                      <li>• Volume relative to 20-day avg</li>
                                      <li>• 5-day & 20-day momentum</li>
                                      <li>• Sector ETF relative strength</li>
                                  </ul>
                              </div>
                              <div>
                                  <p className="text-sm font-bold text-white mb-2">Output:</p>
                                  <div className="bg-green-900/30 p-4 rounded border border-green-500/40">
                                      <p className="text-xs text-gray-300 mb-2">Target: Price direction 3 days forward (up/down)</p>
                                      <p className="text-green-300 font-bold text-lg">62% probability of upward movement</p>
                                  </div>
                              </div>
                          </div>

                          <div className="bg-black/40 p-3 rounded">
                              <p className="text-xs text-cyan-200">
                                  <strong className="text-white">Key Insight:</strong> The model isn't magical—it's still using math on past prices (indicators). But it finds complex, non-linear relationships that would be nearly impossible to discover manually.
                              </p>
                          </div>
                      </div>
                  </div>
              </div>

              {/* Part 4: Building Complete Model */}
              <div className="bg-gray-900/50 p-8 rounded-xl border border-white/10 mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Part 4: Building a Complete Predictive Model</h3>
                  
                  <div className="mb-6">
                      <div className="bg-black/40 p-6 rounded-xl border border-white/5 mb-6 overflow-x-auto">
                          {/* Pipeline Visual Header */}
                          <div className="min-w-[700px] flex items-center justify-between mx-auto md:w-3/4 mb-4 relative">
                              {/* Background Line */}
                              <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-800 -z-10 rounded"></div>
                              <div className="absolute top-1/2 left-0 w-3/4 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 -z-10 rounded"></div>
                              
                              <div className="flex flex-col items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xs ring-4 ring-black">1</div>
                                  <span className="text-[10px] text-blue-400 uppercase font-bold tracking-wider bg-black/50 px-2 py-0.5 rounded">Prepare</span>
                              </div>
                              <div className="flex flex-col items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-xs ring-4 ring-black">2</div>
                                  <span className="text-[10px] text-purple-400 uppercase font-bold tracking-wider bg-black/50 px-2 py-0.5 rounded">Train</span>
                              </div>
                              <div className="flex flex-col items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-xs ring-4 ring-black">3</div>
                                  <span className="text-[10px] text-green-400 uppercase font-bold tracking-wider bg-black/50 px-2 py-0.5 rounded">Validate</span>
                              </div>
                              <div className="flex flex-col items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-gray-300 font-bold text-xs ring-4 ring-black">4</div>
                                  <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider bg-black/50 px-2 py-0.5 rounded">Deploy</span>
                              </div>
                          </div>
                      </div>
                  </div>

                  <div className="space-y-6">
                      {/* Steps */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {[
                              { num: 1, title: 'Define What You\'re Predicting', desc: 'Time horizon? Outcome? Universe? Be specific: "Predict if SPY will be higher 5 days from now"', color: 'blue' },
                              { num: 2, title: 'Select and Calculate Indicators', desc: 'Choose indicators measuring different aspects: momentum (RSI, MACD), trend (SMA position), volatility (BB, ATR), volume', color: 'purple' },
                              { num: 3, title: 'Create the Dataset', desc: 'For each historical date, calculate all indicators and label what happened N days later', color: 'cyan' },
                              { num: 4, title: 'Split Data Properly', desc: 'Training (70%), Validation (15%), Test (15%). NEVER train and test on same data!', color: 'green' },
                              { num: 5, title: 'Train the Model', desc: 'Use training data to find relationships between indicators and outcomes', color: 'yellow' },
                              { num: 6, title: 'Validate and Refine', desc: 'Test on validation set, check accuracy, precision, profitability. Iterate if needed', color: 'orange' },
                              { num: 7, title: 'Final Test', desc: 'Test ONCE on hold-out test set for unbiased performance estimate', color: 'red' },
                              { num: 8, title: 'Walk-Forward Analysis', desc: 'Continually retrain on recent data and test on future unseen data', color: 'pink' }
                          ].map((step) => (
                              <motion.div
                                  key={step.num}
                                  whileHover={{ scale: 1.02 }}
                                  className={`bg-${step.color}-900/10 p-4 rounded-lg border border-${step.color}-500/20`}
                              >
                                  <div className="flex items-start gap-3">
                                      <div className={`w-10 h-10 rounded-full bg-${step.color}-500 flex items-center justify-center text-white font-bold shrink-0`}>
                                          {step.num}
                                      </div>
                                      <div>
                                          <h5 className={`font-bold text-${step.color}-400 mb-1`}>{step.title}</h5>
                                          <p className="text-xs text-gray-400">{step.desc}</p>
                                      </div>
                                  </div>
                              </motion.div>
                          ))}
                      </div>

                      {/* Data Splitting Visual */}
                      <div className="mb-4">
                          <div className="mb-4 bg-black/40 p-4 rounded-xl border border-white/5">
                          <p className="text-white font-bold text-sm mb-3 text-center">Correct Data Splitting Strategy</p>
                          
                          {/* The Split Bar */}
                          <div className="h-12 w-full flex rounded-lg overflow-hidden border border-white/10 mb-2 font-mono text-xs text-white font-bold shadow-lg">
                              <div className="w-[70%] bg-blue-600 flex items-center justify-center hover:bg-blue-500 transition-colors cursor-help" title="Use this to teach the model">Training (70%)</div>
                              <div className="w-[15%] bg-yellow-600 flex items-center justify-center hover:bg-yellow-500 transition-colors cursor-help" title="Use this to tune parameters">Val (15%)</div>
                              <div className="w-[15%] bg-green-600 flex items-center justify-center hover:bg-green-500 transition-colors bg-[length:10px_10px] bg-[linear-gradient(45deg,#0000_25%,#00000022_25%,#00000022_50%,#0000_50%,#0000_75%,#00000022_75%,#00000022)_repeat] cursor-help" title="NEVER LOOK AT THIS UNTIL THE END">Test (15%)</div>
                          </div>
                          
                          {/* Legends/Explanations */}
                          <div className="flex justify-between text-[10px] text-gray-400 px-1">
                              <div className="w-[70%] text-center border-l border-blue-500/30 pl-2">
                                  Build model rules here
                              </div>
                              <div className="w-[15%] text-center border-l border-yellow-500/30">
                                  Tune hyperparameters
                              </div>
                              <div className="w-[15%] text-center font-bold text-red-400 border-l border-red-500/30 flex items-center justify-center gap-1">
                                  <Lock size={10} />
                                  DO NOT TOUCH
                              </div>
                          </div>
                      </div>
                      </div>
                  </div>
              </div>

              {/* Part 5: Why Models Fail */}
              <div className="bg-red-900/10 p-8 rounded-xl border border-red-500/20 mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                      <AlertTriangle className="text-red-400" size={28} />
                      Part 5: The Reality Check—Why Models Fail
                  </h3>

                  <p className="text-gray-300 mb-6">
                      Understanding why predictive models fail is as important as understanding how they work.
                  </p>

                  <div className="space-y-6">
                      {/* Overfitting */}
                      <div className="bg-black/30 p-6 rounded-xl border border-red-500/20">
                          <h4 className="text-xl font-bold text-red-400 mb-4">1. Overfitting: The Biggest Danger</h4>
                          
                          <div className="mb-4">
                          <div className="mb-4 bg-black/40 p-6 rounded-xl border border-white/5">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                  {/* Overfitted Chart */}
                                  <div className="relative">
                                      <p className="text-center text-red-400 font-bold mb-2 text-xs">Overfitted Model (Bad)</p>
                                      <div className="h-32 w-full bg-red-900/10 border border-red-500/20 rounded relative overflow-hidden">
                                          {/* Random Data Points */}
                                          <div className="absolute top-[20%] left-[10%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          <div className="absolute top-[40%] left-[25%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          <div className="absolute top-[10%] left-[40%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          <div className="absolute top-[60%] left-[55%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          <div className="absolute top-[30%] left-[70%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          <div className="absolute top-[50%] left-[90%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          
                                          {/* Zig Zag Line (SVG) */}
                                          <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                                              <path d="M 0,100 L 10%,20% L 25%,40% L 40%,10% L 55%,60% L 70%,30% L 90%,50% L 100%,0" fill="none" stroke="#ef4444" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                                          </svg>
                                      </div>
                                      <p className="text-center text-[10px] text-gray-500 mt-1">Memorizes noise, fails on new data</p>
                                  </div>

                                  {/* Robust Chart */}
                                  <div className="relative">
                                      <p className="text-center text-green-400 font-bold mb-2 text-xs">Robust Model (Good)</p>
                                      <div className="h-32 w-full bg-green-900/10 border border-green-500/20 rounded relative overflow-hidden">
                                          {/* Same Data Points */}
                                          <div className="absolute top-[20%] left-[10%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          <div className="absolute top-[40%] left-[25%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          <div className="absolute top-[10%] left-[40%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          <div className="absolute top-[60%] left-[55%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          <div className="absolute top-[30%] left-[70%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          <div className="absolute top-[50%] left-[90%] w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                          
                                          {/* Smooth Line (SVG) */}
                                          <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                                              <path d="M 0,80 Q 50,20 100,40" fill="none" stroke="#22c55e" strokeWidth="3" vectorEffect="non-scaling-stroke" />
                                          </svg>
                                      </div>
                                      <p className="text-center text-[10px] text-gray-500 mt-1">Captures trend, ignores noise</p>
                                  </div>
                              </div>
                          </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                  <p className="text-sm text-gray-300 mb-2"><strong className="text-white">What it is:</strong> Model learns noise in training data rather than true patterns</p>
                                  <p className="text-sm text-gray-300"><strong className="text-white">Example:</strong> 95% accuracy on training, only 51% on new data</p>
                              </div>
                              <div className="bg-green-900/20 p-3 rounded border border-green-500/30">
                                  <p className="text-xs text-green-200 font-bold mb-2">How to Avoid:</p>
                                  <ul className="text-xs text-gray-300 space-y-1">
                                      <li>• Use simpler models when possible</li>
                                      <li>• Cross-validate thoroughly</li>
                                      <li>• Maintain separate test data</li>
                                      <li>• Look for logical explanations</li>
                                  </ul>
                              </div>
                          </div>
                      </div>

                      {/* Other Failure Modes */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-orange-900/20 p-4 rounded-xl border border-orange-500/20">
                              <h5 className="font-bold text-orange-400 mb-3 flex items-center gap-2">
                                  <TrendingUp size={18} />
                                  Non-Stationarity
                              </h5>
                              <p className="text-sm text-gray-300 mb-3">Market patterns change. What worked 2010-2020 may not work 2020-2030 due to:</p>
                              <ul className="text-xs text-gray-400 space-y-1">
                                  <li>• Market participants changing</li>
                                  <li>• Technology evolution (HFT, AI)</li>
                                  <li>• Regulations shifting</li>
                                  <li>• Strategies becoming crowded</li>
                              </ul>
                              <div className="mt-3 bg-orange-900/30 p-2 rounded text-xs text-orange-200">
                                  <strong>Solution:</strong> Monitor continuously, retrain regularly, shut down failed strategies
                              </div>
                          </div>

                          <div className="bg-yellow-900/20 p-4 rounded-xl border border-yellow-500/20">
                              <h5 className="font-bold text-yellow-400 mb-3 flex items-center gap-2">
                                  <Search size={18} />
                                  Data Snooping
                              </h5>
                              <p className="text-sm text-gray-300 mb-3">Testing so many combinations that you find something great by pure chance</p>
                              <div className="bg-yellow-900/30 p-3 rounded mb-3">
                                  <p className="text-xs text-yellow-200">
                                      If you test 100 combinations, expect ~5 to show significance at 95% confidence purely by random chance!
                                  </p>
                              </div>
                              <div className="bg-yellow-900/30 p-2 rounded text-xs text-yellow-200">
                                  <strong>Solution:</strong> Strict statistical standards, correct for multiple comparisons, demand logical sense
                              </div>
                          </div>

                          <div className="bg-red-900/20 p-4 rounded-xl border border-red-500/20 md:col-span-2">
                              <h5 className="font-bold text-red-400 mb-3 flex items-center gap-2">
                                  <DollarSign size={18} />
                                  Transaction Costs Kill Edge
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                      <p className="text-sm text-gray-300 mb-2">Model predicts 55% direction accuracy = 5% theoretical edge</p>
                                      <p className="text-sm text-gray-300 mb-2">But real costs:</p>
                                      <ul className="text-xs text-gray-400 space-y-1">
                                          <li>• Commissions: $0.01/share</li>
                                          <li>• Bid-ask spread: 0.05%</li>
                                          <li>• Slippage: 0.05%</li>
                                          <li>• Target profit: 0.5%</li>
                                      </ul>
                                  </div>
                                  <div className="flex items-center">
                                      <div className="bg-red-900/30 p-4 rounded border border-red-500/40 w-full">
                                          <p className="text-red-200 text-sm text-center">
                                              <strong>Result:</strong> Your 5% edge disappears into transaction costs!
                                          </p>
                                          <p className="text-xs text-gray-400 text-center mt-2">
                                              Always account for realistic costs in backtesting
                                          </p>
                                      </div>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>

              {/* Part 6: Advanced Concepts */}
              <div className="bg-gradient-to-r from-purple-900/20 to-cyan-900/20 p-8 rounded-xl border border-purple-500/20 mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">Part 6: Advanced Concepts</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Ensemble Methods */}
                      <div className="bg-black/30 p-6 rounded-xl border border-white/10">
                          <h4 className="text-lg font-bold text-purple-400 mb-4">Ensemble Methods</h4>
                          
                          <div className="mb-4">
                          <div className="mb-4 bg-gradient-to-r from-purple-900/20 to-blue-900/20 p-6 rounded-xl border border-white/5">
                              <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
                                  {/* Individual Models */}
                                  <div className="flex flex-row md:flex-col gap-2">
                                      <div className="bg-blue-900/40 p-2 rounded border border-blue-500/30 text-xs text-blue-200 text-center w-24">Model A (57%)</div>
                                      <div className="bg-purple-900/40 p-2 rounded border border-purple-500/30 text-xs text-purple-200 text-center w-24">Model B (55%)</div>
                                      <div className="bg-cyan-900/40 p-2 rounded border border-cyan-500/30 text-xs text-cyan-200 text-center w-24">Model C (59%)</div>
                                  </div>

                                  {/* Arrows */}
                                  <div className="flex flex-col gap-2 justify-center h-full">
                                      <ChevronRight className="text-gray-500 hidden md:block rotate-0" />
                                      <ChevronRight className="text-gray-500 hidden md:block rotate-0" />
                                      <ChevronRight className="text-gray-500 hidden md:block rotate-0" />
                                      {/* Mobile Down Arrow */}
                                      <div className="md:hidden flex justify-center w-full">
                                          <ChevronRight className="text-gray-500 rotate-90" />
                                      </div>
                                  </div>

                                  {/* Ensemble Box */}
                                  <div className="flex flex-col items-center gap-2">
                                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-900/20 border border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.3)] flex flex-col items-center justify-center p-2 text-center">
                                          <Users className="text-green-400 mb-1" size={20} />
                                          <span className="text-[10px] text-green-300 font-bold uppercase">Ensemble</span>
                                          <span className="text-lg font-bold text-white">62%</span>
                                      </div>
                                  </div>
                              </div>
                          </div>
                          </div>

                          <p className="text-sm text-gray-300 mb-3">Instead of one model, combine multiple models:</p>
                          <div className="space-y-2 text-xs">
                              <div className="bg-blue-900/20 p-2 rounded border border-blue-500/20">
                                  <strong className="text-blue-300">Model 1:</strong> <span className="text-gray-400">Random Forest (momentum) → 57%</span>
                              </div>
                              <div className="bg-purple-900/20 p-2 rounded border border-purple-500/20">
                                  <strong className="text-purple-300">Model 2:</strong> <span className="text-gray-400">Gradient Boost (volatility) → 55%</span>
                              </div>
                              <div className="bg-cyan-900/20 p-2 rounded border border-cyan-500/20">
                                  <strong className="text-cyan-300">Model 3:</strong> <span className="text-gray-400">Neural Net (all indicators) → 59%</span>
                              </div>
                              <div className="bg-green-900/30 p-3 rounded border border-green-500/40 text-center">
                                  <strong className="text-green-300">Ensemble:</strong> <span className="text-green-200 font-bold">Average/Voting → 62%</span>
                              </div>
                          </div>
                      </div>

                      {/* Regime Detection */}
                      <div className="bg-black/30 p-6 rounded-xl border border-white/10">
                          <h4 className="text-lg font-bold text-cyan-400 mb-4">Regime Detection</h4>
                          <p className="text-sm text-gray-300 mb-4">Build different models for different market conditions:</p>
                          
                          <div className="space-y-3">
                              <div className="bg-green-900/20 p-3 rounded border-l-4 border-green-500">
                                  <p className="text-xs font-bold text-green-300 mb-1">Bull Market Model</p>
                                  <p className="text-xs text-gray-400">Uses trend-following indicators</p>
                              </div>
                              <div className="bg-red-900/20 p-3 rounded border-l-4 border-red-500">
                                  <p className="text-xs font-bold text-red-300 mb-1">Bear Market Model</p>
                                  <p className="text-xs text-gray-400">Uses mean-reversion indicators</p>
                              </div>
                              <div className="bg-orange-900/20 p-3 rounded border-l-4 border-orange-500">
                                  <p className="text-xs font-bold text-orange-300 mb-1">High Volatility Model</p>
                                  <p className="text-xs text-gray-400">Focuses on volatility trades</p>
                              </div>
                              <div className="bg-blue-900/20 p-3 rounded border-l-4 border-blue-500">
                                  <p className="text-xs font-bold text-blue-300 mb-1">Low Volatility Model</p>
                                  <p className="text-xs text-gray-400">Exploits small inefficiencies</p>
                              </div>
                          </div>

                          <div className="mt-4 bg-cyan-900/30 p-3 rounded border border-cyan-500/30">
                              <p className="text-xs text-cyan-200">
                                  Use a "regime detection" model to determine environment, then apply appropriate sub-model
                              </p>
                          </div>
                      </div>
                  </div>
              </div>

              {/* Final Conclusion Section */}
              <div className="bg-gradient-to-r from-cyan-900/20 via-purple-900/20 to-pink-900/20 p-8 rounded-xl border border-cyan-500/20">
                  <h3 className="text-2xl font-bold text-white mb-6">How Math Becomes Prediction</h3>
                  
                  <div className="space-y-4 text-gray-300">
                      <p className="leading-relaxed">
                          So, how do indicators (which are just math on past data) build predictive models?
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {[
                              { num: 1, text: 'Indicators describe market states mathematically', icon: Database },
                              { num: 2, text: 'Historical analysis reveals patterns', icon: TrendingUp },
                              { num: 3, text: 'Models encode these patterns', icon: Brain },
                              { num: 4, text: 'Probability, not certainty', icon: Target },
                              { num: 5, text: 'Edge compounds over time', icon: Zap },
                              { num: 6, text: 'Continuous adaptation required', icon: History }
                          ].map((item) => (
                              <motion.div
                                  key={item.num}
                                  whileHover={{ scale: 1.05 }}
                                  className="bg-black/40 p-4 rounded-lg border border-white/10"
                              >
                                  <div className="flex items-start gap-3">
                                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                                          {item.num}
                                      </div>
                                      <div>
                                          <div className="mb-2">
                                              <item.icon className="text-cyan-400" size={18} />
                                          </div>
                                          <p className="text-sm text-gray-300">{item.text}</p>
                                      </div>
                                  </div>
                              </motion.div>
                          ))}
                      </div>

                      <div className="bg-gradient-to-r from-pink-900/30 to-purple-900/30 p-6 rounded-xl border border-pink-500/30 mt-6">
                          <p className="text-lg text-pink-200 font-bold mb-3">The Critical Insight</p>
                          <p className="text-sm text-gray-300 leading-relaxed">
                              Indicators don't predict the future through magic—they identify patterns in past behavior that may persist due to human psychology, market structure, or self-fulfilling effects. When applied with statistical rigor, realistic expectations, and proper risk management, these mathematical descriptions of market states can provide tradeable edges.
                          </p>
                          <div className="mt-4 bg-black/40 p-4 rounded">
                              <p className="text-sm text-cyan-200 italic">
                                  You're playing a probabilistic game. Your model won't be right every time—it doesn't need to be. It just needs to be right more often than random chance, by enough to cover costs, long enough to compound returns.
                              </p>
                          </div>
                      </div>
                  </div>
              </div>
          </motion.div>

          {/* Conclusion */}
          <div className="border-t border-white/10 pt-10 mt-12">
              <h2 className="text-2xl font-bold text-white mb-6">Conclusion</h2>
              
               <div className="bg-gradient-to-r from-gray-800 to-black p-4 md:p-8 rounded-2xl border border-white/10 flex flex-col md:flex-row items-center gap-6 md:gap-8 relative overflow-hidden group">
                  <div className="absolute inset-0 bg-pink-500/5 group-hover:bg-pink-500/10 transition-colors"></div>
                  
                  <div className="p-4 bg-white/5 rounded-full ring-1 ring-white/20 shrink-0">
                      <Brain className="text-pink-400" size={32} />
                  </div>
                  
                  <div className="space-y-4 relative z-10">
                       <div>
                           <h4 className="font-bold text-white text-base md:text-lg">Your Paradigm Shift</h4>
                           <p className="text-xs md:text-sm text-gray-400 mt-1">
                               When you stop treating indicators as magic, you become a Quant. You utilize them for what they are: <strong>Data Compression tools</strong>. They take thousands of confusing price points and compress them into a single, readable line. That is their only purpose.
                           </p>
                       </div>
                                              <div className="flex flex-wrap gap-2 md:gap-4 pt-2 items-center justify-center md:justify-start">
                            <div className="px-3 py-1 bg-black/40 rounded border border-white/10 text-xs text-gray-500 font-mono whitespace-nowrap">Input: Price</div>
                            <ArrowRight size={14} className="text-gray-600 hidden md:block" />
                            <div className="px-3 py-1 bg-black/40 rounded border border-white/10 text-xs text-gray-500 font-mono whitespace-nowrap">Process: Math</div>
                            <ArrowRight size={14} className="text-gray-600 hidden md:block" />
                            <div className="px-3 py-1 bg-pink-900/20 rounded border border-pink-500/30 text-xs text-pink-400 font-mono font-bold whitespace-nowrap">Output: Signal</div>
                        </div>
                  </div>
               </div>
          </div>
      </div>
    );
  };

// Lesson T.5: Entry Triggers vs. Filters
const LessonFiltersTriggers = () => (
    <div className="space-y-6 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-blue-400">
          <Filter size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson T.5</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">Entry Triggers vs. Filters</h1>
        <p className="text-xl text-gray-400">The architecture of a professional trading strategy.</p>
      </div>
  
      <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10 mb-6">
        <h2 className="text-xl font-bold text-white mb-4">The Concept</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Amateurs mix everything together. Professionals separate their logic into two distinct buckets: 
          <span className="text-blue-400 font-bold mx-1">Filters</span> and <span className="text-purple-400 font-bold mx-1">Triggers</span>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div className="bg-black/30 p-4 rounded-lg border-l-4 border-blue-500">
                <div className="flex items-center gap-2 mb-2">
                    <Shield size={18} className="text-blue-400" />
                    <h3 className="font-bold text-blue-400">The Filter (The Shield)</h3>
                </div>
                <p className="text-sm text-gray-400">Tells you <strong>IF</strong> you are allowed to trade. Filters never open trades; they only block them.</p>
             </div>
             <div className="bg-black/30 p-4 rounded-lg border-l-4 border-purple-500">
                <div className="flex items-center gap-2 mb-2">
                    <Target size={18} className="text-purple-400" />
                    <h3 className="font-bold text-purple-400">The Trigger (The Sword)</h3>
                </div>
                <p className="text-sm text-gray-400">Tells you <strong>WHEN</strong> to pull the trigger. Specific, momentary events.</p>
             </div>
        </div>
      </div>
  
      <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
        <h3 className="text-xl font-bold text-white mb-6">3. Putting it Together (The "Logic Stack")</h3>
        
        <div className="flex flex-col md:flex-row gap-8 items-start">
             {/* The Funnel Visualization */}
             <div className="w-full md:w-1/2 space-y-2">
                <h4 className="text-center text-xs text-gray-500 uppercase mb-2">The Execution Funnel</h4>
                
                {/* Level 1 */}
                <div className="bg-gray-700 p-3 rounded-t-lg mx-auto w-full text-center border-b border-gray-600">
                    <span className="text-gray-300 font-mono text-xs">Level 1: License & Connection</span>
                </div>
                {/* Level 2 */}
                <div className="bg-blue-900/40 p-3 mx-auto w-[90%] text-center border border-blue-500/30 relative">
                     <span className="text-blue-300 font-bold text-sm">Level 2: Filters (The Shield)</span>
                     <div className="text-[10px] text-blue-200 mt-1">Time, Spread, Trend Regime</div>
                     {/* Filter Gate Visual */}
                     <div className="absolute -right-3 top-1/2 -translate-y-1/2">
                        <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center border border-red-500/50">
                             <span className="text-red-500 text-[10px] font-bold">X</span>
                        </div>
                     </div>
                </div>
                {/* Level 3 */}
                <div className="bg-purple-900/40 p-3 mx-auto w-[80%] text-center border border-purple-500/30 relative">
                     <span className="text-purple-300 font-bold text-sm">Level 3: Trigger (The Signal)</span>
                     <div className="text-[10px] text-purple-200 mt-1">Crossover, RSI &lt; 30, Breakout</div>
                </div>
                {/* Action */}
                <div className="bg-green-600 p-3 rounded-b-lg mx-auto w-[60%] text-center shadow-[0_0_15px_rgba(34,197,94,0.3)]">
                     <span className="text-white font-bold text-sm">ACTION: Send Order</span>
                </div>
             </div>

             {/* Code Structure */}
             <div className="w-full md:w-1/2">
                <h4 className="font-bold text-white mb-4">The Golden Rule</h4>
                <p className="text-sm text-gray-400 mb-4">
                    A Trigger is useless without a Filter. A crossover in a sideways market will kill you. A crossover in a trending market (passed by the Filter) makes money.
                </p>
                
                <div className="bg-black/50 rounded-lg overflow-hidden border border-white/10">
                    <div className="bg-gray-900 px-4 py-2 border-b border-white/10 flex justify-between items-center">
                        <span className="text-xs font-mono text-gray-400">Logic Flow Structure</span>
                    </div>
                    <div className="p-4 font-mono text-xs md:text-sm text-gray-300 overflow-x-auto">
<pre>{`// 1. Check Filters (The Shield)
if (Spread > 2.0) return;
if (Is_Sideways_Market) return;
if (Time_Is_Outside_Session) return;

// 2. Check Trigger if we survived Filters
if (RSI < 30 && Price > 200_SMA) {
    // 3. Action
    OrderSend(OP_BUY...);
}`}</pre>
                    </div>
                </div>
                
                <div className="mt-4 flex gap-3 text-xs">
                     <div className="flex-1 bg-blue-900/20 p-2 rounded border border-blue-500/20">
                        <strong className="block text-blue-400 mb-1">Filters:</strong>
                        Define the "Where" and "Why".
                     </div>
                     <div className="flex-1 bg-purple-900/20 p-2 rounded border border-purple-500/20">
                        <strong className="block text-purple-400 mb-1">Triggers:</strong>
                        Define the "When".
                     </div>
                </div>
             </div>
        </div>
      </div>
  
      <div className="bg-green-900/10 p-6 rounded-xl border border-green-500/20">
        <h3 className="text-xl font-bold text-green-400 mb-4">Debug Tip</h3>
        <ul className="space-y-3">
          {[
            "If your bot isn't trading, check the Filters first (Is it blocking everything?).",
            "If your bot is losing money, check the Triggers (Are they timing it wrong?).",
            "Always separate the logic. Never mix 'Is it safe?' with 'Is it time?'"
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle size={18} className="text-green-500 mt-0.5 shrink-0" />
              <span className="text-gray-300">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

// Lesson T.6: Market Phases - The 30/70 Rule
const LessonMarketPhases = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-purple-400">
          <Activity size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson T.6</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">Market Phases: The 30/70 Rule</h1>
        <p className="text-xl text-gray-400">Understanding the "Physics" of the market before writing code.</p>
      </div>

      <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 p-6 rounded-xl border border-blue-500/20 mb-8">
        <h2 className="text-xl font-bold text-white mb-3"> The Reality Check</h2>
        <p className="text-gray-300">
            Most beginners think the market is always going "Up" or "Down."
            <br/><br/>
            <strong>The Reality:</strong> The market goes <span className="text-yellow-400">"Sideways" about 70% of the time</span> and <span className="text-green-400">"Trends" only 30% of the time</span>.
            <br/><br/>
            A strategy is like a vehicle. You cannot drive a <strong>Ferrari (Trend Bot)</strong> in a <strong>swamp (Ranging Market)</strong>.
        </p>
      </div>

      {/* 1. The Two States of Matter */}
      <h2 className="text-2xl font-bold text-white mb-6">1. The Two States of Matter</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {/* State A: Trend */}
        <div className="bg-gray-900/50 p-6 rounded-xl border border-green-500/20">
            <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="text-green-400" size={24} />
                <h3 className="text-xl font-bold text-green-400">State A: The Trend (30%)</h3>
            </div>
            <ul className="space-y-3 text-gray-300 text-sm">
                <li><strong>What it is:</strong> Higher Highs or Lower Lows. Clear momentum.</li>
                <li><strong>Psychology:</strong> One side (Buyers/Sellers) is in total control.</li>
                <li className="pt-2 border-t border-white/10"><strong>✅ Best Strategies:</strong></li>
                <ul className="list-disc pl-5 text-gray-400">
                    <li>SMC Order Blocks ("Smart Money" push)</li>
                    <li>Breakouts (Breaking Highs)</li>
                    <li>Moving Average Crossovers (Golden Cross)</li>
                </ul>
                <li className="pt-2 border-t border-white/10"><strong>❌ Worst Strategies:</strong></li>
                <ul className="list-disc pl-5 text-red-400">
                    <li>Martingale (Doubling down against trend = blown account)</li>
                    <li>Mean Reversion ("Overbought" means nothing here)</li>
                </ul>
            </ul>
        </div>

        {/* State B: Range */}
        <div className="bg-gray-900/50 p-6 rounded-xl border border-yellow-500/20">
            <div className="flex items-center gap-3 mb-4">
                <Activity className="text-yellow-400" size={24} />
                <h3 className="text-xl font-bold text-yellow-400">State B: The Range (70%)</h3>
            </div>
            <ul className="space-y-3 text-gray-300 text-sm">
                <li><strong>What it is:</strong> Price stuck between Floor and Ceiling. Choppy noise.</li>
                <li><strong>Psychology:</strong> Indecision. Neither side has control.</li>
                <li className="pt-2 border-t border-white/10"><strong>✅ Best Strategies:</strong></li>
                <ul className="list-disc pl-5 text-gray-400">
                    <li>Mean Reversion ("Buy Low, Sell High")</li>
                    <li>Grid Trading (Capturing the chop)</li>
                    <li>Martingale (Price returns to average)</li>
                </ul>
                <li className="pt-2 border-t border-white/10"><strong>❌ Worst Strategies:</strong></li>
                <ul className="list-disc pl-5 text-red-400">
                    <li>Trend Following (Breakout = Fakeout)</li>
                    <li>Golden Cross (Too late, price reverses)</li>
                </ul>
            </ul>
        </div>
      </div>

      {/* 2. The Holy Grail Myth */}
      <h2 className="text-2xl font-bold text-white mb-6">2. Why Algos Lose Money? (The Phase Shift)</h2>
      <div className="bg-gray-900 p-6 rounded-xl border border-white/10 mb-12">
        <p className="text-gray-300 mb-4">
            Losses usually happen during the <strong>Phase Shift</strong>.
        </p>
        <div className="space-y-4">
            <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center shrink-0">1</div>
                <div>
                    <h4 className="font-bold text-white">The Scenario</h4>
                    <p className="text-sm text-gray-400">Your "Range Bot" is making a fortune buying lows and selling highs.</p>
                </div>
            </div>
            <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center shrink-0">2</div>
                <div>
                    <h4 className="font-bold text-white">The Shift</h4>
                    <p className="text-sm text-gray-400">Suddenly, news breaks. The market enters a <span className="text-green-400">Trend Phase</span>.</p>
                </div>
            </div>
            <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center shrink-0">3</div>
                <div>
                    <h4 className="font-bold text-white">The Loss</h4>
                    <p className="text-sm text-gray-400">Your bot buys the "Low", but price crashes through the floor. It buys again, and price crashes lower.</p>
                </div>
            </div>
        </div>
        <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded text-yellow-200">
            <strong>The Lesson:</strong> Losses are the fee you pay when the market changes phases. No strategy works 100% of the time.
        </div>
      </div>

      {/* 3. The Code Logic */}
      <h2 className="text-2xl font-bold text-white mb-6">3. How We Code This (The "Phase Filter")</h2>
      <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
        <p className="text-gray-300 mb-6">
            We will use simple math to ask: "Are we trending?" usually via the <strong>ADX (Average Directional Index)</strong>.
        </p>

        <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
                <thead>
                    <tr className="border-b border-white/10 text-gray-400">
                        <th className="p-3">Market Condition</th>
                        <th className="p-3">ADX Value</th>
                        <th className="p-3">Strategy to Activate</th>
                        <th className="p-3">Dangerous Strategy</th>
                    </tr>
                </thead>
                <tbody className="text-sm text-gray-300">
                    <tr className="bg-green-900/10 border-b border-white/5">
                        <td className="p-3 font-bold text-green-400">Strong Trend</td>
                        <td className="p-3">&gt; 25</td>
                        <td className="p-3">ICT / Breakout</td>
                        <td className="p-3 text-red-400">Martingale / Grids</td>
                    </tr>
                    <tr className="bg-yellow-900/10">
                        <td className="p-3 font-bold text-yellow-400">Choppy / Range</td>
                        <td className="p-3">&lt; 20</td>
                        <td className="p-3">RSI / Bollinger</td>
                        <td className="p-3 text-red-400">Breakouts / MA Cross</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div className="mt-6 text-center">
            <p className="text-gray-400 italic mb-4">"Don't use a hammer to turn a screw."</p>
        </div>
      </div>
    </div>
);

// Lesson T.7: The Hybrid Model - Structure vs. Indicators
const LessonHybridModel = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-purple-400">
          <Layers size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson T.7</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">The Hybrid Model: Structure vs. Indicators</h1>
        <p className="text-xl text-gray-400">Bridging the gap between "Old School" manual trading and "New School" algorithms.</p>
      </div>

      <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 p-6 rounded-xl border border-purple-500/20 mb-8">
        <h2 className="text-xl font-bold text-white mb-3">The "Great Divide"</h2>
        <p className="text-gray-300">
            To build a bot that actually works, you cannot choose sides. You must combine them.
        </p>
      </div>

      {/* 1. The Great Divide Grid */}
      <h2 className="text-2xl font-bold text-white mb-6">1. The Two Schools of Thought</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        {/* Side A: Structure */}
        <div className="bg-gray-900/50 p-6 rounded-xl border border-blue-500/20 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Map size={100} />
            </div>
            <h3 className="text-xl font-bold text-blue-400 mb-2 flex items-center gap-2">
                <Map size={20} /> Use the Map
            </h3>
            <p className="text-xs text-blue-300 uppercase tracking-widest mb-4">Market Structure</p>
            
            <ul className="space-y-4 text-gray-300 text-sm">
                <li>
                    <strong className="block text-white mb-1">Who loves it:</strong>
                    Manual Traders, ICT/SMC, Price Action purists.
                </li>
                <li>
                    <strong className="block text-white mb-1">What it is:</strong>
                    Reading the raw "footprints" of price (Higher Highs, Order Blocks).
                </li>
                <li className="flex gap-2 text-green-400">
                    <CheckCircle size={16} /> <span>It is "Real-Time". Price is truth.</span>
                </li>
                <li className="flex gap-2 text-red-400">
                    <AlertTriangle size={16} /> <span>It is Subjective. Algos hate ambiguity.</span>
                </li>
            </ul>
        </div>

        {/* Side B: Indicators */}
        <div className="bg-gray-900/50 p-6 rounded-xl border border-purple-500/20 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Activity size={100} />
            </div>
            <h3 className="text-xl font-bold text-purple-400 mb-2 flex items-center gap-2">
                <Activity size={20} /> Read the Dash
            </h3>
            <p className="text-xs text-purple-300 uppercase tracking-widest mb-4">Indicators (Math)</p>
            
             <ul className="space-y-4 text-gray-300 text-sm">
                <li>
                    <strong className="block text-white mb-1">Who loves it:</strong>
                    Quants, Algo Developers, Beginners.
                </li>
                <li>
                    <strong className="block text-white mb-1">What it is:</strong>
                    Mathematical formulas (RSI, MACD, MA).
                </li>
                <li className="flex gap-2 text-green-400">
                    <CheckCircle size={16} /> <span>It is Objective. Algos love facts.</span>
                </li>
                <li className="flex gap-2 text-red-400">
                    <AlertTriangle size={16} /> <span>It is Lagging. The move might be over.</span>
                </li>
            </ul>
        </div>
      </div>

      {/* 1.5 The Detail in the Map (Candles & Trendlines) */}
      <h2 className="text-2xl font-bold text-white mb-6">2. The "Map" Details (Candles & Trendlines)</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        
        {/* Candlesticks (Micro-Structure) */}
        <div className="bg-gray-800/50 p-6 rounded-xl border border-white/10">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Search size={18} className="text-yellow-400"/> Micro-Structure (Candlesticks)
            </h3>
            <p className="text-sm text-gray-400 mb-4">
                Candles are the "words" of the market. They tell you immediate sentiment.
            </p>
            <div className="space-y-4">
                <div className="flex items-center gap-4 bg-black/30 p-3 rounded">
                    <div className="w-8 h-12 bg-green-500 rounded-sm relative mx-2">
                        <div className="absolute top-0 bottom-0 left-1/2 w-[1px] bg-green-400 -z-10 h-16 -mt-2"></div>
                    </div>
                    <div>
                        <strong className="text-white block text-sm">The "Engulfing" Bar</strong>
                        <span className="text-xs text-gray-400">Buying power totally overwhelmed selling power. A strong reversal signal.</span>
                    </div>
                </div>
                <div className="flex items-center gap-4 bg-black/30 p-3 rounded">
                     <div className="w-2 h-8 bg-green-500 rounded-sm relative mx-5 mt-4">
                        <div className="absolute bottom-full left-1/2 w-[1px] bg-green-400 h-8"></div>
                    </div>
                    <div>
                        <strong className="text-white block text-sm">The "Pinbar" (Hammer)</strong>
                        <span className="text-xs text-gray-400">Price tried to go down, but was rejected violently. Buyers stepped in.</span>
                    </div>
                </div>
            </div>
             <div className="mt-4 p-2 bg-yellow-900/20 border border-yellow-500/20 rounded">
                <p className="text-xs text-yellow-200">
                    <strong>Algo Tip:</strong> <code className="font-mono">Pinbar = LowerWick {'>'} Body * 2.0</code>
                </p>
            </div>
        </div>

        {/* Trendlines (Macro-Structure) */}
        <div className="bg-gray-800/50 p-6 rounded-xl border border-white/10">
             <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <TrendingUp size={18} className="text-blue-400"/> Macro-Structure (Trendlines)
            </h3>
            <p className="text-sm text-gray-400 mb-4">
                Trendlines define the highway lanes. Support is the floor, Resistance is the ceiling.
            </p>
            <div className="h-48 relative border border-gray-700 bg-black/30 rounded mb-4 overflow-hidden">
                {/* Trend Channel Visualization */}
                <svg className="absolute inset-0 w-full h-full p-4" viewBox="0 0 200 100">
                    {/* Uptrend Line (Support) */}
                    <line x1="10" y1="90" x2="190" y2="40" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4 4" />
                    {/* Price Action ZigZag */}
                    <polyline points="10,90 40,60 60,75 100,50 130,65 190,40" fill="none" stroke="#4ade80" strokeWidth="2" />
                    {/* Touches */}
                    <circle cx="10" cy="90" r="3" fill="#3b82f6" />
                    <circle cx="60" cy="75" r="3" fill="#3b82f6" />
                    <circle cx="130" cy="65" r="3" fill="#3b82f6" />
                </svg>
                <div className="absolute bottom-2 right-2 text-xs text-blue-400 font-mono">Dynamic Support Line</div>
            </div>
            <div className="p-2 bg-blue-900/20 border border-blue-500/20 rounded">
                <p className="text-xs text-blue-200">
                    <strong>Algo Tip:</strong> Trendlines are harder to code because they are diagonal. We often use <strong>Moving Averages</strong> as "dynamic" curved trendlines instead.
                </p>
            </div>
        </div>

      </div>

      {/* 3. Visualizing The Solution (Car Analogy) */}
      <h2 className="text-2xl font-bold text-white mb-6">3. The Solution: The Car Analogy</h2>
      <div className="bg-gray-800/50 p-8 rounded-xl border border-white/10 mb-12">
        <div className="flex flex-col md:flex-row items-center justify-center gap-12">
            
            {/* GPS (Structure) */}
            <div className="text-center w-48">
                <div className="w-24 h-24 bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-blue-500 relative">
                     <Map size={40} className="text-blue-400" />
                     <div className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-bold">The Filter</div>
                </div>
                <h4 className="font-bold text-white mb-2">The GPS</h4>
                <p className="text-xs text-gray-400">Tells you "We are going North." Defines the <strong className="text-blue-400">BIAS</strong>.</p>
            </div>

            <div className="hidden md:block text-gray-500 font-mono text-2xl">+</div>

            {/* Gas Pedal (Indicator) */}
             <div className="text-center w-48">
                <div className="w-24 h-24 bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-purple-500 relative">
                     <Zap size={40} className="text-purple-400" />
                     <div className="absolute -top-2 -right-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-bold">The Trigger</div>
                </div>
                <h4 className="font-bold text-white mb-2">The Gas Pedal</h4>
                <p className="text-xs text-gray-400">Tells you "Accelerate NOW." Defines the <strong className="text-purple-400">TIMING</strong>.</p>
            </div>
            
        </div>
        <div className="mt-8 text-center bg-black/30 p-4 rounded-lg">
             <p className="text-gray-300 italic">"If you press the Gas (Indicator) while the GPS says 'Cliff Ahead' (Structure), you crash."</p>
        </div>
      </div>

      {/* 4. Coding the Hybrid */}
      <h2 className="text-2xl font-bold text-white mb-6">4. Coding the Hybrid</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Python */}
        <div className="bg-gray-900 rounded-lg overflow-hidden border border-yellow-500/30">
            <div className="bg-gray-800 px-4 py-2 border-b border-white/10 flex justify-between items-center">
                <span className="text-yellow-400 font-mono text-sm">🐍 Python (The Analyst)</span>
            </div>
            <div className="p-4 overflow-x-auto">
                <pre className="text-xs font-mono text-gray-300">
{`# 1. Calculate Indicator
df['rsi'] = ta.rsi(df['close'], length=14)

# 2. Define Structure (The Map)
# Is price higher than 50 bars ago?
is_uptrend = current_price > old_price

# 3. Check Trigger (The Gas Pedal)
current_rsi = df.iloc[i]['rsi']

# HYBRID LOGIC
if is_uptrend and current_rsi < 30:
    print("✅ HYBRID BUY")`}
                </pre>
            </div>
        </div>

        {/* MQL5 */}
        <div className="bg-gray-900 rounded-lg overflow-hidden border border-blue-500/30">
            <div className="bg-gray-800 px-4 py-2 border-b border-white/10 flex justify-between items-center">
                <span className="text-blue-400 font-mono text-sm">🤖 MQL5 (The Robot)</span>
            </div>
            <div className="p-4 overflow-x-auto">
                <pre className="text-xs font-mono text-gray-300">
{`// 1. Structure Check (The Map)
double current = iClose(_Symbol, _Period, 1);
double old     = iClose(_Symbol, _Period, 50);
bool isUptrend = (current > old);

// 2. Hybrid Execution
if(isUptrend) // Only if Map agrees
{
   if(rsi[0] < 30) // Gas Pedal
   {
      // Buy Signal
      trade.Buy(0.1, ...);
   }
}`}
                </pre>
            </div>
        </div>
      </div>
      
       <div className="bg-green-900/10 p-6 rounded-xl border border-green-500/20 mt-8">
        <h3 className="text-xl font-bold text-green-400 mb-2">Why this matters for Freelancing</h3>
        <p className="text-gray-300">
            When a client asks for "An RSI bot that only works in a trend," they are asking for this <strong>Hybrid Model</strong>. 
            If you only code the RSI part, the bot fails. If you add the Structure check, you look like a genius.
        </p>
      </div>

    </div>
);

// Lesson T.8: The Exit Protocol - Entries vs Exits
const LessonExitProtocol = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-red-400">
          <Shield size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson T.8</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">The Exit Protocol: The Quant's Reality</h1>
        <p className="text-xl text-gray-400">Entries are for Ego. Exits are for Money.</p>
      </div>

      <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 p-6 rounded-xl border border-red-500/20 mb-8">
        <h2 className="text-xl font-bold text-white mb-3">The Quant's Rule: "Comfort Costs Money"</h2>
        <p className="text-gray-300">
            Every time you reduce risk (e.g., closing early, moving stops to 0), you usually pay a "Premium" in missing profits. 
            We do not seek "Safety". We seek <strong className="text-red-400">Maximum Expectancy</strong>.
        </p>
      </div>

      {/* 1. The 4 Types of Exits */}
      <h2 className="text-2xl font-bold text-white mb-6">1. The 4 Types of Exits</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        
        {/* The Shield (Stop Loss) */}
        <div className="bg-gray-900/50 p-6 rounded-xl border border-red-500/20">
            <div className="flex items-center gap-3 mb-4">
                <Shield className="text-red-400" size={24} />
                <h3 className="text-xl font-bold text-red-400">The Shield (Stop Loss)</h3>
            </div>
            <ul className="space-y-3 text-gray-300 text-sm">
                <li><strong className="text-white">Retail Way:</strong> "I always use a 20-pip stop." (Ignorant)</li>
                <li><strong className="text-white">Quant Way:</strong> ATR Stop (Average True Range).</li>
                <li className="p-2 bg-red-900/10 rounded border border-red-500/10 italic">
                    "If the market moves 50 pips a day, a 20-pip stop is just a donation."
                </li>
            </ul>
        </div>

        {/* The Sword (Take Profit) */}
        <div className="bg-gray-900/50 p-6 rounded-xl border border-green-500/20">
            <div className="flex items-center gap-3 mb-4">
                <Target className="text-green-400" size={24} />
                <h3 className="text-xl font-bold text-green-400">The Sword (Take Profit)</h3>
            </div>
            <ul className="space-y-3 text-gray-300 text-sm">
                <li><strong className="text-white">Retail Way:</strong> "I always aim for 1:3 RR." (greedy)</li>
                <li><strong className="text-white">Quant Way:</strong> Structure-based Target (Next Resistance).</li>
                <li className="p-2 bg-green-900/10 rounded border border-green-500/10 italic">
                    "Don't force a 1:3 ratio on a market only giving 1:1 moves."
                </li>
            </ul>
        </div>

         {/* The Clock (Time-Based) */}
        <div className="bg-gray-900/50 p-6 rounded-xl border border-blue-500/20">
            <div className="flex items-center gap-3 mb-4">
                <Clock className="text-blue-400" size={24} />
                <h3 className="text-xl font-bold text-blue-400">The Clock (Time Exit)</h3>
            </div>
            <ul className="space-y-3 text-gray-300 text-sm">
                <li><strong className="text-white">Retail Way:</strong> "Never hold over the weekend."</li>
                <li><strong className="text-white">Quant Way:</strong> Test it. Often, gaps follow the trend.</li>
                <li className="p-2 bg-blue-900/10 rounded border border-blue-500/10 italic">
                    "Closing on Friday might kill 20% of your annual profit."
                </li>
            </ul>
        </div>

         {/* The Chaser (Trailing Stop) */}
        <div className="bg-gray-900/50 p-6 rounded-xl border border-yellow-500/20">
            <div className="flex items-center gap-3 mb-4">
                <RefreshCw className="text-yellow-400" size={24} />
                <h3 className="text-xl font-bold text-yellow-400">The Chaser (Trailing)</h3>
            </div>
            <ul className="space-y-3 text-gray-300 text-sm">
                <li><strong className="text-white">Retail Way:</strong> "Lock in profit immediately!"</li>
                <li><strong className="text-white">Quant Way:</strong> Only trail if ADX {'>'} 25 (Trending).</li>
                <li className="p-2 bg-yellow-900/10 rounded border border-yellow-500/10 italic">
                    "In a range, trailing stops just get hit before the target."
                </li>
            </ul>
        </div>
      </div>

      {/* 2. The Breakeven Fallacy */}
      <h2 className="text-2xl font-bold text-white mb-6">2. The "Breakeven" Fallacy</h2>
      <div className="bg-gray-800 p-6 rounded-xl border border-white/10 mb-12">
        <p className="text-gray-300 mb-6">
            Moving your stop to Entry Price (Breakeven) feels "Safe", but it is mathematically expensive. 
            You are placing your exit exactly where price is most likely to re-test.
        </p>

        <div className="bg-black/50 rounded-lg p-4 font-mono text-sm border border-gray-700">
            <div className="text-green-400 mb-2"># Python Simulation: Holding vs. Moving to BE</div>
            <pre className="text-gray-400 whitespace-pre-wrap">
{`def run_simulation(move_to_breakeven=False):
    balance = 1000
    for _ in range(1000):
        # 'wiggle_win' = Price goes +10, drops to 0, then hits Target
        outcome = random.choice(['win', 'loss', 'wiggle_win']) 
        
        if outcome == 'wiggle_win':
            if move_to_breakeven:
                balance += 0   # Scared out at 0 (Scratch)
            else:
                balance += 200 # Held through noise (Win)
                
    return balance

# The Result
# Strategy 1 (Hold): $15,400 ✅
# Strategy 2 (Move to BE): $4,200 ❌
# Conclusion: You killed your winners to avoid fear.`}
            </pre>
        </div>
        <div className="mt-4 text-center">
            <span className="text-gray-400 text-sm">Comfort is expensive.</span>
        </div>
      </div>

    </div>
);

// Lesson T.9: The Money Math - Avoiding Gambler's Ruin
const LessonMoneyMath = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-green-400">
          <DollarSign size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson T.9</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">The Money Math: Avoiding Ruin</h1>
        <p className="text-xl text-gray-400">You can have a 90% Win Rate and still go broke. Here is how to stop that.</p>
      </div>

      <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 p-6 rounded-xl border border-green-500/20 mb-8">
        <h2 className="text-xl font-bold text-white mb-3">Gambler's Ruin</h2>
        <p className="text-gray-300">
            If you bet too big, a single streak of bad luck will wipe you out. This is a mathematical certainty called <strong className="text-red-400">Probability of Ruin</strong>.
        </p>
      </div>

      {/* 1. The Ruin Table */}
      <h2 className="text-2xl font-bold text-white mb-6">1. The "Casino" Truth</h2>
      <div className="bg-gray-900 rounded-xl overflow-hidden border border-white/10 mb-12">
        <div className="p-4 bg-gray-800 border-b border-white/10">
            <h3 className="font-bold text-white">How many losses until you die? (-50%)</h3>
        </div>
        <table className="w-full text-left border-collapse text-sm">
            <thead>
                <tr className="text-gray-400 border-b border-white/5">
                    <th className="p-4">Risk per Trade</th>
                    <th className="p-4">Losses to Ruin</th>
                    <th className="p-4">Survival Rating</th>
                </tr>
            </thead>
            <tbody className="text-gray-300">
                <tr className="border-b border-white/5 bg-red-900/20">
                    <td className="p-4 font-bold text-red-400">10%</td>
                    <td className="p-4">7 Trades</td>
                    <td className="p-4">💀 Suicide (Will happen eventually)</td>
                </tr>
                <tr className="border-b border-white/5 bg-yellow-900/20">
                    <td className="p-4 font-bold text-yellow-400">5%</td>
                    <td className="p-4">14 Trades</td>
                    <td className="p-4">⚠️ Dangerous (One bad week kills you)</td>
                </tr>
                <tr className="border-b border-white/5">
                    <td className="p-4 font-bold text-blue-400">2%</td>
                    <td className="p-4">35 Trades</td>
                    <td className="p-4">🛡️ Safe (Hard to do by accident)</td>
                </tr>
                <tr className="bg-green-900/20">
                    <td className="p-4 font-bold text-green-400">1%</td>
                    <td className="p-4">69 Trades</td>
                    <td className="p-4">🏰 Fortress (You can survive almost anything)</td>
                </tr>
            </tbody>
        </table>
         <div className="p-4 bg-black/20 text-center text-gray-400 text-xs italic">
            "The Quant's Rule: Never risk more than 2% on a setup."
        </div>
      </div>

      {/* 2. The Golden Formula */}
      <h2 className="text-2xl font-bold text-white mb-6">2. The Golden Formula (Auto-Sizing)</h2>
      <div className="bg-gray-800/50 p-6 rounded-xl border border-white/10 mb-12">
        <p className="text-gray-300 mb-6">
            Retail traders ask "How many lots?". Quants ask "Where is my Stop Loss?". 
            You cannot calculate Size until you know Distance.
        </p>

        <div className="flex flex-col md:flex-row gap-8 items-center justify-center mb-8">
            <div className="text-center p-4 bg-black/40 rounded border border-gray-600">
                <div className="text-2xl font-bold text-white mb-1">Account Balance</div>
                <div className="text-xs text-gray-500">($10,000)</div>
            </div>
            <div className="text-gray-500 font-bold">×</div>
             <div className="text-center p-4 bg-black/40 rounded border border-gray-600">
                <div className="text-2xl font-bold text-red-400 mb-1">Risk %</div>
                <div className="text-xs text-gray-500">(1% = $100)</div>
            </div>
             <div className="text-gray-500 font-bold">÷</div>
             <div className="text-center p-4 bg-black/40 rounded border border-gray-600">
                <div className="text-2xl font-bold text-blue-400 mb-1">Stop Distance</div>
                <div className="text-xs text-gray-500">(50 Pips)</div>
            </div>
             <div className="text-gray-500 font-bold">=</div>
             <div className="text-center p-4 bg-green-900/30 rounded border border-green-500">
                <div className="text-2xl font-bold text-green-400 mb-1">Lot Size</div>
                <div className="text-xs text-green-300">(0.20 Lots)</div>
            </div>
        </div>

        <div className="bg-black/50 rounded-lg p-4 font-mono text-sm border border-gray-700">
             <div className="text-blue-400 mb-2"># MQL5 Helper Function (Save this!)</div>
             <pre className="text-gray-400 whitespace-pre-wrap">
{`double GetLotSize(double riskPercent, double slPips)
{
   double balance = AccountInfoDouble(ACCOUNT_BALANCE);
   double riskMoney = balance * (riskPercent / 100); 
   
   double tickValue = SymbolInfoDouble(_Symbol, SYMBOL_TRADE_TICK_VALUE);
   
   // The Golden Formula
   double lotSize = riskMoney / (slPips * 10 * tickValue);
   
   // Normalize to strict step (e.g. 0.01)
   double step = SymbolInfoDouble(_Symbol, SYMBOL_VOLUME_STEP);
   return MathFloor(lotSize / step) * step;
}`}
             </pre>
        </div>
      </div>

      {/* 3. Martingale vs Anti-Martingale */}
      <h2 className="text-2xl font-bold text-white mb-6">3. Building Wealth vs. Destroying It</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Martingale */}
        <div className="bg-red-900/10 p-6 rounded-xl border border-red-500/20">
            <h3 className="text-xl font-bold text-red-500 mb-2">Martingale (The Trap)</h3>
            <p className="text-sm text-gray-400 mb-4">"I lost? I'll double my bet to win it back!"</p>
            <ul className="list-disc pl-5 space-y-2 text-gray-300 text-sm">
                <li>Works 99% of the time.</li>
                <li>The 1% time it fails, you lose <strong>everything</strong>.</li>
                <li>Requires infinite money (which you don't have).</li>
            </ul>
        </div>

        {/* Anti-Martingale */}
        <div className="bg-green-900/10 p-6 rounded-xl border border-green-500/20">
            <h3 className="text-xl font-bold text-green-500 mb-2">Anti-Martingale (The Edge)</h3>
            <p className="text-sm text-gray-400 mb-4">"I won? I'll increase size slightly. I lost? I cut size."</p>
             <ul className="list-disc pl-5 space-y-2 text-gray-300 text-sm">
                <li>Compounds winners geometrically.</li>
                <li>Protects you during drawdowns (you bet small when cold).</li>
                <li>This is how Quants turn small accounts into big ones.</li>
            </ul>
        </div>
      </div>

    </div>
);

// Lesson P.1: The Master Template - The Skeleton Bot
const LessonMasterTemplate = () => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2 text-blue-400">
          <Terminal size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Lesson P.1</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">The Master Template (The Skeleton)</h1>
        <p className="text-xl text-gray-400">The Rule: Never write the same code twice.</p>
      </div>

      <div className="bg-gradient-to-r from-gray-900 to-blue-900/20 p-6 rounded-xl border border-blue-500/20 mb-8">
        <h2 className="text-xl font-bold text-white mb-3">The "Factory" Mindset</h2>
        <p className="text-gray-300">
            Amateurs start every project by clicking "New File". Professionals start by clicking <strong>"Save As..."</strong> on a verified template.
            <br/><br/>
            This template is your Safety Harness. It includes the "Guard Rails" that keep your account safe even if your logic bugs out.
        </p>
      </div>

      {/* 1. The Code Block */}
      <h2 className="text-2xl font-bold text-white mb-6">1. The Asset: MasterTemplate.mq5</h2>
      <div className="bg-gray-900 rounded-xl overflow-hidden border border-white/10 mb-8">
        <div className="flex justify-between items-center px-4 py-2 bg-gray-800 border-b border-white/10">
            <span className="text-sm font-mono text-blue-400">MasterTemplate.mq5</span>
            <span className="text-xs text-gray-500">Copy this file</span>
        </div>
        <div className="p-4 overflow-x-auto">
            <pre className="text-xs font-mono text-gray-300">
{`//+------------------------------------------------------------------+
//|                                               MasterTemplate.mq5 |
//|                                  The Skeleton for Every Strategy |
//+------------------------------------------------------------------+
#property copyright "Your Algo Lab"
#property strict

// --- 1. THE LIBRARIES (The Tools) ---
#include <Trade\\Trade.mqh>      // Official Trade Library (Executes Orders)
#include <Trade\\SymbolInfo.mqh> // Official Symbol Library (Gets Prices)

// --- 2. THE OBJECTS ---
CTrade         trade;      // The Object that does the trading
CSymbolInfo    m_symbol;   // The Object that reads the pair

// --- 3. THE INPUTS (The Settings) ---
input group    "--- Risk Management ---"
input double   InpRiskPercent = 1.0;     // Risk per Trade (%)
input int      InpMaxSpread   = 50;      // Max Spread (Points) - Safety Guard
input ulong    InpMagicNum    = 123456;  // Magic Number (ID of this bot)

input group    "--- Strategy Settings ---"
input int      InpSL_Pips     = 30;      // Default Stop Loss (Pips)
input int      InpTP_Pips     = 60;      // Default Take Profit (Pips)

// Global Variables
datetime glbLastBarTime = 0; // For "New Bar" check

//+------------------------------------------------------------------+
//| Initialization (Runs ONCE when you turn it on)                   |
//+------------------------------------------------------------------+
int OnInit()
{
   // 1. Initialize the Symbol (CRITICAL)
   if(!m_symbol.Name(Symbol())) 
   {
      Print("Error initializing symbol!"); 
      return(INIT_FAILED);
   }
   
   // 2. Set the Magic Number (Track specific trades)
   trade.SetExpertMagicNumber(InpMagicNum);
   
   // 3. Refresh Data
   m_symbol.RefreshRates();
   
   Print("Bot Initialized. Waiting for ticks...");
   return(INIT_SUCCEEDED);
}

//+------------------------------------------------------------------+
//| The Heartbeat (Runs EVERY time price changes)                    |
//+------------------------------------------------------------------+
void OnTick()
{
   // --- A. SAFETY CHECKS (The Guard Rails) ---
   
   // 1. Refresh Price Data
   if(!m_symbol.RefreshRates()) return;

   // 2. Spread Check: Don't trade during news/high spread
   if(m_symbol.Spread() > InpMaxSpread) return; 

   // 3. New Bar Check (Optional: Run logic once per candle)
   bool isNewBar = CheckNewBar();
   
   
   // --- B. STRATEGY LOGIC (Where your brain goes) ---
   // This is the ONLY part you will change in future lessons.
   
   /* EXAMPLE:
      if(IsSignalBuy()) 
      {
         OpenBuy();
      }
   */
}

//+------------------------------------------------------------------+
//| HELPER FUNCTIONS (The Toolbox)                                   |
//+------------------------------------------------------------------+

// 1. Check for New Bar
bool CheckNewBar()
{
   datetime currentBarTime = iTime(_Symbol, _Period, 0);
   if(glbLastBarTime != currentBarTime)
   {
      glbLastBarTime = currentBarTime;
      return true;
   }
   return false;
}

// 2. Position Sizing Logic (The Golden Formula)
double GetLotSize(double slPips)
{
   double balance = AccountInfoDouble(ACCOUNT_BALANCE);
   double riskMoney = balance * (InpRiskPercent / 100);
   double tickValue = SymbolInfoDouble(_Symbol, SYMBOL_TRADE_TICK_VALUE);
   double lotSize = riskMoney / (slPips * 10 * tickValue);
   
   double step = SymbolInfoDouble(_Symbol, SYMBOL_VOLUME_STEP);
   return MathFloor(lotSize / step) * step;
}

// 3. Open Buy Order (Standardized)
void OpenBuy()
{
   if(PositionsTotal() > 0) return; // Simple "One trade at a time" filter
   
   double sl = m_symbol.Ask() - InpSL_Pips * _Point;
   double tp = m_symbol.Ask() + InpTP_Pips * _Point;
   double lots = GetLotSize(InpSL_Pips);
   
   trade.Buy(lots, _Symbol, m_symbol.Ask(), sl, tp, "Master Template Buy");
}

// 4. Open Sell Order (Standardized)
void OpenSell()
{
   if(PositionsTotal() > 0) return;
   
   double sl = m_symbol.Bid() + InpSL_Pips * _Point;
   double tp = m_symbol.Bid() - InpTP_Pips * _Point;
   double lots = GetLotSize(InpSL_Pips);
   
   trade.Sell(lots, _Symbol, m_symbol.Bid(), sl, tp, "Master Template Sell");
}`}
            </pre>
        </div>
      </div>

       <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
            <h3 className="text-lg font-bold text-white mb-2">The Magic Number</h3>
            <p className="text-gray-400 text-sm">
                This ID Card prevents your "Trend Bot" from accidentally closing the trades of your "Range Bot". 
                <strong>Always change this number</strong> when you save a new strategy.
            </p>
        </div>
        <div className="bg-gray-800/50 p-6 rounded-xl border border-white/5">
            <h3 className="text-lg font-bold text-white mb-2">The Guard Rails</h3>
            <p className="text-gray-400 text-sm">
                <code>if(Spread {`>`} Max) return;</code>
                <br/>
                When news hits and spreads widen to 100 points, this line puts your bot to sleep. It saves you from immediate losses.
            </p>
        </div>
      </div>
      
      <div className="bg-blue-900/20 p-6 rounded-xl border border-blue-500/20 text-center">
        <h3 className="text-xl font-bold text-blue-400 mb-2">Homework</h3>
        <p className="text-gray-300">
            Open MetaEditor. Create a new EA named <strong>MasterTemplate</strong>. Paste this code. Compile (F7).
            <br/>
            Ensure there are <strong>0 Errors</strong>. Do not lose this file.
        </p>
      </div>

    </div>
);

// Lesson 1.14: The Graduation (Next Steps)
const LessonQuantConstitution = ({ checklist, updateChecklist }) => {
  const isSigned = checklist && checklist['constitution_signed'];

  return (
  <div className="space-y-8">
    <div className="bg-gradient-to-br from-red-900/20 to-orange-900/20 p-8 rounded-2xl border border-red-500/20">
      <h3 className="text-2xl font-bold mb-4 text-red-400">Risk Management is not just a Stop Loss.</h3>
      <p className="text-lg text-gray-300 leading-relaxed mb-6">
        "Risk Management is knowing what you are doing."<br/>
        In this module, we strip away the retail myths and establish the Laws of Survival. As a Quant, you are a Scientist, not a Guru. We do not "feel"; we Test.
      </p>
    </div>

    {/* Section 1: Definition of Risk */}
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-white">1. The Definition of Risk</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white/5 p-6 rounded-xl border border-white/10">
          <h4 className="font-bold text-gray-400 mb-2">Retail Definition</h4>
          <p className="text-gray-300">Risk is losing money on a trade.</p>
        </div>
        <div className="bg-red-900/10 p-6 rounded-xl border border-red-500/30">
          <h4 className="font-bold text-red-400 mb-2">Quant Definition</h4>
          <p className="text-white font-bold mb-2">Risk is Ignorance.</p>
          <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside">
            <li>Trading the wrong timeframe is Risk.</li>
            <li>Trading a strategy in the wrong market phase is Risk.</li>
            <li>Relying on an "Assumption" that hasn't been backtested is Risk.</li>
          </ul>
        </div>
      </div>
      <div className="p-4 bg-yellow-900/10 border border-yellow-500/20 rounded-xl">
         <p className="text-yellow-500"><span className="font-bold">The Golden Rule:</span> Anything that preserves your capital—from code syntax to market theory—is Risk Management.</p>
      </div>
    </div>

    {/* Section 2: The Assumption Trap */}
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-white">2. The "Assumption" Trap (Logic vs. Statistics)</h3>
      <p className="text-gray-400">Logic often lies. Statistics tell the truth.</p>
      
      <div className="bg-black/40 p-6 rounded-xl border border-white/10 space-y-4">
        <div>
            <h5 className="font-bold text-blue-400">The Assumption</h5>
            <p className="text-sm text-gray-300">"A 1:6 Risk:Reward ratio is amazing. I only need to be right 20% of the time!" (Logically correct).</p>
        </div>
        <div>
            <h5 className="font-bold text-red-400">The Reality</h5>
            <p className="text-sm text-gray-300">In a Mean Reversion market, price rarely moves 6x your risk. You might lose 20 times in a row, hit a 50% drawdown, and blow up before you hit that 1 winner.</p>
        </div>
      </div>
      
       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-green-900/10 rounded-xl border border-green-500/20">
            <h5 className="font-bold text-green-400 mb-1">Trend Strategies</h5>
            <p className="text-xs text-gray-400">High R:R (1:3+) works because trends run deep.</p>
        </div>
         <div className="p-4 bg-purple-900/10 rounded-xl border border-purple-500/20">
            <h5 className="font-bold text-purple-400 mb-1">Mean Reversion</h5>
            <p className="text-xs text-gray-400">Low R:R (1:1 or even inverse) works because high win-rates compensate for small wins.</p>
        </div>
      </div>
      <p className="text-sm text-red-400 italic">Trying to use High R:R in Mean Reversion is mathematical suicide.</p>
    </div>

    {/* Section 3: Kill Switch */}
    <div className="space-y-4">
       <h3 className="text-xl font-bold text-white">3. The "Kill Switch" vs. Stop Loss</h3>
       <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1 space-y-4">
              <p className="text-gray-300">A <span className="text-blue-400">Stop Loss</span> protects a Trade. A <span className="text-red-400">Kill Switch</span> protects the Account.</p>
              <div className="bg-white/5 p-4 rounded-lg">
                  <h5 className="font-bold text-white text-sm">Scenario</h5>
                  <p className="text-xs text-gray-400">You have a bug in your code. Or the market enters a "Black Swan" event (e.g., War breaks out).</p>
                  <p className="text-xs text-gray-400 mt-2">The Failure: Your bot keeps placing trades and hitting stop losses, draining your account by 20% in an hour.</p>
              </div>
          </div>
          <div className="flex-1 bg-red-900/20 p-6 rounded-xl border border-red-500/30 flex flex-col justify-center items-center text-center">
              <AlertTriangle size={32} className="text-red-500 mb-2" />
              <h4 className="font-bold text-white mb-2">The Solution: Kill Switch</h4>
              <code className="bg-black/50 p-2 rounded text-red-300 text-xs block w-full">
                If Daily Equity drops by 3%<br/>CLOSE ALL TRADES and SHUT DOWN TERMINAL.
              </code>
              <p className="text-xs text-gray-400 mt-2">Saves you from "Unknown Unknowns."</p>
          </div>
       </div>
    </div>

    {/* Section 4: Backtest Barrier */}
    <div className="space-y-4">
        <h3 className="text-xl font-bold text-white">4. The Backtest Barrier (The Litmus Test)</h3>
        <p className="text-gray-400">In the era of AI and Quants, "Hope" is not a strategy.</p>
        
        <div className="space-y-2">
            {[
                { rule: "Rule #1", text: "Never deploy a strategy that fails a backtest. (Obvious, but ignored).", color: "red" },
                { rule: "Rule #2", text: "Never deploy a strategy that passes a backtest if the data is garbage. (Garbage In, Garbage Out).", color: "yellow" },
                { rule: "Rule #3", text: "Never deviate from the Backtest.", color: "green" }
            ].map((item, i) => (
                <div key={i} className={`flex items-center gap-4 p-3 rounded-lg border border-${item.color}-500/20 bg-${item.color}-900/10`}>
                    <span className={`font-bold text-${item.color}-400`}>{item.rule}</span>
                    <span className="text-sm text-gray-300">{item.text}</span>
                </div>
            ))}
        </div>
        <div className="p-4 bg-white/5 rounded-xl border border-white/10">
            <p className="text-sm text-gray-400">
                If your backtest says "Max Drawdown is 15%," and you are currently down 10%, do not intervene. You are within the statistical variance.
                <br/><span className="text-red-400">If you intervene, you invalidate the probabilities you built the system on.</span>
            </p>
        </div>
    </div>

    {/* Section 6: Mathematics of Survival */}
    <div className="space-y-6">
        <h3 className="text-xl font-bold text-white">5. The Mathematics of Survival</h3>
        <p className="text-gray-400">Three mathematical concepts that will determine if your account survives your learning curve.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-900/10 p-4 rounded-xl border border-blue-500/20">
                <h5 className="text-blue-400 font-bold mb-2">1. Position Sizing</h5>
                <p className="text-sm text-gray-300 mb-2">The risk of betting too big.</p>
                <p className="text-xs text-gray-500">If you risk 10% per trade, a 5-loss streak leaves you with 59% of your capital. If you risk 1%, you still have 95%. Speed kills.</p>
            </div>
             <div className="bg-purple-900/10 p-4 rounded-xl border border-purple-500/20">
                <h5 className="text-purple-400 font-bold mb-2">2. Correlation</h5>
                <p className="text-sm text-gray-300 mb-2">The risk of betting on the same thing twice.</p>
                <p className="text-xs text-gray-500">Long EURUSD and Short USDCHF is the SAME trade. If the dollar moves, you lose both.</p>
            </div>
             <div className="bg-orange-900/10 p-4 rounded-xl border border-orange-500/20">
                <h5 className="text-orange-400 font-bold mb-2">3. Monte Carlo</h5>
                <p className="text-sm text-gray-300 mb-2">The risk of bad luck.</p>
                <p className="text-xs text-gray-500">Even a 50% win rate strategy has a 3% chance of losing 10 trades in a row. Are you capitalized for that?</p>
            </div>
        </div>
    </div>

    {/* Section 5: Scientist's Oath */}
    <div className="border border-blue-500/30 bg-blue-900/10 p-8 rounded-2xl text-center">
        <h3 className="text-2xl font-bold text-blue-400 mb-6 flex items-center justify-center gap-2">
            <Shield /> The Scientist's Oath
        </h3>
        <div className="space-y-4 bg-black/30 p-6 rounded-xl border border-blue-500/10 max-w-lg mx-auto">
            <p className="text-gray-200 italic">"I will not Guess. I will Measure."</p>
            <p className="text-gray-200 italic">"I will not trade Assumptions. I will trade Evidence."</p>
            <p className="text-gray-200 italic">"I will respect Market Phases. I will not force a Trend tool into a Range market."</p>
            <p className="text-gray-200 italic">"I will trust my Data. If the backtest says it works, I trust the math. If the backtest says it fails, I kill the idea."</p>
            <p className="text-gray-200 italic">"I won't deposit less than the amount needed for my drawdown."</p>
        </div>
        <div className="mt-6">
             <button 
                onClick={() => updateChecklist('constitution_signed', true)}
                disabled={isSigned}
                className={`px-8 py-3 font-bold rounded-lg transition-all flex items-center justify-center gap-2 mx-auto ${
                    isSigned 
                    ? 'bg-green-600 text-white opacity-100 cursor-default shadow-[0_0_15px_rgba(22,163,74,0.5)]' 
                    : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                }`}
             >
                {isSigned ? (
                    <>
                        <CheckCircle size={20} /> Signed & Accepted
                    </>
                ) : (
                    "I Agree"
                )}
             </button>
             {isSigned && (
                 <p className="mt-4 text-xs text-green-400 animate-pulse">
                     Constitution ratified. Risk protocols active.
                 </p>
             )}
        </div>
    </div>
  </div>
  );
};

// Lesson T.11: The Quant's Scorecard - Understanding Expectancy
const LessonQuantScorecard = () => {
    const [winRate, setWinRate] = useState(40);
    const [avgWin, setAvgWin] = useState(500);
    const [avgLoss, setAvgLoss] = useState(100);

    const winPercent = winRate / 100;
    const lossPercent = (100 - winRate) / 100;
    const expectancy = (winPercent * avgWin) - (lossPercent * avgLoss);
    const isProfitable = expectancy > 0;

    return (
        <div className="space-y-8">
            <div className="bg-gradient-to-br from-green-900/20 to-blue-900/20 p-8 rounded-2xl border border-green-500/20">
                <h3 className="text-2xl font-bold mb-4 text-green-400">Win Rate is vanity. Expectancy is sanity.</h3>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    In this final theory module, we kill the biggest myth in trading: The obsession with being "Right."<br/>
                    You can be right 90% of the time and go broke. You can be right 30% of the time and be a millionaire.
                </p>
            </div>

            {/* Section 1: The Magic Formula */}
            <div className="space-y-4">
                <h3 className="text-xl font-bold text-white">1. The Magic Formula: Expectancy</h3>
                <p className="text-gray-400">This single number tells you if your bot is an Asset or a Liability.</p>
                <div className="bg-black/40 p-6 rounded-xl border border-white/5 text-center">
                     <code className="text-2xl text-blue-400 font-mono">E = (Win% × Avg Win) - (Loss% × Avg Loss)</code>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white/5 p-6 rounded-xl border border-white/10 opacity-70">
                        <h5 className="font-bold text-gray-300 mb-2">Example A: The "Guru" Strategy</h5>
                        <ul className="text-sm text-gray-400 space-y-1">
                            <li>Win Rate: 90%</li>
                            <li>Avg Win: $10 (Scalping)</li>
                            <li>Avg Loss: $200 (No Stop Loss)</li>
                        </ul>
                        <div className="mt-4 pt-4 border-t border-white/10">
                            <p className="font-mono text-red-400">Result: -$11.00 per trade</p>
                            <p className="text-xs text-gray-500 mt-1">Bankruptcy.</p>
                        </div>
                    </div>
                    <div className="bg-green-900/10 p-6 rounded-xl border border-green-500/20">
                        <h5 className="font-bold text-white mb-2">Example B: The "Trend" Strategy</h5>
                        <ul className="text-sm text-gray-300 space-y-1">
                            <li>Win Rate: 40% (You lose more often)</li>
                            <li>Avg Win: $500 (Catching trends)</li>
                            <li>Avg Loss: $100 (Tight stops)</li>
                        </ul>
                        <div className="mt-4 pt-4 border-t border-white/10">
                            <p className="font-mono text-green-400">Result: +$140.00 per trade</p>
                            <p className="text-xs text-gray-400 mt-1">Profitability.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Interactive Calculator */}
            <div className="bg-black/30 p-6 rounded-xl border border-blue-500/30">
                <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Activity size={20} className="text-blue-400"/> Expectancy Calculator
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Win Rate (%)</label>
                        <input 
                            type="number" 
                            value={winRate} 
                            onChange={(e) => setWinRate(Number(e.target.value))}
                            className="bg-black/50 border border-white/10 rounded p-2 text-white w-full"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Avg Win ($)</label>
                        <input 
                            type="number" 
                            value={avgWin} 
                            onChange={(e) => setAvgWin(Number(e.target.value))}
                            className="bg-black/50 border border-white/10 rounded p-2 text-white w-full"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Avg Loss ($)</label>
                        <input 
                            type="number" 
                            value={avgLoss} 
                            onChange={(e) => setAvgLoss(Number(e.target.value))}
                            className="bg-black/50 border border-white/10 rounded p-2 text-white w-full"
                        />
                    </div>
                </div>
                <div className={`p-4 rounded-lg border flex items-center justify-between ${isProfitable ? 'bg-green-900/20 border-green-500/30' : 'bg-red-900/20 border-red-500/30'}`}>
                    <div>
                        <span className="text-sm text-gray-400 block">Expectancy per Trade</span>
                    </div>
                    <div className={`text-3xl font-bold font-mono ${isProfitable ? 'text-green-400' : 'text-red-400'}`}>
                        ${expectancy.toFixed(2)}
                    </div>
                </div>
            </div>

            {/* Section 2: Sharpe Ratio */}
            <div className="space-y-4">
                 <h3 className="text-xl font-bold text-white">2. The Sharpe Ratio (Simplified)</h3>
                 <div className="bg-white/5 p-6 rounded-xl">
                    <p className="text-gray-300 mb-4">The Question: <span className="italic">"Is this profit worth the stress?"</span></p>
                    <div className="flex gap-4 items-center">
                        <div className="bg-blue-500/20 p-3 rounded-full">
                            <TrendingUp className="text-blue-400" />
                        </div>
                        <div className="text-sm text-gray-400">
                            We don't just want money; we want <strong className="text-white">smooth money</strong>. A jagged equity curve suggests your strategy is lucky, not robust.
                        </div>
                    </div>
                 </div>
            </div>

             {/* Section 3: Summary */}
             <div className="border border-white/10 bg-white/5 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-white mb-4">3. Summary: The 3 Metrics That Matter</h3>
                <p className="text-gray-400 mb-6">When you build your bots in the next section, you will ignore "Net Profit." You will look at:</p>
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 bg-black/20 rounded-lg">
                        <h5 className="font-bold text-green-400 mb-1">Expectancy</h5>
                        <p className="text-xs text-gray-500">Is the math positive?</p>
                    </div>
                    <div className="p-4 bg-black/20 rounded-lg">
                        <h5 className="font-bold text-red-400 mb-1">Drawdown</h5>
                        <p className="text-xs text-gray-500">Can I stomach the worst-case?</p>
                    </div>
                    <div className="p-4 bg-black/20 rounded-lg">
                        <h5 className="font-bold text-blue-400 mb-1">Trade Count</h5>
                         <p className="text-xs text-gray-500">Is N {'>'} 100? (Statistical Significance)</p>
                    </div>
                </div>
             </div>
        </div>
    );
};

const LessonStrategicRoadmap = () => {
  const [activePhase, setActivePhase] = useState(null);

  const phases = [
    {
      id: 1,
      title: "Phase 1: Foundations",
      time: "Months 1-3",
      color: "blue",
      focus: "The Core Competence",
      items: [
        { label: "Math (The Truth)", desc: "Probability & Statistics (YES) vs Stochastic Calculus (NO)", status: "critical" },
        { label: "Tech Stack", desc: "Master Retail Languages: Python MQL5 (MetaTrader) or PineScript (TradingView).", status: "critical" },
        { label: "Reading", desc: "'Trading Systems' by Urban Jaekle. 'Python for Data Analysis'.", status: "recommended" }
      ],
      reality: "Institutional Quants need Calculus for Pricing. You need Statistics for Directional Alpha. Do not confuse the two."
    },
    {
      id: 2,
      title: "Phase 2: Data & APIs",
      time: "Months 4-6",
      color: "purple",
      focus: "The Infrastructure",
      items: [
        { label: "Data Engineering", desc: "Learn Polars (Rust-based DF) for speed. Build a local SQLite DB.", status: "critical" },
        { label: "Connectivity", desc: "Standard Broker Feeds (MT5) or Crypto Exchange APIs. No Bloomberg needed.", status: "critical" },
        { label: "The Edge", desc: "Emerging Markets & Inefficiencies. Don't fight HFTs on price.", status: "insight" }
      ],
      reality: "You are a Data Engineer first, Trader second. Garbage Data = Garbage Backtest."
    },
    {
      id: 3,
      title: "Phase 3: Strategy & Sim",
      time: "Months 7-12",
      color: "green",
      focus: "The Laboratory",
      items: [
        { label: "Backtesting", desc: "Event-Driven Frameworks (Backtrader/Lean). Avoid Vector-only sims.", status: "critical" },
        { label: "Robustness", desc: "Walk-Forward Analysis. Detect Overfitting. Kill bad ideas fast.", status: "critical" },
        { label: "Risk Math", desc: "Kelly Criterion Sizing. Volatility Targeting.", status: "critical" }
      ],
      reality: "A backtest is a lie until proven otherwise. Optimizing for 'Net Profit' is a rookie mistake. Optimize for Stability."
    },
    {
      id: 4,
      title: "Phase 4: Execution",
      time: "Year 2+",
      color: "orange",
      focus: "The Business",
      items: [
        { label: "Deployment", desc: "Windows VPS (Contabo/AWS). Retail platforms like MT5 are Windows-native.", status: "advanced" },
        { label: "Optimization", desc: "Rust for execution latency. Machine Learning (Scikit-learn) for filters.", status: "advanced" },
        { label: "Scale", desc: "Multi-Strategy Portfolio. Uncorrelated Assets.", status: "advanced" }
      ],
      reality: "Trading from a laptop is a hobby. Trading from a server is a business. 99% uptime is the requirement."
    }
  ];

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-8 rounded-2xl border border-white/10 text-center">
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mb-4">
          The Retail Quantitative Roadmap
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          A strategic path from <span className="text-white">Speculator</span> to <span className="text-white">Systematic Architect</span>. 
          Focus on what matters, ignore the institutional noise.
        </p>
      </div>

      <div className="space-y-4 relative">
        {/* Connection Line */}
        <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-orange-500 hidden md:block"></div>

        {phases.map((phase, index) => (
          <motion.div
            key={phase.id}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`relative pl-0 md:pl-20 transition-all duration-300 ${activePhase === phase.id ? 'scale-105 z-10' : 'hover:opacity-100 opacity-90'}`}
            onClick={() => setActivePhase(activePhase === phase.id ? null : phase.id)}
          >
            {/* Node Dot */}
            <div className={`hidden md:flex absolute left-6 top-8 w-4 h-4 rounded-full border-2 border-${phase.color}-500 bg-gray-900 z-10 items-center justify-center`}>
               <div className={`w-2 h-2 rounded-full bg-${phase.color}-500`}></div>
            </div>

            <div className={`cursor-pointer bg-gray-900/80 border ${activePhase === phase.id ? `border-${phase.color}-500 ring-1 ring-${phase.color}-500/50` : 'border-white/10'} p-6 rounded-xl hover:border-${phase.color}-500/30 transition-all`}>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-2">
                <div>
                   <span className={`text-xs font-bold uppercase tracking-wider text-${phase.color}-400 mb-1 block`}>{phase.time}</span>
                   <h3 className="text-xl font-bold text-white flex items-center gap-2">
                     {phase.title}
                     {activePhase === phase.id ? <ChevronLeft className="rotate-90 text-gray-500" size={20}/> : <ChevronRight className="text-gray-500" size={20}/>}
                   </h3>
                </div>
                <div className={`px-3 py-1 rounded-full bg-${phase.color}-900/30 border border-${phase.color}-500/30 text-${phase.color}-400 text-xs font-bold`}>
                  {phase.focus}
                </div>
              </div>

              {/* Collapsible Content */}
              <AnimatePresence>
                {activePhase === phase.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 border-t border-white/5 space-y-4">
                       <div className="p-4 bg-white/5 rounded-lg italic text-gray-300 border-l-2 border-white/20">
                          <span className="text-white font-bold not-italic mr-2">Retail Reality:</span>
                          "{phase.reality}"
                       </div>
                       
                       <div className="grid grid-cols-1 gap-3">
                          {phase.items.map((item, i) => (
                             <div key={i} className="flex flex-col p-3 rounded bg-black/20 hover:bg-black/40 transition-colors">
                                <div className="flex items-center justify-between mb-1">
                                   <span className="font-bold text-gray-200 text-sm">{item.label}</span>
                                   {item.status === 'critical' && <span className="text-[10px] text-red-400 bg-red-900/20 px-2 py-0.5 rounded uppercase">Critical</span>}
                                   {item.status === 'advanced' && <span className="text-[10px] text-orange-400 bg-orange-900/20 px-2 py-0.5 rounded uppercase">Advanced</span>}
                                </div>
                                <span className="text-xs text-gray-400">{item.desc}</span>
                             </div>
                          ))}
                       </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {!activePhase && (
                  <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                      {phase.items.map(i => i.label).join(" • ")}
                  </p>
              )}
            </div>
          </motion.div>
        ))}
      </div>
      
      <div className="text-center pt-8">
        <p className="text-gray-500 text-sm italic">Click on any phase to expand the details.</p>
      </div>
    </div>
  );
};

// Visual Components for Module 6.5

const LatencyVisual = () => (
  <svg viewBox="0 0 800 300" className="w-full h-full bg-gray-900 rounded-xl border border-white/10">
    <defs>
      <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#ef4444" stopOpacity="0.2" />
        <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#22c55e" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
      </linearGradient>
      <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="3" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>

    {/* Background Grid */}
    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
    </pattern>
    <rect width="800" height="300" fill="url(#grid)" />

    {/* Scenario A: Retail */}
    <g transform="translate(50, 80)">
      <text x="0" y="-20" fill="#9ca3af" fontSize="14" fontWeight="bold">RETAIL TRADER (Chicago)</text>
      <text x="600" y="-20" fill="#9ca3af" fontSize="14" fontWeight="bold" textAnchor="end">NYSE (New Jersey)</text>
      
      {/* Path */}
      <path d="M 50 20 C 150 20, 150 60, 250 60 S 350 20, 450 20 S 550 60, 600 20" 
            fill="none" stroke="#ef4444" strokeWidth="2" strokeOpacity="0.3" strokeDasharray="5,5" />
      
      {/* Moving Packet */}
      <circle r="4" fill="#ef4444" filter="url(#glow)">
        <animateMotion dur="3s" repeatCount="indefinite"
          path="M 50 20 C 150 20, 150 60, 250 60 S 350 20, 450 20 S 550 60, 600 20" />
      </circle>

      {/* Latency Label */}
      <rect x="280" y="30" width="80" height="24" rx="4" fill="#1f2937" stroke="#ef4444" strokeWidth="1" />
      <text x="320" y="47" fill="#ef4444" fontSize="12" textAnchor="middle" fontWeight="bold">50ms</text>
    </g>

    {/* Scenario B: HFT */}
    <g transform="translate(50, 200)">
      <text x="0" y="-20" fill="#9ca3af" fontSize="14" fontWeight="bold">HFT SERVER (Colo)</text>
      <text x="600" y="-20" fill="#9ca3af" fontSize="14" fontWeight="bold" textAnchor="end">NYSE (Matching Engine)</text>
      
      {/* Path */}
      <line x1="50" y1="20" x2="600" y2="20" stroke="#22c55e" strokeWidth="2" strokeOpacity="0.3" />
      
      {/* Moving Packet - Super Fast */}
      <circle r="4" fill="#22c55e" filter="url(#glow)">
        <animateMotion dur="0.1s" repeatCount="indefinite" path="M 50 20 L 600 20" />
      </circle>

      {/* Latency Label */}
      <rect x="280" y="35" width="80" height="24" rx="4" fill="#1f2937" stroke="#22c55e" strokeWidth="1" />
      <text x="320" y="52" fill="#22c55e" fontSize="12" textAnchor="middle" fontWeight="bold">0.001ms</text>
    </g>

    {/* Distance Scale Visual */}
    <g transform="translate(700, 50)">
        <line x1="0" y1="0" x2="0" y2="200" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="4,4" />
        <text x="10" y="100" fill="rgba(255,255,255,0.3)" fontSize="12" transform="rotate(90, 10, 100)">PHYSICAL DISTANCE</text>
    </g>
  </svg>
);

const CryptoTriangleVisual = () => (
  <svg viewBox="0 0 600 400" className="w-full h-full bg-gray-900 rounded-xl border border-white/10">
    <defs>
      <marker id="arrow" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto" markerUnits="strokeWidth">
        <path d="M0,0 L0,6 L9,3 z" fill="#22c55e" />
      </marker>
       <filter id="neon" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
        <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>

    {/* Center Profit Zone */}
    <g transform="translate(300, 200)">
        <circle r="60" fill="rgba(34, 197, 94, 0.1)" stroke="#22c55e" strokeWidth="1" strokeDasharray="4,4">
            <animate attributeName="r" values="60;65;60" dur="3s" repeatCount="indefinite" />
            <animate attributeName="stroke-opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite" />
        </circle>
        <text x="0" y="-5" fill="#22c55e" fontSize="16" fontWeight="bold" textAnchor="middle">PROFIT</text>
        <text x="0" y="15" fill="#22c55e" fontSize="12" textAnchor="middle">+0.5%</text>
    </g>

    {/* Node: USDT (Top) */}
    <g transform="translate(300, 80)">
        <circle r="30" fill="#262626" stroke="#22c55e" strokeWidth="2" filter="url(#neon)"/>
        <text x="0" y="5" fill="white" textAnchor="middle" fontWeight="bold">USDT</text>
    </g>

    {/* Node: BTC (Bottom Left) */}
    <g transform="translate(150, 300)">
        <circle r="30" fill="#262626" stroke="#f59e0b" strokeWidth="2"/>
        <text x="0" y="5" fill="white" textAnchor="middle" fontWeight="bold">BTC</text>
    </g>

    {/* Node: ETH (Bottom Right) */}
    <g transform="translate(450, 300)">
        <circle r="30" fill="#262626" stroke="#6366f1" strokeWidth="2"/>
        <text x="0" y="5" fill="white" textAnchor="middle" fontWeight="bold">ETH</text>
    </g>

    {/* Arrows & Flow */}
    {/* USDT -> BTC */}
    <path d="M 275 95 Q 150 150 150 265" fill="none" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrow)" strokeDasharray="5,5">
        <animate attributeName="stroke-dashoffset" from="100" to="0" dur="2s" repeatCount="indefinite" />
    </path>
    <text x="180" y="180" fill="#9ca3af" fontSize="10">Buy BTC</text>

    {/* BTC -> ETH */}
    <path d="M 185 300 L 415 300" fill="none" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrow)" strokeDasharray="5,5">
        <animate attributeName="stroke-dashoffset" from="100" to="0" dur="2s" repeatCount="indefinite" />
    </path>
    <text x="300" y="320" fill="#9ca3af" fontSize="10" textAnchor="middle">Trade BTC/ETH</text>


    {/* ETH -> USDT */}
    <path d="M 450 265 Q 450 150 325 95" fill="none" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrow)" strokeDasharray="5,5">
        <animate attributeName="stroke-dashoffset" from="100" to="0" dur="2s" repeatCount="indefinite" />
    </path>
    <text x="420" y="180" fill="#9ca3af" fontSize="10">Sell ETH</text>

  </svg>
);
const LessonStrategyMyth = () => {
  const [latencyTest] = React.useState(null);
  
  return (
    <div className="space-y-12 max-w-5xl mx-auto p-6 pb-24">
      {/* Header */}
      <div className="mb-12">
        <div className="flex items-center gap-2 mb-2 text-orange-400">
          <Zap size={20} />
          <span className="font-mono text-xs tracking-wider uppercase">Module M.1</span>
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">The Speed Game (HFT Myths & Retail Reality)</h1>
        <p className="text-xl text-gray-300 leading-relaxed">
          Distinguish between <em>Institutional HFT</em> (Physics) and <em>Retail Algo Trading</em> (Logic). 
          Learn where the "Speed Edge" is impossible (Forex/Stocks) and where it is accessible (Crypto).
        </p>
      </div>

      {/* Part 1: What HFT Actually Is */}
      <section className="space-y-6">
        <h2 className="text-3xl font-bold text-white flex items-center gap-3">
          <div className="bg-red-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">1</div>
          What HFT Actually Is (The Institutional Game)
        </h2>
        
        <div className="bg-gradient-to-br from-red-900/30 to-orange-900/30 p-8 rounded-2xl border border-red-500/30">
          <p className="text-xl text-gray-200 italic mb-4">High-Frequency Trading is not a strategy; it is an arms race of physics.</p>
        </div>

        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-white">The Reality of True HFT</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-black/40 p-6 rounded-xl border border-purple-500/20">
              <div className="flex items-center gap-3 mb-3">
                <Zap className="text-purple-400" size={24} />
                <h4 className="font-bold text-white text-lg">Speed</h4>
              </div>
              <p className="text-gray-300 text-sm">Trades execute in <strong className="text-purple-400">microseconds (μs)</strong> or <strong className="text-purple-400">nanoseconds (ns)</strong>.</p>
              <div className="mt-2 p-3 bg-purple-900/20 rounded font-mono text-xs text-purple-300">
                1 microsecond = 0.000001 seconds
              </div>
            </div>

            <div className="bg-black/40 p-6 rounded-xl border border-blue-500/20">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="text-blue-400" size={24} />
                <h4 className="font-bold text-white text-lg">Holding Time</h4>
              </div>
              <p className="text-gray-300 text-sm">An HFT firm might hold a position for only <strong className="text-blue-400">2 seconds</strong>.</p>
            </div>

            <div className="bg-black/40 p-6 rounded-xl border border-green-500/20">
              <div className="flex items-center gap-3 mb-3">
                <Radio className="text-green-400" size={24} />
                <h4 className="font-bold text-white text-lg">Infrastructure</h4>
              </div>
              <p className="text-gray-300 text-sm">They do not use the Internet. They use <strong className="text-green-400">Microwave Towers</strong> because light travels faster through air than through fiber-optic cables.</p>
            </div>

            <div className="bg-black/40 p-6 rounded-xl border border-yellow-500/20">
              <div className="flex items-center gap-3 mb-3">
                <Server className="text-yellow-400" size={24} />
                <h4 className="font-bold text-white text-lg">Colocation</h4>
              </div>
              <p className="text-gray-300 text-sm">Their servers are physically <strong className="text-yellow-400">bolted inside the Exchange's data center</strong>.</p>
              <p className="text-xs text-gray-500 mt-2">Cost: ~$50,000/month for rack space alone.</p>
            </div>
          </div>
        </div>

        {/* Latency Comparison Visual */}
        <div className="bg-gray-900/50 p-8 rounded-2xl border border-white/10">
          <h4 className="text-xl font-bold text-white mb-6 text-center">The Physical Distance Matters</h4>
          <div className="mb-6">
            <LatencyVisual />
          </div>
          <p className="text-center text-gray-400 text-sm italic">
            This image shows the physical distance: Retail Trader at home [500 miles away] vs. HFT Server [5 feet from the matching engine].
          </p>
        </div>
      </section>

      {/* Part 2: Why You Can't "HFT" in Forex or Stocks */}
      <section className="space-y-6 mt-16">
        <h2 className="text-3xl font-bold text-white flex items-center gap-3">
          <div className="bg-orange-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">2</div>
          Why You Can't "HFT" in Forex or Stocks
        </h2>

        <div className="bg-gradient-to-br from-red-900/20 to-rose-900/20 p-6 rounded-xl border border-red-500/20">
          <p className="text-lg text-gray-200 italic">
            If you are trading EURUSD or Apple Stock from home, you have already lost the speed war.
          </p>
        </div>

        {/* 1. The Latency Gap */}
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-white flex items-center gap-2">
            <span className="text-red-400">1.</span> The Latency Gap
          </h3>
          
          <p className="text-gray-300">Even with "Fiber Optic" internet, your order must travel through:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {[
              { step: '1', label: 'Your Router', icon: Wifi },
              { step: '2', label: 'Your ISP', desc: '(Comcast/Starlink)', icon: Globe },
              { step: '3', label: 'Broker\'s Bridge', icon: Server },
              { step: '4', label: 'Liquidity Provider', icon: Database }
            ].map((item, i) => (
              <div key={i} className="bg-black/50 p-4 rounded-lg border border-white/5 text-center">
                <div className="flex justify-center mb-2">
                  <item.icon className="text-blue-400" size={24} />
                </div>
                <div className="font-bold text-white text-sm">{item.step}. {item.label}</div>
                {item.desc && <div className="text-xs text-gray-500 mt-1">{item.desc}</div>}
              </div>
            ))}
          </div>

          <div className="bg-red-900/20 p-6 rounded-xl border border-red-500/30 mt-4">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Your Best Case:</p>
                <p className="text-3xl font-bold text-red-400">50ms latency</p>
              </div>
              <div>
                <p className="text-sm text-gray-400 mb-1">HFT Speed:</p>
                <p className="text-3xl font-bold text-green-400">0.001ms latency</p>
              </div>
            </div>
            <div className="border-t border-white/10 pt-4">
              <p className="text-lg font-bold text-white mb-2">Analogy:</p>
              <p className="text-gray-300">You are trying to race a Formula 1 car <em>on foot</em>. It doesn't matter how fast you run; you are playing a different sport.</p>
            </div>
          </div>
        </div>

        {/* 2. The "Toxic Flow" Trap */}
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-white flex items-center gap-2">
            <span className="text-red-400">2.</span> The "Toxic Flow" Trap (Latency Arbitrage)
          </h3>
          
          <div className="bg-gradient-to-br from-red-900/20 to-yellow-900/20 p-6 rounded-xl border border-red-500/20">
            <p className="text-lg text-gray-200 italic mb-4">The most common "HFT Scam" sold to retail traders.</p>
            
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-3">
                <div className="px-3 py-1 bg-blue-900/50 text-blue-400 rounded font-bold text-sm shrink-0 w-full sm:w-auto text-center sm:text-left">The Pitch</div>
                <p className="text-gray-300">"Our bot sees the price on a fast feed (LMAX) and buys on a slow broker before they update."</p>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-3">
                <div className="px-3 py-1 bg-yellow-900/50 text-yellow-400 rounded font-bold text-sm shrink-0 w-full sm:w-auto text-center sm:text-left">The Reality</div>
                <p className="text-gray-300">This is called <strong className="text-yellow-400">Latency Arbitrage</strong>.</p>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-3">
                <div className="px-3 py-1 bg-red-900/50 text-red-400 rounded font-bold text-sm shrink-0 w-full sm:w-auto text-center sm:text-left">The Consequence</div>
                <div className="text-gray-300">
                  <p className="mb-2">Brokers use "Virtual Dealer Plugins." If they catch you doing this (and they will), they will:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Reject your orders (Requotes)</li>
                    <li>Add 2 seconds of "artificial delay" to your account</li>
                    <li><strong className="text-red-400">Ban you</strong> and deny your prop firm payout</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-black/40 p-4 rounded-lg border-l-4 border-red-500">
            <p className="text-gray-300 text-sm">
              <strong className="text-red-400">Warning:</strong> Latency arbitrage is explicitly banned in most broker Terms of Service and will result in account termination.
            </p>
          </div>
        </div>
      </section>

      {/* Part 3: The Exception - Crypto */}
      <section className="space-y-6 mt-16">
        <h2 className="text-3xl font-bold text-white flex items-center gap-3">
          <div className="bg-green-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">3</div>
          The Exception – Crypto (The "Wild West" of Speed)
        </h2>

        <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 p-6 rounded-xl border border-green-500/30">
          <p className="text-xl text-gray-200 italic">
            Here is where the playing field is leveled. Retail traders <strong className="text-green-400">CAN</strong> compete here.
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-white">Why Crypto is Different</h3>
          
          <p className="text-gray-300 text-lg">
            Unlike the NYSE (which is a physical building in New Jersey), Crypto exchanges like <strong className="text-blue-400">Binance</strong> or <strong className="text-purple-400">Bybit</strong> are cloud-based.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-black/40 p-6 rounded-xl border border-blue-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="text-blue-400" size={20} />
                <h4 className="font-bold text-white">The Secret</h4>
              </div>
              <p className="text-gray-300 text-sm">
                Binance's servers are likely hosted on <strong className="text-blue-400">AWS (Tokyo Region)</strong>.
              </p>
            </div>

            <div className="bg-black/40 p-6 rounded-xl border border-green-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Server className="text-green-400" size={20} />
                <h4 className="font-bold text-white">Your Move</h4>
              </div>
              <p className="text-gray-300 text-sm">
                If you rent an AWS server in the <strong className="text-green-400">same Tokyo region</strong>, you are technically "Co-located." 
                You can achieve <strong className="text-green-400">&lt;5ms latency</strong> for <strong>$20/month</strong>.
              </p>
            </div>
          </div>
        </div>

        {/* Valid Retail Speed Strategies */}
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-white">Valid Retail Speed Strategies (Crypto Only)</h3>

          {/* A. Triangular Arbitrage */}
          <div className="bg-gradient-to-br from-green-900/20 to-cyan-900/20 p-6 rounded-xl border border-green-500/30">
            <h4 className="text-xl font-bold text-green-400 mb-4 flex items-center gap-2">
              <RefreshCw size={20} />
              A. Triangular Arbitrage
            </h4>
            
            <div className="space-y-4">
              <div className="grid md:grid-cols-3 gap-3 text-sm">
                <div className="bg-black/30 p-3 rounded">
                  <div className="text-gray-400 mb-1">Logic:</div>
                  <div className="font-mono text-cyan-400">USDT → BTC → ETH → USDT</div>
                </div>
                <div className="bg-black/30 p-3 rounded">
                  <div className="text-gray-400 mb-1">How:</div>
                  <div className="text-gray-300">Cross-rates misalign for 1-2 seconds</div>
                </div>
                <div className="bg-black/30 p-3 rounded">
                  <div className="text-gray-400 mb-1">Viability:</div>
                  <div className="text-gray-300">Possible for retail with <strong className="text-green-400">Python/C++</strong></div>
                </div>
              </div>

              <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10">
                <div className="mb-4">
                  <CryptoTriangleVisual />
                </div>
                <p className="text-center text-gray-400 text-sm italic">
                  Simple triangle diagram showing three currency pairs and how profit is captured in the loop.
                </p>
              </div>

              <div className="bg-yellow-900/20 p-4 rounded-lg border-l-4 border-yellow-500">
                <p className="text-gray-300 text-sm">
                  <strong className="text-yellow-400">Note:</strong> MQL5 is too slow for this. You need <strong>Python/C++</strong> with a direct <strong>WebSocket connection</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* B. DeFi Market Making */}
          <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 p-6 rounded-xl border border-purple-500/30">
            <h4 className="text-xl font-bold text-purple-400 mb-4 flex items-center gap-2">
              <DollarSign size={20} />
              B. DeFi Market Making (The Yield Game)
            </h4>
            
            <div className="space-y-4">
              <div className="grid md:grid-cols-3 gap-3 text-sm">
                <div className="bg-black/30 p-3 rounded">
                  <div className="text-gray-400 mb-1">The Strategy:</div>
                  <div className="text-gray-300">You don't need to be fast; you need to be <strong className="text-purple-400">smart</strong>.</div>
                </div>
                <div className="bg-black/30 p-3 rounded">
                  <div className="text-gray-400 mb-1">How:</div>
                  <div className="text-gray-300">Provide liquidity to pools (e.g., Uniswap USDC/ETH)</div>
                </div>
                <div className="bg-black/30 p-3 rounded">
                  <div className="text-gray-400 mb-1">Retail Edge:</div>
                  <div className="text-gray-300">You're the <strong className="text-purple-400">"House"</strong>. Earn fees on every trade.</div>
                </div>
              </div>

              <div className="bg-purple-900/20 p-4 rounded-lg border-l-4 border-purple-500">
                <p className="text-gray-300 text-sm">
                  <strong className="text-purple-400">Note:</strong> This is not "HFT" in speed, but it is <strong>Automated Market Making (AMM)</strong>, which is the retail equivalent of what banks do.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Part 4: The Reality Check */}
      <section className="space-y-6 mt-16">
        <h2 className="text-3xl font-bold text-white flex items-center gap-3">
          <div className="bg-blue-600 w-10 h-10 rounded-full flex items-center justify-center text-lg">4</div>
          The Reality Check (Where Your Edge Is)
        </h2>

        <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 p-8 rounded-2xl border border-blue-500/30 text-center">
          <p className="text-2xl font-bold text-white mb-2">
            Don't chase Speed (Microseconds). Chase Structure (Logic).
          </p>
        </div>

        {/* Comparison Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-white/20">
                <th className="p-4 text-left text-gray-400 font-bold">Feature</th>
                <th className="p-4 text-left text-red-400 font-bold">Institutional HFT</th>
                <th className="p-4 text-left text-green-400 font-bold">Retail Quant (You)</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <tr className="border-b border-white/10">
                <td className="p-4 font-semibold">Edge Source</td>
                <td className="p-4">Speed (Hardware)</td>
                <td className="p-4"><strong className="text-green-400">Statistics (Math)</strong></td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="p-4 font-semibold">Timeframe</td>
                <td className="p-4">&lt; 1 Second</td>
                <td className="p-4"><strong className="text-green-400">M15 to H4</strong></td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="p-4 font-semibold">Profit per Trade</td>
                <td className="p-4">$0.0001</td>
                <td className="p-4"><strong className="text-green-400">$50 - $500</strong></td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="p-4 font-semibold">Competition</td>
                <td className="p-4">Goldman Sachs</td>
                <td className="p-4"><strong className="text-green-400">Other Retail Traders</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* The KSQuant Verdict */}
        <div className="bg-black/40 p-8 rounded-2xl border border-blue-500/30">
          <h3 className="text-2xl font-bold text-blue-400 mb-6 flex items-center gap-2">
            <Shield size={24} />
            The KSQuant Verdict
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white font-bold">1</div>
              <div>
                <p className="text-gray-200">
                  <strong className="text-white">Forex/Stocks:</strong> Forget HFT. Focus on <strong className="text-blue-400">Statistical Edges</strong> (Mean Reversion, Trend Following, News Bias) on <strong className="text-blue-400">M15+ timeframes</strong>.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="shrink-0 w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white font-bold">2</div>
              <div>
                <p className="text-gray-200">
                  <strong className="text-white">Crypto:</strong> If you are an advanced coder (<strong className="text-green-400">C++/Rust</strong>), you <em>can</em> attempt high-frequency arbitrage by renting cloud servers near the exchange.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="shrink-0 w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">3</div>
              <div>
                <p className="text-gray-200">
                  <strong className="text-white">The Bottom Line:</strong> Being "Fast" is expensive. Being "Smart" is free. <strong className="text-blue-400">We teach you to be Smart.</strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actionable Next Step */}
        <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 p-8 rounded-2xl border border-cyan-500/30">
          <h3 className="text-xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
            <Terminal size={24} />
            Actionable Next Step for Module M.1
          </h3>
          
          <div className="bg-black/50 p-6 rounded-xl border border-white/10">
            <p className="text-gray-300 mb-4">
              "Open your command prompt. We are going to ping the Binance API server to check your latency. 
              If it's over 100ms, you are a <strong className="text-yellow-400">Swing Trader</strong>. 
              If we can get it under 10ms with a VPS, you can start looking at <strong className="text-green-400">scalping</strong>."
            </p>
            
            <div className="flex gap-3 items-start">
              <div className="bg-gray-900 p-4 rounded-lg border border-white/10 flex-1 space-y-3">
                <div>
                   <div className="text-xs text-gray-500 mb-1">Windows:</div>
                   <code className="text-cyan-400 font-mono text-sm block">ping api.binance.com -n 10</code>
                </div>
                <div>
                   <div className="text-xs text-gray-500 mb-1">Mac/Linux:</div>
                   <code className="text-cyan-400 font-mono text-sm block">ping -c 10 api.binance.com</code>
                </div>
              </div>
              
              
              {latencyTest !== null && (
                <div className={`p-4 rounded-lg border ${latencyTest < 100 ? 'bg-green-900/20 border-green-500/30' : 'bg-yellow-900/20 border-yellow-500/30'}`}>
                  <div className="text-xs text-gray-400 mb-1">Your Result:</div>
                  <div className={`text-2xl font-bold ${latencyTest < 100 ? 'text-green-400' : 'text-yellow-400'}`}>
                    {latencyTest}ms
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    {latencyTest < 100 ? 'Consider scalping strategies' : 'Focus on swing trading'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Lesson 1.14: The Graduation (Next Steps)

const LessonGraduation = ({ navigate }) => (
    <div className="space-y-8 max-w-4xl mx-auto p-6 pb-24">
      <div className="mb-8 text-center">
        <div className="inline-block p-4 bg-yellow-500/20 rounded-full mb-4 border border-yellow-500/50">
           <Zap size={40} className="text-yellow-400" />
        </div>
        <h1 className="text-5xl font-bold text-white mb-4">Congratulations, Quant.</h1>
        <p className="text-2xl text-gray-400">You have built the Engine. Now you need the Racetrack.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
         {/* What they have */}
         <div className="bg-gray-900/50 p-6 rounded-xl border border-white/10 opacity-70">
            <h2 className="text-xl font-bold text-gray-400 mb-4">✅ What You Have Built</h2>
            <ul className="space-y-3">
                <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="text-green-500" size={16} /> A Python Strategy Scanner
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="text-green-500" size={16} /> An MQL5 Execution Bot
                </li>
                <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="text-green-500" size={16} /> A Professional Workflow
                </li>
            </ul>
         </div>

         {/* What is missing - UPGRADED SALES COPY */}
         <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 p-6 rounded-xl border border-blue-500/50 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-2 bg-blue-600 text-white text-xs font-bold rounded-bl-lg">
                NEXT LEVEL
            </div>
            <h2 className="text-xl font-bold text-white mb-4">🚀 The "Hedge Fund" Standard</h2>
            <ul className="space-y-3">
                <li className="flex items-center gap-2 text-white">
                    <Cpu className="text-blue-400" size={16} /> 
                    <span><strong>AI-Powered Trading:</strong> Don't just scan. Predict with ML.</span>
                </li>
                <li className="flex items-center gap-2 text-white">
                    <Briefcase className="text-yellow-400" size={16} /> 
                    <span><strong>Fund Management:</strong> Manage Our Capital (Not just yours).</span>
                </li>
                <li className="flex items-center gap-2 text-white">
                    <TrendingUp className="text-green-400" size={16} /> 
                    <span><strong>Career Opportunity:</strong> Zero upfront cost to start earning.</span>
                </li>
            </ul>
         </div>
      </div>

      {/* The Offer */}
      <div className="bg-gray-900 p-8 rounded-2xl border border-white/10 text-center">
        <h3 className="text-2xl font-bold text-white mb-4">Don't Trade Alone. Trade With Us.</h3>
        <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            You have the code. Now get the <strong>Capital</strong> and the <strong>AI Edge</strong>. Join the ProTrader Cohort and start your career as a funded quant.
        </p>
        
        <div className="flex flex-col md:flex-row justify-center gap-4">
             <button 
                onClick={() => navigate('/courses/python-for-finance')}
                className="px-8 py-4 bg-white text-black font-bold text-lg rounded-lg hover:bg-gray-200 transition shadow-xl shadow-white/10 flex items-center justify-center gap-2"
             >
                Master AI & Get Funded <ArrowRight size={20} />
             </button>
        </div>
        <p className="mt-4 text-xs text-gray-500">Limited spots available for the next intake.</p>
      </div>

    </div>
);
// Main Component
const IntroCourse = () => {
  const navigate = useNavigate();
  // Default open on desktop (w > 768px), closed on mobile
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768); 
  
  // Use Persistence Hook
  const { progress, loading, updateLesson, updateChecklist } = useCourseProgress('retail_quant_launchpad');

  const lessons = [
    { id: '0.1', title: "The Arena", part: "Module 0: The Reality", icon: Target, component: LessonTheArena },
    { id: '0.2', title: "The Hierarchy", part: "Module 0: The Reality", icon: Layers, component: LessonTheHierarchy },
    { id: '0.3', title: "Market Mechanics", part: "Module 0: The Reality", icon: Activity, component: LessonMarketMechanics },
    { id: '0.4', title: "The Identity", part: "Module 0: The Reality", icon: Zap, component: LessonTheIdentity },
    { id: '0.5', title: "Strategy Archetypes", part: "Module 0: The Reality", icon: Layers, component: LessonStrategyArchetypes },
    { id: '0.6', title: "The Workflow", part: "Module 0: The Reality", icon: GitMerge, component: LessonQuantitativeWorkflow },
    { id: '0.7', title: "Tools of the Trade", part: "Module 0: The Reality", icon: Wrench, component: LessonToolsOfTheTrade },
    { id: '0.8', title: "Getting Started", part: "Module 0: The Reality", icon: Rocket, component: LessonGettingStarted },
    { id: '0.9', title: "Financial Markets & Access", part: "Module 0: The Reality", icon: Globe, component: LessonFinancialMarkets },
    { id: '1.1', title: "David vs. Goliath", part: "Part 1: The Mindset", icon: Target, component: LessonDavidGoliath },
    { id: '1.2', title: "The Casino Operator", part: "Part 1: The Mindset", icon: Zap, component: LessonCasinoMindset },
    { id: '1.3', title: "The 3 Stages of Automation", part: "Part 1: The Mindset", icon: TrendingUp, component: LessonAutomationStages },
    { id: '1.4', title: "The Quant Dictionary", part: "Part 2: The Vocabulary", icon: BookOpen, component: LessonQuantDictionary },
    { id: '1.5', title: "The Ecosystem", part: "Part 3: The Toolkit", icon: Layers, component: LessonEcosystem },
    { id: '1.6', title: "The Funding", part: "Part 3: The Toolkit", icon: DollarSign, component: LessonFunding },
    { id: '1.7', title: "Preparing Your Lab", part: "Part 4: The Setup", icon: Terminal, component: LessonSetup },
    { id: '1.7b', title: "Python vs. MQL5", part: "Part 4: The Setup", icon: BookOpen, component: LessonRosettaStone },
    { id: 'T.1', title: "The Anatomy of Price", part: "Systematic Trading Fundamentals", icon: BarChart2, component: LessonAnatomyOfPrice },
    { id: 'T.2', title: "Market Microstructure", part: "Systematic Trading Fundamentals", icon: Layers, component: LessonMarketMicrostructure },
    { id: 'T.3', title: "Trend Logic", part: "Systematic Trading Fundamentals", icon: TrendingUp, component: LessonTrendLogic },
    { id: 'T.4', title: "Indicators are just Math", part: "Systematic Trading Fundamentals", icon: Activity, component: LessonIndicatorsMath },
    { id: 'T.5', title: "Entry Triggers vs. Filters", part: "Systematic Trading Fundamentals", icon: Filter, component: LessonFiltersTriggers },
    { id: 'T.6', title: "Market Phases (30/70)", part: "Systematic Trading Fundamentals", icon: Activity, component: LessonMarketPhases },
    { id: 'T.7', title: "The Hybrid Model", part: "Systematic Trading Fundamentals", icon: Layers, component: LessonHybridModel },
    { id: 'T.8', title: "The Exit Protocol", part: "Systematic Trading Fundamentals", icon: Shield, component: LessonExitProtocol },
    { id: 'T.9', title: "The Money Math", part: "Systematic Trading Fundamentals", icon: DollarSign, component: LessonMoneyMath },
    { id: 'T.10', title: "The Quant’s Constitution", part: "Systematic Trading Fundamentals", icon: Shield, component: LessonQuantConstitution },
    { id: 'T.11', title: "The Quant's Scorecard", part: "Systematic Trading Fundamentals", icon: Activity, component: LessonQuantScorecard },
    { id: 'T.12', title: "Strategic Roadmap", part: "Systematic Trading Fundamentals", icon: Map, component: LessonStrategicRoadmap },
    { id: 'P.1', title: "The Master Template", part: "Part 2: The Application", icon: Terminal, component: LessonMasterTemplate },
    { id: '1.8', title: "Decoding Retail Patterns", part: "Part 2: The Application", icon: Search, component: LessonRetailPatterns },
    { id: '1.9', title: "Coding the Scanner", part: "Part 2: The Application", icon: Code, component: LessonPatternScanner },
    { id: '1.10', title: "Backtesting the Discovery", part: "Part 2: The Application", icon: Target, component: LessonBacktesting },
    { id: '1.11', title: "The Smart Trader", part: "Part 2: The Application", icon: Filter, component: LessonSmartTrader },
    { id: '1.12', title: "The Freelancer's Goldmine", part: "Part 2: The Application", icon: DollarSign, component: LessonSMC },
    { id: '1.13', title: "Capstone Project", part: "Part 2: The Application", icon: Target, component: LessonCapstone },
    
    // --- ADVANCED MODULES ---
    { id: '2.1', title: "OrderSend Mastery", part: "Module 2: Execution Engine", icon: Shield, component: LessonOrderWrapper },
    { id: '2.2', title: "Event Architecture", part: "Module 2: Execution Engine", icon: Zap, component: LessonEvents },
    { id: '2.3', title: "Project: Trade Manager", part: "Module 2: Execution Engine", icon: Terminal, component: LessonTradeManager },
    { id: '3.1', title: "Vectorized Backtesting", part: "Module 3: Research Lab", icon: FastForward, component: LessonVectorTesting },
    { id: '3.2', title: "The Quant's Filter", part: "Module 3: Research Lab", icon: Activity, component: LessonStationarity },
    { id: '3.3', title: "Project: Strategy Validator", part: "Module 3: Research Lab", icon: Target, component: LessonStrategyValidator },
    { id: '4.1', title: "Sentiment Analysis", part: "Module 4: AI Edge", icon: Globe, component: LessonSentiment },
    { id: '4.2', title: "LLMs in Trading", part: "Module 4: AI Edge", icon: Cpu, component: LessonLLMs },
    { id: '4.3', title: "Project: AI Dashboard", part: "Module 4: AI Edge", icon: Target, component: LessonAIDashboard },
    
    // --- SPECIAL MODULES ---
    { id: 'M.1', title: "Strategy & Myth", part: "Series M: Myths & Realities", icon: Zap, component: LessonStrategyMyth },
    
    { id: '1.14', title: "The Next Level", part: "Completion", icon: Zap, component: LessonGraduation },
  ];

  const goBack = () => navigate('/academy/dashboard');
  
  // Safe access to active lesson
  const activeIndex = progress.currentLessonIndex < lessons.length ? progress.currentLessonIndex : 0;
  
  // Prepare component props (inject props for specific lessons)
  const ActiveComponent = lessons[activeIndex].component;
  let componentProps = {};
  
  if(lessons[activeIndex].id === '1.7') {
    componentProps = { checklist: progress.checklist, updateChecklist };
  } else if(lessons[activeIndex].id === 'T.10') {
    componentProps = { checklist: progress.checklist, updateChecklist };
  } else if(['1.9', '1.10'].includes(lessons[activeIndex].id)) {
    componentProps = { updateLesson, lessons };
  } else if(lessons[activeIndex].id === '1.14') {
    componentProps = { navigate };
  }

  if (loading) {
     return <div className="h-screen bg-black flex items-center justify-center text-white">Loading course progress...</div>
  }

  return (
    <div className="flex h-screen bg-black text-white font-sans overflow-hidden">
      
      {/* Sidebar - Desktop & Wrapper */}
      <motion.div 
        className={`${sidebarOpen ? 'w-full md:w-80' : 'w-0'} bg-gray-900 border-r border-white/10 flex flex-col shrink-0 transition-all duration-300 absolute md:relative z-20 h-full overflow-hidden`}
        initial={false}
      >
        <div className="p-4 border-b border-white/10 flex justify-between items-center bg-gray-900/95 backdrop-blur">
          <div>
            <h2 className="font-bold text-lg text-white">Retail Quant</h2>
            <p className="text-xs text-blue-400 font-mono">LAUNCHPAD MODULE 1</p>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="md:hidden p-2 text-gray-400">
            <ChevronLeft />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Group lessons by Part */}
          {[...new Set(lessons.map(l => l.part))].map((part) => (
            <div key={part}>
              <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 px-2">{part}</h3>
              <div className="space-y-1">
                {lessons.map((lesson, index) => {
                  if (lesson.part !== part) return null;
                  const isActive = activeIndex === index;
                  const isCompleted = index < progress.maxLessonReachedIndex;

                  return (
                    <button
                      key={lesson.id}
                      onClick={() => { updateLesson(index); if(window.innerWidth < 768) setSidebarOpen(false); }}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                        isActive 
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' 
                          : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                      }`}
                    >
                      <div className={`p-1.5 rounded ${isActive ? 'bg-white/20' : 'bg-gray-800'}`}>
                        {React.createElement(lesson.icon, { size: 14 })}
                      </div>
                      <span className="text-sm font-medium flex-1">{lesson.id} {lesson.title}</span>
                      {isCompleted && !isActive && <CheckCircle size={12} className="text-green-500" />}
                      {isActive && <Play size={12} className="ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        
        <div className="p-4 border-t border-white/10">
          <button onClick={goBack} className="w-full flex items-center justify-center gap-2 py-3 text-sm text-gray-400 hover:text-white transition-colors">
            <ChevronLeft size={16} /> Returns to Dashboard
          </button>
        </div>
      </motion.div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-full relative bg-black/50 overflow-hidden">
        {/* Mobile Header Overlay */}
        {!sidebarOpen && (
          <div className="absolute top-4 left-4 z-10 md:hidden">
            <button onClick={() => setSidebarOpen(true)} className="p-2 bg-gray-800 rounded-lg text-white shadow-lg">
              <Menu size={24} />
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 md:p-12 relative">
          <div className="max-w-4xl mx-auto pb-20">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Header */}
              <div className="mb-8">
                <span className="text-blue-400 font-mono text-sm tracking-wider">{lessons[activeIndex].part}</span>
                <h1 className="text-3xl md:text-5xl font-bold mt-2 mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">
                  {lessons[activeIndex].id} {lessons[activeIndex].title}
                </h1>
                <div className="h-1 w-24 bg-blue-600 rounded-full"></div>
              </div>

              {/* Content Body */}
              <div className="min-h-[400px]">
                <ActiveComponent {...componentProps} />
              </div>

              {/* Footer Navigation */}
              <div className="flex justify-between mt-16 pt-8 border-t border-white/10">
               <button 
                  onClick={() => updateLesson(Math.max(0, activeIndex - 1))}
                  disabled={activeIndex === 0}
                  className="px-6 py-3 rounded-lg border border-white/10 text-gray-400 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <ChevronLeft size={20} /> Previous
                </button>
                
                <button 
                  onClick={() => updateLesson(Math.min(lessons.length - 1, activeIndex + 1))}
                  disabled={activeIndex === lessons.length - 1}
                  className="px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  Next Lesson <Play size={16} />
                </button>
              </div>

            </motion.div>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default IntroCourse;
